import { CommonModule } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, HostListener, OnInit, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgSelectModule } from '@ng-select/ng-select';
import type * as echarts from 'echarts';

import { DrillDownComponent } from '../../chart-components/drill-down/drill-down.component';
import { UnifiedTableMatrixChartComponent } from '../../chart-components/unified-table-matrix-chart/unified-table-matrix-chart.component';
import { DvmFileUploadComponent } from '../../chart-components/dvm-file-upload/dvm-file-upload.component';
import type { TableMatrixCapabilities } from '../../chart-components/unified-table-matrix-chart/unified-table-matrix-chart.types';
import { HorizontalBarChartComponent } from '../../chart-components/horizontal-bar-chart/horizontal-bar-chart.component';
import { UnifiedBarColumnChartComponent } from '../../chart-components/unified-bar-column-chart/unified-bar-column-chart.component';
import type { BarColumnCapabilities, BarColumnVariant, BarColumnOrientation } from '../../chart-components/unified-bar-column-chart/unified-bar-column-chart.types';
import { UnifiedScatterBubbleChartComponent } from '../../chart-components/unified-scatter-bubble-chart/unified-scatter-bubble-chart.component';
import type { ScatterBubbleCapabilities, ScatterBubbleVariant } from '../../chart-components/unified-scatter-bubble-chart/unified-scatter-bubble-chart.types';
import { UnifiedLineAreaChartComponent } from '../../chart-components/unified-line-area-chart/unified-line-area-chart.component';
import type { LineAreaCapabilities, LineAreaMeasure, LineAreaVariant } from '../../chart-components/unified-line-area-chart/unified-line-area-chart.types';
import { WaterfallChartComponent } from '../../chart-components/waterfall-chart/waterfall-chart.component';
import { MatSliderComponent } from '../../global-components/mat-slider/mat-slider.component';
import { InputBoxComponent } from '../../global-components/input-box/input-box.component';
import { GlobalDropdownComponent } from '../../global-components/global-dropdown/global-dropdown.component';
import { NumberStatsComponent } from '../../global-components/number-stats/number-stats.component';
import { CapitalCasePipe } from '../../pipes/capital-case.pipe';
import { SelfServiceUiService } from '../shared/self-service-ui.service';
import { SelfServiceConfigService } from '../shared/self-service-config.service';
import { SelfServiceDatasetService } from '../shared/self-service-dataset.service';
import { resolveSelfServiceApiUrl, buildSelfServiceApiUrl } from '../shared/self-service-api.utils';
import { buildBarComputeWidgetData, buildLineAreaComputeWidgetData, buildWaterfallOption, extractBarComputeRows, extractLineAreaComputeRows, recomputeEntryHasData, resolveRecomputeChartType } from '../shared/self-service-recompute.utils';
import { toSerializable } from '../shared/self-service-json.utils';
import { SelfServiceDashboardService } from '../shared/self-service-dashboard.service';
import { SelfServiceChartPersistenceService } from '../shared/self-service-chart-persistence.service';
import { ConfirmationDialogService } from '../../shared-services/confirmation-dialog.service';
import { firstValueFrom, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { KeycloakService } from 'keycloak-angular';
import { ActivatedRoute, Router } from '@angular/router';
import { BarVariantOption, BuilderLayoutConfig, CapabilityRegistryEntry, ChartCategory, FormatControl, FormatControlGroup, FormulaBuilderConfig, LabelValueOption, LayoutStepSection, LayoutStepsDefinition, ScatterBubbleModeOption, ScatterBubbleVariantOption, LineAreaVariantOption } from '../shared/self-service-config.types';
import { SavedSelfServiceDashboard, ComputedColumn, FormulaTemplate } from '../shared/self-service-types';
import { SelfServiceFormulaService } from '../shared/self-service-formula.service';
import { readDynamicDashboardCache, readJson, writeDynamicDashboardCache } from '../shared/self-service-storage.utils';

export type BridgeStepType = 'START' | 'CHANGE' | 'TOTAL';
export type BridgeSign = 'AUTO' | 'POSITIVE' | 'NEGATIVE';

/** One entry in the Table & Matrix "Table type" dropdown (from config
 *  `options.tableTypes`). `group` places it under a heading; `normal`/`editable`/
 *  `drilldown` are the render-dispatch flags read instead of comparing `value`. */
export interface TableTypeOption extends LabelValueOption {
  group?: string;
  hint?: string;
  normal?: boolean;
  editable?: boolean;
  drilldown?: boolean;
  [flag: string]: unknown;
}
type BuilderPane = 'preview' | 'steps' | 'data';
const PANE_DRAG_TYPE = 'application/x-dvm-pane';
const FIELD_DRAG_TYPE = 'application/x-dvm-field';
const TILE_DRAG_TYPE = 'application/x-dvm-tile';

/** One row in the sequential bridge builder. Every field is always present on
 *  the working object (simplifies template binding); `sanitizeStepForPayload`
 *  strips the fields that don't apply to the step's current type when the
 *  final configuration is generated. */
export interface BridgeStep {
  id: string;
  stepType: BridgeStepType;
  label: string;
  dataset: string;
  field: string;
  aggregation: string;
  sign: BridgeSign;
  formula: string;
  formulaMode?: boolean;
}

/** A typed-in reference for one bracket token used in a target's formula —
 *  tells the backend that token's live value comes from ANOTHER input's
 *  output (`inputTitle`) landing on a specific target/row (`targetLabel` /
 *  `rowLabel`), rather than a plain dataset column. Fully user-typed. */
export interface TargetFormulaReferenceEntry {
  token: string;
  inputTitle: string;
  targetLabel: string;
  rowLabel: string;
  /** Which WATERFALL bridge step this reference belongs to — undefined for a
   *  non-waterfall target's single formula (which has no steps at all). */
  step?: string;
}

/** Pre-calculated bar the analytics engine returns per bridge step. */
export interface WaterfallBar {
  label: string;
  delta: number;
  bottom: number;
  top: number;
  type: string;
  /** The step's configured "Direction" — a display-only color hint. Never
   *  used to derive delta/bottom/top, only which bar color to paint. */
  colorSign?: string;
}

/** One hierarchy level in the Drill-Down Table's dimension list — e.g. Level 1
 *  = "Type Grouping", Level 2 = "Option Code". Unlike a bridge step, a
 *  dimension carries no aggregation of its own; it only groups. All
 *  dimensions (and measures) share the ONE dataset picked for the table. */
export interface DrillDownDimension {
  id: string;
  field: string;
  label: string;
  /** OPTIONAL fixed display order for this level's values, as the user typed it
   *  (comma or newline separated). Emitted to the payload as `valueOrder: []`
   *  only when filled in — omitted entirely otherwise, so the backend keeps its
   *  default ordering. Use when a level is categorical and its natural order
   *  isn't alphabetical, e.g. "Base Options, Additional Options, Routing Cost". */
  valueOrder?: string;
}

/** One column of a Normal/Editable table. Unlike the drill-down levels (one
 *  shared dataset), EACH column carries its own dataset so a table can mix
 *  columns from different datasets — the compute payload sends every entry as
 *  `{dataset, field, displayName}` and the backend joins/returns flat rows.
 *  A blank `dataset` falls back to the table's primary dataset; a blank
 *  `displayName` falls back to a title-cased version of the field name.
 *
 *  `filterField`/`filterOperator`/`filterValue` are an OPTIONAL default filter
 *  attached to THIS column's row in the builder, applied on the table's very
 *  first data request, before any global filter is picked. `filterField`
 *  picks WHICH column the filter runs against — it defaults to this column's
 *  own `field` but can be pointed at any OTHER column (e.g. attach a
 *  "rule_categories = Certifications" filter to the Rule Name row even though
 *  `rule_categories` isn't itself a displayed column). A blank `filterValue`
 *  means no filter for this row — it's skipped entirely. */
export interface TmColumnDef {
  id: string;
  dataset: string;
  field: string;
  displayName: string;
  filterField: string;
  filterOperator: string;
  filterValue: string;
}

/** One value column shown (and rolled up) at every level of the drill-down
 *  tree — e.g. "DN Price" summed as the tree rolls up from leaf to root. */
export interface DrillDownMeasure {
  id: string;
  field: string;
  label: string;
  aggregation: string;
  rollUpStrategy: string;
  /** When true, the backend adds a dataset-wide grand-total row for this
   *  measure (ignoring all dimension grouping); when false, that measure is
   *  simply left out of the grand-total row (see DrillDownExecutionService /
   *  the drill-down table's sticky total row). */
  total: boolean;
}

/** One value column mapped in the Bar & Column builder. Multiple measures give
 *  "Multiple Measures"; `renderAs`/`axis` drive Line/Area combos + dual axis. A
 *  `series` split (on the layout, not per-measure) uses the FIRST measure. */
export interface BarBuilderMeasure {
  id: string;
  field: string;
  label: string;
  renderAs: 'bar' | 'line' | 'area';
  axis: 'y1' | 'y2';
}

/** One value column mapped in the Line & Area builder. Multiple measures give
 *  "Multi-Line"; `renderAs`/`axis` drive Line/Area combos + dual axis. A `series`
 *  split (on the layout, not per-measure) uses the FIRST measure. */
export interface LineAreaBuilderMeasure {
  id: string;
  field: string;
  label: string;
  renderAs: 'line' | 'area';
  axis: 'y1' | 'y2';
}

/**
 * CLIENT-SIDE cross-filter registry. Declares, per layoutType, HOW a tile's
 * render data + its unfiltered rows are stored on the DashboardTile, so the
 * cross-filter engine can publish/react WITHOUT knowing about any specific chart
 * type. A tile whose layoutType is listed here participates in cross-filtering.
 *
 * Adding a new client-side chart (Pie, Donut, Line, …) to cross-filter is then a
 * ONE-LINE registry entry — no changes to the cross-filter logic:
 *   PIE: { dataField: 'pieData', rowsField: 'pieRows' },
 *
 * `dataField`  — the DashboardTile key holding the widget `[data]` object (whose
 *                `staticData` is the rendered rows).
 * `rowsField`  — the DashboardTile key holding the ORIGINAL unfiltered rows
 *                (so a filter can be reversed). Defaults to `${dataField}Rows`.
 */
export interface ClientChartCrossFilterSpec {
  dataField: keyof DashboardTile;
  rowsField: keyof DashboardTile;
}

export const CLIENT_CHART_CROSS_FILTER: Record<string, ClientChartCrossFilterSpec> = {
  BAR: { dataField: 'barData', rowsField: 'barRows' },
  SCATTER_BUBBLE: { dataField: 'scatterBubbleData', rowsField: 'scatterBubbleRows' },
  LINE_AREA: { dataField: 'lineAreaData', rowsField: 'lineAreaRows' },
  // Future client-side charts drop in here — e.g.
  // PIE:  { dataField: 'pieData',  rowsField: 'pieRows'  },
  // LINE: { dataField: 'lineData', rowsField: 'lineRows' },
};

/** One chart/input placed on the dashboard preview canvas. Tiles flow into a
 *  fixed 12-column grid (order = array order) and never overlap; each tile spans
 *  `colSpan` columns and `rowSpan` row-units. Exactly one of the render payloads
 *  (previewOption / drillData / hbData / kpiData / inputControl) is populated per
 *  tile, chosen by `layoutType`. */
export interface DashboardTile {
  id: string;
  componentId: string;
  layoutType: string;
  title: string;
  /** Width in grid columns (1..DASH_COLS) and height in row-units. */
  colSpan: number;
  rowSpan: number;
  /** Optional published-dashboard free placement, preserved when reopening from published view. */
  x?: number;
  y?: number;
  customSize?: boolean;
  /** The compute payload that produced this tile (echoed for save/debug). */
  payload: Record<string, unknown>;
  // ── Exactly one of the following, per layoutType ──
  previewOption?: echarts.EChartsOption | null;
  waterfallData?: Record<string, unknown> | null;
  drillData?: Record<string, unknown> | null;
  hbData?: Record<string, unknown> | null;
  kpiData?: Record<string, unknown> | null;
  /** `[data]` input for <app-unified-bar-column-chart> (BAR layout). */
  barData?: Record<string, unknown> | null;
  /** Unfiltered rows for a BAR tile, kept so a cross-filter (which replaces the
   *  rendered staticData with a filtered subset) can be reversed. */
  barRows?: Array<Record<string, unknown>> | null;
  /** `[data]` input for <app-unified-scatter-bubble-chart> (SCATTER_BUBBLE). */
  scatterBubbleData?: Record<string, unknown> | null;
  /** Unfiltered rows for a SCATTER_BUBBLE tile (mirrors barRows). */
  scatterBubbleRows?: Array<Record<string, unknown>> | null;
  /** `[data]` input for <app-unified-line-area-chart> (LINE_AREA layout). */
  lineAreaData?: Record<string, unknown> | null;
  /** Unfiltered rows for a LINE_AREA tile (mirrors barRows). */
  lineAreaRows?: Array<Record<string, unknown>> | null;
  /** `[data]` input for <app-unified-table-matrix-chart> (TABLE_MATRIX). */
  tableMatrixData?: Record<string, unknown> | null;
  inputControl?: DashboardTileInput | null;
  dropdownControl?: DashboardTileDropdown | null;
  fileUploadControl?: DashboardTileFileUpload | null;
  /** Per-tile formatting chosen in the tile's Format popup (keyed by FormatControl.key). */
  format?: Record<string, unknown>;
  /** Raw waterfall bars kept so formatting can re-derive the ECharts option. */
  waterfallBars?: WaterfallBar[];
  /** The UNFILTERED bars, kept only while a cross-filter recompute is showing
   *  filtered bars — clearing the filter restores these instantly (no API call). */
  waterfallBarsAll?: WaterfallBar[];
  /** Cross-filter: the column this tile PUBLISHES when a data point is clicked
   *  (mirrors the dashboard-builder's filterKey). Empty/undefined = doesn't
   *  publish. Defaults to the tile's category column when a chart click fires. */
  filterKey?: string;
  /** Cross-filter: the ids of the OTHER tiles that REACT to this tile's filter.
   *  undefined = not yet configured (defaults to every other chart tile); an
   *  explicit (possibly empty) array = exactly those tiles react. */
  listenerTileIds?: string[];
  /** GROUP tiles only: user-editable card title + the member tiles nested
   *  inside (stacked in the card, own colSpan/rowSpan, independent of the
   *  outer dashboard grid). Grouping just moves existing tiles in here —
   *  their own render data (waterfallData/kpiData/etc.) is untouched. */
  groupTitle?: string;
  groupMembers?: DashboardTile[];
  /** How many side-by-side columns the group's members lay out in (1 or 2).
   *  Defaults to 1 (stacked) when absent. */
  groupColumns?: number;
}

interface PublishedLayoutSnapshotItem {
  id: string;
  componentId: string;
  colSpan: number;
  rowSpan: number;
  x: number;
  y: number;
  order: number;
  customSize: boolean;
}

/** Render state for an INPUT_BOX / SLIDER tile. The tile reuses the existing
 *  shared widgets (app-mat-slider / app-input-box) rather than bespoke markup —
 *  `widgetData` is the `[data]` input those components consume. `formula` /
 *  `value` / `targets` are retained for the compute payload we push. */
export interface DashboardTileInput {
  kind: 'INPUT_BOX' | 'SLIDER';
  formula: string;
  value: number | string | null;
  targets: string[];
  /** `[data]` input for the reused app-mat-slider / app-input-box component. */
  widgetData: Record<string, unknown>;
}

export interface DashboardTileDropdown {
  field: string;
  options: string[];
  value: string[];
  targets: string[];
  widgetData: Record<string, unknown>;
}

/** Render state for a standalone FILE_UPLOAD tile — an uploader that POSTs the
 *  picked file to the compute API and re-renders the selected `targets`. Mirrors
 *  the dropdown/slider input-control pattern (componentType:'INPUT'). */
export interface DashboardTileFileUpload {
  targets: string[];
  buttonLabel: string;
  accept: string;
  /** `[data]` input the rendered uploader widget consumes. */
  widgetData: Record<string, unknown>;
}

/**
 * DVM Layout Builder — a Power BI-style split-pane workbench for modelling a
 * DVM chart layout's metadata configuration against one or more entities.
 * Generic by design (a "Chart layout" picker selects the target DVM layout
 * type), with WATERFALL the only implemented layout today — the step-builder
 * concept (START/CHANGE/TOTAL bridge steps) is specific to that layout; future
 * layout types would bring their own step shape alongside this shell.
 *
 * Left pane: layout-type + dataset selection, and a re-orderable list of bridge
 * steps (START anchor / CHANGE floating segment / TOTAL subtotal). Right pane:
 * an ECharts preview built from the analytics engine's response once the user
 * generates a configuration.
 *
 * Config-driven like the other builders (see SelfServiceConfigService): the
 * step-type/aggregation/sign option lists and the analytics-engine endpoint
 * come from assets/self-service-config/dvm-layout-builder.json, falling back
 * to DEFAULT_LAYOUT when the file is absent or invalid.
 */
@Component({
  selector: 'app-dvm-layout-builder',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    NgSelectModule,
    DrillDownComponent,
    UnifiedTableMatrixChartComponent,
    DvmFileUploadComponent,
    HorizontalBarChartComponent,
    UnifiedBarColumnChartComponent,
    UnifiedScatterBubbleChartComponent,
    UnifiedLineAreaChartComponent,
    WaterfallChartComponent,
    MatSliderComponent,
    InputBoxComponent,
    GlobalDropdownComponent,
    NumberStatsComponent,
    CapitalCasePipe,
  ],
  templateUrl: './dvm-layout-builder.component.html',
  styleUrl: './dvm-layout-builder.component.scss',
})
export class DvmLayoutBuilderComponent implements OnInit {
  private readonly http = inject(HttpClient);
  private readonly ui = inject(SelfServiceUiService);
  private readonly configService = inject(SelfServiceConfigService);
  private readonly datasetService = inject(SelfServiceDatasetService);
  private readonly formulaService = inject(SelfServiceFormulaService);
  private readonly dashboardService = inject(SelfServiceDashboardService);
  private readonly confirmationDialog = inject(ConfirmationDialogService);
  private static readonly SAVED_DASHBOARDS_STORAGE_KEY = 'dvm.selfService.savedDashboards';

  private readonly persistence = inject(SelfServiceChartPersistenceService);
  private readonly keycloak = inject(KeycloakService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  static readonly STEP_FIELD_IDS: ReadonlySet<string> = new Set<string>(['dvmLayoutWorkbench']);

  private static readonly DEFAULT_LAYOUT: BuilderLayoutConfig = {
    version: '1.0',
    builder: 'dvm-layout',
    steps: [
      { id: 'workbench', label: 'DVM Layout Workbench', sections: [{ id: 'workbench', fields: [{ field: 'dvmLayoutWorkbench' }] }] },
    ],
    options: {
      layoutTypes: [
        { label: 'Waterfall', value: 'WATERFALL' },
        { label: 'Drill-Down Table', value: 'DRILL_DOWN_TABLE' },
        { label: 'Horizontal Bar Chart', value: 'HORIZONTAL_BAR_CHART' },
        { label: 'Input Box', value: 'INPUT_BOX' },
        { label: 'Slider', value: 'SLIDER' },
      ],
      stepTypes: [
        { label: 'Start (anchor pillar)', value: 'START' },
        { label: 'Change (floating segment)', value: 'CHANGE' },
        { label: 'Total (subtotal / milestone)', value: 'TOTAL' },
      ],
      layoutAggregations: [
        { label: 'Sum', value: 'SUM' },
        { label: 'Average', value: 'AVG' },
      ],
      signOptions: [
        { label: 'Auto (let the query decide)', value: 'AUTO' },
        { label: 'Positive (adds)', value: 'POSITIVE' },
        { label: 'Negative (subtracts)', value: 'NEGATIVE' },
      ],
      measureAggregations: [
        { label: 'None (raw value)', value: 'NONE' },
        { label: 'Sum', value: 'SUM' },
        { label: 'Average', value: 'AVG' },
        { label: 'Count', value: 'COUNT' },
      ],
      rollUpStrategies: [
        { label: 'Sum', value: 'SUM' },
        { label: 'Average', value: 'AVG' },
        { label: 'Minimum', value: 'MIN' },
        { label: 'Maximum', value: 'MAX' },
      ],
      entityGroup: 'NontimeSeriesEntities',
      hiddenColumns: ['uuid', '__rowKey', 'source_row_hash', 'sourced_row_hash', 'spriced_row_hash'],
    },
    defaults: {
      componentType: 'CHART',
      dashboardId: 101,
    },
  };

  layout: BuilderLayoutConfig = DvmLayoutBuilderComponent.DEFAULT_LAYOUT;

  get layoutTypes(): LabelValueOption[] {
    return this.layout.options?.layoutTypes?.length
      ? this.layout.options.layoutTypes
      : [
          { label: 'Waterfall', value: 'WATERFALL' },
          { label: 'Drill-Down Table', value: 'DRILL_DOWN_TABLE' },
        ];
  }
  // ── LAYER 1: Chart Catalogue (Category → Type) ─────────────────────────────

  /** All catalogue categories from config (empty when the block is absent). */
  get chartCategories(): ChartCategory[] {
    return this.layout.options?.chartCatalogue ?? [];
  }

  /** True when a catalogue is configured — the UI shows the Category picker only
   *  then, otherwise it falls back to the flat layout-type dropdown. */
  get hasChartCatalogue(): boolean {
    return this.chartCategories.length > 0;
  }

  /** The chart TYPES (as {label,value}) belonging to the selected category, using
   *  the labels from `layoutTypes`. MEMOIZED — a fresh [items] array each CD
   *  cycle breaks ng-select appendTo:'body' (the panel rebuilds mid-click and
   *  swallows the selection); see [[project_ngselect_appendtobody_memoize]]. */
  private typesForCategoryCache: LabelValueOption[] = [];
  private typesForCategoryKey = '';
  get typesForSelectedCategory(): LabelValueOption[] {
    const category = this.chartCategories.find((c) => c.id === this.selectedChartCategory);
    if (!category) return this.layoutTypes;
    const key = `${category.id}::${category.types.join('|')}::${this.layoutTypes.length}`;
    if (key !== this.typesForCategoryKey) {
      const byValue = new Map(this.layoutTypes.map((t) => [t.value, t]));
      this.typesForCategoryCache = category.types
        .map((type) => byValue.get(type) || { label: type, value: type })
        .filter(Boolean);
      this.typesForCategoryKey = key;
    }
    return this.typesForCategoryCache;
  }

  /** The category id that owns a given layout type (first match), or ''. */
  private categoryOfType(type: string): string {
    return this.chartCategories.find((c) => c.types.includes(type))?.id || '';
  }

  /** Switch category → select that category's first type (keeps type + category
   *  consistent). No-op if the category has no types. */
  onChartCategoryChange(): void {
    const types = this.typesForSelectedCategory;
    if (types.length && !types.some((t) => t.value === this.selectedLayoutType)) {
      this.selectedLayoutType = types[0].value;
      this.onLayoutTypeChange();
    }
  }

  // ── LAYER 2: Capability Registry ───────────────────────────────────────────

  /** The capability entry for a chart type (empty object when unregistered). */
  capabilitiesForType(type: string): CapabilityRegistryEntry {
    return this.layout.options?.capabilityRegistry?.[type] ?? {};
  }

  /** The active chart's capabilities. */
  get activeCapabilities(): CapabilityRegistryEntry {
    return this.capabilitiesForType(this.selectedLayoutType);
  }

  /** Whether the active chart supports a capability flag (registry-driven). When
   *  the chart has no registry entry at all, default to `true` so an unconfigured
   *  chart isn't accidentally stripped of controls. */
  chartSupports(capability: string): boolean {
    const entry = this.activeCapabilities;
    if (!Object.keys(entry).length) return true;
    return entry[capability] === true;
  }

  /** Whether the active chart exposes a given Configuration section (accordion).
   *  Registry `sections` is the allow-list; absent → allow (defaults preserved). */
  chartExposesSection(section: string): boolean {
    const sections = this.activeCapabilities.sections;
    return Array.isArray(sections) ? sections.includes(section) : true;
  }

  get isWaterfallLayout(): boolean {
    return this.selectedLayoutType === 'WATERFALL';
  }
  get isDrillDownLayout(): boolean {
    return this.selectedLayoutType === 'DRILL_DOWN_TABLE';
  }
  get isHorizontalBarLayout(): boolean {
    return this.selectedLayoutType === 'HORIZONTAL_BAR_CHART';
  }
  /** Unified Bar & Column layout — one builder → many bar/column variants. */
  get isBarLayout(): boolean {
    return this.selectedLayoutType === 'BAR';
  }
  /** Unified Scatter & Bubble layout — one builder → Scatter/Multi/Bubble/Quadrant. */
  get isScatterBubbleLayout(): boolean {
    return this.selectedLayoutType === 'SCATTER_BUBBLE';
  }
  /** Unified Line & Area layout — one builder → line/step/spline/area variants. */
  get isLineAreaLayout(): boolean {
    return this.selectedLayoutType === 'LINE_AREA';
  }

  /** Heading for the middle "Build" panel — per active layout. */
  get buildPanelTitle(): string {
    if (this.isDrillDownLayout) return 'Drill-down table';
    if (this.isBarLayout) return 'Bar & column';
    if (this.isScatterBubbleLayout) return 'Scatter & bubble';
    if (this.isHorizontalBarLayout) return 'Horizontal bar';
    if (this.isKpiLayout) return 'KPI card';
    if (this.isInputControlLayout) return this.isSliderLayout ? 'Slider input' : 'Input box';
    return 'Bridge steps';
  }

  /** Count badge for the build panel; null hides it (layouts with no list). */
  get buildPanelCount(): number | null {
    if (this.isDrillDownLayout) return this.drillDownDimensions.length;
    if (this.isBarLayout) return this.barMeasures.length;
    if (this.isWaterfallLayout) return this.steps.length;
    return null;
  }
  get isKpiLayout(): boolean {
    return this.selectedLayoutType === 'KPI';
  }

  get isDropdownLayout(): boolean {
    return this.selectedLayoutType === 'DROPDOWN';
  }

  get isFileUploadLayout(): boolean {
    return this.selectedLayoutType === 'FILE_UPLOAD';
  }

  get isTableMatrixLayout(): boolean {
    return this.selectedLayoutType === 'TABLE_MATRIX';
  }

  /** CHART layouts (everything except the input controls / dropdown) can also
   *  declare interaction targets — besides filterKey/filterListeners, a chart
   *  can name the component(s) it interacts with; sent as `payload.targets`
   *  (same shape the input controls use, minus per-target formulas). */
  get isChartTargetLayout(): boolean {
    // FILE_UPLOAD already has its OWN target picker in the Upload settings
    // section, so the generic "Chart interactions" accordion would be a
    // duplicate (both bind the same `selectedTargetCharts`) — exclude it.
    return !this.isInputControlLayout && !this.isDropdownLayout && !this.isFileUploadLayout;
  }

  // ── Table & Matrix: config-driven table-type picker + flag getters ──────────
  /** The "Table type" options from config (Normal / Editable / Drill-Down). */
  get tableTypes(): TableTypeOption[] {
    return ((this.layout.options as Record<string, unknown> | undefined)?.['tableTypes'] as TableTypeOption[] | undefined) ?? [
      { label: 'Normal Table', value: 'normal', group: 'Standard', normal: true },
      { label: 'Editable Table', value: 'editable', group: 'Interactive', editable: true },
      { label: 'Drill-Down Table', value: 'drilldown', group: 'Interactive', drilldown: true },
    ];
  }
  /** The currently-selected table-type config entry. */
  get tmTypeEntry(): TableTypeOption | undefined {
    return this.tableTypes.find((entry) => entry.value === this.tmType);
  }
  /** True flags on the active table-type entry (drives builder fields + render). */
  tmFlagsFor(type: string): Record<string, boolean> {
    const entry = this.tableTypes.find((e) => e.value === type);
    const flags: Record<string, boolean> = {};
    if (entry) {
      Object.keys(entry).forEach((key) => {
        if (entry[key] === true) flags[key] = true;
      });
    }
    // Fallback: derive from the value name when config carries no flag.
    if (!flags['normal'] && !flags['editable'] && !flags['drilldown']) {
      if (type === 'editable') flags['editable'] = true;
      else if (type === 'drilldown') flags['drilldown'] = true;
      else flags['normal'] = true;
    }
    return flags;
  }
  get isTmNormal(): boolean {
    return !!this.tmFlagsFor(this.tmType)['normal'];
  }
  get isTmEditable(): boolean {
    return !!this.tmFlagsFor(this.tmType)['editable'];
  }
  get isTmDrilldown(): boolean {
    return !!this.tmFlagsFor(this.tmType)['drilldown'];
  }
  get isTmMatrix(): boolean { return !!this.tmFlagsFor(this.tmType)['matrix']; }
  get isTmPivot(): boolean { return !!this.tmFlagsFor(this.tmType)['pivot']; }
  get isTmGrouped(): boolean { return !!this.tmFlagsFor(this.tmType)['grouped']; }
  get isTmTreegrid(): boolean { return !!this.tmFlagsFor(this.tmType)['treegrid']; }
  get isTmFinancial(): boolean { return !!this.tmFlagsFor(this.tmType)['financial']; }
  /** True for the shape types that render from flat rows via getEntityRows (all
   *  except drill-down, which uses the compute tree). */
  get isTmFlatShape(): boolean {
    return this.isTableMatrixLayout && !this.isTmDrilldown;
  }
  /** Hint text for the active table type (shown under the picker). */
  get tmTypeHint(): string {
    return String(this.tmTypeEntry?.['hint'] ?? '');
  }
  /** Row/group aggregation options for Normal tables (from config). */
  get tmAggregations(): LabelValueOption[] {
    return ((this.layout.options as Record<string, unknown> | undefined)?.['tmAggregations'] as LabelValueOption[] | undefined) ?? [
      { label: 'None (raw rows)', value: 'none' },
      { label: 'Sum', value: 'sum' },
      { label: 'Average', value: 'avg' },
      { label: 'Count', value: 'count' },
    ];
  }
  /** Operator options for a column's default filter (from config). */
  get tmInitialFilterOperators(): LabelValueOption[] {
    return ((this.layout.options as Record<string, unknown> | undefined)?.['tmInitialFilterOperators'] as LabelValueOption[] | undefined) ?? [
      { label: 'Equals', value: 'eq' },
      { label: 'Not equals', value: 'neq' },
      { label: 'Contains', value: 'contains' },
      { label: 'In (comma-separated list)', value: 'in' },
    ];
  }

  // ── Navigation: saved-dashboard + target-component option sources ───────────
  private navDashboardsMemo: { key: string; value: LabelValueOption[] } | null = null;
  /** The user's saved/published dashboards as {label:name, value:slug}. Reads the
   *  same localStorage cache the builder already consolidates. Memoized so the
   *  ng-select [items] reference stays stable across change-detection cycles
   *  (appendTo:body panels churn if the array identity changes mid-interaction). */
  get tmNavTargetDashboards(): LabelValueOption[] {
    // Source 1 — the API-published list (authoritative, logout-proof).
    const options: LabelValueOption[] = this.apiTargetDashboards.map((d) => ({ label: d.label, value: d.value }));
    const seen = new Set(options.map((o) => o.value));
    // Source 2 — localStorage saved dashboards (may include unpublished drafts
    // not yet on the API); union in, de-duped by slug.
    const saved = this.persistence.consolidateSavedDashboards(
      readJson<SavedSelfServiceDashboard[]>(DvmLayoutBuilderComponent.SAVED_DASHBOARDS_STORAGE_KEY, []),
    );
    saved.forEach((dashboard) => {
      const slug = String(dashboard.config?.['dashboardId'] ?? '');
      if (slug && !seen.has(slug)) {
        seen.add(slug);
        options.push({ label: dashboard.name || slug, value: slug });
      }
    });
    // Source 3 — a pinned target restored on edit that the async API/local lists
    // don't (yet) contain. Without this, the ng-select can't render the saved
    // slug as selected and shows BLANK, and a re-Generate then drops it.
    if (this.navPinnedTarget && !seen.has(this.navPinnedTarget.value)) {
      options.push(this.navPinnedTarget);
      seen.add(this.navPinnedTarget.value);
    }
    const key = options.map((o) => o.value).join('|');
    if (!this.navDashboardsMemo || this.navDashboardsMemo.key !== key) {
      this.navDashboardsMemo = { key, value: options };
    }
    return this.navDashboardsMemo.value;
  }

  /** A restored target dashboard that may not be in the async-loaded lists yet —
   *  pinned into the dropdown so the ng-select renders it as selected on edit. */
  private navPinnedTarget: LabelValueOption | null = null;
  private ensureNavTargetInList(slug: string): void {
    const existing = [...this.apiTargetDashboards.map((d) => d.value)];
    if (existing.includes(slug)) { this.navPinnedTarget = null; return; }
    // Label it from the API/local list if findable, else prettify the slug.
    const apiHit = this.apiTargetDashboards.find((d) => d.value === slug);
    const label = apiHit?.label
      || slug.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    this.navPinnedTarget = { label, value: slug };
    this.navDashboardsMemo = null; // force the getter to include the pin
  }

  private navComponentsMemo: { key: string; value: LabelValueOption[] } | null = null;
  /** Components (charts) on the chosen target dashboard as {label:title, value:componentId}.
   *  Walks the saved dashboard's pageContent (+ tabbed tabs[].pageContent). */
  get tmNavTargetComponentOptions(): LabelValueOption[] {
    const slug = this.tmNavTargetDashboard;
    if (!slug) { this.navComponentsMemo = { key: '', value: [] }; return []; }
    // Prefer the API dashboard's layout_json; fall back to localStorage config.
    const apiEntry = this.apiTargetDashboards.find((d) => d.value === slug);
    let config: Record<string, unknown> = apiEntry?.layoutJson || {};
    if (!config['pageContent'] && !config['tabs']) {
      const saved = this.persistence.consolidateSavedDashboards(
        readJson<SavedSelfServiceDashboard[]>(DvmLayoutBuilderComponent.SAVED_DASHBOARDS_STORAGE_KEY, []),
      );
      const dashboard = saved.find((d) => String(d.config?.['dashboardId'] ?? '') === slug);
      config = (dashboard?.config as Record<string, unknown>) || {};
    }
    const options: LabelValueOption[] = [];
    const seen = new Set<string>();
    const collectFromPage = (pageContent: unknown) => {
      if (!Array.isArray(pageContent)) return;
      pageContent.forEach((row) => {
        const cols = (row as Record<string, unknown>)?.['columns'];
        if (!Array.isArray(cols)) return;
        cols.forEach((widget) => {
          const w = widget as Record<string, unknown>;
          const id = String(w?.['componentId'] ?? '').trim();
          const type = String(w?.['type'] ?? '');
          // Only chartable components can be filter targets (skip pure inputs).
          const isInput = type === 'slider' || type === 'input-box' || type === 'dropdown';
          if (id && !isInput && !seen.has(id)) { seen.add(id); options.push({ label: String(w?.['title'] ?? id), value: id }); }
        });
      });
    };
    collectFromPage(config['pageContent']);
    const tabs = config['tabs'];
    if (Array.isArray(tabs)) tabs.forEach((tab) => collectFromPage((tab as Record<string, unknown>)?.['pageContent']));
    const key = slug + '::' + options.map((o) => o.value).join('|');
    if (!this.navComponentsMemo || this.navComponentsMemo.key !== key) {
      this.navComponentsMemo = { key, value: options };
    }
    return this.navComponentsMemo.value;
  }

  /** Reset the dependent target-components selection when the target dashboard changes. */
  onTmNavTargetDashboardChange(): void {
    this.tmNavTargetComponents = [];
    this.navComponentsMemo = null;
    this.onTableMatrixTypeChange();
  }
  toggleTmNavAccordion(): void { this.tmNavAccordionOpen = !this.tmNavAccordionOpen; }

  /** Config-driven show/hide for the "Reference" UI under a formula field,
   *  scoped to ONE chart/component layout type (KPI, HORIZONTAL_BAR_CHART,
   *  WATERFALL, INPUT_BOX, SLIDER, ...). A plain boolean config applies to
   *  every layout type; a per-type map is checked by key; either way, a
   *  missing/absent config defaults to shown. */
  formulaReferencesEnabledFor(layoutType: string): boolean {
    const config = this.layout.options?.enableFormulaReferences;
    if (config === undefined) return true;
    if (typeof config === 'boolean') return config;
    return config[layoutType] !== false;
  }

  /** The layout type of a target/reference id — resolves a dashboard tile's
   *  own componentId to its chart type, so the Reference toggle can be scoped
   *  per chart type even when the id refers to ANOTHER tile (an input
   *  control's target), not the chart currently being built. */
  targetLayoutTypeForId(id: string): string {
    return this.allTilesFlat().find((tile) => tile.componentId === id)?.layoutType || '';
  }

  /** True when another reference row (for the same id/step) already uses this
   *  row's token — such a collision means only ONE of them actually reaches
   *  the backend (`referencesForFormula` keys the sent map by token), so the
   *  UI surfaces it instead of silently dropping the earlier row. */
  isDuplicateReferenceToken(id: string, ref: TargetFormulaReferenceEntry, step?: string): boolean {
    const token = ref.token.trim();
    if (!token) return false;
    return this.targetReferences(id, step).some((other) => other !== ref && other.token.trim() === token);

  }
  get isInputBoxLayout(): boolean {
    return this.selectedLayoutType === 'INPUT_BOX';
  }
  get isSliderLayout(): boolean {
    return this.selectedLayoutType === 'SLIDER';
  }
  /** INPUT_BOX and SLIDER share the same builder shape — a titled control that
   *  feeds a formula-driven value into one or more target charts. */
  get isInputControlLayout(): boolean {
    return this.isInputBoxLayout || this.isSliderLayout;
  }
  private targetChartsCache: LabelValueOption[] = [];
  private targetChartsKey = '';

  /** Tiles an input control can drive — built DYNAMICALLY from every OTHER tile
   *  currently on the dashboard: charts (KPI included) AND other input controls
   *  (a slider/input box can feed its value into another slider/input box, which
   *  can in turn drive its own targets). Value = the tile's componentId, which
   *  the recompute response resolves back to. The tile currently being edited is
   *  excluded so an input can never target itself.
   *
   *  MEMOIZED: a getter returns on every change-detection cycle, so returning a
   *  fresh array each time made ng-select rebuild its option list mid-click and
   *  swallow the selection. Return a STABLE reference, recomputed only when the
   *  tiles (id/title/layoutType) actually change. */
  get targetCharts(): LabelValueOption[] {
    const editingComponentId = this.editingTileId
      ? this.findTileAnywhere(this.editingTileId)?.componentId
      : undefined;
    const chartTiles = this.allTilesFlat().filter((tile) => tile.componentId !== editingComponentId);
    const key = chartTiles.map((tile) => `${tile.componentId}|${tile.title}|${tile.layoutType}`).join('~');
    if (key !== this.targetChartsKey) {
      this.targetChartsKey = key;
      const seen = new Set<string>();
      const options: LabelValueOption[] = [];
      chartTiles.forEach((tile) => {
        const value = String(tile.componentId ?? '');
        if (!value || seen.has(value)) return;
        seen.add(value);
        const isInputTile = tile.layoutType === 'SLIDER' || tile.layoutType === 'INPUT_BOX' || tile.layoutType === 'DROPDOWN' || tile.layoutType === 'FILE_UPLOAD';
        options.push({ label: (tile.title || tile.layoutType) + (isInputTile ? ' (Input)' : ''), value });
      });
      this.targetChartsCache = options;
    }
    return this.targetChartsCache;
  }

  /** True when the given target componentId is itself a WATERFALL tile — such a
   *  target gets one formula PER BRIDGE STEP instead of a single chart-wide
   *  formula, since each bar (Start / Change / Total) is its own independent
   *  calculation. */
  isWaterfallTarget(targetId: string): boolean {
    return this.dashboardTiles.some((tile) => tile.componentId === targetId && tile.layoutType === 'WATERFALL');
  }

  private targetStepsCache = new Map<string, { key: unknown; result: Array<{ label: string; stepType: string }> }>();

  /** The bridge steps (label + stepType) of a WATERFALL target, read back from
   *  that tile's saved payload — used to render one formula row per bar.
   *
   *  MEMOIZED per target id: this is called from the template's `*ngFor`, so a
   *  fresh array/objects on every change-detection pass made Angular tear down
   *  and rebuild the formula `<input>` rows on every keystroke (default
   *  `*ngFor` tracking is by object identity) — the input lost focus/cursor
   *  position constantly. Returning the SAME array reference while the
   *  underlying steps haven't changed keeps those rows (and their focus) stable. */
  targetSteps(targetId: string): Array<{ label: string; stepType: string }> {
    const tile = this.allTilesFlat().find((item) => item.componentId === targetId && item.layoutType === 'WATERFALL');
    const rawSteps = Array.isArray(tile?.payload?.['steps']) ? tile!.payload['steps'] as Array<Record<string, unknown>> : [];
    const cached = this.targetStepsCache.get(targetId);
    if (cached && cached.key === rawSteps) return cached.result;
    const result = rawSteps.map((step) => ({ label: String(step['label'] ?? ''), stepType: String(step['stepType'] ?? '') }));
    this.targetStepsCache.set(targetId, { key: rawSteps, result });
    return result;
  }

  trackByStepLabel(index: number, step: { label: string; stepType: string }): string {
    return step.label || `step-${index}`;
  }

  targetStepFormula(targetId: string, stepLabel: string): string {
    return this.targetStepFormulas[targetId]?.[stepLabel] || '';
  }

  setTargetStepFormula(targetId: string, stepLabel: string, value: string): void {
    const forTarget = { ...(this.targetStepFormulas[targetId] || {}) };
    forTarget[stepLabel] = value;
    this.targetStepFormulas = { ...this.targetStepFormulas, [targetId]: forTarget };
  }

  /** Display/back-compat summary for a target's formula(s) — one joined string
   *  whether the target takes a single chart-wide formula or a per-step map. */
  private targetFormulaSummary(targetId: string): string {
    if (this.isWaterfallTarget(targetId)) {
      const perStep: Record<string, string> = this.targetStepFormulas[targetId] || {};
      return Object.keys(perStep)
        .map((label) => (perStep[label] || '').trim() ? `${label}: ${perStep[label].trim()}` : '')
        .filter(Boolean)
        .join(', ');
    }
    return (this.targetFormulas[targetId] || '').trim();
  }

  /** The `formulas` array sent on the wire for one target — ONE consistent
   *  shape for every target type. A non-waterfall target gets a single entry
   *  with no `step`; a WATERFALL target gets one entry PER bar that actually has
   *  a formula filled in (bars left blank are simply omitted, not sent empty).
   *  Each entry also carries `references` when the formula uses a bracket
   *  token the user has typed a reference for (see `targetFormulaReferences`). */
  private targetFormulasForPayload(
    targetId: string,
  ): Array<{ step?: string; formula: string; references?: Record<string, { inputTitle: string; targetLabel: string; rowLabel: string }> }> {
    if (this.isWaterfallTarget(targetId)) {
      return this.targetSteps(targetId)
        .map((step) => {
          const formula = this.targetStepFormula(targetId, step.label).trim();
          const references = this.referencesForFormula(targetId, formula, step.label);
          return { step: step.label, formula, ...(references ? { references } : {}) };
        })
        .filter((entry) => !!entry.formula);
    }
    const formula = (this.targetFormulas[targetId] || '').trim();
    // An INPUT-control target (slider/input box) with no formula still expects
    // the driving value — default to a plain `value` pass-through so "input
    // drives input" works without typing a formula. Other targets stay filtered
    // out: with no formula the backend has nothing to compute, its response
    // entry comes back empty and recomputeEntryHasData skips it (the tile shows
    // a loader but never updates).
    if (!formula) return this.isInputControlTargetId(targetId) ? [{ formula: 'value' }] : [];
    const references = this.referencesForFormula(targetId, formula);
    return [{ formula, ...(references ? { references } : {}) }];
  }

  /** True when the target componentId belongs to a SLIDER / INPUT_BOX tile.
   *  Public: the templates use it to hint the `value` pass-through default. */
  isInputControlTargetId(targetId: string): boolean {
    const layoutType = this.targetLayoutTypeForId(targetId);
    return layoutType === 'SLIDER' || layoutType === 'INPUT_BOX';
  }

  /** Builds the `references` object for one formula string — only tokens
   *  that (a) the user has typed a reference row for THIS step (or, for a
   *  non-waterfall target, any row at all — there are no steps), AND (b)
   *  actually appear as a `[Token]` in the formula, are included. */
  private referencesForFormula(
    targetId: string,
    formula: string,
    step?: string,
  ): Record<string, { inputTitle: string; targetLabel: string; rowLabel: string }> | undefined {
    const entries = (this.targetFormulaReferences[targetId] || []).filter((entry) => (step ? entry.step === step : !entry.step));
    if (!entries.length || !formula) return undefined;
    const tokensInFormula = new Set(Array.from(formula.matchAll(/\[([^\]]+)\]/g)).map((match) => match[1].trim()));
    const references: Record<string, { inputTitle: string; targetLabel: string; rowLabel: string }> = {};
    entries.forEach((entry) => {
      const token = entry.token.trim();
      if (!token || !tokensInFormula.has(token)) return;
      references[token] = {
        inputTitle: entry.inputTitle.trim(),
        targetLabel: entry.targetLabel.trim(),
        rowLabel: entry.rowLabel.trim(),
      };
    });
    return Object.keys(references).length ? references : undefined;
  }

  /** Reverse of `referencesForFormula` — rebuilds typed-in reference rows from
   *  a saved `references` map (one row per token), for a chart's OWN formula
   *  (KPI / horizontal bar), keyed by its own componentId. Same shape as the
   *  per-target rehydration in `editTile`'s INPUT_BOX/SLIDER branch. */
  private rehydrateOwnFormulaReferences(componentId: string, references: unknown): void {
    const rows: TargetFormulaReferenceEntry[] = [];
    if (references && typeof references === 'object') {
      Object.keys(references as Record<string, Record<string, unknown>>).forEach((token) => {
        const ref = (references as Record<string, Record<string, unknown>>)[token] || {};
        rows.push({
          token,
          inputTitle: String(ref['inputTitle'] ?? ''),
          targetLabel: String(ref['targetLabel'] ?? ''),
          rowLabel: String(ref['rowLabel'] ?? ''),
        });
      });
    }
    this.targetFormulaReferences = { ...this.targetFormulaReferences, [componentId]: rows };
  }

  /** References typed in for one target, for the template's *ngFor. Pass
   *  `step` to scope to one WATERFALL bar; omit it for a non-waterfall
   *  target's single formula. */
  targetReferences(targetId: string, step?: string): TargetFormulaReferenceEntry[] {
    return (this.targetFormulaReferences[targetId] || []).filter((entry) => (step ? entry.step === step : !entry.step));
  }

  /** Adds a blank, fully-typed-by-hand reference row for a target's formula
   *  (or one specific WATERFALL bar, when `step` is given). */
  addTargetReference(targetId: string, step?: string): void {
    const forTarget = [...(this.targetFormulaReferences[targetId] || []), { token: '', inputTitle: '', targetLabel: '', rowLabel: '', step }];
    this.targetFormulaReferences = { ...this.targetFormulaReferences, [targetId]: forTarget };
  }

  /** Removes a reference row (matched by identity, since it may live in a
   *  step-filtered subset of the target's full reference list). */
  removeTargetReference(targetId: string, entry: TargetFormulaReferenceEntry): void {
    const forTarget = (this.targetFormulaReferences[targetId] || []).filter((item) => item !== entry);
    this.targetFormulaReferences = { ...this.targetFormulaReferences, [targetId]: forTarget };
  }

  /** Resolves the current layout type's builder-step definition from the config's
   *  `layoutSteps` map, following a string alias (shared builders — INPUT_BOX /
   *  SLIDER both point at "INPUT_CONTROL"). Null when the config omits it, in
   *  which case the template falls back to its built-in headings/hints. */
  get activeLayoutSteps(): LayoutStepsDefinition | null {
    const map = this.layout.layoutSteps;
    if (!map) return null;
    let entry = map[this.selectedLayoutType];
    // One hop of alias resolution ("SLIDER" -> "INPUT_CONTROL" -> {...}).
    if (typeof entry === 'string') entry = map[entry];
    return entry && typeof entry !== 'string' ? entry : null;
  }

  private layoutSection(sectionId: string): LayoutStepSection | undefined {
    return this.activeLayoutSteps?.sections.find((section) => section.id === sectionId);
  }

  /** Config-driven section heading with a hardcoded fallback so a missing/partial
   *  `layoutSteps` block never blanks out a title. */
  layoutSectionTitle(sectionId: string, fallback: string): string {
    return this.layoutSection(sectionId)?.title || fallback;
  }

  /** Config-driven section helper text; empty string hides the hint. */
  layoutSectionHint(sectionId: string, fallback = ''): string {
    return this.layoutSection(sectionId)?.hint ?? fallback;
  }

  /** Whether a control should render, per the active layout's config. Returns
   *  true when the field is listed in the section's `fields`; false when the
   *  section defines `fields` but omits it (so removing a field from the JSON
   *  hides its control). Falls back to true when the layout/section has no
   *  `fields` config at all, so an absent/partial config never blanks controls. */
  fieldEnabled(sectionId: string, fieldKey: string): boolean {
    const section = this.layoutSection(sectionId);
    if (!section || !section.fields) return true;
    return section.fields.some((field) => field.field === fieldKey);
  }

  /** Config-driven label for a specific field, with a hardcoded fallback. */
  fieldLabel(sectionId: string, fieldKey: string, fallback: string): string {
    const section = this.layoutSection(sectionId);
    return section?.fields?.find((field) => field.field === fieldKey)?.label || fallback;
  }
  get stepTypes(): LabelValueOption[] {
    return this.layout.options?.stepTypes?.length
      ? this.layout.options.stepTypes
      : [
          { label: 'Start (anchor pillar)', value: 'START' },
          { label: 'Change (floating segment)', value: 'CHANGE' },
          { label: 'Total (subtotal / milestone)', value: 'TOTAL' },
        ];
  }
  get layoutAggregations(): LabelValueOption[] {
    return this.layout.options?.layoutAggregations?.length
      ? this.layout.options.layoutAggregations
      : [{ label: 'Sum', value: 'SUM' }, { label: 'Average', value: 'AVG' }];
  }
  get signOptions(): LabelValueOption[] {
    return this.layout.options?.signOptions?.length
      ? this.layout.options.signOptions
      : [
          { label: 'Auto (let the query decide)', value: 'AUTO' },
          { label: 'Positive (adds)', value: 'POSITIVE' },
          { label: 'Negative (subtracts)', value: 'NEGATIVE' },
        ];
  }
  get measureAggregations(): LabelValueOption[] {
    return this.layout.options?.measureAggregations?.length
      ? this.layout.options.measureAggregations
      : [
          { label: 'None (raw value)', value: 'NONE' },
          { label: 'Sum', value: 'SUM' },
          { label: 'Average', value: 'AVG' },
          { label: 'Count', value: 'COUNT' },
        ];
  }
  get rollUpStrategies(): LabelValueOption[] {
    return this.layout.options?.rollUpStrategies?.length
      ? this.layout.options.rollUpStrategies
      : [
          { label: 'Sum', value: 'SUM' },
          { label: 'Average', value: 'AVG' },
          { label: 'Minimum', value: 'MIN' },
          { label: 'Maximum', value: 'MAX' },
        ];
  }
  get kpiAggregations(): LabelValueOption[] {
    return this.layout.options?.kpiAggregations?.length
      ? this.layout.options.kpiAggregations
      : [
          { label: 'Sum', value: 'SUM' },
          { label: 'Average', value: 'AVG' },
          { label: 'Count', value: 'COUNT' },
          { label: 'Distinct Count', value: 'COUNT_DISTINCT' },
          { label: 'Minimum', value: 'MIN' },
          { label: 'Maximum', value: 'MAX' },
        ];
  }
  get barVariants(): BarVariantOption[] {
    return this.layout.options?.barVariants?.length
      ? this.layout.options.barVariants
      : [
          { label: 'Simple Bar', value: 'simple', usesSeries: false },
          { label: 'Grouped Bar', value: 'grouped', usesSeries: true },
          { label: 'Stacked Bar', value: 'stacked', usesSeries: true },
          { label: '100% Stacked Bar', value: 'stacked-100', usesSeries: true },
          { label: 'Clustered Bar Chart', value: 'clustered', usesSeries: true },
          { label: 'Comparison Bar (A vs B)', value: 'comparison', needsTwoMeasures: true },
        ];
  }

  /** The config entry for the currently-selected variant (behaviour flags + hint). */
  private get barVariantEntry(): BarVariantOption | undefined {
    return this.barVariants.find((v) => v.value === this.barVariant);
  }
  get barRenderTypes(): LabelValueOption[] {
    return this.layout.options?.barRenderTypes?.length
      ? this.layout.options.barRenderTypes
      : [
          { label: 'Bar', value: 'bar' },
          { label: 'Line', value: 'line' },
          { label: 'Area', value: 'area' },
        ];
  }
  get barAxisOptions(): LabelValueOption[] {
    return this.layout.options?.barAxisOptions?.length
      ? this.layout.options.barAxisOptions
      : [
          { label: 'Primary axis', value: 'y1' },
          { label: 'Secondary axis (dual)', value: 'y2' },
        ];
  }
  /** Whether the selected variant splits each category into sub-bars by a series
   *  column — read from the variant's `usesSeries` config flag (no hardcoded list). */
  get barVariantUsesSeries(): boolean {
    return this.barVariantEntry?.usesSeries === true;
  }

  get barVariantSeriesFromMeasures(): boolean {
    return this.barVariantEntry?.seriesFromMeasures === true;
  }

  /** Series is coming from the measure list (variant allows it, no Split picked). */
  get barSeriesComesFromMeasures(): boolean {
    return this.barVariantSeriesFromMeasures && !this.barSeries;
  }

  /** A Split column is mandatory. `seriesFromMeasures` makes it optional, since
   *  a computed column is a measure and can never be a categorical split. */
  get barSeriesRequired(): boolean {
    return this.barVariantUsesSeries && !this.barVariantSeriesFromMeasures;
  }

  /** Per-variant guidance shown under the Variant picker — from config `hint`. */
  get barVariantHint(): string {
    return this.barVariantEntry?.hint || '';
  }

  // ── Line & Area variant getters (config-driven, from line-area.json) ──
  private readonly lineAreaVariantsFallback: LineAreaVariantOption[] = [
    { label: 'Line', value: 'line', group: 'Line', usesSeries: false, allowMultipleMeasures: true },
    { label: 'Multi-Line', value: 'multi-line', group: 'Line', usesSeries: true },
    { label: 'Step Line', value: 'step', group: 'Line', usesSeries: false, allowMultipleMeasures: true, step: true },
    { label: 'Spline Line', value: 'spline', group: 'Line', usesSeries: false, allowMultipleMeasures: true, smooth: true },
    { label: 'Area', value: 'area', group: 'Area', usesSeries: false, allowMultipleMeasures: true, area: true },
    { label: 'Stacked Area', value: 'stacked-area', group: 'Area', usesSeries: true, area: true, stack: true },
    { label: '100% Stacked Area', value: 'area-100', group: 'Area', usesSeries: true, area: true, stack: true, normalize100: true },
    { label: 'Spline Area', value: 'spline-area', group: 'Area', usesSeries: false, allowMultipleMeasures: true, area: true, smooth: true },
  ];
  get lineAreaVariants(): LineAreaVariantOption[] {
    return this.layout.options?.lineAreaVariants?.length
      ? (this.layout.options.lineAreaVariants as LineAreaVariantOption[])
      : this.lineAreaVariantsFallback;
  }
  private get laVariantEntry(): LineAreaVariantOption | undefined {
    return this.lineAreaVariants.find((v) => v.value === this.laVariant);
  }
  private readonly lineAreaRenderTypesFallback: LabelValueOption[] = [
    { label: 'Line', value: 'line' }, { label: 'Area', value: 'area' },
  ];
  get lineAreaRenderTypes(): LabelValueOption[] {
    return this.layout.options?.lineAreaRenderTypes?.length ? this.layout.options.lineAreaRenderTypes : this.lineAreaRenderTypesFallback;
  }
  private readonly lineAreaAxisOptionsFallback: LabelValueOption[] = [
    { label: 'Primary axis', value: 'y1' }, { label: 'Secondary axis (dual)', value: 'y2' },
  ];
  get lineAreaAxisOptions(): LabelValueOption[] {
    return this.layout.options?.lineAreaAxisOptions?.length ? this.layout.options.lineAreaAxisOptions : this.lineAreaAxisOptionsFallback;
  }
  /** Whether the selected variant splits into one line/area per series value. */
  get laVariantUsesSeries(): boolean {
    return this.laVariantEntry?.usesSeries === true;
  }
  get laSeriesRequired(): boolean {
    return this.laVariantUsesSeries;
  }
  get laVariantHint(): string {
    return this.laVariantEntry?.hint || '';
  }
  /** Whether adding MORE than one measure is meaningful for the selected variant. */
  get laAllowsMultipleMeasures(): boolean {
    if (this.laVariantEntry?.allowMultipleMeasures === true) return true;
    return !this.laVariantUsesSeries;
  }

  // ── Scatter & Bubble variant getters (config-driven, from scatter-bubble.json) ──
  /** Stable fallback instance — the getter must not mint a fresh array per CD
   *  cycle (ng-select appendTo:'body' identity-churn hazard). */
  private readonly scatterBubbleVariantsFallback: ScatterBubbleVariantOption[] = [
    { label: 'Scatter', value: 'scatter', mode: 'scatter', group: 'Scatter' },
    { label: 'Multi Scatter', value: 'multi-scatter', mode: 'scatter', group: 'Scatter', usesSeries: true, multiSeries: true },
    { label: 'Bubble', value: 'bubble', mode: 'bubble', group: 'Bubble', usesSize: true, bubble: true },
    { label: 'Quadrant', value: 'quadrant', mode: 'scatter', group: 'Advanced', quadrant: true },
  ];
  get scatterBubbleVariants(): ScatterBubbleVariantOption[] {
    return this.layout.options?.scatterBubbleVariants?.length
      ? (this.layout.options.scatterBubbleVariants as ScatterBubbleVariantOption[])
      : this.scatterBubbleVariantsFallback;
  }
  /** The Orientation toggle's sides — FULLY config-driven (label + icon +
   *  value) from `scatterBubbleModes`; a JSON edit can add/remove/rename a
   *  side. Stable fallback instance (no fresh array per CD cycle). */
  private readonly sbModesFallback: ScatterBubbleModeOption[] = [
    { label: 'Scatter', value: 'scatter', icon: 'scatter_plot' },
    { label: 'Bubble', value: 'bubble', icon: 'bubble_chart' },
  ];
  get sbModes(): ScatterBubbleModeOption[] {
    return this.layout.options?.scatterBubbleModes?.length
      ? (this.layout.options.scatterBubbleModes as ScatterBubbleModeOption[])
      : this.sbModesFallback;
  }
  /** The config-declared default side = the FIRST mode entry. */
  private get sbDefaultMode(): string {
    return this.sbModes[0]?.value ?? 'scatter';
  }
  /** Which toggle side a variant belongs to — config `mode`, else its render flag. */
  private sbVariantModeOf(v: ScatterBubbleVariantOption): string {
    return v.mode ?? (v.bubble ? 'bubble' : this.sbDefaultMode);
  }
  /** Legacy marker: pre-overlay configs listed advanced entries INSIDE
   *  scatterBubbleVariants under a `group: 'Advanced'` heading — keep them out
   *  of the Variant dropdown if such a config is still deployed. */
  private sbVariantIsAdvanced(v: ScatterBubbleVariantOption): boolean {
    return v.group === 'Advanced';
  }
  /** Variant dropdown items — the active toggle side's BASE variants. MEMOIZED
   *  (fresh array each CD cycle breaks ng-select appendTo:'body'). */
  private sbVariantItemsCache: ScatterBubbleVariantOption[] = [];
  private sbVariantItemsCacheKey: { source: ScatterBubbleVariantOption[]; mode: string } | null = null;
  get sbVariantItems(): ScatterBubbleVariantOption[] {
    const source = this.scatterBubbleVariants;
    if (this.sbVariantItemsCacheKey?.source !== source || this.sbVariantItemsCacheKey.mode !== this.sbMode) {
      this.sbVariantItemsCache = source.filter((v) => this.sbVariantModeOf(v) === this.sbMode && !this.sbVariantIsAdvanced(v));
      this.sbVariantItemsCacheKey = { source, mode: this.sbMode };
    }
    return this.sbVariantItemsCache;
  }
  /** Advanced analytic OVERLAYS (Quadrant / Regression / Correlation / Cluster)
   *  — from config `scatterBubbleAdvancedOptions`. NOT variants: one can be
   *  layered onto whichever base variant is active (Quadrant + Bubble, …) by
   *  ORing its flags into the base flags. Stable fallback instance (no fresh
   *  array per CD cycle). */
  private readonly sbAdvancedOptionsFallback: ScatterBubbleVariantOption[] = [
    { label: 'Simple', value: '', hint: 'The plain base chart — no analytic overlay applied.' },
    { label: 'Quadrant', value: 'quadrant', quadrant: true },
    { label: 'Regression', value: 'regression', regression: true },
    { label: 'Correlation', value: 'correlation', correlation: true },
    { label: 'Cluster Scatter', value: 'cluster', cluster: true },
  ];
  /** Accordion rows — overlays available on the active toggle side (an entry
   *  with no `mode` shows on both sides). MEMOIZED. */
  private sbAdvancedItemsCache: ScatterBubbleVariantOption[] = [];
  private sbAdvancedItemsCacheKey: { source: ScatterBubbleVariantOption[]; mode: string } | null = null;
  get sbAdvancedVariantItems(): ScatterBubbleVariantOption[] {
    const source = this.layout.options?.scatterBubbleAdvancedOptions?.length
      ? (this.layout.options.scatterBubbleAdvancedOptions as ScatterBubbleVariantOption[])
      : this.sbAdvancedOptionsFallback;
    if (this.sbAdvancedItemsCacheKey?.source !== source || this.sbAdvancedItemsCacheKey.mode !== this.sbMode) {
      this.sbAdvancedItemsCache = source.filter((v) => !v.mode || v.mode === this.sbMode);
      this.sbAdvancedItemsCacheKey = { source, mode: this.sbMode };
    }
    return this.sbAdvancedItemsCache;
  }
  /** Aggregation picker choices — config `sbAggregations`, stable fallback. */
  private readonly sbAggregationOptionsFallback: LabelValueOption[] = [
    { label: 'None (raw points)', value: 'none' },
    { label: 'Sum', value: 'sum' },
    { label: 'Average', value: 'avg' },
    { label: 'Count', value: 'count' },
    { label: 'Min', value: 'min' },
    { label: 'Max', value: 'max' },
  ];
  get sbAggregationOptions(): LabelValueOption[] {
    return this.layout.options?.sbAggregations?.length
      ? this.layout.options.sbAggregations
      : this.sbAggregationOptionsFallback;
  }
  /** The active overlay's config entry (undefined = no overlay). */
  private get sbAdvancedEntry(): ScatterBubbleVariantOption | undefined {
    if (!this.sbAdvanced) return undefined;
    return this.sbAdvancedVariantItems.find((v) => v.value === this.sbAdvanced);
  }
  /** Scatter/Bubble segmented-toggle click. Keeps the variant valid for the new
   *  side (falls back to the side's first variant); the advanced overlay
   *  carries across sides (Quadrant + Scatter ↔ Quadrant + Bubble). */
  setSbMode(mode: string): void {
    if (this.sbMode === mode) return;
    this.sbMode = mode;
    if (!this.sbVariantItems.some((v) => v.value === this.sbVariant)) {
      this.sbVariant = (this.sbVariantItems[0]?.value ?? 'scatter') as ScatterBubbleVariant;
    }
    // Drop an overlay the new side doesn't offer (only possible with mode-scoped entries).
    if (this.sbAdvanced && !this.sbAdvancedVariantItems.some((v) => v.value === this.sbAdvanced)) {
      this.sbAdvanced = '';
    }
    this.onScatterBubbleChange();
  }
  /** Advanced-accordion row click — single-select, click again to turn off. */
  toggleSbAdvanced(value: string): void {
    this.sbAdvanced = this.sbAdvanced === value ? '' : value;
    this.onScatterBubbleChange();
  }
  /** Toggle side owning a saved variant — for rehydrating the segmented toggle. */
  private sbModeForVariant(variant: string): string {
    const entry = this.scatterBubbleVariants.find((v) => v.value === variant);
    return entry ? this.sbVariantModeOf(entry) : this.sbDefaultMode;
  }
  private get sbVariantEntry(): ScatterBubbleVariantOption | undefined {
    return this.scatterBubbleVariants.find((v) => v.value === this.sbVariant);
  }
  /** Whether the Series column is consumed — by the base variant (Multi
   *  Scatter) OR the active advanced overlay (Cluster Scatter). */
  get sbVariantUsesSeries(): boolean {
    return this.sbVariantEntry?.usesSeries === true || this.sbAdvancedEntry?.usesSeries === true;
  }
  /** Whether the Size column is consumed — base variant (Bubble) or overlay. */
  get sbVariantUsesSize(): boolean {
    return this.sbVariantEntry?.usesSize === true || this.sbAdvancedEntry?.usesSize === true;
  }
  /** Base-variant hint (the overlay's own hint shows inside the accordion). */
  get sbVariantHint(): string {
    return this.sbVariantEntry?.hint || '';
  }
  /** Active overlay's hint — shown under the Advanced accordion rows. */
  get sbAdvancedHint(): string {
    return this.sbAdvancedEntry?.hint || '';
  }

  // ── Scatter Build-form format accordions (reuse the generic groups computation
  //    keyed off selectedLayoutType; own cache + open-set since sbFormat is a
  //    separate state map from barFormat). ──
  private sbFormatGroupsCache: FormatControlGroup[] | null = null;
  private sbFormatGroupsCacheType = '';
  get scatterBubbleFormatGroups(): FormatControlGroup[] {
    if (this.sbFormatGroupsCache && this.sbFormatGroupsCacheType === this.selectedLayoutType) {
      return this.sbFormatGroupsCache;
    }
    const applicable = this.applicableFormatControls(this.selectedLayoutType);
    const byGroup = new Map<string, FormatControl[]>();
    const seen: string[] = [];
    applicable.forEach((control) => {
      const group = control.group || 'Format';
      if (!byGroup.has(group)) { byGroup.set(group, []); seen.push(group); }
      byGroup.get(group)!.push(control);
    });
    const order = this.formatGroupOrder;
    seen.sort((a, b) => {
      const ia = order.indexOf(a);
      const ib = order.indexOf(b);
      return (ia === -1 ? order.length + seen.indexOf(a) : ia) - (ib === -1 ? order.length + seen.indexOf(b) : ib);
    });
    const exposed = seen.filter((name) => this.chartExposesSection(name));
    this.sbFormatGroupsCacheType = this.selectedLayoutType;
    this.sbFormatGroupsCache = exposed.map((name) => ({ name, controls: byGroup.get(name)! }));
    return this.sbFormatGroupsCache;
  }
  isSbFormatGroupOpen(group: string): boolean {
    return this.openSbFormatGroups.has(group);
  }
  toggleSbFormatGroup(group: string): void {
    const next = new Set(this.openSbFormatGroups);
    if (next.has(group)) next.delete(group);
    else next.add(group);
    this.openSbFormatGroups = next;
  }
  private seedScatterBubbleFormatDefaults(): void {
    this.applicableFormatControls('SCATTER_BUBBLE').forEach((control) => {
      if (control.default !== undefined && this.sbFormat[control.key] === undefined) {
        this.sbFormat[control.key] = control.default;
      }
    });
  }

  /** Column list for the Scatter/Bubble field pickers — MEMOIZED (fresh array each
   *  CD cycle breaks ng-select appendTo:'body'; see [[project_ngselect_appendtobody_memoize]]). */
  private sbColumnsCache: string[] = [];
  private sbColumnsKey = '';
  get scatterBubbleColumns(): string[] {
    const base = this.drillDatasetColumns;
    const key = base.join('|');
    if (key !== this.sbColumnsKey) {
      this.sbColumnsKey = key;
      this.sbColumnsCache = [...base];
    }
    return this.sbColumnsCache;
  }

  /** Comparison-style variant needs two measures — from config `needsTwoMeasures`. */
  get isBarComparison(): boolean {
    return this.barVariantEntry?.needsTwoMeasures === true;
  }

  /** Whether adding MORE than one measure is meaningful for the selected variant,
   *  so the "Add measure" button + per-measure reorder/remove UI only show where
   *  extra measures actually render. Config-driven:
   *   - variant `allowMultipleMeasures: true`  → allowed (e.g. Simple Bar),
   *   - variant `needsTwoMeasures: true`       → allowed (Comparison: A vs B),
   *   - series-split variants (grouped/stacked/…) → NOT allowed: the split column
   *     supplies the series and the option builder uses only the FIRST measure for
   *     bar values, so extra bar measures are silently ignored. (A Line/Area combo
   *     overlay is a separate, advanced case handled via each measure's Draw-as.)
   *  Falls back to "allowed when the variant doesn't use a series split". */
  get barAllowsMultipleMeasures(): boolean {
    if (this.barVariantEntry?.allowMultipleMeasures === true) return true;
    if (this.isBarComparison) return true;
    // Measures ARE the series here; picking a Split reverts to one measure.
    if (this.barSeriesComesFromMeasures) return true;
    return !this.barVariantUsesSeries;
  }

  /** Config-driven orientation-aware axis labels (X/Y ↔ category/value). */
  private get axisLabelsConfig(): Record<string, string> {
    return (this.layout.options?.axisLabels ?? {}) as Record<string, string>;
  }
  get barCategoryAxisLabel(): string {
    const c = this.axisLabelsConfig;
    return this.barOrientation === 'horizontal'
      ? (c['categoryHorizontal'] || 'Y-axis (category)')
      : (c['categoryVertical'] || 'X-axis (category)');
  }
  get barValueAxisLabel(): string {
    const c = this.axisLabelsConfig;
    return this.barOrientation === 'horizontal'
      ? (c['valueHorizontal'] || 'X-axis (values)')
      : (c['valueVertical'] || 'Y-axis (values)');
  }
  /** Which secondary picker is the REAL dual value-axis for the current
   *  orientation: Secondary Y when Vertical, Secondary X when Horizontal. The
   *  other is the category side and can't carry a second data column on a bar. */
  get barSecondaryValueAxis(): 'x' | 'y' {
    return this.barOrientation === 'horizontal' ? 'x' : 'y';
  }
  /** True for the secondary picker that actually drives a dual axis (the other is
   *  shown but inert, with a hint, per the "both pickers" request). */
  isBarSecondaryAxisActive(which: 'x' | 'y'): boolean {
    return this.barSecondaryValueAxis === which;
  }
  /** The column chosen on whichever secondary picker is active (or ''). */
  get barActiveSecondaryField(): string {
    return this.barSecondaryValueAxis === 'x' ? this.barSecondaryXField : this.barSecondaryYField;
  }

  /** Memoized accordion groups — see barFormatGroups. Built once from the config
   *  (which is stable after load); a plain getter here would return a NEW array +
   *  new objects every change-detection cycle, so *ngFor (no trackBy) would tear
   *  down & rebuild the whole accordion on every CD — which made a header click
   *  never appear to open its section. Cached ⇒ stable reference ⇒ clicks stick. */
  private barFormatGroupsCache: FormatControlGroup[] | null = null;
  /** Layout type the cache was built for — a chart-type switch rebuilds it. */
  private barFormatGroupsCacheType = '';

  /** Format controls applicable to the ACTIVE layout type, grouped into ordered
   *  accordion sections for the Build form (Bar & Waterfall both render these).
   *  Title lives in its OWN accordion (the top-form Title field was removed), so
   *  every configured group is shown as an accordion. */
  get barFormatGroups(): FormatControlGroup[] {
    // Table & Matrix gates controls per sub-type (visibleWhenType), so its
    // accordion must rebuild when the Table type changes — key on it.
    const cacheKey = this.isTableMatrixLayout ? `${this.selectedLayoutType}|${this.tmType}` : this.selectedLayoutType;
    if (this.barFormatGroupsCache && this.barFormatGroupsCacheType === cacheKey) {
      return this.barFormatGroupsCache;
    }
    const applicable = this.applicableFormatControls(this.selectedLayoutType)
      .filter((control) => this.controlVisibleForSubType(control, this.isTableMatrixLayout ? this.tmType : ''));
    const byGroup = new Map<string, FormatControl[]>();
    const seen: string[] = [];
    applicable.forEach((control) => {
      const group = control.group || 'Format';
      if (!byGroup.has(group)) {
        byGroup.set(group, []);
        seen.push(group);
      }
      byGroup.get(group)!.push(control);
    });
    const order = this.formatGroupOrder;
    seen.sort((a, b) => {
      const ia = order.indexOf(a);
      const ib = order.indexOf(b);
      return (ia === -1 ? order.length + seen.indexOf(a) : ia) - (ib === -1 ? order.length + seen.indexOf(b) : ib);
    });
    // LAYER 2 — the Capability Registry's `sections` allow-list gates which
    // accordions appear, so unsupported sections are never shown for this chart.
    const exposed = seen.filter((name) => this.chartExposesSection(name));
    this.barFormatGroupsCacheType = cacheKey;
    this.barFormatGroupsCache = exposed.map((name) => ({ name, controls: byGroup.get(name)! }));
    return this.barFormatGroupsCache;
  }

  /** Whether a format control applies to the given family sub-type (Table &
   *  Matrix normal/editable/drilldown). Empty subType or no `visibleWhenType`
   *  on the control → always visible (non-family charts are unaffected). */
  private controlVisibleForSubType(control: FormatControl, subType: string): boolean {
    if (!subType) return true;
    const allowed = control.visibleWhenType;
    return !Array.isArray(allowed) || !allowed.length || allowed.includes(subType);
  }

  isBarFormatGroupOpen(group: string): boolean {
    return this.openBarFormatGroups.has(group);
  }

  toggleBarFormatGroup(group: string): void {
    // Reassign the Set (new reference) so change detection is unambiguous.
    const next = new Set(this.openBarFormatGroups);
    if (next.has(group)) next.delete(group);
    else next.add(group);
    this.openBarFormatGroups = next;
  }

  /** Seed barFormat with every control default applicable to the ACTIVE layout
   *  type (so a value exists before the user touches anything). Idempotent —
   *  only fills missing keys. */
  private seedBarFormatDefaults(): void {
    this.applicableFormatControls(this.selectedLayoutType).forEach((control) => {
      if (control.default !== undefined && this.barFormat[control.key] === undefined) {
        this.barFormat[control.key] = control.default;
      }
    });
  }

  get numberFormats(): LabelValueOption[] {
    return this.layout.options?.numberFormats?.length
      ? this.layout.options.numberFormats
      : [
          { label: 'Default', value: 'NONE' },
          { label: 'Commas (1,234)', value: 'THOUSANDS' },
          { label: 'Auto scale (K/M/B)', value: 'COMPACT' },
          { label: 'Thousands (K)', value: 'SCALE_THOUSANDS' },
          { label: 'Millions (M)', value: 'MILLIONS' },
          { label: 'Billions (B)', value: 'BILLIONS' },
          { label: 'Percent (12%)', value: 'PERCENT' },
        ];
  }
  /** All configured per-tile format controls (unfiltered). */
  get formatControls(): FormatControl[] {
    return this.layout.options?.formatControls ?? [];
  }

  /** Enabled controls applicable to a layout type, DEDUPED by key: when a
   *  chart's own control (explicit `appliesTo`) and a shared shell control (no
   *  `appliesTo`) share a key, the chart-specific one wins — e.g. Scatter's
   *  "Show point labels" replaces the shell's "Show data labels" instead of
   *  both toggles rendering (and fighting over the same format key). */
  private applicableFormatControls(layoutType: string): FormatControl[] {
    // A GROUP card is a container, not a chart, so it takes ONLY controls that
    // opt into it explicitly (its own border / background design set). Shared
    // shell controls carry no `appliesTo` and would otherwise all land in the
    // group's popup — "Show data labels" on a card that draws no series.
    const groupOnly = layoutType === 'GROUP';
    const applicable = this.formatControls.filter(
      (control) =>
        control.enabled !== false &&
        (groupOnly
          ? !!control.appliesTo?.includes('GROUP')
          : !control.appliesTo || !control.appliesTo.length || control.appliesTo.includes(layoutType)),
    );
    const winners = new Map<string, FormatControl>();
    applicable.forEach((control) => {
      const prev = winners.get(control.key);
      const specific = !!(control.appliesTo && control.appliesTo.length);
      const prevSpecific = !!(prev && prev.appliesTo && prev.appliesTo.length);
      if (!prev || (specific && !prevSpecific)) winners.set(control.key, control);
    });
    return applicable.filter((control) => winners.get(control.key) === control);
  }
  /** Options list for a select-type format control (resolved via optionsKey). */
  formatControlOptions(control: FormatControl): LabelValueOption[] {
    const key = control.optionsKey;
    const list = key ? ((this.layout.options as Record<string, unknown> | undefined)?.[key] as LabelValueOption[] | undefined) : undefined;
    return Array.isArray(list) ? list : [];
  }

  // ── Phase A: workspace & target context ───────────────────────────────────
  selectedLayoutType = 'WATERFALL';
  /** LAYER 1 — the Chart Catalogue category currently selected (drives the Type
   *  picker). Defaults to whichever category owns the initial layout type. */
  selectedChartCategory = '';
  private lastSelectedLayoutType = this.selectedLayoutType;
  entityOptions: string[] = [];
  entitiesLoading = false;
  selectedDatasets: string[] = [];

  title = '';
  componentId = this.createComponentId();
  dashboardId: number | null = null;
  componentType = 'CHART';

  // ── Phase B: sequential step builder (WATERFALL) ──────────────────────────
  steps: BridgeStep[] = [];

  // ── Drill-down builder (DRILL_DOWN_TABLE) — single dataset, N dimensions, N measures.
  drillDownDimensions: DrillDownDimension[] = [];
  drillDownMeasures: DrillDownMeasure[] = [];
  /** `data` input for the shared <lib-drill-down> widget — null until the
   *  user clicks Generate; the widget self-fetches via ConfigService.getConfig
   *  using apiUrl/customPayload/skipDefaultPayload once mounted. */
  drillDownWidgetData: Record<string, unknown> | null = null;

  // ── Table & Matrix (TABLE_MATRIX) — one unified family, config-driven type.
  //    `tmType` selects Normal / Editable / Drill-Down (flags from config).
  //    Normal/Editable pick columns (empty = all); Drill-Down REUSES the
  //    drillDownDimensions/drillDownMeasures state above.
  tmType = 'normal';
  /** Columns chosen for Normal/Editable tables (empty = show every column of
   *  the primary dataset). Each row picks its OWN dataset (defaults to the
   *  primary one) + field + display name — the payload sends them as
   *  `{dataset, field, displayName}` objects so columns can mix datasets. */
  tmColumns: TmColumnDef[] = [];
  /** Field-name view of `tmColumns` — kept for every consumer that only needs
   *  the flat field list (tree payload fallback, reorder sync, drop dedup). */
  get tmColumnFields(): string[] {
    return this.tmColumns.map((column) => column.field).filter(Boolean);
  }
  /** Column to group Normal-table rows by (subtotals per group). */
  tmGroupBy = '';
  /** Row/group roll-up aggregation for Normal tables. */
  tmAggregation = 'none';
  // ── New shape types (Matrix/Pivot/Grouped/Tree Grid/Financial) ──────────────
  tmRowField = '';           // Matrix row dimension
  tmColumnField = '';        // Matrix column dimension
  tmValueField = '';         // Matrix/Pivot measure
  tmRowFields: string[] = [];    // Pivot row fields
  tmColumnFields2: string[] = []; // Pivot column fields
  tmGroupByFields: string[] = []; // Grouped: ordered group levels
  tmGroupsExpanded = true;
  tmTreeIdField = '';        // Tree Grid: row id column
  tmTreeParentField = '';    // Tree Grid: parent id column
  tmRoleField = '';          // Financial: row role column
  tmIndentField = '';        // Financial: indent column
  /** `data` input for <app-unified-table-matrix-chart> — null until Generate. */
  tableMatrixWidgetData: Record<string, unknown> | null = null;

  // ── Table & Matrix navigation (cell → target dashboard, filtered) ──────────
  tmNavEnabled = false;
  /** Column whose clicked value is sent as the filter (defaults to first column). */
  tmNavFilterKey = '';
  /** Columns whose cells are clickable (empty = the filterKey column only). */
  tmNavColumns: string[] = [];
  /** Target published dashboard slug. */
  tmNavTargetDashboard = '';
  /** Target component ids on that dashboard (empty = all charts). */
  tmNavTargetComponents: string[] = [];
  /** Navigate button label. */
  tmNavButtonLabel = 'Navigate';
  /** Whether the Navigation accordion is open in the form. */
  tmNavAccordionOpen = false;
  /** Published dashboards fetched from the API (same list the sidebar shows) —
   *  the authoritative target-dashboard source (localStorage may be cold). Each:
   *  {label:name, value:slug, layoutJson} so we can also list its components. */
  private apiTargetDashboards: Array<{ label: string; value: string; layoutJson: Record<string, unknown> }> = [];

  // ── Horizontal Bar builder (HORIZONTAL_BAR_CHART) — single dataset, one
  //    group-by field on the Y-axis, one formula-driven value on the X-axis.
  hbGroupByField = '';
  hbGroupByLabel = '';
  hbValueLabel = 'Value';
  hbValueFormula = '';
  /** Per-group formula overrides (backend: `value.groupFormulas`) - when two
   *  groups genuinely compute their value differently (e.g. "Base Options"
   *  folds in SSA $ while "Additional Options" folds in Surcharge instead),
   *  rather than one formula applying uniformly to every group. A group whose
   *  value isn't listed here falls back to `hbValueFormula`. */
  hbGroupFormulas: Array<{ group: string; formula: string }> = [];
  /** `data` input for the shared <app-horizontal-bar-chart> — null until the
   *  user clicks Generate; set with the POST response's rows as staticData. */
  hbWidgetData: Record<string, unknown> | null = null;

  addHbGroupFormula(): void {
    this.hbGroupFormulas = [...this.hbGroupFormulas, { group: '', formula: '' }];
  }

  removeHbGroupFormula(entry: { group: string; formula: string }): void {
    this.hbGroupFormulas = this.hbGroupFormulas.filter((item) => item !== entry);
  }

  // ── KPI card (KPI) — single dataset, one column aggregated to a single value.
  kpiField = '';
  kpiAggregation = 'SUM';

  // ── Bar & Column (BAR) — one dataset, one category, N measures, optional
  //    series split. Orientation toggle + variant picker decide how it renders.
  barOrientation: BarColumnOrientation = 'vertical';
  barVariant: BarColumnVariant = 'simple';
  barCategory = '';
  /** Optional outer grouping column → Hierarchical Category Axis (parent → child). */
  barCategoryParent = '';
  barCategoryLabel = '';
  barSeries = '';

  // ── Bar value formula (BACKEND-computed, unlike Formula Builder columns —
  //    these can reference a live input via [Token] + references). ───────────
  /** Blank → falls back to `SUM(<first measure>)`. */
  barValueFormula = '';
  barValueLabel = '';
  /** Per-category overrides; unlisted categories use `barValueFormula`. */
  barGroupFormulas: Array<{ group: string; formula: string }> = [];

  addBarGroupFormula(): void {
    this.barGroupFormulas = [...this.barGroupFormulas, { group: '', formula: '' }];
  }

  removeBarGroupFormula(entry: { group: string; formula: string }): void {
    this.barGroupFormulas = this.barGroupFormulas.filter((item) => item !== entry);
  }

  get barUsesValueFormula(): boolean {
    return !!this.barValueFormula.trim()
      || this.barGroupFormulas.some((entry) => entry.group.trim() && entry.formula.trim());
  }

  /** Reference / target lines drawn across the value axis (value + label + color). */
  barReferenceLines: Array<{ id: string; value: number | null; label: string; color: string }> = [];
  private barRefLineIdCounter = 0;
  barMeasures: BarBuilderMeasure[] = [];
  /** Optional column mapped onto the SECONDARY value axis. A bar chart has one
   *  category axis + up to two value axes, so only the value-side secondary is
   *  real: Secondary Y is live when Vertical, Secondary X when Horizontal. The
   *  active one is folded into capabilities as an extra measure on the y2/x2 axis. */
  barSecondaryXField = '';
  barSecondaryYField = '';
  private barMeasureIdCounter = 0;

  // ── Line & Area (LINE_AREA) — one dataset, one category (X), N measures, optional
  //    series split. Variant picker decides line/step/spline/area/stacked rendering.
  //    Reuses `barFormat` (the shared Format-accordion bag) like KPI/HBAR/Table do.
  laVariant: LineAreaVariant = 'line';
  laCategory = '';
  /** Kept optional for a future Hierarchical Category Axis (phase 1: unused). */
  laCategoryParent = '';
  laCategoryLabel = '';
  laSeries = '';
  /** Reference / target lines drawn across the value axis (value + label + color). */
  laReferenceLines: Array<{ id: string; value: number | null; label: string; color: string }> = [];
  private laRefLineIdCounter = 0;
  laMeasures: LineAreaBuilderMeasure[] = [];
  /** Optional column mapped onto the SECONDARY (y2) value axis. Folded into
   *  capabilities as an extra measure {renderAs:'line', axis:'y2'}. */
  laSecondaryYField = '';
  /** Optional column mapped onto a SECONDARY (top) X axis — its own value series
   *  plotted against a second x-axis (capabilities.secondaryXField/…YForX). */
  laSecondaryXField = '';
  /** The value column plotted against the secondary X axis (defaults to the
   *  first measure when a secondary X column is chosen but no value picked). */
  laSecondaryXValueField = '';
  private laMeasureIdCounter = 0;

  /** Bar chart's Title/Axis/Tooltip/Formatting/Advanced options, chosen in the
   *  Build form (keyed by FormatControl.key) — folded into the capabilities on
   *  Generate. Same control config the tile Format popup uses, rendered inline. */
  barFormat: Record<string, unknown> = {};
  /** Which Build-form format accordions are expanded (by group name). */
  openBarFormatGroups = new Set<string>();
  /** The bespoke "Chart Data" accordion (axis/measure mapping) — open by default. */
  barDataAccordionOpen = true;
  /** dataset name → full rows, cached so the BAR chart aggregates client-side
   *  without re-fetching (getEntityRows already returns full row objects). */
  private entityRowsCache = new Map<string, Array<Record<string, unknown>>>();

  // ── Scatter & Bubble (SCATTER_BUBBLE) — one dataset; X + Y measures, optional
  //    Size (bubble), Color-by, and Series (multi-scatter). Variant picker chooses
  //    Scatter / Multi Scatter / Bubble / Quadrant. Renders client-side.
  /** Active side of the Orientation segmented toggle (a `scatterBubbleModes`
   *  value) — filters the Variant dropdown. Not persisted: derived from the
   *  saved variant on rehydrate. */
  sbMode = 'scatter';
  /** Advanced analytic overlay applied on top of the base variant ('' = none).
   *  Persisted in capabilities as `advanced`; flags OR into variantFlags. */
  sbAdvanced = '';
  /** Advanced-overlays accordion (Quadrant / Regression / …) expanded? */
  sbAdvancedAccordionOpen = false;
  sbVariant: ScatterBubbleVariant = 'scatter';
  sbXField = '';
  sbYField = '';
  sbSizeField = '';
  sbColorField = '';
  sbSeries = '';
  /** Row rollup for points sharing X + group ('none' = plot every raw row). */
  sbAggregation = 'none';
  /** Extra dataset columns appended to the hover tooltip. */
  sbTooltipColumns: string[] = [];
  sbXLabel = '';
  sbYLabel = '';
  /** Scatter/Bubble Title/Axis/…/Advanced options chosen in the Build form. */
  sbFormat: Record<string, unknown> = {};
  /** Which Build-form format accordions are expanded (by group name). */
  openSbFormatGroups = new Set<string>();
  /** The bespoke "Chart Data" accordion (X/Y/Size/Color/Series mapping). */
  sbDataAccordionOpen = true;

  // ── Formula Builder (BAR) — row-level COMPUTED columns derived from the
  //    dataset's columns (e.g. "Margin = (profit / net_sales) * 100"). Reuses the
  //    generate-visualization formula engine (SelfServiceFormulaService). Each
  //    computed column is applied to the dataset rows before the chart aggregates,
  //    and its name appears in the Category/Measure/Split pickers like any column.
  barComputedColumns: ComputedColumn[] = [];
  /** New-formula draft (the "Add column" inputs). */
  newBarColumnName = '';
  newBarColumnExpression = '';
  newBarColumnError = '';
  /** The Formula Builder sub-accordion (inside Chart Data) — collapsed by default. */
  barFormulaAccordionOpen = false;
  /** Optional formula-driven value (e.g. "[price] * [qty]") — when set, the
   *  compute API uses it instead of the plain Column + Aggregation pair, same
   *  contract as the horizontal bar chart's `hbValueFormula`. */
  kpiFormula = '';

  // ── Dropdown control (DROPDOWN) — distinct values from one dataset column
  //    are exposed as options and the selected value filters the chosen targets.
  dropdownField = '';
  dropdownDisplayLabel = '';
  dropdownIsMultiSelect = false;
  dropdownDefaultValue = '';
  /** How the control renders: 'dropdown' (classic select) | 'buttons' | 'tiles'
   *  (every option an always-visible filter button). Rides the payload +
   *  widgetData so the published view needs no extra wiring. */
  dropdownDisplayMode = 'dropdown';
  /** "Display as" items (config `dropdownDisplayModes`, built-in fallback).
   *  MEMOIZED — a fresh [items] array each CD cycle breaks ng-select
   *  appendTo:'body' (panel rebuilds mid-click and swallows the selection). */
  private dropdownDisplayModesCache: LabelValueOption[] = [];
  private dropdownDisplayModesKey: unknown = undefined;
  get dropdownDisplayModes(): LabelValueOption[] {
    const fromConfig = (this.layout.options as Record<string, unknown> | undefined)?.['dropdownDisplayModes'] as LabelValueOption[] | undefined;
    const key = Array.isArray(fromConfig) && fromConfig.length ? fromConfig : 'fallback';
    if (key !== this.dropdownDisplayModesKey) {
      this.dropdownDisplayModesCache = key === 'fallback'
        ? [
            { label: 'Dropdown', value: 'dropdown' },
            { label: 'Buttons', value: 'buttons' },
            { label: 'Tiles', value: 'tiles' },
          ]
        : (fromConfig as LabelValueOption[]);
      this.dropdownDisplayModesKey = key;
    }
    return this.dropdownDisplayModesCache;
  }

  // ── File Upload control (FILE_UPLOAD) — a standalone uploader that POSTs the
  //    picked file to the compute API and re-renders the chosen target charts/
  //    tables. Targets are captured in `selectedTargetCharts` (shared).
  fileUploadButtonLabel = 'Upload file';
  fileUploadAccept = '.xlsx,.xls,.csv';

  // ── Input controls (INPUT_BOX / SLIDER) — a dashboard input whose value feeds
  //    a formula/measure into one or more target charts. Title comes from the
  //    shared `title` field above; the rest is captured here.
  inputFormula = '';
  /** Per-target formula, keyed by the target chart's componentId. The input's
   *  value feeds EACH target's own expression (e.g. a KPI can use `[price]*value`
   *  while a waterfall uses a different one). `value` in the expression is the
   *  control's live value. */
  targetFormulas: Record<string, string> = {};
  /** Per-step formulas for a WATERFALL target, keyed by [target componentId][step
   *  label] — used instead of `targetFormulas` when the target is a waterfall,
   *  since each bar (Start / Change / Total) needs its own independent formula. */
  targetStepFormulas: Record<string, Record<string, string>> = {};
  /** Typed-in references for a target's formula tokens, keyed by target
   *  componentId. Each entry says "this bracket token isn't a plain dataset
   *  column — it's another input's live/recomputed output", so the backend
   *  can resolve the CURRENT value instead of a stale one. User types every
   *  field by hand (no drag-and-drop for this). */
  targetFormulaReferences: Record<string, TargetFormulaReferenceEntry[]> = {};
  /** Default/seed value the control shows on load (slider handle position, or
   *  the input box's pre-filled number). Null = no default. */
  inputDefaultValue: number | null = null;
  /** Target charts (by component id) this input drives — see targetCharts. */
  selectedTargetCharts: string[] = [];

  /** The OPTIONAL Chart-interactions accordion in the build form — collapsed by
   *  default; opened by its toggle, or automatically when editing a tile that
   *  already has targets wired. (Steps/axes sections stay always visible —
   *  those are required.) */
  interactionsSectionOpen = false;
  // Slider-only bounds.
  sliderMin = 0;
  sliderMax = 100;
  sliderStep = 1;
  /** Live value bound to the rendered preview control once generated. */
  inputPreviewValue: number | string | null = null;
  /** True once Generate has run for an input control — drives the preview. */
  inputControlReady = false;

  /** Drill-Down Table AND Horizontal Bar bind to exactly one dataset (unlike
   *  WATERFALL, which can combine several) — the first of the shared multi-select. */
  get drillDataset(): string {
    return this.selectedDatasets[0] || '';
  }
  get drillDatasetColumns(): string[] {
    return this.drillDataset ? this.entityColumnsCache.get(this.drillDataset) || [] : [];
  }
  get isDrillDatasetColumnsLoading(): boolean {
    return this.isDatasetColumnsLoading(this.drillDataset);
  }

  // ── Dashboard preview canvas ───────────────────────────────────────────────
  // The right/left pane is a full dashboard (not a single chart): a named canvas
  // holding N tiles laid out in a fixed 12-column grid (they flow, never overlap,
  // and fit the width). Each "Generate Configuration" appends the built
  // chart/input as a new tile; "New chart" resets the builder form.
  static readonly DASH_COLS = 12;
  static readonly DASH_ROW_PX = 40;
  static readonly DASH_GAP = 12;

  dashboardName = '';
  dashboardTiles: DashboardTile[] = [];
  /** Active dashboard cross-filter published by a bar tile click (null = none).
   *  Listener tiles re-render from their rows filtered by column IN `values`
   *  (multi-select: each bar click toggles a value in/out). */
  activeCrossFilter: { column: string; values: string[]; sourceTileId: string } | null = null;
  /** Which tile's cross-filter "listener editor" panel is expanded (null = none).
   *  Mirrors the dashboard-builder's expandedListenerEditor. */
  expandedListenerTileId: string | null = null;
  /** Tile currently loaded into Bridge steps for editing. Generate replaces it. */
  editingTileId: string | null = null;
  /** Auto-created DRAFT bar tile shown live on the canvas while building, BEFORE
   *  Generate. Generate finalizes it (clears this id → becomes a normal tile). */
  private previewTileId: string | null = null;
  /** "Group" mode: click tiles to select (checkbox appears), then Group them
   *  into one card. Off by default so normal builder use is untouched. */
  groupSelectMode = false;
  selectedTileIds = new Set<string>();
  activeSavedDashboardId = '';
  /** The exact `dashboardId`/URL slug this dashboard was already published under,
   *  read back from the restored layout_json when "Back to Builder" reopens an
   *  existing dashboard. `activeSavedDashboardId` alone isn't enough to survive
   *  that round trip — it's only ever set by a save/publish performed in THIS
   *  component instance, and the route navigation destroys and recreates it, so
   *  without this saveDashboardInternal fell back to matching the local
   *  saved-dashboards cache by name, minted a brand-new slug when that lookup
   *  missed, and republished as an insert — a different row than the one being
   *  edited, discarding the prior layout edits. */
  private restoredDashboardId = '';
  private restoredPublishedLayout: Record<string, unknown> | null = null;
  isSavingDashboard = false;
  /** True once this dashboard is known to already have an owned `dashboard_layouts`
   *  row (found by name match against the locally cached saved dashboards, or by
   *  a resolved uuid from a prior publish this session) — drives the Publish
   *  button's label ("Update" vs "Publish") since a re-save now updates that row
   *  instead of inserting a duplicate. */
  isExistingPublishedDashboard = false;
  /** Full-screen read-only view of the finished dashboard. */
  isDashboardFullscreen = false;
  /** In Preview, toggles the tile edit chrome (drag/resize) back on so the user
   *  can rearrange the layout without leaving the published-style preview. */
  previewEditLayout = false;
  /** Id of the tile whose Format popup is open (null = none). */
  openFormatTileId: string | null = null;
  formatPopupAlign: 'left' | 'right' = 'right';
  /** Id of the tile whose Interactions popup is open (null = none). Lets a
   *  user wire targets/references AFTER other components exist, instead of only
   *  at create time (see toggleTileInteractions). */
  openInteractionTileId: string | null = null;
  private tileIdCounter = 0;
  /** Index of the tile being dragged to reorder (HTML5 DnD), or null. */
  tileDragIndex: number | null = null;
  @ViewChild('dashboardCanvas') private dashboardCanvasRef?: ElementRef<HTMLElement>;
  /** Active corner-resize of a tile (grid-snapped), null when idle. */
  private activeTileInteraction:
    | {
        tile: DashboardTile;
        startX: number;
        startY: number;
        startColSpan: number;
        startRowSpan: number;
      }
    | null = null;

  // ── Generate / preview ─────────────────────────────────────────────────────
  isGenerating = false;
  generateError = '';
  /** True while an input-control recompute POST is in flight — drives the
   *  "Recomputing…" overlay on the dashboard canvas. */
  isRecomputing = false;
  previewOption: echarts.EChartsOption | null = null;

  targetPanelCollapsed = false;
  stepsPanelCollapsed = false;
  previewPanelCollapsed = false;
  dataPanelCollapsed = false;
  paneOrder: BuilderPane[] = ['preview', 'steps', 'data'];
  draggingPane: BuilderPane | null = null;

  targetPaneWidth = 230;
  stepsPaneWidth = 320;
  dataPaneWidth = 320;
  dataPaneFilter = '';
  fieldPaneFilter = '';
  activeExplorerEntity = '';
  explorerColumns: string[] = [];
  explorerColumnsLoading = false;

  private activePaneResize: { pane: 'steps' | 'data'; startX: number; startWidth: number; direction: 1 | -1 } | null = null;

  private stepIdCounter = 0;
  private drillDimensionIdCounter = 0;
  private drillMeasureIdCounter = 0;
  /** dataset name -> column names for that dataset — shared across bridge
   *  steps AND drill-down dimensions/measures, since they all just need
   *  "columns for X dataset". */
  private entityColumnsCache = new Map<string, string[]>();
  private datasetColumnsLoading: Record<string, boolean> = {};
  private datasetColumnsError: Record<string, string> = {};
  // Same identity-churn hazard as the chart-builder's mapping selects: memoize
  // per row id (bridge-step id — the only row kind still needing per-row
  // column lists, since drill-down dimensions/measures all share
  // drillDatasetColumns directly) so ng-select's [items] array reference only
  // changes when the underlying dataset's columns actually change.
  private rowColumnsMemo = new Map<string, { dataset: string; source: string[] | undefined; result: string[] }>();

  get filteredExplorerEntities(): string[] {
    const query = this.dataPaneFilter.trim().toLowerCase();
    return query ? this.entityOptions.filter((entity) => entity.toLowerCase().includes(query)) : this.entityOptions;
  }

  get filteredExplorerColumns(): string[] {
    const query = this.fieldPaneFilter.trim().toLowerCase();
    return query ? this.explorerColumns.filter((column) => column.toLowerCase().includes(query)) : this.explorerColumns;
  }

  get paneGridTemplate(): string {
    return this.paneOrder.map((pane) => this.paneTrack(pane)).join(' 6px ');
  }

  paneGridColumn(pane: BuilderPane): number {
    return this.paneOrder.indexOf(pane) * 2 + 1;
  }

  /** Keeps the collapse affordance correct after panes are reordered. */
  paneCollapseIcon(pane: BuilderPane): 'chevron_left' | 'chevron_right' {
    const sitsOnRight = this.paneOrder.indexOf(pane) >= Math.ceil(this.paneOrder.length / 2);
    const collapsed = this.isPaneCollapsed(pane);
    return sitsOnRight === collapsed ? 'chevron_left' : 'chevron_right';
  }

  onPaneDragStart(pane: BuilderPane, event: DragEvent): void {
    this.draggingPane = pane;
    event.dataTransfer?.setData(PANE_DRAG_TYPE, pane);
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
  }

  onPaneDragOver(event: DragEvent): void {
    if (!this.draggingPane && !Array.from(event.dataTransfer?.types || []).includes(PANE_DRAG_TYPE)) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
  }

  onPaneDrop(target: BuilderPane, event: DragEvent): void {
    if (!this.draggingPane && !Array.from(event.dataTransfer?.types || []).includes(PANE_DRAG_TYPE)) return;
    event.preventDefault();
    const source = this.draggingPane || event.dataTransfer?.getData(PANE_DRAG_TYPE) as BuilderPane;
    if (!source || source === target || !this.paneOrder.includes(source)) return;
    const reordered = this.paneOrder.filter((pane) => pane !== source);
    const targetElement = event.currentTarget as HTMLElement;
    const targetBounds = targetElement.getBoundingClientRect();
    const insertAfterTarget = event.clientX > targetBounds.left + targetBounds.width / 2;
    const targetIndex = reordered.indexOf(target);
    reordered.splice(targetIndex + (insertAfterTarget ? 1 : 0), 0, source);
    this.paneOrder = reordered;
    this.draggingPane = null;
  }

  onPaneDragEnd(): void {
    this.draggingPane = null;
  }

  startDividerResize(dividerIndex: 0 | 1, event: MouseEvent): void {
    event.preventDefault();
    const leftPane = this.paneOrder[dividerIndex];
    const rightPane = this.paneOrder[dividerIndex + 1];
    const pane = rightPane === 'preview' ? leftPane : rightPane;
    if (pane === 'preview' || this.isPaneCollapsed(pane)) return;
    const startWidth = pane === 'steps' ? this.stepsPaneWidth : this.dataPaneWidth;
    this.activePaneResize = { pane, startX: event.clientX, startWidth, direction: pane === leftPane ? 1 : -1 };
  }

  isDividerDisabled(dividerIndex: 0 | 1): boolean {
    const adjacent = [this.paneOrder[dividerIndex], this.paneOrder[dividerIndex + 1]];
    return adjacent.every((pane) => pane === 'preview' || this.isPaneCollapsed(pane));
  }

  @HostListener('window:mousemove', ['$event'])
  onPaneResize(event: MouseEvent): void {
    // A tile corner-resize takes priority over pane resizing. Convert the pixel
    // drag into whole grid-cell span changes (snapped) so tiles stay aligned and
    // can't overlap.
    if (this.activeTileInteraction) {
      const i = this.activeTileInteraction;
      const { colUnit, rowUnit } = this.gridUnitSizes();
      const deltaCols = colUnit ? Math.round((event.clientX - i.startX) / colUnit) : 0;
      const deltaRows = rowUnit ? Math.round((event.clientY - i.startY) / rowUnit) : 0;
      i.tile.colSpan = this.clamp(i.startColSpan + deltaCols, 2, DvmLayoutBuilderComponent.DASH_COLS);
      i.tile.rowSpan = this.clamp(i.startRowSpan + deltaRows, 3, 40);
      return;
    }
    if (!this.activePaneResize) return;
    const delta = event.clientX - this.activePaneResize.startX;
    const { pane, startWidth, direction } = this.activePaneResize;
    if (pane === 'steps') this.stepsPaneWidth = this.clamp(startWidth + delta * direction, 240, 560);
    if (pane === 'data') this.dataPaneWidth = this.clamp(startWidth + delta * direction, 300, 620);
  }

  @HostListener('window:mouseup')
  stopPaneResize(): void {
    this.activePaneResize = null;
    if (this.activeTileInteraction) {
      this.activeTileInteraction = null;
      // Tile changed size — nudge the embedded ECharts widgets to re-fit.
      this.reflowCharts();
    }
  }

  /** Pixel size of one grid column-step and one row-step (track + gap), measured
   *  from the live canvas so snapped resizing matches what the user sees. */
  private gridUnitSizes(): { colUnit: number; rowUnit: number } {
    const gap = DvmLayoutBuilderComponent.DASH_GAP;
    const cols = DvmLayoutBuilderComponent.DASH_COLS;
    const canvas = this.dashboardCanvasRef?.nativeElement;
    // scrollWidth covers the case where the narrow builder pane made the canvas
    // scroll sideways (grid min column width) — the tracks span the CONTENT
    // width, not the visible clientWidth.
    const inner = canvas ? Math.max(canvas.clientWidth, canvas.scrollWidth) - 32 /* padding */ : cols * 80;
    const track = (inner - (cols - 1) * gap) / cols;
    return { colUnit: track + gap, rowUnit: DvmLayoutBuilderComponent.DASH_ROW_PX + gap };
  }

  /** ECharts-based widgets resize on window resize; dispatch one so they re-fit
   *  their tile after a layout change (resize / reorder / fullscreen toggle). */
  private reflowCharts(): void {
    setTimeout(() => window.dispatchEvent(new Event('resize')), 60);
  }

  private paneTrack(pane: BuilderPane): string {
    if (this.isPaneCollapsed(pane)) return '48px';
    if (pane === 'steps') return `${this.stepsPaneWidth}px`;
    if (pane === 'data') return `${this.dataPaneWidth}px`;
    return 'minmax(440px, 1fr)';
  }

  private isPaneCollapsed(pane: BuilderPane): boolean {
    return pane === 'preview' ? this.previewPanelCollapsed : pane === 'steps' ? this.stepsPanelCollapsed : this.dataPanelCollapsed;
  }

  selectExplorerEntity(entity: string): void {
    this.activeExplorerEntity = entity;
    this.fieldPaneFilter = '';
    const cached = this.entityColumnsCache.get(entity);
    if (cached) {
      this.explorerColumns = cached;
      return;
    }
    this.explorerColumns = [];
    this.explorerColumnsLoading = true;
    this.datasetService.getEntityAttributes(entity, this.entityGroupName).subscribe({
      next: (attributes) => {
        const columns = this.filterVisibleColumns(attributes);
        this.entityColumnsCache.set(entity, columns);
        // Cache the rows too — the Bar & Column chart aggregates them client-side.
        // Without this, expanding a table here (columns-only) then Generating a
        // bar chart left entityRowsCache empty → a blank chart.
        this.loadRowsForDataset(entity);
        if (this.activeExplorerEntity === entity) this.explorerColumns = columns;
        this.explorerColumnsLoading = false;
      },
      error: () => {
        this.explorerColumnsLoading = false;
        this.ui.showError(`Unable to load columns for "${entity}".`);
      },
    });
  }

  onExplorerFieldDragStart(event: DragEvent, dataset: string, field: string): void {
    event.stopPropagation();
    event.dataTransfer?.setData(FIELD_DRAG_TYPE, JSON.stringify({ dataset, field }));
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'copy';
  }

  allowFieldDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
  }

  onStepFieldDrop(event: DragEvent, step: BridgeStep): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping || step.stepType === 'TOTAL') return;
    if (step.stepType === 'CHANGE' && step.formulaMode) {
      step.formula = this.appendFormulaToken(step.formula, mapping.field);
      this.onStepFormulaOrLabelChange();
      return;
    }
    if (!this.selectedDatasets.includes(mapping.dataset)) this.selectedDatasets = [...this.selectedDatasets, mapping.dataset];
    step.dataset = mapping.dataset;
    step.field = mapping.field;
    this.loadColumnsForDataset(mapping.dataset);
    this.steps = [...this.steps];
    this.invalidatePreview();
  }

  onDrillDimensionFieldDrop(event: DragEvent, dimension?: DrillDownDimension): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    const target = dimension || { id: this.nextDrillDimensionId(), field: '', label: '' };
    target.field = mapping.field;
    if (!target.label || /^Level \d+$/.test(target.label)) target.label = mapping.field;
    if (!dimension) this.drillDownDimensions = [...this.drillDownDimensions, target];
    this.onDrillDimensionChange();
  }

  onDrillMeasureFieldDrop(event: DragEvent, measure?: DrillDownMeasure): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    const target = measure || {
      id: this.nextDrillMeasureId(), field: '', label: '', aggregation: 'NONE', rollUpStrategy: 'SUM', total: false,
    };
    target.field = mapping.field;
    if (!target.label || /^Measure \d+$/.test(target.label)) target.label = mapping.field;
    if (!measure) this.drillDownMeasures = [...this.drillDownMeasures, target];
    this.onDrillMeasureChange();
  }

  /** Drop a Data-pane field onto the Table & Matrix "Columns" list — appends a
   *  column row for it (in drop order, skipping duplicates), keeping the
   *  dropped field's OWN dataset so cross-dataset columns work. */
  onTmColumnFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    const dataset = mapping.dataset || this.drillDataset;
    const duplicate = this.tmColumns.some(
      (column) => column.field === mapping.field && (column.dataset || this.drillDataset) === dataset,
    );
    if (!duplicate) {
      this.tmColumns = [...this.tmColumns, {
        id: this.nextTmColumnId(),
        dataset,
        field: mapping.field,
        displayName: this.defaultColumnDisplayName(mapping.field),
        filterField: '',
        filterOperator: 'eq',
        filterValue: '',
      }];
      if (dataset) this.loadColumnsForDataset(dataset);
    }
    this.onTableMatrixTypeChange();
  }

  /** Drop a field onto the Table & Matrix "Group rows by" picker (Normal). */
  onTmGroupByFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    this.tmGroupBy = mapping.field;
    this.onTableMatrixTypeChange();
  }

  onHorizontalBarGroupFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    this.hbGroupByField = mapping.field;
    if (!this.hbGroupByLabel) this.hbGroupByLabel = mapping.field;
    this.invalidatePreview();
  }

  onHorizontalBarValueFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    // Append (build up) rather than replace, so several columns can be dragged
    // in to compose an expression (e.g. "[price] + [cost]").
    this.hbValueFormula = this.appendFormulaToken(this.hbValueFormula, mapping.field);
    if (!this.hbValueLabel || this.hbValueLabel === 'Value') this.hbValueLabel = mapping.field;
    this.invalidatePreview();
  }

  /** Appends the token so several columns can compose one expression. */
  onBarValueFormulaFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    this.barValueFormula = this.appendFormulaToken(this.barValueFormula, mapping.field);
    this.onBarChange();
  }

  /** Drop a column onto a WATERFALL TOTAL step's formula — appends its token. */
  onWaterfallFormulaDrop(step: BridgeStep, event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    step.formula = this.appendFormulaToken(step.formula, mapping.field);
    this.onStepFormulaOrLabelChange();
  }

  /** Drop a column onto the KPI card's column picker. */
  onKpiFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    this.kpiField = mapping.field;
    this.invalidatePreview();
  }

  /** Drop a column onto the KPI card's formula field — appends its token so
   *  several columns can be dragged in to compose an expression. */
  onKpiFormulaFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    this.kpiFormula = this.appendFormulaToken(this.kpiFormula, mapping.field);
    this.invalidatePreview();
  }

  /** Drop a dataset column onto the dropdown option-field picker. */
  onDropdownFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    this.dropdownField = mapping.field;
    if (!this.dropdownDisplayLabel) this.dropdownDisplayLabel = mapping.field;
    this.invalidatePreview();
  }

  /** Drop a column onto a specific target's formula — appends its token. When
   *  `stepLabel` is given, the target is a WATERFALL and the token goes into
   *  that bridge step's own formula instead of a single chart-wide one. */
  onTargetFormulaDrop(event: DragEvent, targetId: string, stepLabel?: string): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    if (stepLabel) {
      this.setTargetStepFormula(targetId, stepLabel, this.appendFormulaToken(this.targetStepFormula(targetId, stepLabel), mapping.field));
      return;
    }
    this.targetFormulas[targetId] = this.appendFormulaToken(this.targetFormulas[targetId] || '', mapping.field);
  }

  /** Display label for a target chart id (falls back to the id). */
  targetLabelForId(id: string): string {
    return this.targetCharts.find((chart) => chart.value === id)?.label || id;
  }

  /** Append a dropped column to a formula as a bracketed token, space-separated
   *  from whatever's already there — lets users build expressions by dragging. */
  private appendFormulaToken(current: string, field: string): string {
    const token = `[${field}]`;
    const trimmed = (current || '').trimEnd();
    return trimmed ? `${trimmed} ${token}` : token;
  }

  private readFieldDrop(event: DragEvent): { dataset: string; field: string } | null {
    event.preventDefault();
    event.stopPropagation();
    const raw = event.dataTransfer?.getData(FIELD_DRAG_TYPE);
    if (!raw) return null;
    try {
      const mapping = JSON.parse(raw) as { dataset?: string; field?: string };
      return mapping.dataset && mapping.field ? { dataset: mapping.dataset, field: mapping.field } : null;
    } catch {
      return null;
    }
  }

  private useDroppedSingleDataset(dataset: string): void {
    this.onSelectedDatasetsChange([dataset, ...this.selectedDatasets.filter((item) => item !== dataset)]);
    this.loadColumnsForDataset(dataset);
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
  }

  ngOnInit(): void {
    this.configService.loadBuilderLayout('dvm-layout', DvmLayoutBuilderComponent.DEFAULT_LAYOUT).subscribe((layout) => {
      const validation = this.configService.validateLayout(layout, DvmLayoutBuilderComponent.STEP_FIELD_IDS);
      if (validation.unknownFields.length) {
        console.warn('[dvm-layout-builder] config references unknown field ids; they will be skipped:', validation.unknownFields);
      }
      this.layout = layout;
      // Config just loaded — drop the memoized accordion groups so they rebuild
      // from the (now real) formatControls instead of the DEFAULT_LAYOUT's.
      this.barFormatGroupsCache = null;
      this.selectedLayoutType = this.layoutTypes[0]?.value || 'WATERFALL';
      // Seed the catalogue category that owns the initial type (Layer 1).
      this.selectedChartCategory = this.categoryOfType(this.selectedLayoutType) || this.chartCategories[0]?.id || '';
      this.componentType = layout.defaults?.componentType || 'CHART';
      this.dashboardId = typeof layout.defaults?.dashboardId === 'number' ? layout.defaults.dashboardId : null;
      this.loadEntityOptions();
      this.restoreDashboardFromRoute();
      this.loadApiTargetDashboards();
    });
  }

  /** Fetch the user's published dashboards from the API (the SAME email-filtered
   *  `dashboard_layouts/dynamic_filter` list the sidebar shows) so the Table
   *  navigation "Target dashboard" picker works even when localStorage is cold
   *  (fresh session / different user). Merged with localStorage in the getter. */
  private async loadApiTargetDashboards(): Promise<void> {
    let email = '';
    try {
      if (await this.keycloak.isLoggedIn()) email = (await this.keycloak.loadUserProfile()).email || '';
    } catch {
      // Not logged in — the localStorage list is the only source.
    }
    if (!email) return;
    this.dashboardService.fetchOwnedDashboardsFromApi(email).subscribe({
      next: (owned) => {
        this.apiTargetDashboards = (owned || []).map((d) => ({
          label: d.name || d.slug,
          value: d.slug,
          layoutJson: (d.layoutJson || {}) as Record<string, unknown>,
        }));
        // Invalidate the memoized option lists so the picker re-renders.
        this.navDashboardsMemo = null;
        this.navComponentsMemo = null;
      },
      error: () => { /* API unavailable — fall back to localStorage list */ },
    });
  }

  /** When the published view's "Back to Builder" returns here (view=layout), it
   *  passes the dashboard slug as ?dashboard. Fetch that published layout and
   *  rebuild the canvas tiles so the user can keep adding/editing charts. */
  private async restoreDashboardFromRoute(): Promise<void> {
    const slug = this.route.snapshot.queryParamMap.get('dashboard');
    if (!slug) return;

    const stateLayout = this.cloneRecord(history.state?.['layoutJson']);
    if (stateLayout && this.hasRenderableContent(stateLayout)) {
      this.applyRestoredDashboardLayout(stateLayout, slug);
      return;
    }

    const cached = readDynamicDashboardCache<Record<string, unknown> | null>(slug, null);
    if (cached && this.hasRenderableContent(cached)) {
      this.applyRestoredDashboardLayout(cached, slug);
      return;
    }

    let email = '';
    try {
      if (await this.keycloak.isLoggedIn()) email = (await this.keycloak.loadUserProfile()).email || '';
    } catch {
      // no keycloak - getPublishedLayoutBySlug falls back to the email-independent select
    }
    this.dashboardService.getPublishedLayoutBySlug(slug, email).subscribe({
      next: (layoutJson) => {
        if (!layoutJson) return;
        this.applyRestoredDashboardLayout(layoutJson, slug);
      },
      error: (error) => console.warn('[dvm-layout-builder] could not restore published dashboard:', error),
    });
  }

  private applyRestoredDashboardLayout(layoutJson: Record<string, unknown>, slug: string): void {
    // Keep the exact dashboard id/slug this published dashboard already uses so
    // the next Save/Publish updates the same API row instead of deriving a fresh id.
    this.restoredDashboardId = String(layoutJson['dashboardId'] ?? slug);
    const savedDashboards = this.persistence.consolidateSavedDashboards(
      readJson<SavedSelfServiceDashboard[]>(DvmLayoutBuilderComponent.SAVED_DASHBOARDS_STORAGE_KEY, []),
    );
    const matched = savedDashboards.find((dashboard) => dashboard.config?.['dashboardId'] === this.restoredDashboardId);
    if (matched) this.activeSavedDashboardId = matched.id;
    this.restoreTilesFromLayout(layoutJson);
  }

  private hasRenderableContent(layoutJson: Record<string, unknown> | null | undefined): boolean {
    return Array.isArray(layoutJson?.['pageContent']);
  }


  /** Reverse of buildDashboardLayoutJson: rebuild the canvas tiles from a stored
   *  layout_json's pageContent so a published DVM dashboard can be re-opened and
   *  extended in the builder. */
  private restoreTilesFromLayout(layout: Record<string, unknown>): void {
    this.dashboardName = String(layout['dashboard-name'] ?? layout['name'] ?? '') || '';
    this.restoredPublishedLayout = this.cloneRecord(layout['layout']);
    const rows = (layout['pageContent'] as Array<Record<string, unknown>>) || [];
    // ngOnInit stamps `this.dashboardId` from the STATIC config default
    // (DEFAULT_LAYOUT.defaults.dashboardId, e.g. 101) and nothing ever updates
    // it afterwards - so reopening a dashboard whose components actually live
    // under a DIFFERENT numeric dashboardId (e.g. 9101) left every subsequent
    // Save/compute call stamping the WRONG dashboardId onto each tile's payload.
    // The backend then upserts by (dashboardId, title), so a save silently
    // merged this tile with an unrelated same-titled component under the
    // default dashboard. Every tile's own saved payload already carries the
    // CORRECT dashboardId it was built with - reconcile from the first one found.
    for (const row of rows) {
      const cols = (row['columns'] as Array<Record<string, unknown>>) || [];
      const found = cols
        .map((widget) => (widget['payload'] as Record<string, unknown> | undefined)?.['dashboardId'])
        .find((id) => typeof id === 'number');
      if (typeof found === 'number') {
        this.dashboardId = found;
        break;
      }
    }
    const tiles: DashboardTile[] = [];
    rows.forEach((row) => {
      const cols = (row['columns'] as Array<Record<string, unknown>>) || [];
      cols.forEach((widget) => tiles.push(this.tileFromWidget(widget)));
    });
    if (tiles.length) {
      this.dashboardTiles = this.applyPublishedLayoutToTiles(tiles, this.restoredPublishedLayout);
      // Autofill the build form from the restored chart so "Back to Builder"
      // lands with the values pre-filled (no manual pencil click needed). Prefer
      // the first BAR tile (its whole capability config round-trips); else the
      // first editable tile of any type.
      const firstBar = tiles.find((t) => t.layoutType === 'BAR');
      const firstEditable = firstBar
        ?? tiles.find((t) => ['WATERFALL', 'DRILL_DOWN_TABLE', 'TABLE_MATRIX', 'SCATTER_BUBBLE', 'LINE_AREA', 'HORIZONTAL_BAR_CHART', 'KPI', 'INPUT_BOX', 'SLIDER', 'DROPDOWN', 'FILE_UPLOAD'].includes(t.layoutType));
      if (firstEditable) {
        // Defer so entity options / columns can begin loading first.
        setTimeout(() => this.loadTileIntoForm(firstEditable), 0);
      }
      // The ECharts widgets init while the grid is still settling — nudge them
      // to re-measure their tile, otherwise restored charts can stay blank/0-height.
      this.reflowCharts();
    }
  }

  private static readonly WIDGET_TYPE_TO_LAYOUT: Record<string, string> = {
    'waterfall-chart': 'WATERFALL',
    'drill-down': 'DRILL_DOWN_TABLE',
    'horizontal-bar-chart': 'HORIZONTAL_BAR_CHART',
    'kpi-card': 'KPI',
    'bar-column': 'BAR',
    'scatter-bubble': 'SCATTER_BUBBLE',
    'line-area': 'LINE_AREA',
    'slider': 'SLIDER',
    'input-box': 'INPUT_BOX',
    'dropdown': 'DROPDOWN',
    'table-matrix': 'TABLE_MATRIX',
    'file-upload': 'FILE_UPLOAD',
    // Legacy alias: older publishes emitted Bar tiles as 'bar-chart' (the
    // typeMap fallback). Without this entry they restored as WATERFALL and a
    // re-publish then stored a data-less waterfall widget titled after the
    // bar chart (the "double Bar header / No data" tiles).
    'bar-chart': 'BAR',
  };

  /** Rebuild one DashboardTile from its published widget object — extracted
   *  from restoreTilesFromLayout so a 'group-card' widget (below) can rebuild
   *  its children the exact same way, recursively. */
  private tileFromWidget(widget: Record<string, unknown>): DashboardTile {
    if (String(widget['type']) === 'group-card') return this.groupTileFromWidget(widget);

    const layoutType = DvmLayoutBuilderComponent.WIDGET_TYPE_TO_LAYOUT[String(widget['type'])] || 'WATERFALL';
    const wLayout = (widget['layout'] as { colSpan?: number; rowSpan?: number }) || {};
    const payload = (widget['payload'] as Record<string, unknown>) || {};
    // Everything except the meta keys is the render data (spread at publish).
    const { type, title, widgetId, componentId, layout: _l, payload: _p, format: savedFormat, height: _h, class: _c, recomputeUrl: _r, filterKey: savedFilterKey, listenerTileIds: savedListeners, ...renderData } =
      widget as Record<string, unknown>;
    void type; void widgetId; void _l; void _p; void _h; void _c; void _r;
    // Reuse the persisted widgetId as the tile id so cross-filter listener
    // references (which point at these ids) still resolve after restore.
    const tile: DashboardTile = {
      id: this.restoreTileId(widgetId),
      componentId: String(componentId ?? this.createComponentId()),
      layoutType,
      title: String(title ?? 'Untitled'),
      // Clamp to the same minimums as the interactive corner-resize so a
      // restored tile is never too small to show its content.
      colSpan: this.clamp(Math.round(wLayout.colSpan ?? 6), 2, DvmLayoutBuilderComponent.DASH_COLS),
      rowSpan: Math.max(3, Math.round(wLayout.rowSpan ?? 8)),
      payload,
      // Restore the cross-filter config (published widget carries these).
      ...(typeof savedFilterKey === 'string' && savedFilterKey ? { filterKey: savedFilterKey } : {}),
      ...(Array.isArray(savedListeners) ? { listenerTileIds: (savedListeners as unknown[]).map(String) } : {}),
    };
    // The Format popup (isFormattableTile: WATERFALL/HORIZONTAL_BAR_CHART/KPI)
    // only renders when `tile.format` is truthy — but `format` is a builder-only
    // field never written into the published widget (buildDashboardLayoutJson's
    // renderData carries only drillData/hbData/kpiData/option), so a tile
    // rebuilt here from a saved/published layout always came back with
    // `format` undefined and the popup silently never opened. Reseed it with
    // the same defaults a freshly-generated tile gets.
    if (this.isFormattableTile(tile)) {
      tile.format = (savedFormat as Record<string, unknown>) ?? this.defaultTileFormat(layoutType);
    }
    if (layoutType === 'DRILL_DOWN_TABLE') tile.drillData = renderData;
    else if (layoutType === 'TABLE_MATRIX') tile.tableMatrixData = renderData;
    else if (layoutType === 'HORIZONTAL_BAR_CHART') tile.hbData = renderData;
    else if (layoutType === 'KPI') tile.kpiData = renderData;
    else if (layoutType === 'BAR') {
      tile.barData = renderData;
      // Re-seed the ORIGINAL unfiltered rows from the published staticData.
      // Without this, `isCrossFilterTile` (which requires BOTH barData AND an
      // array barRows) returned false on a reopened dashboard, so the
      // cross-filter icon disappeared and cross-filtering stopped working
      // after "Back to Builder". staticData IS the tile's full row set.
      const staticRows = renderData['staticData'];
      tile.barRows = Array.isArray(staticRows) ? (staticRows as Array<Record<string, unknown>>) : [];
    }
    else if (layoutType === 'LINE_AREA') {
      tile.lineAreaData = renderData;
      const staticRows = renderData['staticData'];
      tile.lineAreaRows = Array.isArray(staticRows) ? (staticRows as Array<Record<string, unknown>>) : [];
    }
    else if (layoutType === 'SCATTER_BUBBLE') {
      tile.scatterBubbleData = renderData;
      // Reseed unfiltered rows so isCrossFilterTile stays true after restore.
      const staticRows = renderData['staticData'];
      tile.scatterBubbleRows = Array.isArray(staticRows) ? (staticRows as Array<Record<string, unknown>>) : [];
    }
    else if (layoutType === 'WATERFALL') {
      // Restore BOTH — buildDashboardLayoutJson's renderData carries
      // `{ waterfallBars, option }`. Without `waterfallBars`, onTileFormatChange's
      // WATERFALL branch falls back to withWaterfallXAxisLabelRotation (rotation
      // only), so every OTHER format control (color, number format, show
      // values, wrap) silently did nothing when editing a reopened dashboard.
      tile.waterfallBars = (renderData['waterfallBars'] as WaterfallBar[] | undefined) ?? undefined;
      tile.previewOption = (renderData['option'] as echarts.EChartsOption) ?? null;
      tile.waterfallData = this.buildWaterfallTileData(tile);
    }
    else if (layoutType === 'SLIDER' || layoutType === 'INPUT_BOX') {
      tile.inputControl = {
        kind: layoutType,
        formula: String(payload['formula'] ?? ''),
        value: (payload['value'] as number | string | null) ?? null,
        targets: Array.isArray(payload['targets'])
          ? (payload['targets'] as Array<{ componentId?: string }>).map((t) => t.componentId || '').filter(Boolean)
          : [],
        widgetData: renderData,
      };
    } else if (layoutType === 'DROPDOWN') {
      const options = Array.isArray(renderData['optionsList']) ? (renderData['optionsList'] as unknown[]).map(String) : [];
      const rawValue = payload['value'];
      tile.dropdownControl = {
        field: String(payload['field'] ?? renderData['key'] ?? ''),
        options,
        value: Array.isArray(rawValue) ? rawValue.map(String) : rawValue == null || rawValue === '' ? [] : [String(rawValue)],
        targets: Array.isArray(payload['targets'])
          ? (payload['targets'] as Array<{ componentId?: string }>).map((target) => target.componentId || '').filter(Boolean)
          : [],
        // The payload's displayMode is authoritative — overlay it so a reopened
        // dashboard renders buttons/tiles even if the stored widget record
        // predates the displayMode key in widgetData.
        widgetData: { ...renderData, displayMode: payload['displayMode'] ?? renderData['displayMode'] ?? 'dropdown' },
      };
    } else if (layoutType === 'FILE_UPLOAD') {
      const targets = Array.isArray(payload['targets'])
        ? (payload['targets'] as Array<{ componentId?: string }>).map((t) => t.componentId || '').filter(Boolean)
        : [];
      tile.fileUploadControl = {
        targets,
        buttonLabel: String(payload['buttonLabel'] ?? renderData['buttonLabel'] ?? 'Upload file'),
        accept: String(payload['accept'] ?? renderData['accept'] ?? '.xlsx,.xls,.csv'),
        widgetData: renderData,
      };
    }
    return tile;
  }

  private groupTileFromWidget(widget: Record<string, unknown>): DashboardTile {
    const wLayout = (widget['layout'] as { colSpan?: number; rowSpan?: number }) || {};
    const children = Array.isArray(widget['children']) ? widget['children'] as Array<Record<string, unknown>> : [];
    return {
      id: this.restoreTileId(widget['widgetId']),
      componentId: String(widget['componentId'] ?? this.createComponentId('GRP')),
      layoutType: 'GROUP',
      title: String(widget['title'] ?? 'Grouped card'),
      groupTitle: String(widget['title'] ?? 'Grouped card'),
      colSpan: this.clamp(Math.round(wLayout.colSpan ?? 6), 2, DvmLayoutBuilderComponent.DASH_COLS),
      rowSpan: Math.max(3, Math.round(wLayout.rowSpan ?? 8)),
      payload: {},
      // Card design travels with the published widget; fall back to the control
      // defaults for cards published before those controls existed.
      format: (widget['format'] as Record<string, unknown> | undefined) ?? this.defaultTileFormat('GROUP'),
      groupColumns: this.clamp(Math.round(Number(widget['groupColumns']) || 1), 1, 2),
      groupMembers: children.map((child) => this.tileFromWidget(child)),
    };
  }

  private loadEntityOptions(): void {
    const entityGroup = this.layout.options?.entityGroup?.trim() || 'NontimeSeriesEntities';
    this.entitiesLoading = true;
    this.entitiesLoadFailed = false;
    this.datasetService.getEntities(entityGroup).subscribe({
      next: (entities) => {
        this.entityOptions = entities;
        this.entitiesLoading = false;
      },
      error: () => {
        this.entityOptions = [];
        this.entitiesLoading = false;
        this.entitiesLoadFailed = true;
        this.ui.showError('Unable to load datasets/entities.');
      },
    });
  }

  entitiesLoadFailed = false;

  reloadDatasets(): void {
    this.loadEntityOptions();
  }

  /** Dataset status shown by every chart builder's picker. */
  get datasetStatus(): { kind: 'error' | 'hint' | 'none'; text: string; canRetry: boolean } {
    if (this.entitiesLoading) {
      return { kind: 'hint', text: 'Loading datasets…', canRetry: false };
    }
    if (this.entitiesLoadFailed) {
      return { kind: 'error', text: 'Could not load the dataset list. Check your connection and try again.', canRetry: true };
    }
    if (!this.entityOptions.length) {
      return { kind: 'error', text: 'No datasets found. Create one in the Data Builder first, then reload.', canRetry: true };
    }
    if (!this.drillDataset) {
      return { kind: 'hint', text: 'Pick a dataset to enable the field pickers and Generate.', canRetry: false };
    }
    if (this.isDrillDatasetColumnsLoading) {
      return { kind: 'hint', text: `Loading columns from "${this.drillDataset}"…`, canRetry: false };
    }
    if (this.drillColumnsErrorMessage) {
      return { kind: 'error', text: `${this.drillColumnsErrorMessage} (see console for details)`, canRetry: false };
    }
    return { kind: 'none', text: '', canRetry: false };
  }

  onSelectedDatasetsChange(datasets: string[] | null): void {
    const previousPrimary = this.drillDataset;
    this.selectedDatasets = datasets || [];
    // Both single-dataset layouts (drill-down, horizontal bar) need the chosen
    // dataset's columns for their field pickers.
    if ((this.isDrillDownLayout || this.isTableMatrixLayout || this.isHorizontalBarLayout || this.isKpiLayout || this.isBarLayout || this.isLineAreaLayout || this.isScatterBubbleLayout || this.isDropdownLayout) && this.drillDataset) {
      this.loadColumnsForDataset(this.drillDataset);
    }
    // Drill-down levels/measures keep their field picks across a dataset switch,
    // which the compute API then rejects ("Dataset 'x' has no field 'y'") — drop
    // any pick the new dataset doesn't have (deferred until its columns load).
    if (this.drillDataset && this.drillDataset !== previousPrimary) {
      this.pruneDrillFieldsForDataset(this.drillDataset);
    }
    // WATERFALL bridges across the selected datasets, and each START/CHANGE step
    // pins its OWN `step.dataset`. Changing the chart's dataset(s) never
    // re-pointed those, so buildPayload's `sanitizeStepForPayload` kept emitting
    // the OLD per-step dataset — which then got published and recomputed against
    // the stale dataset. Re-point any step whose dataset is no longer among the
    // selected ones onto the new primary dataset (and clear its field, since the
    // column set changes) so the dataset change actually flows through.
    if (this.isWaterfallLayout && this.steps.length) {
      const valid = new Set(this.selectedDatasets);
      const primary = this.drillDataset;
      let changed = false;
      this.steps.forEach((step) => {
        if (step.dataset && !valid.has(step.dataset)) {
          step.dataset = primary;
          step.field = '';
          changed = true;
        }
      });
      if (changed) {
        if (primary) this.loadColumnsForDataset(primary);
        this.steps = [...this.steps];
        this.invalidatePreview();
      }
    }
  }

  /** Switching layout type swaps the whole left-pane builder + the preview it
   *  drives — clear the stale preview so a leftover waterfall chart doesn't
   *  linger while in Drill-Down mode (or vice versa). Working steps/levels are
   *  kept so flipping back and forth doesn't lose entered data. */
  onLayoutTypeChange(): void {
    const previousLayoutType = this.lastSelectedLayoutType;
    this.lastSelectedLayoutType = this.selectedLayoutType;
    // Switching chart type — drop any bar draft preview tile.
    this.removePreviewTile();
    this.previewOption = null;
    this.drillDownWidgetData = null;
    this.hbWidgetData = null;
    this.tableMatrixWidgetData = null;
    this.inputControlReady = false;
    this.generateError = '';
    // Keep the catalogue category in sync when the type changes directly (Layer 1).
    const owningCategory = this.categoryOfType(this.selectedLayoutType);
    if (owningCategory) this.selectedChartCategory = owningCategory;
    // BAR seeds an empty first measure so the picker isn't blank on first open.
    if (this.isBarLayout && !this.barMeasures.length) this.addBarMeasure();
    if (this.isLineAreaLayout && !this.laMeasures.length) this.addLaMeasure();
    // Every layout has a Build-form format accordion now (charts + inputs) —
    // seed its options with the configured defaults for the active type.
    this.seedBarFormatDefaults();
    if (this.isScatterBubbleLayout) this.seedScatterBubbleFormatDefaults();
    if (!this.editingTileId && previousLayoutType !== this.selectedLayoutType) {
      this.clearInputTargetState();
    }
    if ((this.isDrillDownLayout || this.isTableMatrixLayout || this.isHorizontalBarLayout || this.isKpiLayout || this.isBarLayout || this.isLineAreaLayout || this.isScatterBubbleLayout || this.isDropdownLayout) && this.drillDataset) {
      this.loadColumnsForDataset(this.drillDataset);
    }
  }

  /** Table-type change (Normal/Editable/Drill-Down) — clears the stale preview
   *  so the form re-renders the fields for the newly-selected type. */
  onTableMatrixTypeChange(): void {
    this.tableMatrixWidgetData = null;
    this.generateError = '';
    // If we're editing a table tile that's already on the canvas, push the
    // updated capabilities (incl. navigation) into it LIVE — so toggling
    // navigation / changing its config reflects immediately without needing to
    // click Generate again. Only capabilities change here (not the rows).
    this.refreshEditingTableMatrixTile();
  }

  /** Rebuild the capabilities of the currently-edited table tile from the live
   *  form state, preserving its already-fetched rows / drill data. No-op unless
   *  a TABLE_MATRIX tile is being edited and already has render data. */
  private refreshEditingTableMatrixTile(): void {
    if (!this.editingTileId) return;
    const tile = this.findTileAnywhere(this.editingTileId);
    if (!tile || tile.layoutType !== 'TABLE_MATRIX' || !tile.tableMatrixData) return;
    const capabilities = this.buildTableMatrixCapabilities(tile.format);
    tile.tableMatrixData = { ...tile.tableMatrixData, capabilities };
    this.updateTileInPlace({ ...tile });
  }

  private clearInputTargetState(): void {
    this.selectedTargetCharts = [];
    this.targetFormulas = {};
    this.targetStepFormulas = {};
    this.targetFormulaReferences = {};
  }

  // ── Shared dataset/column loading (bridge steps AND drill-down levels) ────

  private loadColumnsForDataset(dataset: string): void {
    if (!dataset) return;
    // Columns already cached — but the ROWS may not be (e.g. cached column-only by
    // selectExplorerEntity on an older build). Backfill rows for the bar chart,
    // then skip the re-fetch.
    if (this.entityColumnsCache.has(dataset)) {
      if (!this.entityRowsCache.has(dataset)) {
        this.datasetService.getEntityRows(dataset).subscribe({
          next: (rows) => { this.entityRowsCache.set(dataset, Array.isArray(rows) ? rows : []); this.onDatasetRowsLoaded(dataset); },
          error: () => this.entityRowsCache.set(dataset, []),
        });
      }
      return;
    }

    this.datasetColumnsLoading[dataset] = true;
    delete this.datasetColumnsError[dataset];
    this.datasetService.getEntityAttributes(dataset, this.entityGroupName).subscribe({
      next: (attributes) => {
        const columns = this.filterVisibleColumns(attributes);
        this.entityColumnsCache.set(dataset, columns);
        if (this.pendingDrillFieldPrune === dataset) this.pruneDrillFieldsForDataset(dataset);
        // Cache the raw rows too — the Bar & Column chart aggregates them
        // client-side (no separate compute call for the preview).
        this.loadRowsForDataset(dataset);
        this.datasetColumnsLoading[dataset] = false;
        if (!columns.length) {
          // The call succeeded but yielded zero usable columns — the
          // getEntityAttributes response didn't match any of the shapes it
          // normalizes, or the entity genuinely exposes none. Log the raw
          // response so it's diagnosable instead of a silent empty picker.
          console.warn(`[dvm-layout-builder] "${dataset}" returned 0 attributes — raw response:`, attributes);
          this.datasetColumnsError[dataset] = `"${dataset}" returned no columns — check the entity name or its metadata.`;
        }
      },
      error: (error: HttpErrorResponse) => {
        this.datasetColumnsLoading[dataset] = false;
        const message = error.error?.message || error.message || `Unable to load columns for "${dataset}".`;
        this.datasetColumnsError[dataset] = message;
        console.error(`[dvm-layout-builder] getEntityAttributes("${dataset}") failed:`, error);
        this.ui.showError(message);
      },
    });
  }

  /** Dataset whose columns, once loaded, should trigger a stale-field prune of
   *  the drill-down levels/measures (set when the dataset switches before its
   *  column list has arrived). */
  private pendingDrillFieldPrune = '';

  /** After a dataset switch, clear drill-down dimension/measure FIELD picks the
   *  new dataset doesn't have — left in place they ride into the compute payload
   *  and the API rejects the whole Generate ("Dataset 'x' has no field 'y'").
   *  Rows and display labels stay; only the stale field is emptied so the user
   *  re-picks from the new dataset's column list. */
  private pruneDrillFieldsForDataset(dataset: string): void {
    if (!dataset || (!this.drillDownDimensions.length && !this.drillDownMeasures.length && !this.tmColumns.length)) return;
    const columns = this.entityColumnsCache.get(dataset);
    if (!columns) {
      this.pendingDrillFieldPrune = dataset;
      return;
    }
    this.pendingDrillFieldPrune = '';
    const known = new Set(columns);
    let changed = false;
    this.drillDownDimensions.forEach((dimension) => {
      if (dimension.field && !known.has(dimension.field)) {
        dimension.field = '';
        changed = true;
      }
    });
    this.drillDownMeasures.forEach((measure) => {
      if (measure.field && !known.has(measure.field)) {
        measure.field = '';
        changed = true;
      }
    });
    // Table column rows that FOLLOW the primary dataset (blank `dataset`) point
    // at the new primary now — clear picks it doesn't have. Rows pinned to
    // their own explicit dataset are untouched (cross-dataset is the point).
    let tmChanged = false;
    this.tmColumns.forEach((column) => {
      if (!column.dataset && column.field && !known.has(column.field)) {
        column.field = '';
        tmChanged = true;
      }
    });
    if (tmChanged) this.tmColumns = [...this.tmColumns];
    if (changed || tmChanged) {
      this.drillDownDimensions = [...this.drillDownDimensions];
      this.drillDownMeasures = [...this.drillDownMeasures];
      this.invalidatePreview();
      this.ui.showError(`Some fields pointed at columns "${dataset}" doesn't have — re-pick them for the new dataset.`);
    }
  }

  private loadRowsForDataset(dataset: string): void {
    if (!dataset || this.entityRowsCache.has(dataset)) return;
    this.datasetService.getEntityRows(dataset).subscribe({
      next: (rows) => { this.entityRowsCache.set(dataset, Array.isArray(rows) ? rows : []); this.onDatasetRowsLoaded(dataset); },
      error: () => this.entityRowsCache.set(dataset, []),
    });
  }

  /** Rows for `dataset` just landed. The client-side chart families (bar /
   *  line-area / scatter) build their tiles SYNCHRONOUSLY from the rows cache,
   *  so a tile edited or previewed while the fetch was still in flight rendered
   *  with ZERO rows — a permanently blank chart (nothing re-triggered a rebuild
   *  when the rows arrived). Re-run the active layout's live refresh so the
   *  editing/preview tile picks the rows up the moment they exist. */
  private onDatasetRowsLoaded(dataset: string): void {
    if (this.drillDataset !== dataset) return;
    if (this.isLineAreaLayout) this.refreshEditingLineAreaTile();
    else if (this.isBarLayout) this.refreshEditingBarTile();
    else if (this.isScatterBubbleLayout) this.refreshEditingScatterBubbleTile();
  }
  isDatasetColumnsLoading(dataset: string): boolean {
    return !!dataset && !!this.datasetColumnsLoading[dataset];
  }

  /** Memoized per row id (bridge-step or drill-level) so ng-select's [items]
   *  array reference only changes when the underlying dataset's columns
   *  actually change — see rowColumnsMemo's declaration comment. */
  private columnsForRow(rowId: string, dataset: string): string[] {
    const source = dataset ? this.entityColumnsCache.get(dataset) : undefined;
    const cached = this.rowColumnsMemo.get(rowId);
    if (cached && cached.dataset === dataset && cached.source === source) {
      return cached.result;
    }
    const result = source || [];
    this.rowColumnsMemo.set(rowId, { dataset, source, result });
    return result;
  }

  // ── Phase B: bridge-step CRUD + reordering (WATERFALL) ────────────────────

  addStep(): void {
    const step: BridgeStep = {
      id: this.nextStepId(),
      stepType: this.steps.length === 0 ? 'START' : 'CHANGE',
      label: this.steps.length === 0 ? 'Starting value' : `Step ${this.steps.length + 1}`,
      dataset: this.selectedDatasets[0] || '',
      field: '',
      aggregation: 'SUM',
      sign: 'AUTO',
      formula: '',
      formulaMode: false,
    };
    this.steps = [...this.steps, step];
    if (step.dataset) this.loadColumnsForDataset(step.dataset);
  }

  removeStep(step: BridgeStep): void {
    this.steps = this.steps.filter((item) => item.id !== step.id);
    this.rowColumnsMemo.delete(step.id);
    this.invalidatePreview();
  }

  moveStepUp(index: number): void {
    if (index <= 0) return;
    this.reorderStep(index, index - 1);
  }

  moveStepDown(index: number): void {
    if (index >= this.steps.length - 1) return;
    this.reorderStep(index, index + 1);
  }

  private reorderStep(fromIndex: number, toIndex: number): void {
    const next = [...this.steps];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    this.steps = next;
    this.invalidatePreview();
  }

  // ── Phase C: state sanitization on step-type change ───────────────────────

  onStepTypeChange(step: BridgeStep): void {
    if (step.stepType === 'TOTAL') {
      step.formulaMode = true;
      step.dataset = '';
      step.field = '';
      step.aggregation = '';
      step.sign = 'AUTO';
      step.formula = step.formula || '';
    } else {
      // START and CHANGE both support the dataset-vs-formula toggle, so each
      // can carry its own independent formula (an anchor with its own
      // computed baseline, or a floating segment computed from other bars).
      step.formulaMode = (step.stepType === 'CHANGE' || step.stepType === 'START') ? !!step.formulaMode : false;
      if (!step.formulaMode) step.formula = '';
      if (!step.dataset) {
        step.dataset = this.selectedDatasets[0] || '';
      }
      if (!step.aggregation) {
        step.aggregation = 'SUM';
      }
      // START is an anchor pillar — it never carries a sign modifier.
      step.sign = step.stepType === 'START' ? 'AUTO' : (step.sign || 'AUTO');
      if (step.dataset) this.loadColumnsForDataset(step.dataset);
    }
    this.steps = [...this.steps];
    this.invalidatePreview();
  }

  /** Toggles a START or CHANGE step between a dataset-driven aggregation and a
   *  free-form formula — each step gets its OWN independent formula this way
   *  (TOTAL is always formula-driven, so it never needs this toggle). */
  toggleStepFormulaMode(step: BridgeStep): void {
    if (step.stepType !== 'CHANGE' && step.stepType !== 'START') return;
    step.formulaMode = !step.formulaMode;
    if (step.formulaMode) {
      step.dataset = '';
      step.field = '';
      step.aggregation = '';
    } else {
      step.formula = '';
      step.dataset = this.selectedDatasets[0] || '';
      step.aggregation = 'SUM';
      if (step.dataset) this.loadColumnsForDataset(step.dataset);
    }
    this.steps = [...this.steps];
    this.invalidatePreview();
  }

  onStepDatasetChange(step: BridgeStep, dataset: string | null): void {
    step.dataset = dataset || '';
    step.field = '';
    this.steps = [...this.steps];
    if (step.dataset) this.loadColumnsForDataset(step.dataset);
    this.invalidatePreview();
  }

  onStepFieldChange(step: BridgeStep, field: string | null): void {
    step.field = field || '';
    this.steps = [...this.steps];
    this.invalidatePreview();
  }

  onStepFormulaOrLabelChange(): void {
    this.steps = [...this.steps];
    this.invalidatePreview();
  }

  isStepColumnsLoading(step: BridgeStep): boolean {
    return this.isDatasetColumnsLoading(step.dataset);
  }

  stepFieldPlaceholder(step: BridgeStep): string {
    if (!step.dataset) return 'Select a dataset first';
    return this.isStepColumnsLoading(step) ? 'Loading columns...' : 'Select field';
  }

  columnsForStep(step: BridgeStep): string[] {
    return this.columnsForRow(step.id, step.dataset);
  }

  // ── Table & Matrix column rows (Normal/Editable) — per-column dataset ──────
  // Same per-row dataset/field pattern as bridge steps: each column row loads
  // (and memoizes) its OWN dataset's column list, so a table can mix columns
  // from several datasets. The payload sends {dataset, field, displayName}.

  addTmColumn(): void {
    this.tmColumns = [...this.tmColumns, {
      id: this.nextTmColumnId(),
      dataset: this.drillDataset,
      field: '',
      displayName: '',
      filterField: '',
      filterOperator: 'eq',
      filterValue: '',
    }];
  }

  removeTmColumn(column: TmColumnDef): void {
    this.tmColumns = this.tmColumns.filter((item) => item.id !== column.id);
    this.onTableMatrixTypeChange();
  }

  onTmColumnDatasetChange(column: TmColumnDef, dataset: string | null): void {
    column.dataset = dataset || '';
    column.field = '';
    this.tmColumns = [...this.tmColumns];
    if (column.dataset) this.loadColumnsForDataset(column.dataset);
    this.onTableMatrixTypeChange();
  }

  onTmColumnFieldChange(column: TmColumnDef, field: string | null): void {
    const previousDefault = this.defaultColumnDisplayName(column.field);
    column.field = field || '';
    // Follow the field with a title-cased default display name unless the user
    // typed their own (i.e. it no longer matches the previous field's default).
    if (!column.displayName || column.displayName === previousDefault) {
      column.displayName = this.defaultColumnDisplayName(column.field);
    }
    this.tmColumns = [...this.tmColumns];
    this.onTableMatrixTypeChange();
  }

  onTmColumnDisplayNameChange(column: TmColumnDef, displayName: string): void {
    column.displayName = displayName;
    this.tmColumns = [...this.tmColumns];
    this.onTableMatrixTypeChange();
  }

  /** Per-column default filter — WHICH column it runs against (blank = this
   *  row's own field; can be pointed at any OTHER column of the same
   *  dataset). */
  onTmColumnFilterFieldChange(column: TmColumnDef, field: string | null): void {
    column.filterField = field || '';
    this.tmColumns = [...this.tmColumns];
    this.onTableMatrixTypeChange();
  }

  /** Operator (blank `filterValue` = no filter for this row, regardless of
   *  operator/filterField). */
  onTmColumnFilterOperatorChange(column: TmColumnDef, operator: string | null): void {
    column.filterOperator = operator || 'eq';
    this.tmColumns = [...this.tmColumns];
    this.onTableMatrixTypeChange();
  }

  onTmColumnFilterValueChange(column: TmColumnDef, value: string): void {
    column.filterValue = value;
    this.tmColumns = [...this.tmColumns];
    this.onTableMatrixTypeChange();
  }

  columnsForTmColumn(column: TmColumnDef): string[] {
    return this.columnsForRow(column.id, column.dataset || this.drillDataset);
  }

  isTmColumnLoading(column: TmColumnDef): boolean {
    return this.isDatasetColumnsLoading(column.dataset || this.drillDataset);
  }

  tmColumnFieldPlaceholder(column: TmColumnDef): string {
    if (!(column.dataset || this.drillDataset)) return 'Select a dataset first';
    return this.isTmColumnLoading(column) ? 'Loading columns...' : 'Select column';
  }

  /** "technical_configuration" → "Technical Configuration" — the default
   *  `displayName` when the user hasn't typed one. */
  private defaultColumnDisplayName(field: string): string {
    return String(field || '')
      .replace(/[_\-.]+/g, ' ')
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /** The `columns` array for payloads/capabilities: one {dataset, field,
   *  displayName} per configured row (rows without a field yet are skipped;
   *  blanks fall back to the primary dataset / title-cased field). */
  private tmColumnEntries(): Array<{ dataset: string; field: string; displayName: string; filterField?: string; filterOperator?: string; filterValue?: string }> {
    return this.tmColumns
      .filter((column) => column.field)
      .map((column) => ({
        dataset: column.dataset || this.drillDataset,
        field: column.field,
        displayName: column.displayName || this.defaultColumnDisplayName(column.field),
        // Carried along (harmless extra keys) purely so `restoreTmColumns` can
        // rebuild this exact row's filter on edit — the actual filtering
        // instruction the backend acts on is the separate `initialFilters` key.
        ...(column.filterValue?.trim()
          ? { filterField: column.filterField || column.field, filterOperator: column.filterOperator || 'eq', filterValue: column.filterValue.trim() }
          : {}),
      }));
  }

  /** Rebuild `tmColumns` from a saved payload's `columns` — accepts BOTH the
   *  new object shape ({dataset, field, displayName, filterField,
   *  filterOperator, filterValue}) and the legacy plain string[] shape, so
   *  older saved dashboards restore cleanly. `legacyInitialFilters` backfills
   *  a row's filter from the OLD standalone `initialFilters` payload shape
   *  (matched onto the column whose OWN field equals that filter's key), for
   *  dashboards saved before columns carried their own filter state. */
  private restoreTmColumns(raw: unknown, legacyInitialFilters?: unknown): void {
    const list = Array.isArray(raw) ? raw : [];
    const legacyByField = new Map<string, { operator: string; value: string }>();
    if (Array.isArray(legacyInitialFilters)) {
      legacyInitialFilters.forEach((entry) => {
        if (!entry || typeof entry !== 'object') return;
        const record = entry as Record<string, unknown>;
        const field = String(record['filterKey'] ?? '');
        if (!field) return;
        legacyByField.set(field, {
          operator: String(record['filterOperator'] ?? 'eq'),
          value: String(record['filterValue'] ?? ''),
        });
      });
    }
    this.tmColumns = list
      .map((entry) => {
        if (entry && typeof entry === 'object') {
          const record = entry as Record<string, unknown>;
          const field = String(record['field'] ?? '');
          const legacy = legacyByField.get(field);
          return {
            id: this.nextTmColumnId(),
            dataset: String(record['dataset'] ?? ''),
            field,
            displayName: String(record['displayName'] ?? record['label'] ?? ''),
            filterField: String(record['filterField'] ?? ''),
            filterOperator: String(record['filterOperator'] ?? legacy?.operator ?? 'eq'),
            filterValue: String(record['filterValue'] ?? legacy?.value ?? ''),
          };
        }
        const field = String(entry ?? '');
        const legacy = legacyByField.get(field);
        return {
          id: this.nextTmColumnId(), dataset: '', field, displayName: '', filterField: '',
          filterOperator: legacy?.operator ?? 'eq', filterValue: legacy?.value ?? '',
        };
      })
      .filter((column) => column.field);
    // Warm every referenced dataset's column cache so the row pickers render
    // their selections (instead of empty dropdowns) straight away.
    const datasets = new Set(this.tmColumns.map((column) => column.dataset || this.drillDataset).filter(Boolean));
    datasets.forEach((dataset) => this.loadColumnsForDataset(dataset));
  }

  // ── Drill-down dimension CRUD + reordering (DRILL_DOWN_TABLE) ────────────────────
  // Dimensions are the hierarchy levels (Level 1 = outermost group, etc.) —
  // order in the array IS the level order, so no separate "level" field to
  // keep in sync.

  addDrillDimension(): void {
    const dimension: DrillDownDimension = {
      id: this.nextDrillDimensionId(),
      field: '',
      label: `Level ${this.drillDownDimensions.length + 1}`,
    };
    this.drillDownDimensions = [...this.drillDownDimensions, dimension];
  }

  /** Split the typed value-order into a clean list. Accepts comma OR newline
   *  separators so pasting a column of values from Excel works as well as
   *  typing them inline. Blank entries are dropped, so trailing commas and
   *  stray blank lines are harmless. */
  private parseValueOrder(raw: string | undefined): string[] {
    return String(raw ?? '')
      .split(/[\n,]/)
      .map((value) => value.trim())
      .filter(Boolean);
  }

  /** Reverse of `parseValueOrder` — turn a saved `valueOrder` array back into
   *  the comma-separated text the input edits. Tolerates a plain string too, in
   *  case a hand-written config supplied one. */
  private valueOrderToText(raw: unknown): string {
    if (Array.isArray(raw)) return raw.map((value) => String(value).trim()).filter(Boolean).join(', ');
    return typeof raw === 'string' ? raw : '';
  }

  /** The `valueOrder` fragment for one dimension's payload entry — an empty
   *  object when the user left it blank, so the key never reaches the backend
   *  unless it was actually configured. */
  private valueOrderPayload(dimension: DrillDownDimension): Record<string, string[]> {
    const order = this.parseValueOrder(dimension.valueOrder);
    return order.length ? { valueOrder: order } : {};
  }

  removeDrillDimension(dimension: DrillDownDimension): void {
    this.drillDownDimensions = this.drillDownDimensions.filter((item) => item.id !== dimension.id);
    this.invalidatePreview();
  }

  moveDrillDimensionUp(index: number): void {
    if (index <= 0) return;
    this.reorderDrillDimension(index, index - 1);
  }

  moveDrillDimensionDown(index: number): void {
    if (index >= this.drillDownDimensions.length - 1) return;
    this.reorderDrillDimension(index, index + 1);
  }

  private reorderDrillDimension(fromIndex: number, toIndex: number): void {
    const next = [...this.drillDownDimensions];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    this.drillDownDimensions = next;
    this.invalidatePreview();
  }

  onDrillDimensionChange(): void {
    this.drillDownDimensions = [...this.drillDownDimensions];
    this.invalidatePreview();
  }

  // ── Drill-down measure CRUD + reordering (DRILL_DOWN_TABLE) ──────────────────────
  // Every measure applies at every level of the tree, rolled up per its own
  // rollUpStrategy — they're columns of the matrix, not per-level mappings.

  addDrillMeasure(): void {
    const measure: DrillDownMeasure = {
      id: this.nextDrillMeasureId(),
      field: '',
      label: `Measure ${this.drillDownMeasures.length + 1}`,
      aggregation: 'NONE',
      rollUpStrategy: 'SUM',
      total: false,
    };
    this.drillDownMeasures = [...this.drillDownMeasures, measure];
  }

  removeDrillMeasure(measure: DrillDownMeasure): void {
    this.drillDownMeasures = this.drillDownMeasures.filter((item) => item.id !== measure.id);
    this.invalidatePreview();
  }

  moveDrillMeasureUp(index: number): void {
    if (index <= 0) return;
    this.reorderDrillMeasure(index, index - 1);
  }

  moveDrillMeasureDown(index: number): void {
    if (index >= this.drillDownMeasures.length - 1) return;
    this.reorderDrillMeasure(index, index + 1);
  }

  private reorderDrillMeasure(fromIndex: number, toIndex: number): void {
    const next = [...this.drillDownMeasures];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    this.drillDownMeasures = next;
    this.invalidatePreview();
  }

  onDrillMeasureChange(): void {
    this.drillDownMeasures = [...this.drillDownMeasures];
    this.invalidatePreview();
  }

  // ── Bar & Column builder (BAR) — category + measures + optional series ─────
  // One dataset (drillDataset), one category column, N measures (each with a
  // draw type + axis), and an optional series split. All variant/orientation
  // logic lives in the pure option builder.

  /** Columns of the chosen bar dataset (shared with the drill/hbar pickers). */
  /** Memoized barColumns list — a getter that returned a fresh array every
   *  change-detection cycle made ng-select (appendTo:'body') rebuild its panel
   *  mid-click, so selecting a Measure/Category/Split value silently failed.
   *  Recompute the array ONLY when the dataset columns or the computed columns
   *  actually change → STABLE reference → clicks stick. */
  private barColumnsCache: string[] = [];
  private barColumnsKey = '';
  get barColumns(): string[] {
    // Base dataset columns PLUS any computed (formula) columns, so a computed
    // column can be mapped onto Category / Measure / Split like a real column.
    const base = this.drillDatasetColumns;
    const computed = this.barComputedColumns.map((c) => c.name);
    const key = `${base.join('|')}::${computed.join('|')}`;
    if (key !== this.barColumnsKey) {
      this.barColumnsKey = key;
      this.barColumnsCache = [...base, ...computed.filter((n) => !base.includes(n))];
    }
    return this.barColumnsCache;
  }

  // ── Formula Builder — row-level computed columns (chart-agnostic, config-driven)

  /** The Formula Builder config block (options.formulaBuilder), or {}. All UI text,
   *  templates, validation, and the enabled flag come from here — nothing hardcoded. */
  get formulaBuilderConfig(): FormulaBuilderConfig {
    return ((this.layout.options as Record<string, unknown> | undefined)?.['formulaBuilder'] as FormulaBuilderConfig | undefined) ?? {};
  }
  /** Whether the Formula Builder is shown (config `enabled`, default true). */
  get formulaBuilderEnabled(): boolean {
    return this.formulaBuilderConfig.enabled !== false;
  }
  /** Row-level formula templates (kind: 'column') offered as quick-fill chips —
   *  purely from config; empty when none configured (no dataset-specific defaults). */
  get barFormulaTemplates(): FormulaTemplate[] {
    const list = Array.isArray(this.formulaBuilderConfig.templates) ? this.formulaBuilderConfig.templates : [];
    return list.filter((t) => t.kind === 'column');
  }

  /** Fill the add-column inputs from a template chip. */
  applyBarFormulaTemplate(tpl: FormulaTemplate): void {
    this.newBarColumnName = tpl.defaultName;
    this.newBarColumnExpression = tpl.expressionTemplate;
    this.newBarColumnError = '';
  }

  /** Add a computed column (name + expression), validated against existing
   *  columns. Applies to the preview immediately. */
  addBarComputedColumn(): void {
    const name = this.newBarColumnName.trim();
    const expression = this.newBarColumnExpression.trim();
    this.newBarColumnError = '';
    const cfg = this.formulaBuilderConfig;
    if (!name) { this.newBarColumnError = 'Column name is required.'; return; }
    if (!expression) { this.newBarColumnError = 'Expression is required.'; return; }
    // Config-driven name validation (pattern + message); safe default if absent.
    const pattern = cfg.namePattern ? new RegExp(cfg.namePattern) : /^[A-Za-z_][A-Za-z0-9_ ]*$/;
    if (!pattern.test(name)) {
      this.newBarColumnError = cfg.nameError || 'Invalid column name.';
      return;
    }
    if (this.drillDatasetColumns.includes(name) || this.barComputedColumns.some((c) => c.name === name)) {
      this.newBarColumnError = `A column named "${name}" already exists.`;
      return;
    }
    // Validate the expression compiles against the current columns.
    try {
      this.formulaService.buildRowEvaluator(expression, this.barColumns);
    } catch (e) {
      this.newBarColumnError = `Invalid expression: ${e instanceof Error ? e.message : 'parse error'}`;
      return;
    }
    this.barComputedColumns = [...this.barComputedColumns, { name, expression }];
    this.newBarColumnName = '';
    this.newBarColumnExpression = '';
    this.onBarChange();
  }

  removeBarComputedColumn(name: string): void {
    this.barComputedColumns = this.barComputedColumns.filter((c) => c.name !== name);
    // Clear any mapping that pointed at the removed column.
    if (this.barCategory === name) this.barCategory = '';
    if (this.barSeries === name) this.barSeries = '';
    this.barMeasures = this.barMeasures.map((m) => (m.field === name ? { ...m, field: '' } : m));
    this.onBarChange();
  }

  /** Apply every computed column to a rows array, returning NEW row objects with
   *  the derived values added. Reuses the shared formula engine. Bad expressions
   *  are skipped (leave the column absent) rather than throwing. */
  private applyBarComputedColumns(rows: Array<Record<string, unknown>>): Array<Record<string, unknown>> {
    if (!this.barComputedColumns.length || !rows.length) return rows;
    let working = rows.map((r) => ({ ...r }));
    const known = [...this.drillDatasetColumns];
    for (const computed of this.barComputedColumns) {
      try {
        const evaluator = this.formulaService.buildRowEvaluator(computed.expression, known);
        working = working.map((row) => {
          const value = evaluator(row as Record<string, string>);
          const out = value === null || value === undefined ? '' : value;
          return { ...row, [computed.name]: typeof out === 'number' && !Number.isFinite(out) ? '' : out };
        });
        known.push(computed.name);
      } catch {
        // Skip a broken formula — its column just won't be present.
      }
    }
    return working;
  }

  addBarMeasure(): void {
    this.barMeasures = [
      ...this.barMeasures,
      { id: this.nextBarMeasureId(), field: '', label: '', renderAs: 'bar', axis: 'y1' },
    ];
  }

  removeBarMeasure(measure: BarBuilderMeasure): void {
    this.barMeasures = this.barMeasures.filter((item) => item.id !== measure.id);
    this.invalidatePreview();
  }

  moveBarMeasureUp(index: number): void {
    if (index <= 0) return;
    this.reorderBarMeasure(index, index - 1);
  }

  moveBarMeasureDown(index: number): void {
    if (index >= this.barMeasures.length - 1) return;
    this.reorderBarMeasure(index, index + 1);
  }

  private reorderBarMeasure(fromIndex: number, toIndex: number): void {
    const next = [...this.barMeasures];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    this.barMeasures = next;
    this.invalidatePreview();
  }

  onBarMeasureChange(): void {
    this.barMeasures = [...this.barMeasures];
    this.invalidatePreview();
    this.refreshEditingBarTile();
  }

  onBarChange(): void {
    this.invalidatePreview();
    this.refreshEditingBarTile();
  }

  // ── Line & Area handlers ──────────────────────────────────────────────────
  addLaMeasure(): void {
    this.laMeasures = [...this.laMeasures, { id: this.nextLaMeasureId(), field: '', label: '', renderAs: 'line', axis: 'y1' }];
  }
  removeLaMeasure(measure: LineAreaBuilderMeasure): void {
    this.laMeasures = this.laMeasures.filter((item) => item.id !== measure.id);
    this.invalidatePreview();
    this.refreshEditingLineAreaTile();
  }
  moveLaMeasureUp(index: number): void { if (index > 0) this.reorderLaMeasure(index, index - 1); }
  moveLaMeasureDown(index: number): void { if (index < this.laMeasures.length - 1) this.reorderLaMeasure(index, index + 1); }
  private reorderLaMeasure(fromIndex: number, toIndex: number): void {
    const next = [...this.laMeasures];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    this.laMeasures = next;
    this.invalidatePreview();
    this.refreshEditingLineAreaTile();
  }
  onLaMeasureChange(): void {
    this.laMeasures = [...this.laMeasures];
    this.invalidatePreview();
    this.refreshEditingLineAreaTile();
  }
  onLineAreaChange(): void {
    this.invalidatePreview();
    this.refreshEditingLineAreaTile();
  }
  private nextLaMeasureId(): string {
    this.laMeasureIdCounter += 1;
    return `la-measure-${this.laMeasureIdCounter}`;
  }
  onLaMeasureFieldDrop(event: DragEvent, measure?: LineAreaBuilderMeasure): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    const target = measure || { id: this.nextLaMeasureId(), field: '', label: '', renderAs: 'line' as const, axis: 'y1' as const };
    target.field = mapping.field;
    if (!target.label) target.label = mapping.field;
    if (!measure) this.laMeasures = [...this.laMeasures, target];
    this.onLaMeasureChange();
  }
  onLaCategoryFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    this.laCategory = mapping.field;
    if (!this.laCategoryLabel) this.laCategoryLabel = mapping.field;
    this.onLineAreaChange();
  }
  onLaSeriesFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    this.laSeries = mapping.field;
    this.onLineAreaChange();
  }
  onLaSecondaryYFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    this.laSecondaryYField = mapping.field;
    this.onLineAreaChange();
  }
  onLaSecondaryXFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    this.laSecondaryXField = mapping.field;
    this.onLineAreaChange();
  }
  // ── Reference lines (Line & Area) ──
  addLaReferenceLine(): void {
    this.laRefLineIdCounter += 1;
    this.laReferenceLines = [...this.laReferenceLines, { id: `la-refline-${this.laRefLineIdCounter}`, value: null, label: '', color: '#64748b' }];
  }
  removeLaReferenceLine(id: string): void {
    this.laReferenceLines = this.laReferenceLines.filter((line) => line.id !== id);
    this.invalidatePreview();
    this.refreshEditingLineAreaTile();
  }
  onLaReferenceLineChange(): void {
    this.laReferenceLines = [...this.laReferenceLines];
    this.invalidatePreview();
    this.refreshEditingLineAreaTile();
  }

  /** A Build-form Format control changed (Title/Axis/Legend/Labels/Tooltip/
   *  Formatting/Advanced) — keep the chart `title` in sync with the Title-accordion
   *  control (which writes barFormat['title']), then live-refresh the edited tile. */
  onBarFormatChange(): void {
    // Sync the chart title from the Title-accordion control — but ONLY for layouts
    // that actually surface Title as an accordion (Bar / Waterfall / Table-Matrix
    // Drill-Down). Normal/Editable tables keep the INLINE title field, so a stray
    // empty `barFormat['title']` (seeded from the drilldown-only Title control's
    // default) must not clobber `this.title` and disable Generate/Update.
    const titleFromAccordion = this.isBarLayout || this.isLineAreaLayout || this.isWaterfallLayout || (this.isTableMatrixLayout && this.isTmDrilldown);
    if (titleFromAccordion && typeof this.barFormat['title'] === 'string') this.title = this.barFormat['title'] as string;
    if (this.isWaterfallLayout) {
      this.refreshEditingWaterfallTile();
      return;
    }
    if (this.isTableMatrixLayout) {
      this.foldFormatIntoEditingTableMatrixTile();
      return;
    }
    if (this.isLineAreaLayout) {
      this.refreshEditingLineAreaTile();
      return;
    }
    if (this.isKpiLayout || this.isDrillDownLayout || this.isHorizontalBarLayout) {
      this.foldFormatIntoEditingTile();
      return;
    }
    this.refreshEditingBarTile();
  }

  /** Generic live-refresh for layouts whose Build-form Format accordion has no
   *  chart-specific rebuild (KPI, Drill-Down): fold barFormat into the editing
   *  tile's format so the header colours apply immediately, and let
   *  onTileFormatChange re-derive any type-specific render data (KPI numbers). */
  private foldFormatIntoEditingTile(): void {
    if (!this.editingTileId) return;
    const index = this.dashboardTiles.findIndex((t) => t.id === this.editingTileId);
    const tile = index >= 0 ? this.dashboardTiles[index] : null;
    if (!tile) return;
    tile.format = { ...(tile.format || {}), ...this.barFormat };
    this.onTileFormatChange(tile);
    this.dashboardTiles = this.dashboardTiles.map((t, i) => (i === index ? { ...tile } : t));
  }

  /** Live-refresh the table tile being edited when a Build-form Format control
   *  changes — folds barFormat into the tile's format, then rebuilds its
   *  capabilities from the form state (same path as the tile Format popup). */
  private foldFormatIntoEditingTableMatrixTile(): void {
    if (!this.editingTileId) return;
    const index = this.dashboardTiles.findIndex((t) => t.id === this.editingTileId);
    const tile = index >= 0 ? this.dashboardTiles[index] : null;
    if (!tile || tile.layoutType !== 'TABLE_MATRIX') return;
    tile.format = { ...(tile.format || {}), ...this.barFormat };
    this.dashboardTiles = this.dashboardTiles.map((t, i) => (i === index ? tile : t));
    this.refreshEditingTableMatrixTile();
  }

  /** Live-refresh the waterfall tile being edited when a Build-form Format
   *  control changes — folds barFormat into the tile's format and re-derives its
   *  rendered option from the stored bars (same path as the tile Format popup). */
  private refreshEditingWaterfallTile(): void {
    if (!this.editingTileId) return;
    const index = this.dashboardTiles.findIndex((t) => t.id === this.editingTileId);
    const tile = index >= 0 ? this.dashboardTiles[index] : null;
    if (!tile || tile.layoutType !== 'WATERFALL') return;
    const updated: DashboardTile = {
      ...tile,
      format: { ...(tile.format || {}), ...this.barFormat, title: this.title.trim() || undefined },
    };
    this.onTileFormatChange(updated);
    this.dashboardTiles = this.dashboardTiles.map((t, i) => (i === index ? updated : t));
  }

  /** LIVE PREVIEW — reflect the current bar config on the canvas as it's built:
   *   - Editing an existing tile → rebuild THAT tile in place.
   *   - Otherwise, once enough is mapped (canPreviewBar) → auto-create/update a
   *     DRAFT preview tile so the chart appears BEFORE Generate; it updates on
   *     every change. If the config becomes incomplete, the draft is removed.
   *  Generate later finalizes the draft (see generateConfiguration). */
  private refreshEditingBarTile(): void {
    if (!this.isBarLayout) return;

    // Editing a real (already-generated) tile — refresh it live.
    if (this.editingTileId) {
      const index = this.dashboardTiles.findIndex((t) => t.id === this.editingTileId);
      const tile = index >= 0 ? this.dashboardTiles[index] : null;
      if (tile && tile.layoutType === 'BAR') {
        this.dashboardTiles = this.dashboardTiles.map((t, i) => (i === index ? this.rebuildBarTile(tile) : t));
      }
      return;
    }

    // Not enough config → drop any existing draft preview tile.
    if (!this.canPreviewBar) {
      this.removePreviewTile();
      return;
    }

    // Create the draft preview tile on first eligibility, else update it.
    const existingIndex = this.previewTileId ? this.dashboardTiles.findIndex((t) => t.id === this.previewTileId) : -1;
    if (existingIndex < 0) {
      const base = this.newTileBase(this.buildPayload());
      this.previewTileId = base.id;
      const draft = this.rebuildBarTile({ ...base, layoutType: 'BAR' });
      this.dashboardTiles = [...this.dashboardTiles, draft];
    } else {
      const tile = this.dashboardTiles[existingIndex];
      this.dashboardTiles = this.dashboardTiles.map((t, i) => (i === existingIndex ? this.rebuildBarTile(tile) : t));
    }
  }

  /** Rebuild ONE bar tile's render data from the current builder state (fresh
   *  object refs so the chart's ngOnChanges fires → live re-render). */
  private rebuildBarTile(tile: DashboardTile): DashboardTile {
    const format = { ...this.defaultTileFormat('BAR'), ...this.barFormat, title: this.title.trim() || undefined };
    // SERVER-computed tile: overlay title + format onto the existing widget
    // data instead of rebuilding from raw rows (mirrors rebuildLineAreaTile —
    // a rebuild wiped the server rows on every Title keypress).
    const existing = tile.barData as Record<string, unknown> | null | undefined;
    if (existing && this.isServerComputedClientTile(existing, this.barCategory)) {
      const caps = this.applyBarFormatToCapabilities(
        { ...(existing['capabilities'] as BarColumnCapabilities) },
        format,
      );
      return {
        ...tile,
        title: this.title.trim() || tile.title || 'Untitled',
        format,
        barData: { ...existing, title: this.title.trim() || tile.title, capabilities: caps },
      };
    }
    const rows = this.applyBarComputedColumns(this.entityRowsCache.get(this.drillDataset) || []);
    return {
      ...tile,
      title: this.title.trim() || tile.title || 'Untitled',
      format,
      barRows: rows,
      barData: this.buildBarWidgetData(tile.id, format),
    };
  }

  // ── Line & Area live-preview (mirror the BAR trio) ──
  private refreshEditingLineAreaTile(): void {
    if (!this.isLineAreaLayout) return;
    if (this.editingTileId) {
      const index = this.dashboardTiles.findIndex((t) => t.id === this.editingTileId);
      const tile = index >= 0 ? this.dashboardTiles[index] : null;
      if (tile && tile.layoutType === 'LINE_AREA') {
        this.dashboardTiles = this.dashboardTiles.map((t, i) => (i === index ? this.rebuildLineAreaTile(tile) : t));
      }
      return;
    }
    if (!this.canPreviewLineArea) {
      this.removePreviewTile();
      return;
    }
    const existingIndex = this.previewTileId ? this.dashboardTiles.findIndex((t) => t.id === this.previewTileId) : -1;
    if (existingIndex < 0) {
      const base = this.newTileBase(this.buildPayload());
      this.previewTileId = base.id;
      const draft = this.rebuildLineAreaTile({ ...base, layoutType: 'LINE_AREA' });
      this.dashboardTiles = [...this.dashboardTiles, draft];
    } else {
      const tile = this.dashboardTiles[existingIndex];
      this.dashboardTiles = this.dashboardTiles.map((t, i) => (i === existingIndex ? this.rebuildLineAreaTile(tile) : t));
    }
  }

  /** Swap a line-area tile onto SERVER-computed rows when the compute response
   *  carries the `data[0].lineArea` envelope (pre-aggregated {department, value}
   *  rows). Nulling `lineAreaRows` matters: it takes the tile OUT of the
   *  client-side cross-filter/dropdown registry, so filters drive it through
   *  the compute API (like waterfall/KPI) instead of filtering rows locally. */
  private applyLineAreaComputeResponse(componentId: string, response: Record<string, unknown> | null): void {
    const rows = extractLineAreaComputeRows(response?.['responseList']) ?? extractLineAreaComputeRows(response);
    if (!rows?.length || !componentId) return;
    const index = this.dashboardTiles.findIndex((t) => t.componentId === componentId && t.layoutType === 'LINE_AREA');
    if (index < 0) return;
    const tile = this.dashboardTiles[index];
    const data = buildLineAreaComputeWidgetData(response, {
      widgetId: 'dvm-tile-line-area-' + tile.id,
      title: tile.title,
      capabilities: (tile.lineAreaData?.['capabilities'] as Record<string, unknown> | undefined),
      format: tile.format,
    });
    this.dashboardTiles = this.dashboardTiles.map((t, i) => (i === index ? { ...t, lineAreaRows: null, lineAreaData: data } : t));
    this.reflowCharts();
  }

  /** True when a client-family tile currently renders SERVER-computed rows (the
   *  remapped {department, value} shape): its capabilities.category IS a key of
   *  its staticData rows while the form's configured category is NOT. Rebuilding
   *  such a tile from raw dataset rows wipes the server data (blank chart). */
  private isServerComputedClientTile(data: Record<string, unknown> | null | undefined, formCategory: string): boolean {
    const rows = data?.['staticData'] as Array<Record<string, unknown>> | undefined;
    const caps = data?.['capabilities'] as Record<string, unknown> | undefined;
    const first = Array.isArray(rows) ? rows[0] : undefined;
    if (!first || !caps) return false;
    const capCategory = String(caps['category'] ?? '');
    return !!capCategory && capCategory in first && !!formCategory && !(formCategory in first);
  }

  /** Rebuild ONE line-area tile's render data from the current builder state. */
  private rebuildLineAreaTile(tile: DashboardTile): DashboardTile {
    const format = { ...this.defaultTileFormat('LINE_AREA'), ...this.barFormat, title: this.title.trim() || undefined };
    // SERVER-computed tile: a live refresh (title/format keystrokes) must NOT
    // rebuild from raw dataset rows — that wiped the server rows on every
    // Title-accordion keypress. Overlay title + format onto the EXISTING
    // widget data; structural edits take effect on Update Configuration when
    // the fresh compute response is re-applied.
    const existing = tile.lineAreaData as Record<string, unknown> | null | undefined;
    if (existing && this.isServerComputedClientTile(existing, this.laCategory)) {
      const caps = this.applyLineAreaFormatToCapabilities(
        { ...(existing['capabilities'] as LineAreaCapabilities) },
        format,
      );
      return {
        ...tile,
        title: this.title.trim() || tile.title || 'Untitled',
        format,
        lineAreaData: { ...existing, title: this.title.trim() || tile.title, capabilities: caps },
      };
    }
    const rows = this.entityRowsCache.get(this.drillDataset) || [];
    return {
      ...tile,
      title: this.title.trim() || tile.title || 'Untitled',
      format,
      lineAreaRows: rows,
      lineAreaData: this.buildLineAreaWidgetData(tile.id, format),
    };
  }

  // ── Scatter & Bubble live-preview (mirror the BAR trio) ──
  onScatterBubbleChange(): void {
    this.invalidatePreview();
    this.refreshEditingScatterBubbleTile();
  }
  onScatterBubbleFormatChange(): void {
    if (typeof this.sbFormat['title'] === 'string') this.title = this.sbFormat['title'] as string;
    this.refreshEditingScatterBubbleTile();
  }
  private refreshEditingScatterBubbleTile(): void {
    if (!this.isScatterBubbleLayout) return;
    if (this.editingTileId) {
      const index = this.dashboardTiles.findIndex((t) => t.id === this.editingTileId);
      const tile = index >= 0 ? this.dashboardTiles[index] : null;
      if (tile && tile.layoutType === 'SCATTER_BUBBLE') {
        this.dashboardTiles = this.dashboardTiles.map((t, i) => (i === index ? this.rebuildScatterBubbleTile(tile) : t));
      }
      return;
    }
    if (!this.canPreviewScatterBubble) {
      this.removePreviewTile();
      return;
    }
    const existingIndex = this.previewTileId ? this.dashboardTiles.findIndex((t) => t.id === this.previewTileId) : -1;
    if (existingIndex < 0) {
      const base = this.newTileBase(this.buildPayload());
      this.previewTileId = base.id;
      const draft = this.rebuildScatterBubbleTile({ ...base, layoutType: 'SCATTER_BUBBLE' });
      this.dashboardTiles = [...this.dashboardTiles, draft];
    } else {
      const tile = this.dashboardTiles[existingIndex];
      this.dashboardTiles = this.dashboardTiles.map((t, i) => (i === existingIndex ? this.rebuildScatterBubbleTile(tile) : t));
    }
  }
  private rebuildScatterBubbleTile(tile: DashboardTile): DashboardTile {
    const format = { ...this.defaultTileFormat('SCATTER_BUBBLE'), ...this.sbFormat, title: this.title.trim() || undefined };
    const rows = this.applyBarComputedColumns(this.entityRowsCache.get(this.drillDataset) || []);
    return {
      ...tile,
      title: this.title.trim() || tile.title || 'Untitled',
      format,
      scatterBubbleRows: rows,
      scatterBubbleData: this.buildScatterBubbleWidgetData(tile.id, format),
    };
  }

  /** Remove the auto-created draft preview tile (if any). */
  private removePreviewTile(): void {
    if (!this.previewTileId) return;
    const id = this.previewTileId;
    this.previewTileId = null;
    this.dashboardTiles = this.dashboardTiles.filter((t) => t.id !== id);
  }

  /** Drop a column onto the bar category picker. */
  onBarCategoryFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    this.barCategory = mapping.field;
    if (!this.barCategoryLabel) this.barCategoryLabel = mapping.field;
    this.invalidatePreview();
  }

  /** Drop a column onto the bar series (split) picker. */
  onBarSeriesFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    this.barSeries = mapping.field;
    this.invalidatePreview();
  }

  /** Drop a column onto the hierarchical parent (outer group) picker. */
  onBarParentFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    this.barCategoryParent = mapping.field;
    this.invalidatePreview();
  }

  // ── Scatter & Bubble field drops (mirror the BAR handlers) ──
  onSbXFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    this.sbXField = mapping.field;
    if (!this.sbXLabel) this.sbXLabel = mapping.field;
    this.onScatterBubbleChange();
  }
  onSbYFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    this.sbYField = mapping.field;
    if (!this.sbYLabel) this.sbYLabel = mapping.field;
    this.onScatterBubbleChange();
  }
  onSbSizeFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    this.sbSizeField = mapping.field;
    this.onScatterBubbleChange();
  }
  onSbColorFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    this.sbColorField = mapping.field;
    this.onScatterBubbleChange();
  }
  onSbSeriesFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    this.sbSeries = mapping.field;
    this.onScatterBubbleChange();
  }

  // ── Reference lines (Bar) ──────────────────────────────────────────────────
  addBarReferenceLine(): void {
    this.barRefLineIdCounter += 1;
    this.barReferenceLines = [
      ...this.barReferenceLines,
      { id: `bar-refline-${this.barRefLineIdCounter}`, value: null, label: '', color: '#64748b' },
    ];
  }

  removeBarReferenceLine(id: string): void {
    this.barReferenceLines = this.barReferenceLines.filter((line) => line.id !== id);
    this.invalidatePreview();
  }

  onBarReferenceLineChange(): void {
    this.barReferenceLines = [...this.barReferenceLines];
    this.invalidatePreview();
    this.refreshEditingBarTile();
  }

  /** Drop a column onto a secondary-axis picker. */
  onBarSecondaryAxisFieldDrop(event: DragEvent, which: 'x' | 'y'): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    if (which === 'x') this.barSecondaryXField = mapping.field;
    else this.barSecondaryYField = mapping.field;
    this.invalidatePreview();
  }

  /** Drop a column onto a measure row (or the measures zone → new measure). */
  onBarMeasureFieldDrop(event: DragEvent, measure?: BarBuilderMeasure): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    const target = measure || { id: this.nextBarMeasureId(), field: '', label: '', renderAs: 'bar' as const, axis: 'y1' as const };
    target.field = mapping.field;
    if (!target.label) target.label = mapping.field;
    if (!measure) this.barMeasures = [...this.barMeasures, target];
    this.onBarMeasureChange();
  }

  barColumnPlaceholder(): string {
    if (!this.drillDataset) return 'Select a dataset first';
    if (this.isDrillDatasetColumnsLoading) return 'Loading columns...';
    return 'Select column';
  }

  private nextBarMeasureId(): string {
    this.barMeasureIdCounter += 1;
    return `bar-measure-${this.barMeasureIdCounter}`;
  }

  drillColumnPlaceholder(kind: 'dimension' | 'measure'): string {
    if (!this.drillDataset) return 'Select a dataset first';
    if (this.isDrillDatasetColumnsLoading) return 'Loading columns...';
    if (this.drillColumnsErrorMessage) return 'Unable to load columns';
    return kind === 'dimension' ? 'Select group-by column' : 'Select value column';
  }

  /** Non-empty once a column load for the current drill dataset has failed,
   *  or succeeded with zero usable columns — see loadColumnsForDataset. */
  get drillColumnsErrorMessage(): string {
    return this.drillDataset ? this.datasetColumnsError[this.drillDataset] || '' : '';
  }

  /** Entity group for the metadata/attribute endpoints — config-driven. */
  private get entityGroupName(): string {
    return this.layout.options?.entityGroup?.trim() || 'NontimeSeriesEntities';
  }

  /** Drop the configured `hiddenColumns` (case-insensitive) and de-dupe, keeping
   *  first-seen order — applied to the attribute names from getEntityAttributes. */
  private filterVisibleColumns(names: string[]): string[] {
    const hidden = new Set((this.layout.options?.hiddenColumns ?? ['uuid', '__rowKey']).map((column) => column.toLowerCase()));
    const seen = new Set<string>();
    const columns: string[] = [];
    (names || []).forEach((name) => {
      const key = String(name || '');
      if (!key || hidden.has(key.toLowerCase()) || seen.has(key)) return;
      seen.add(key);
      columns.push(key);
    });
    return columns;
  }

  // ── Generate configuration + render preview ───────────────────────────────

  /** Enough BAR config is mapped to draw a preview (variant + category + at least
   *  one measure; series-based variants also need a split; comparison needs two
   *  measures). Deliberately does NOT require a title — the preview appears while
   *  building, before Generate. Also needs the dataset's rows to be available. */
  get canPreviewBar(): boolean {
    if (!this.isBarLayout || !this.drillDataset) return false;
    if (!this.entityRowsCache.get(this.drillDataset)?.length) return false;
    const filledMeasures = this.barMeasures.filter((m) => !!m.field);
    if (!this.barCategory || !filledMeasures.length) return false;
    if (this.isBarComparison) return filledMeasures.length >= 2;
    if (this.barSeriesRequired && !this.barSeries) return false;
    return true;
  }

  /** Enough is mapped to render a live Scatter/Bubble preview: dataset with rows,
   *  X + Y; Bubble also needs Size, Multi Scatter also needs Series. */
  get canPreviewScatterBubble(): boolean {
    if (!this.isScatterBubbleLayout || !this.drillDataset) return false;
    if (!this.entityRowsCache.get(this.drillDataset)?.length) return false;
    if (!this.sbXField || !this.sbYField) return false;
    if (this.sbVariantUsesSize && !this.sbSizeField) return false;
    if (this.sbVariantUsesSeries && !this.sbSeries) return false;
    return true;
  }

  /** Enough LINE_AREA config is mapped to draw a live preview. */
  get canPreviewLineArea(): boolean {
    if (!this.isLineAreaLayout || !this.drillDataset) return false;
    if (!this.entityRowsCache.get(this.drillDataset)?.length) return false;
    const filledMeasures = this.laMeasures.filter((m) => !!m.field);
    if (!this.laCategory || !filledMeasures.length) return false;
    if (this.laVariantUsesSeries && !this.laSeries) return false;
    return true;
  }

  get canGenerate(): boolean {
    if (this.isGenerating || !this.title.trim()) return false;
    // Input controls don't bind a dataset the way the chart layouts do — they
    // just need at least one target selected. A formula per target is
    // optional, not required: a target can be wired up now and given its
    // formula later (or driven with just the raw value, no formula at all),
    // so Generate/Update must not block on that.
    if (this.isInputControlLayout) {
      return this.selectedTargetCharts.length > 0;
    }
    // File upload is also target-driven and needs no dataset — just a target.
    if (this.isFileUploadLayout) {
      return this.selectedTargetCharts.length > 0;
    }
    // A KPI driven purely by a formula (e.g. a computed expression or a
    // cross-chart reference) doesn't require a dataset to be picked — enable
    // Generate on the formula alone, BEFORE the dataset gate below. (A KPI that
    // instead uses Column + Aggregation still needs a dataset, handled below.)
    // Same for a KPI wired up with a [Token] reference row but no formula text
    // yet typed — the reference itself is the intent to drive this KPI from
    // another chart/input's live output, so it shouldn't need a dataset either.
    if (this.isKpiLayout && (this.kpiFormula.trim() || this.targetReferences(this.componentId).length > 0)) return true;
    if (!this.selectedDatasets.length) return false;
    if (this.isDropdownLayout) {
      return !!this.dropdownField && this.selectedTargetCharts.length > 0;
    }
    if (this.isDrillDownLayout) {
      return this.drillDownDimensions.length > 0 && this.drillDownMeasures.length > 0;
    }
    if (this.isTableMatrixLayout) {
      // Drill-Down type needs dimensions+measures; Normal/Editable need only a
      // dataset (already checked above) — columns default to "all" when empty.
      if (this.isTmDrilldown) {
        return this.drillDownDimensions.length > 0 && this.drillDownMeasures.length > 0;
      }
      return true;
    }
    if (this.isHorizontalBarLayout) {
      const hasGroupFormula = this.hbGroupFormulas.some((entry) => entry.group.trim() && entry.formula.trim());
      return !!this.hbGroupByField && (!!this.hbValueFormula.trim() || hasGroupFormula);
    }
    if (this.isKpiLayout) {
      return (!!this.kpiField && !!this.kpiAggregation) || !!this.kpiFormula.trim(); 
    }
    if (this.isBarLayout) {
      const filledMeasures = this.barMeasures.filter((measure) => !!measure.field);
      const hasMeasure = filledMeasures.length > 0 || this.barUsesValueFormula;
      // Series-based variants need a split column; simple/clustered don't.
      const hasSeries = !this.barSeriesRequired || !!this.barSeries;
      // Comparison needs a Primary + a Comparison measure (two).
      if (this.isBarComparison) return !!this.barCategory && filledMeasures.length >= 2;
      return !!this.barCategory && hasMeasure && hasSeries;
    }
    if (this.isLineAreaLayout) {
      const filledMeasures = this.laMeasures.filter((measure) => !!measure.field);
      const hasMeasure = filledMeasures.length > 0;
      const hasSeries = !this.laVariantUsesSeries || !!this.laSeries;
      return !!this.laCategory && hasMeasure && hasSeries;
    }
    if (this.isScatterBubbleLayout) {
      // X + Y required; Bubble also needs Size, Multi Scatter also needs Series.
      const hasSize = !this.sbVariantUsesSize || !!this.sbSizeField;
      const hasSeries = !this.sbVariantUsesSeries || !!this.sbSeries;
      return !!this.sbXField && !!this.sbYField && hasSize && hasSeries;
    }
    return this.steps.length > 0;
  }

  generateConfiguration(): void {
    if (!this.canGenerate) return;

    // Pre-flight for the drill-down builders (standalone layout AND the Table &
    // Matrix Drill-Down type): an empty or stale field pick (e.g. left over from
    // a dataset switch) would be rejected by the compute API with an opaque
    // "Dataset 'x' has no field 'y'" — fail fast with a fixable message instead.
    if (this.isDrillDownLayout || (this.isTableMatrixLayout && this.isTmDrilldown)) {
      const columns = this.entityColumnsCache.get(this.drillDataset);
      const rows = [...this.drillDownDimensions, ...this.drillDownMeasures];
      const stale = columns ? rows.map((row) => row.field).filter((field) => field && !columns.includes(field)) : [];
      if (stale.length || rows.some((row) => !row.field)) {
        this.generateError = stale.length
          ? `Dataset "${this.drillDataset}" has no column ${stale.map((field) => `'${field}'`).join(', ')} — re-pick those fields for the selected dataset.`
          : 'Pick a column for every hierarchy level and measure.';
        return;
      }
    }

    this.generateError = '';
    this.isGenerating = true;

    // Give each CHART a stable, type-derived component id (CMP-WATERFALL, …) so
    // the input-control recompute response's `results[].componentId` matches the
    // rendered chart tile directly. Input controls keep their random CMP-<n> id
    // (that id is echoed back as `inputComponentId`).
    //
    // ONLY mint a fresh id when this is a brand-new tile — re-generating an
    // EXISTING tile (editingTileId set) must keep its original componentId.
    // editTile() already restored it onto `this.componentId`; minting a new one
    // here would silently orphan every input control that already targets this
    // tile (their `selectedTargetCharts`/payload targets still point at the OLD
    // id, which would no longer match anything on the dashboard).
    if (!this.editingTileId) {
      const previousComponentId = this.componentId;
      this.componentId = this.componentIdForLayout(this.selectedLayoutType);
      // A chart's OWN formula references (KPI / horizontal bar — see
      // `formulaReferencesEnabledFor`) are keyed by `componentId` at the time
      // the user typed them, which is the id BEFORE this reassignment. Without
      // migrating them here, they'd silently vanish from the UI (the template
      // now looks up the NEW id, which has no entries) and — worse — never
      // reach `buildPayload()` at all, since `referencesForFormula` below also
      // reads off the NEW `this.componentId`.
      if (previousComponentId !== this.componentId && this.targetFormulaReferences[previousComponentId]?.length) {
        const { [previousComponentId]: moved, ...rest } = this.targetFormulaReferences;
        this.targetFormulaReferences = { ...rest, [this.componentId]: moved };
      }
    }

    const payload = this.buildPayload();
    const url = resolveSelfServiceApiUrl(this.layout.apiEndpoints?.generateConfigApi, '/api/dvm/self-service/waterfall/generate');

    // Persist the component config to the DB in parallel with the compute call
    // below — fired for every layout so the config is saved on every Generate.
    this.saveConfigToDb(payload);

    if (this.isDropdownLayout) {
      // The /compute endpoint's response (`{inputComponentId, results: [...]}`)
      // never carries the field's distinct values at all — it's a target-
      // simulation result, not a row listing — so `dropdownOptionsFromComputeResponse`
      // always came back empty ("No items found"). The dropdown's own values
      // come from `apiEndpoints.filterApi` (`/getData`) scoped to just this
      // field — NOT getEntityRows/dynamic_filter, which pulled every row of
      // the whole dataset over the network just to dedupe one column here.
      const filterApiUrl = resolveSelfServiceApiUrl(this.layout.apiEndpoints?.filterApi, '/getData');
      this.datasetService.getDistinctFieldValues(filterApiUrl, this.drillDataset, this.dropdownField).subscribe({
        next: (rows) => {
          const options = this.distinctDropdownOptions(rows, this.dropdownField);
          this.isGenerating = false;
          const dropdownTile = this.buildDropdownTile(payload, options);
          const updated = this.addTile(dropdownTile);
          this.ui.showSuccess(updated ? 'Dropdown updated on the dashboard.' : 'Dropdown added to the dashboard.');
          // Persist the config + apply the initial (default) filter to targets -
          // independent of the options list above, so a slow/failed compute call
          // never blocks the dropdown itself from appearing with real options.
          this.http.post<Record<string, unknown>>(url, payload).subscribe({
            error: () => this.ui.showError('Dropdown added, but saving its target-filter configuration failed.'),
          });
          // Immediately drive the target(s) with the dropdown's default value —
          // otherwise a freshly generated dropdown just sits there and its
          // target chart keeps its unfiltered data until the user manually
          // changes the selection (the "target not updating on generate" bug).
          const persisted = this.dashboardTiles.find((tile) => tile.id === dropdownTile.id) ?? dropdownTile;
          const value = persisted.dropdownControl?.value ?? [];
          if ((Array.isArray(value) ? value.length : value != null && value !== '')) {
            this.onTileDropdownChange(persisted, value);
          }
        },
        error: (error: HttpErrorResponse) => {
          this.isGenerating = false;
          this.generateError = error.error?.message || error.message || 'Unable to load dropdown option values.';
          this.ui.showError(this.generateError);
        },
      });
      return;
    }

    // Input controls (slider / input box) are pure UI — they render from the
    // builder inputs alone and don't need the compute response. Add the tile
    // immediately so it always appears; the POST below just registers the
    // config with the backend (its response isn't required to draw the tile).
    if (this.isInputControlLayout) {
      this.isGenerating = false;
      const updated = this.addTile(this.buildInputTile(payload));
      this.http.post<Record<string, unknown>>(url, payload).subscribe({
        next: () => this.ui.showSuccess(updated ? 'Input control updated on the dashboard.' : 'Input control added to the dashboard.'),
        error: () => this.ui.showError('Input control added, but saving its configuration failed.'),
      });
      return;
    }

    // Bar & Column renders CLIENT-SIDE from the dataset rows the builder already
    // fetched (getEntityRows), so the preview is instant and needs no compute
    // response. The POST below just registers the config (structured so a future
    // server-side compute is a drop-in swap: feed its rows to buildBarWidgetData
    // instead of the cached rows). Add the tile immediately.
    if (this.isBarLayout) {
      this.isGenerating = false;
      // If a live draft preview tile already exists, FINALIZE it in place (so we
      // don't add a duplicate) — just refresh it with the final payload/title and
      // promote it to a normal tile. Otherwise add a fresh tile.
      if (this.previewTileId && this.dashboardTiles.some((t) => t.id === this.previewTileId)) {
        const id = this.previewTileId;
        this.dashboardTiles = this.dashboardTiles.map((t) =>
          t.id === id ? { ...this.rebuildBarTile(t), payload } : t,
        );
        this.previewTileId = null; // promoted → no longer a draft
      } else {
        const fresh = this.buildBarTile(payload);
        // UPDATING a server-computed tile: keep its CURRENT server rows on
        // screen until the fresh compute response lands (mirrors the
        // line-area path — replacing outright blanked the chart).
        const editing = this.editingTileId ? this.dashboardTiles.find((t) => t.id === this.editingTileId) : undefined;
        if (editing?.barData && this.isServerComputedClientTile(editing.barData as Record<string, unknown>, this.barCategory)) {
          fresh.barData = { ...(editing.barData as Record<string, unknown>), title: this.title.trim() || fresh.title };
          fresh.barRows = editing.barRows ?? [];
        }
        this.addTile(fresh);
      }
      this.http.post<Record<string, unknown>>(url, payload).subscribe({
        next: (response) => {
          // Formula values exist only in this response; the cached rows lack
          // them. ALSO: a compute backend that answers plain bar configs with
          // SERVER-side pre-aggregated rows (`data[0].bar`, keys
          // {department, value} — same convention as lineArea) must render from
          // those, or the chart shows nothing when the client rows don't line
          // up. Absent envelope + no formula → client-side render as before.
          const serverRows = extractBarComputeRows(response?.['responseList']) ?? extractBarComputeRows(response);
          if (this.barUsesValueFormula || serverRows?.length) this.applyBarComputeResponse(response, payload);
          this.ui.showSuccess('Bar chart added to the dashboard.');
        },
        error: () => this.ui.showError('Bar chart added, but saving its configuration failed.'),
      });
      return;
    }

    // Scatter & Bubble also renders CLIENT-SIDE from the cached dataset rows, so
    // it mirrors the Bar path: finalize the draft in place or add a fresh tile.
    if (this.isScatterBubbleLayout) {
      this.isGenerating = false;
      if (this.previewTileId && this.dashboardTiles.some((t) => t.id === this.previewTileId)) {
        const id = this.previewTileId;
        this.dashboardTiles = this.dashboardTiles.map((t) =>
          t.id === id ? { ...this.rebuildScatterBubbleTile(t), payload } : t,
        );
        this.previewTileId = null;
      } else {
        this.addTile(this.buildScatterBubbleTile(payload));
      }
      this.http.post<Record<string, unknown>>(url, payload).subscribe({
        next: () => this.ui.showSuccess('Scatter / bubble chart added to the dashboard.'),
        error: () => this.ui.showError('Scatter / bubble chart added, but saving its configuration failed.'),
      });
      return;
    }

    // Line & Area also renders CLIENT-SIDE from the cached dataset rows.
    if (this.isLineAreaLayout) {
      this.isGenerating = false;
      if (this.previewTileId && this.dashboardTiles.some((t) => t.id === this.previewTileId)) {
        const id = this.previewTileId;
        this.dashboardTiles = this.dashboardTiles.map((t) =>
          t.id === id ? { ...this.rebuildLineAreaTile(t), payload } : t,
        );
        this.previewTileId = null;
      } else {
        const fresh = this.buildLineAreaTile(payload);
        // UPDATING a server-computed tile: keep its CURRENT server rows on
        // screen until the fresh compute response lands — the client-side
        // build above has no usable rows for it, so replacing outright
        // blanked the chart on every Update Configuration.
        const editing = this.editingTileId ? this.dashboardTiles.find((t) => t.id === this.editingTileId) : undefined;
        if (editing?.lineAreaData && this.isServerComputedClientTile(editing.lineAreaData as Record<string, unknown>, this.laCategory)) {
          fresh.lineAreaData = { ...(editing.lineAreaData as Record<string, unknown>), title: this.title.trim() || fresh.title };
          fresh.lineAreaRows = editing.lineAreaRows ?? null;
        }
        this.addTile(fresh);
      }
      this.http.post<Record<string, unknown>>(url, payload).subscribe({
        next: (response) => {
          // A compute backend can answer the line-area config with SERVER-side
          // pre-aggregated rows (`data[0].lineArea`, keys {department, value}).
          // When present, render FROM the response — the configured dataset
          // columns don't exist on those rows, so the client-side render alone
          // showed an empty chart. Absent → the tile keeps its client rows.
          this.applyLineAreaComputeResponse(String(payload['componentId'] ?? ''), response);
          this.ui.showSuccess('Line / area chart added to the dashboard.');
        },
        error: () => this.ui.showError('Line / area chart added, but saving its configuration failed.'),
      });
      return;
    }

    // File Upload control — pure UI (renders from builder inputs); the actual
    // file is POSTed later when the user picks one. Add the tile at once + POST
    // the descriptor to register the config.
    if (this.isFileUploadLayout) {
      // Pure UI control — at generate time there's NO file to compute, so we do
      // NOT POST to the compute API (it requires a real `value`/file and would
      // 400 with "No value provided"). Just add the uploader tile; the actual
      // file + compute call happens later when the user picks a file.
      this.isGenerating = false;
      const updated = this.addTile(this.buildFileUploadTile(payload));
      this.ui.showSuccess(updated ? 'File upload updated on the dashboard.' : 'File upload added to the dashboard.');
      return;
    }

    // Normal / Editable tables go through the SAME compute API as every other
    // chart. The backend's table contract is dimensions + measures (its flat
    // `columns` shape is rejected server-side: a string array fails to
    // deserialize, column objects 500), so translate the form's column picks
    // into that contract and flatten the tree response back into the flat rows
    // buildTableModel renders. The tile keeps the ORIGINAL form payload
    // (columns/groupBy/aggregation) so save/edit-restore stay unchanged.
    if (this.isTableMatrixLayout && !this.isTmDrilldown) {
      // Normal / Editable use the FLAT table contract now — the backend returns
      // flat rows in `data[]`, read directly (no tree flatten). extractTableMatrixRows
      // handles the common envelopes (data[], responseList[0].data, bare array).
      this.http.post<Record<string, unknown>>(url, this.buildTableMatrixComputePayload()).subscribe({
        next: (response) => {
          this.isGenerating = false;
          const rows = this.extractTableMatrixRows(response);
          const updated = this.addTileFromResponse({ data: rows }, payload);
          // Keep this table tile "live-linked" to the form so subsequent config
          // edits reflect immediately without needing to re-Generate.
          if (!this.editingTileId) {
            const last = this.dashboardTiles[this.dashboardTiles.length - 1];
            if (last?.layoutType === 'TABLE_MATRIX') this.editingTileId = last.id;
          }
          this.ui.showSuccess(updated ? 'Table updated on the dashboard.' : 'Table added to the dashboard.');
        },
        error: (error: HttpErrorResponse) => {
          this.isGenerating = false;
          this.generateError = error.error?.message || error.message || 'Unable to generate the table.';
          this.ui.showError(this.generateError);
        },
      });
      return;
    }

    // STEP 1 — call the compute API, then append the result as a dashboard tile.
    this.http.post<Record<string, unknown>>(url, payload).subscribe({
      next: (response) => {
        this.isGenerating = false;
        const updated = this.addTileFromResponse(response, payload);
        this.ui.showSuccess(updated ? 'Chart updated on the dashboard.' : 'Chart added to the dashboard.');
      },
      error: (error: HttpErrorResponse) => {
        this.isGenerating = false;
        // Horizontal bar keeps a bundled sample fallback for now (per request)
        // so it renders without a reachable compute API; the others surface the
        // error directly.
        if (this.isHorizontalBarLayout) {
          this.http.get<Record<string, unknown>>('/assets/charts-config/horizontal-bar-chart-config.json').subscribe({
            next: (dummy) => {
              const updated = this.addTileFromResponse(dummy, payload);
              this.ui.showSuccess(updated ? 'Analytics engine not reachable - updated with sample bar-chart data.' : 'Analytics engine not reachable - added sample bar-chart data.');
            },
            error: () => {
              this.generateError = 'Unable to generate the configuration.';
              this.ui.showError(this.generateError);
            },
          });
          return;
        }
        this.generateError = error.error?.message || error.message || 'Unable to generate the configuration.';
        this.ui.showError(this.generateError);
      },
    });
  }

  /** Persist the component config to the DB via `saveDbConfigApi`, fired in
   *  parallel with the compute call on every Generate. Non-blocking: the tile
   *  renders from the compute response regardless of this call's outcome, so a
   *  failure here only surfaces a toast — it never aborts the generate flow.
   *  No-op when the endpoint isn't configured. */
  private saveConfigToDb(payload: Record<string, unknown>): void {
    const saveUrl = this.layout.apiEndpoints?.saveDbConfigApi ? resolveSelfServiceApiUrl(this.layout.apiEndpoints.saveDbConfigApi) : '';
    if (!saveUrl) return;
    this.http.post<Record<string, unknown>>(saveUrl, payload).subscribe({
      error: () => this.ui.showError('Chart added, but saving its configuration to the database failed.'),
    });
  }

  /** Build a tile from the compute response for the active (non-input) layout
   *  and append it to the dashboard canvas. */
  private addTileFromResponse(response: Record<string, unknown> | null, payload: Record<string, unknown>): boolean {
    const base = this.newTileBase(payload);
    base.format = this.defaultTileFormat(this.selectedLayoutType);
    // Layouts with a Build-form Format accordion fold its values in so Generate
    // honors what was set there (Bar/Waterfall/Table already fold in their own
    // branches below).
    if (this.isKpiLayout || this.isDrillDownLayout || this.isHorizontalBarLayout) {
      base.format = { ...base.format, ...this.barFormat };
    }
    let tile: DashboardTile;
    if (this.isDrillDownLayout) {
      const drillData = this.buildDrillDownWidgetData(response, base.format);
      // Unique per tile so each drill-down instance is independent.
      drillData['widgetId'] = 'dvm-tile-drill-' + base.id;
      tile = { ...base, drillData };
    } else if (this.isTableMatrixLayout) {
      // Fold the Build-form format accordion values into the tile's format (like
      // the Bar/Waterfall paths) so Generate honors what was set in the builder.
      base.format = { ...base.format, ...this.barFormat };
      const tableMatrixData = this.buildTableMatrixWidgetData(response, base.id, base.format);
      tile = { ...base, tableMatrixData };
    } else if (this.isHorizontalBarLayout) {
      tile = { ...base, hbData: this.buildHbWidgetData(response, base.id, base.format) };
    } else if (this.isKpiLayout) {
      tile = { ...base, colSpan: 3, rowSpan: 3, kpiData: this.buildKpiWidgetData(response, base.id, base.format) };
    } else {
      // Fold the Build-form format accordion values into the tile's format (like
      // buildBarTile does) so Generate honors what was set in the builder.
      base.format = { ...base.format, ...this.barFormat, title: this.title.trim() || undefined };
      const rawBars = (Array.isArray(response) ? response : (response as { data?: WaterfallBar[] })?.data || []) as WaterfallBar[];
      const bars = this.applyStepSignsToWaterfallBars(rawBars, payload);
      // Keep the raw bars so the Format popup can re-derive the ECharts option.
      tile = { ...base, waterfallBars: bars, previewOption: this.buildWaterfallPreviewOption(bars, base.format) };
      tile.waterfallData = this.buildWaterfallTileData(tile);
    }
    return this.addTile(tile);
  }

  /** Seed a tile's format object from the configured controls' defaults —
   *  ONLY the controls that actually apply to this layout type (same
   *  `appliesTo` filtering as `formatControlsFor`), so e.g. a KPI-scoped
   *  `numberFormat` default doesn't leak onto Waterfall/HBar tiles when two
   *  controls share the same `key` but target different layout types. */
  private defaultTileFormat(layoutType: string): Record<string, unknown> {
    const format: Record<string, unknown> = {};
    // Table & Matrix gates controls per sub-type (visibleWhenType) — honor that
    // here too, so a control scoped to ONE type (e.g. the drill-down-only `title`
    // accordion control) doesn't seed its default into another type's format.
    const subType = layoutType === 'TABLE_MATRIX' ? this.tmType : '';
    this.applicableFormatControls(layoutType).forEach((control) => {
      if (subType && !this.controlVisibleForSubType(control, subType)) return;
      if (control.default !== undefined) format[control.key] = control.default;
    });
    return format;
  }

  /** Wrap the compute response's single aggregated value as the shared
   *  <app-number-stats-shared>'s `[data]` input. With no apiUrl, getConfig
   *  returns `data.data` unwrapped and NumberStats reads res.data[0][key], so we
   *  inline the computed value under a fixed `value` key. */
  private buildKpiWidgetData(response: Record<string, unknown> | null, tileId: string, format?: Record<string, unknown>): Record<string, unknown> {
    const value = this.extractKpiValue(response);
    return {
      title: this.title.trim() || 'KPI',
      key: 'value',
      widgetId: 'dvm-tile-kpi-' + tileId,
      alignment: 'center',
      numberFormat: this.toKpiNumberFormat(format),
      numberScale: this.toNumberScale(format?.['numberFormat']),
      decimals: Number(format?.['decimals'] ?? 1),
      prefix: (format?.['prefix'] as string) || '',
      suffix: (format?.['suffix'] as string) || '',
      // Inline single-row payload — NumberStats reads res.data[0].value.
      data: { data: [{ value }] },
    };
  }

  /** Pull the KPI's single numeric value out of the compute response, trying the
   *  common envelope shapes (responseList[0].data[0], data[0], data, top-level). */
  private extractKpiValue(response: Record<string, unknown> | null): number | string | null {
    if (response == null) return null;
    const list = (response as { responseList?: Array<Record<string, unknown>> }).responseList;
    const fromList = Array.isArray(list) ? (list[0]?.['data'] as unknown) : undefined;
    const dataField = fromList ?? (response as { data?: unknown }).data ?? response;
    const row = Array.isArray(dataField) ? dataField[0] : dataField;
    if (row == null) return null;
    if (typeof row === 'number' || typeof row === 'string') return row;
    const record = row as Record<string, unknown>;
    // Prefer an explicit value/result key, else the chosen field, else the first numeric.
    const candidate =
      record['value'] ?? record['result'] ?? record[this.kpiField] ??
      Object.values(record).find((v) => typeof v === 'number');
    return (candidate as number | string | undefined) ?? null;
  }

  /** Build an INPUT_BOX / SLIDER tile from the current builder inputs. The tile
   *  renders the EXISTING shared widget (app-mat-slider / app-input-box); this
   *  just assembles the `[data]` object each of those consumes. See
   *  docs/DVM_LAYOUT_BUILDER_DATA_API.md for the contract. */
  private buildInputTile(payload: Record<string, unknown>): DashboardTile {
    const base = this.newTileBase(payload);
    // Seed the Format popup (decimals/round-off scale) same as any other
    // formattable tile — without this tile.format stays undefined and the
    // popup never opens (isFormattableTile gates on tile.format truthiness
    // via the template's *ngIf="openFormatTileId === tile.id && tile.format").
    base.format = this.defaultTileFormat(this.selectedLayoutType);
    const dataset = this.selectedDatasets[0] || '';
    // Column list (if the dataset's columns were loaded) powers the widgets'
    // built-in compute/formula panels.
    const computationColumns = (this.entityColumnsCache.get(dataset) || []).map((name) => ({ name }));
    const value = this.inputDefaultValue ?? (this.isSliderLayout ? this.sliderMin : '');
    // `numberFormat` doubles as the display SCALE (K/M/B/%/plain) here — same
    // preset values formatNumber() (number-format.utils.ts) already knows how
    // to render; `decimals` is the round-off precision on that display.
    const numberFormat = (base.format?.['numberFormat'] as string) || 'NONE';
    const decimals = Number(base.format?.['decimals'] ?? 1);

    const widgetData: Record<string, unknown> = this.isSliderLayout
      ? {
          // app-mat-slider self-reads min/max/step via ConfigService.getConfig.
          // With NO apiUrl, getConfig returns `data.data` unwrapped, and the
          // slider reads res.data.{min,max,step,name,rangeStart,rangeEnd}.
          key: this.componentId,
          textCase: 'capital',
          numberFormat,
          decimals,
          data: {
            data: {
              name: this.title.trim() || 'Slider',
              min: this.sliderMin,
              max: this.sliderMax,
              step: this.sliderStep,
              // MatSliderComponent's thumb/input are bound to `rangeEnd` (it's a
              // single-handle slider despite the "range" naming — `rangeStart`
              // is read but never shown), so the configured default value MUST
              // go there, not into rangeStart, or the handle always starts at
              // whatever rangeEnd happens to be (previously always `sliderMax`
              // — the slider looked like it was pinned to its maximum).
              rangeStart: this.inputDefaultValue ?? this.sliderMin,
              rangeEnd: this.inputDefaultValue ?? this.sliderMin,
            },
          },
          computationEntityName: dataset,
          computationColumns,
          // Hide the on-the-fly compute (bolt) button on this tile.
          hideCompute: true,
        }
      : {
          // app-input-box renders directly from `data` (title / placeholder) and
          // drives its own compute panel from computationColumns/entityName.
          title: this.title.trim() || 'Input',
          label: this.title.trim() || 'Input',
          placeholder: 'Enter a value',
          entityName: dataset,
          computationEntityName: dataset,
          computationColumns,
          // Hide the on-the-fly compute (bolt) button on this tile.
          hideCompute: true,
          // The configured default — InputBoxComponent reads this to seed (and
          // later re-sync) its displayed value.
          value,
          numberFormat,
          decimals,
        };

    return {
      ...base,
      // Input controls are compact. No `format`: Slider / Input Box take no
      // card-header formatting (builder or published), by request.
      colSpan: 4,
      rowSpan: 4,
      inputControl: {
        kind: this.isSliderLayout ? 'SLIDER' : 'INPUT_BOX',
        // Display summary only (per-target formulas live in the payload targets).
        formula: this.selectedTargetCharts
          .map((id) => this.targetFormulaSummary(id))
          .filter(Boolean)
          .join(' · '),
        value,
        targets: [...this.selectedTargetCharts],
        widgetData,
      },
    };
  }

  /** Build a Bar & Column tile from the current builder inputs, rendering
   *  client-side from the cached dataset rows via the pure option builder. The
   *  tile's `format` starts from the Build-form choices (barFormat) over the
   *  configured defaults, so the tile's Format popup opens pre-filled with what
   *  the user set here. */
  private buildBarTile(payload: Record<string, unknown>): DashboardTile {
    const base = this.newTileBase(payload);
    base.format = { ...this.defaultTileFormat('BAR'), ...this.barFormat, title: this.title.trim() || undefined };
    // Keep the unfiltered rows (with computed columns) so cross-filter reverses cleanly.
    const rows = this.applyBarComputedColumns(this.entityRowsCache.get(this.drillDataset) || []);
    return { ...base, barRows: rows, barData: this.buildBarWidgetData(base.id, base.format) };
  }

  /** Assemble the BarColumnCapabilities from the current builder state. */
  private buildBarCapabilities(): BarColumnCapabilities {
    const measures = this.barMeasures
      .filter((measure) => measure.field)
      .map((measure) => ({
        field: measure.field,
        label: measure.label || measure.field,
        renderAs: measure.renderAs,
        axis: measure.axis,
      }));
    // The active Secondary axis column (Y when vertical, X when horizontal) is
    // appended as an extra measure bound to the secondary (y2) axis — the option
    // builder resolves y2→x2 for horizontal internally.
    const secondaryField = this.barActiveSecondaryField.trim();
    if (secondaryField && !measures.some((m) => m.field === secondaryField)) {
      measures.push({ field: secondaryField, label: secondaryField, renderAs: 'line', axis: 'y2' });
    }
    // Valid reference lines (numeric value set).
    const referenceLines = this.barReferenceLines
      .filter((line) => line.value !== null && Number.isFinite(Number(line.value)))
      .map((line) => ({ value: Number(line.value), label: line.label || undefined, color: line.color || undefined }));
    return {
      orientation: this.barOrientation,
      variant: this.barVariant,
      category: this.barCategory,
      // Hierarchical Category Axis — only meaningful when a distinct parent is set.
      categoryParent: this.barCategoryParent && this.barCategoryParent !== this.barCategory ? this.barCategoryParent : undefined,
      categoryLabel: this.barCategoryLabel || this.barCategory,
      // Series split only applies to the series-based variants.
      series: this.barVariantUsesSeries && this.barSeries ? this.barSeries : undefined,
      measures,
      ...(referenceLines.length ? { referenceLines } : {}),
      // Config-driven ECharts styling defaults → the option builder reads these
      // instead of hardcoded constants.
      ...(this.layout.options?.barChartDefaults ? { chartDefaults: this.layout.options.barChartDefaults } : {}),
      // Config-resolved render-dispatch flags (stack/normalize100/comparison) so
      // the option builder needs no variant-name string checks.
      variantFlags: {
        stack: this.barVariantEntry?.stack === true,
        normalize100: this.barVariantEntry?.normalize100 === true,
        comparison: this.barVariantEntry?.comparison === true,
      },
      // Formula-Builder computed columns — carried on the payload so they round-
      // trip on save/publish/edit. The rows passed to the chart already have these
      // applied (see applyBarComputedColumns), so the option builder ignores them.
      ...(this.barComputedColumns.length ? { computedColumns: this.barComputedColumns } : {}),
    } as BarColumnCapabilities;
  }

  /** Fold the tile's Format-popup choices (all accordion sections) into a bar
   *  capabilities object. Shared by initial build + live re-format so the two
   *  paths never drift. Mutates & returns `capabilities`. */
  private applyBarFormatToCapabilities(
    capabilities: BarColumnCapabilities,
    format?: Record<string, unknown>,
  ): BarColumnCapabilities {
    const f = format || {};
    // Formatting section (mirrors the chart-builder's ChartStyle).
    capabilities.showValues = f['showValues'] !== false;
    capabilities.numberFormat = String(f['numberFormat'] ?? 'NONE');
    // numberScale is now its own control; fall back to deriving it from the
    // numberFormat preset (COMPACT/MILLIONS/…) when not explicitly chosen.
    capabilities.numberScale = f['numberScale'] ? String(f['numberScale']) : this.toNumberScale(f['numberFormat']);
    capabilities.decimals = typeof f['decimals'] === 'number' ? (f['decimals'] as number) : Number(f['decimals'] ?? 1);
    capabilities.labelMode = f['labelMode'] ? String(f['labelMode']) : undefined;
    capabilities.showLegend = f['showLegend'] !== false;
    capabilities.legendPosition = f['legendPosition'] ? String(f['legendPosition']) : undefined;
    // Named palette → resolved colors (from config's palettes registry). Bar
    // coloring is PALETTE-driven; the waterfall-only `positiveColor` must NOT
    // seed `capabilities.colors` here — its green default (#10b981) was winning
    // over the palette (single-color precedence), so a chosen palette never
    // reflected. Leave `colors` unset so the palette always applies.
    capabilities.palette = f['palette'] ? String(f['palette']) : undefined;
    capabilities.paletteColors = this.resolvePaletteColors(capabilities.palette);
    capabilities.colors = undefined;
    // Axis section — X / Y / Secondary X / Secondary Y titles (mirrors chart-builder).
    capabilities.xAxisLabel = (f['xAxisLabel'] as string) || undefined;
    capabilities.yAxisLabel = (f['yAxisLabel'] as string) || undefined;
    capabilities.xAxisLabel2 = (f['xAxisLabel2'] as string) || undefined;
    capabilities.yAxisLabel2 = (f['yAxisLabel2'] as string) || undefined;
    capabilities.axisLabelRotation = f['axisLabelRotation'] ? String(f['axisLabelRotation']) : undefined;
    capabilities.showGridLines = f['showGridLines'] !== false;
    // Tooltip section (full block).
    capabilities.tooltipEnabled = f['tooltipEnabled'] !== false;
    capabilities.tooltipTrigger = f['tooltipTrigger'] ? String(f['tooltipTrigger']) : undefined;
    capabilities.tooltipPosition = f['tooltipPosition'] ? String(f['tooltipPosition']) : undefined;
    capabilities.tooltipShowChart = f['tooltipShowChart'] === true;
    capabilities.tooltipAxisPointer = f['tooltipAxisPointer'] ? String(f['tooltipAxisPointer']) : undefined;
    capabilities.tooltipBackground = (f['tooltipBackground'] as string) || undefined;
    capabilities.tooltipTextColor = (f['tooltipTextColor'] as string) || undefined;
    capabilities.tooltipBorderColor = (f['tooltipBorderColor'] as string) || undefined;
    if (f['tooltipBorderRadius'] !== undefined) capabilities.tooltipBorderRadius = Number(f['tooltipBorderRadius']);
    if (f['tooltipFontSize'] !== undefined) capabilities.tooltipFontSize = Number(f['tooltipFontSize']);
    // Advanced section.
    if (f['barWidthPercent'] !== undefined) capabilities.barWidthPercent = Number(f['barWidthPercent']);
    capabilities.smoothLines = f['smoothLines'] === true;
    capabilities.showVariance = f['showVariance'] !== false;
    capabilities.varianceUpColor = (f['varianceUpColor'] as string) || undefined;
    capabilities.varianceDownColor = (f['varianceDownColor'] as string) || undefined;
    if (f['barGapPercent'] !== undefined) capabilities.barGapPercent = Number(f['barGapPercent']);
    if (f['barCategoryGapPercent'] !== undefined) capabilities.barCategoryGapPercent = Number(f['barCategoryGapPercent']);
    if (f['barCornerRadius'] !== undefined) capabilities.barCornerRadius = Number(f['barCornerRadius']);
    capabilities.sortBy = f['sortBy'] ? String(f['sortBy']) : undefined;
    capabilities.topN = f['topN'] !== undefined && f['topN'] !== '' && f['topN'] !== null ? Number(f['topN']) : undefined;
    // Blank number inputs come through as '' / null → leave as auto (undefined).
    capabilities.valueAxisMin = f['valueAxisMin'] !== undefined && f['valueAxisMin'] !== '' && f['valueAxisMin'] !== null ? Number(f['valueAxisMin']) : undefined;
    capabilities.valueAxisMax = f['valueAxisMax'] !== undefined && f['valueAxisMax'] !== '' && f['valueAxisMax'] !== null ? Number(f['valueAxisMax']) : undefined;
    capabilities.valueAxisLog = f['valueAxisLog'] === true;
    capabilities.showStackTotals = f['showStackTotals'] === true;
    capabilities.animation = f['animation'] !== false;
    // Interaction section.
    capabilities.enableZoom = f['enableZoom'] === true;
    capabilities.zoomAxis = f['zoomAxis'] ? String(f['zoomAxis']) : undefined;
    capabilities.showZoomSlider = f['showZoomSlider'] !== false;
    capabilities.highlightOnClick = f['highlightOnClick'] === true;
    capabilities.crossFilter = f['crossFilter'] === true;
    capabilities.emitSelection = f['emitSelection'] === true;
    return capabilities;
  }

  /** Resolve a palette key (from the config's `palettes` registry) to its color
   *  array; empty when unknown/absent so the option builder uses its default. */
  private resolvePaletteColors(paletteKey?: string): string[] | undefined {
    if (!paletteKey) return undefined;
    const palettes = (this.layout.options as Record<string, unknown> | undefined)?.['palettes'] as
      | Array<{ key?: string; colors?: string[] }>
      | undefined;
    const match = Array.isArray(palettes) ? palettes.find((p) => p.key === paletteKey) : undefined;
    return match?.colors?.length ? match.colors : undefined;
  }

  /** `[data]` input for <app-unified-bar-column-chart>. Carries the capabilities
   *  plus the pre-fetched dataset rows (client-side render) and per-tile format. */
  /** Matched by componentId so a slow response can't land on the wrong tile. */
  private applyBarComputeResponse(response: Record<string, unknown> | null, payload: Record<string, unknown>): void {
    const componentId = String(payload['componentId'] ?? '');
    const tile = this.dashboardTiles.find((candidate) => candidate.componentId === componentId);
    if (!tile) return;
    const rows = extractBarComputeRows(response?.['responseList']) ?? extractBarComputeRows(response);
    if (!rows?.length) {
      this.ui.showError('The value formula returned no data — check the formula and its references.');
      return;
    }
    const widgetData = buildBarComputeWidgetData(response, {
      widgetId: 'dvm-tile-bar-' + tile.id,
      title: this.title.trim(),
      capabilities: this.applyBarFormatToCapabilities(this.buildBarCapabilities(), tile.format) as unknown as Record<string, unknown>,
      payload,
      format: tile.format,
    });
    this.dashboardTiles = this.dashboardTiles.map((candidate) =>
      candidate.id === tile.id
        ? { ...candidate, barRows: rows, barData: widgetData }
        : candidate,
    );
  }

  private buildBarWidgetData(tileId: string, format?: Record<string, unknown>): Record<string, unknown> {
    // Apply any Formula-Builder computed columns to the rows so a computed column
    // mapped onto Category/Measure/Split resolves at render time.
    const rows = this.applyBarComputedColumns(this.entityRowsCache.get(this.drillDataset) || []);
    const capabilities = this.applyBarFormatToCapabilities(this.buildBarCapabilities(), format);
    return {
      widgetId: 'dvm-tile-bar-' + tileId,
      title: this.title.trim(),
      capabilities,
      staticData: rows,
      height: '100%',
    };
  }

  // ── Line & Area builders (mirror the BAR trio) ──
  /** Assemble the LineAreaCapabilities from the current builder state. */
  private buildLineAreaCapabilities(): LineAreaCapabilities {
    const measures: LineAreaMeasure[] = this.laMeasures
      .filter((measure) => measure.field)
      .map((measure) => ({ field: measure.field, label: measure.label || measure.field, renderAs: measure.renderAs, axis: measure.axis }));
    const secondaryField = this.laSecondaryYField.trim();
    if (secondaryField && !measures.some((m) => m.field === secondaryField)) {
      measures.push({ field: secondaryField, label: secondaryField, renderAs: 'line', axis: 'y2' });
    }
    const referenceLines = this.laReferenceLines
      .filter((line) => line.value !== null && Number.isFinite(Number(line.value)))
      .map((line) => ({ value: Number(line.value), label: line.label || undefined, color: line.color || undefined }));
    return {
      variant: this.laVariant,
      category: this.laCategory,
      categoryParent: this.laCategoryParent && this.laCategoryParent !== this.laCategory ? this.laCategoryParent : undefined,
      categoryLabel: this.laCategoryLabel || this.laCategory,
      series: this.laVariantUsesSeries && this.laSeries ? this.laSeries : undefined,
      measures,
      // Secondary (top) X axis — a second category column with its own value
      // series drawn against a top x-axis. Value defaults to the first measure.
      ...(this.laSecondaryXField.trim()
        ? {
            secondaryXField: this.laSecondaryXField.trim(),
            secondaryXValueField: (this.laSecondaryXValueField.trim() || this.laMeasures.find((m) => m.field)?.field || ''),
          }
        : {}),
      ...(referenceLines.length ? { referenceLines } : {}),
      ...(this.layout.options?.lineAreaChartDefaults ? { chartDefaults: this.layout.options.lineAreaChartDefaults } : {}),
      variantFlags: {
        step: this.laVariantEntry?.step === true,
        smooth: this.laVariantEntry?.smooth === true,
        area: this.laVariantEntry?.area === true,
        stack: this.laVariantEntry?.stack === true,
        normalize100: this.laVariantEntry?.normalize100 === true,
      },
    } as LineAreaCapabilities;
  }

  /** Fold the tile's Format-popup choices into a line-area capabilities object. */
  private applyLineAreaFormatToCapabilities(capabilities: LineAreaCapabilities, format?: Record<string, unknown>): LineAreaCapabilities {
    const f = format || {};
    capabilities.showValues = f['showValues'] === true;
    capabilities.numberScale = f['numberScale'] ? String(f['numberScale']) : this.toNumberScale(f['numberFormat']);
    capabilities.decimals = typeof f['decimals'] === 'number' ? (f['decimals'] as number) : Number(f['decimals'] ?? 1);
    capabilities.showLegend = f['showLegend'] !== false;
    capabilities.legendPosition = f['legendPosition'] ? String(f['legendPosition']) : undefined;
    capabilities.palette = f['palette'] ? String(f['palette']) : undefined;
    capabilities.paletteColors = this.resolvePaletteColors(capabilities.palette);
    capabilities.colors = undefined;
    capabilities.xAxisLabel = (f['xAxisLabel'] as string) || undefined;
    capabilities.yAxisLabel = (f['yAxisLabel'] as string) || undefined;
    capabilities.yAxisLabel2 = (f['yAxisLabel2'] as string) || undefined;
    capabilities.axisLabelRotation = f['axisLabelRotation'] ? String(f['axisLabelRotation']) : undefined;
    capabilities.showGridLines = f['showGridLines'] !== false;
    capabilities.tooltipEnabled = f['tooltipEnabled'] !== false;
    capabilities.tooltipTrigger = f['tooltipTrigger'] ? String(f['tooltipTrigger']) : undefined;
    capabilities.tooltipAxisPointer = f['tooltipAxisPointer'] ? String(f['tooltipAxisPointer']) : undefined;
    capabilities.tooltipBackground = (f['tooltipBackground'] as string) || undefined;
    capabilities.tooltipTextColor = (f['tooltipTextColor'] as string) || undefined;
    if (f['lineWidth'] !== undefined && f['lineWidth'] !== '' && f['lineWidth'] !== null) capabilities.lineWidth = Number(f['lineWidth']);
    if (f['symbolSize'] !== undefined && f['symbolSize'] !== '' && f['symbolSize'] !== null) capabilities.symbolSize = Number(f['symbolSize']);
    if (f['areaOpacity'] !== undefined && f['areaOpacity'] !== '' && f['areaOpacity'] !== null) capabilities.areaOpacity = Number(f['areaOpacity']);
    capabilities.smoothLines = f['smoothLines'] === true;
    capabilities.connectNulls = f['connectNulls'] === true;
    capabilities.showTrendLine = f['showTrendLine'] === true;
    capabilities.forecastPeriods = f['forecastPeriods'] !== undefined && f['forecastPeriods'] !== '' && f['forecastPeriods'] !== null ? Number(f['forecastPeriods']) : undefined;
    capabilities.movingAverageWindow = f['movingAverageWindow'] !== undefined && f['movingAverageWindow'] !== '' && f['movingAverageWindow'] !== null ? Number(f['movingAverageWindow']) : undefined;
    capabilities.analyticsColor = (f['analyticsColor'] as string) || undefined;
    capabilities.sortBy = f['sortBy'] ? String(f['sortBy']) : undefined;
    capabilities.topN = f['topN'] !== undefined && f['topN'] !== '' && f['topN'] !== null ? Number(f['topN']) : undefined;
    capabilities.valueAxisMin = f['valueAxisMin'] !== undefined && f['valueAxisMin'] !== '' && f['valueAxisMin'] !== null ? Number(f['valueAxisMin']) : undefined;
    capabilities.valueAxisMax = f['valueAxisMax'] !== undefined && f['valueAxisMax'] !== '' && f['valueAxisMax'] !== null ? Number(f['valueAxisMax']) : undefined;
    capabilities.valueAxisLog = f['valueAxisLog'] === true;
    capabilities.animation = f['animation'] !== false;
    capabilities.enableZoom = f['enableZoom'] === true;
    capabilities.showZoomSlider = f['showZoomSlider'] !== false;
    capabilities.highlightOnClick = f['highlightOnClick'] === true;
    capabilities.crossFilter = f['crossFilter'] === true;
    return capabilities;
  }

  /** Build a Line & Area tile, rendering client-side from cached dataset rows. */
  private buildLineAreaTile(payload: Record<string, unknown>): DashboardTile {
    const base = this.newTileBase(payload);
    base.format = { ...this.defaultTileFormat('LINE_AREA'), ...this.barFormat, title: this.title.trim() || undefined };
    const rows = this.entityRowsCache.get(this.drillDataset) || [];
    return { ...base, lineAreaRows: rows, lineAreaData: this.buildLineAreaWidgetData(base.id, base.format) };
  }

  /** `[data]` input for <app-unified-line-area-chart>. */
  private buildLineAreaWidgetData(tileId: string, format?: Record<string, unknown>): Record<string, unknown> {
    const rows = this.entityRowsCache.get(this.drillDataset) || [];
    const capabilities = this.applyLineAreaFormatToCapabilities(this.buildLineAreaCapabilities(), format);
    return {
      widgetId: 'dvm-tile-line-area-' + tileId,
      title: this.title.trim(),
      capabilities,
      staticData: rows,
      height: '100%',
    };
  }

  // ── Scatter & Bubble builders (mirror the BAR trio: capabilities → format →
  //    widgetData → tile) so the format popup, publish and restore reuse them. ──

  /** Build a Scatter/Bubble tile, rendering client-side from cached dataset rows. */
  private buildScatterBubbleTile(payload: Record<string, unknown>): DashboardTile {
    const base = this.newTileBase(payload);
    base.format = { ...this.defaultTileFormat('SCATTER_BUBBLE'), ...this.sbFormat, title: this.title.trim() || undefined };
    const rows = this.applyBarComputedColumns(this.entityRowsCache.get(this.drillDataset) || []);
    return { ...base, scatterBubbleRows: rows, scatterBubbleData: this.buildScatterBubbleWidgetData(base.id, base.format) };
  }

  /** Non-flag keys on a config variant/overlay entry. Every OTHER `true` key is
   *  a render-dispatch flag and passes through to `variantFlags` GENERICALLY —
   *  a new flag invented in JSON reaches the option builder with no code edit
   *  here (the renderer ignores flags it has no drawing logic for). */
  private static readonly SB_NON_FLAG_KEYS = new Set(['label', 'value', 'mode', 'group', 'hint', 'icon', 'usesSize', 'usesSeries']);

  /** Assemble the ScatterBubbleCapabilities from the current builder state. */
  private buildScatterBubbleCapabilities(): ScatterBubbleCapabilities {
    // Render-dispatch flags = base variant ∪ advanced overlay — the pure option
    // builder applies each flag independently, so Quadrant + Bubble etc. compose.
    const base = this.sbVariantEntry;
    const adv = this.sbAdvancedEntry;
    const variantFlags: Record<string, boolean> = {};
    [base, adv].forEach((entry) => {
      if (!entry) return;
      Object.entries(entry as Record<string, unknown>).forEach(([key, val]) => {
        if (val === true && !DvmLayoutBuilderComponent.SB_NON_FLAG_KEYS.has(key)) variantFlags[key] = true;
      });
    });
    return {
      variant: this.sbVariant,
      advanced: this.sbAdvanced || undefined,
      xField: this.sbXField,
      yField: this.sbYField,
      // Size only applies when a size-based variant/overlay is active (Bubble).
      sizeField: this.sbVariantUsesSize && this.sbSizeField ? this.sbSizeField : undefined,
      colorField: this.sbColorField || undefined,
      // Series only applies when a series-based variant/overlay is active
      // (Multi Scatter / Cluster).
      series: this.sbVariantUsesSeries && this.sbSeries ? this.sbSeries : undefined,
      aggregation: (this.sbAggregation || 'none') as ScatterBubbleCapabilities['aggregation'],
      tooltipColumns: this.sbTooltipColumns.length ? [...this.sbTooltipColumns] : undefined,
      xLabel: this.sbXLabel || this.sbXField,
      yLabel: this.sbYLabel || this.sbYField,
      // Config-driven ECharts styling defaults.
      ...(this.layout.options?.scatterBubbleChartDefaults
        ? { chartDefaults: this.layout.options.scatterBubbleChartDefaults as ScatterBubbleCapabilities['chartDefaults'] }
        : {}),
      // Config-resolved render-dispatch flags so the builder needs no name checks.
      variantFlags,
    } as ScatterBubbleCapabilities;
  }

  /** Fold the tile's Format-popup choices into a scatter-bubble capabilities
   *  object. Shared by initial build + live re-format. Mutates & returns caps. */
  private applyScatterBubbleFormatToCapabilities(
    capabilities: ScatterBubbleCapabilities,
    format?: Record<string, unknown>,
  ): ScatterBubbleCapabilities {
    const f = format || {};
    // Labels (off by default — point labels clutter a dense scatter).
    capabilities.showValues = f['showValues'] === true;
    capabilities.labelPosition = f['labelPosition'] ? String(f['labelPosition']) : undefined;
    if (f['labelFontSize'] !== undefined && f['labelFontSize'] !== '' && f['labelFontSize'] !== null) capabilities.labelFontSize = Number(f['labelFontSize']);
    capabilities.labelColor = (f['labelColor'] as string) || undefined;
    // Formatting.
    capabilities.numberFormat = String(f['numberFormat'] ?? 'NONE');
    capabilities.numberScale = f['numberScale'] ? String(f['numberScale']) : this.toNumberScale(f['numberFormat']);
    capabilities.decimals = typeof f['decimals'] === 'number' ? (f['decimals'] as number) : Number(f['decimals'] ?? 1);
    capabilities.labelMode = f['labelMode'] ? String(f['labelMode']) : undefined;
    if (f['pointOpacity'] !== undefined) capabilities.pointOpacity = Number(f['pointOpacity']);
    capabilities.palette = f['palette'] ? String(f['palette']) : undefined;
    capabilities.paletteColors = this.resolvePaletteColors(capabilities.palette);
    capabilities.colors = undefined;
    // Legend.
    capabilities.showLegend = f['showLegend'] !== false;
    capabilities.legendPosition = f['legendPosition'] ? String(f['legendPosition']) : undefined;
    // Axis.
    capabilities.xAxisLabel = (f['xAxisLabel'] as string) || undefined;
    capabilities.yAxisLabel = (f['yAxisLabel'] as string) || undefined;
    capabilities.showGridLines = f['showGridLines'] !== false;
    // Only a categorical X axis (Bubble Matrix) rotates labels; numeric axes ignore it.
    capabilities.axisLabelRotation = f['axisLabelRotation'] ? String(f['axisLabelRotation']) : undefined;
    // Tooltip.
    capabilities.tooltipEnabled = f['tooltipEnabled'] !== false;
    capabilities.tooltipTrigger = f['tooltipTrigger'] ? String(f['tooltipTrigger']) : undefined;
    capabilities.tooltipPosition = f['tooltipPosition'] ? String(f['tooltipPosition']) : undefined;
    capabilities.tooltipAxisPointer = f['tooltipAxisPointer'] ? String(f['tooltipAxisPointer']) : undefined;
    capabilities.tooltipBackground = (f['tooltipBackground'] as string) || undefined;
    capabilities.tooltipTextColor = (f['tooltipTextColor'] as string) || undefined;
    capabilities.tooltipBorderColor = (f['tooltipBorderColor'] as string) || undefined;
    if (f['tooltipBorderRadius'] !== undefined) capabilities.tooltipBorderRadius = Number(f['tooltipBorderRadius']);
    if (f['tooltipFontSize'] !== undefined) capabilities.tooltipFontSize = Number(f['tooltipFontSize']);
    // Advanced — symbol sizes, axis bounds, animation.
    if (f['symbolSize'] !== undefined && f['symbolSize'] !== '' && f['symbolSize'] !== null) capabilities.symbolSize = Number(f['symbolSize']);
    if (f['symbolSizeMin'] !== undefined && f['symbolSizeMin'] !== '' && f['symbolSizeMin'] !== null) capabilities.symbolSizeMin = Number(f['symbolSizeMin']);
    if (f['symbolSizeMax'] !== undefined && f['symbolSizeMax'] !== '' && f['symbolSizeMax'] !== null) capabilities.symbolSizeMax = Number(f['symbolSizeMax']);
    capabilities.xAxisMin = f['xAxisMin'] !== undefined && f['xAxisMin'] !== '' && f['xAxisMin'] !== null ? Number(f['xAxisMin']) : undefined;
    capabilities.xAxisMax = f['xAxisMax'] !== undefined && f['xAxisMax'] !== '' && f['xAxisMax'] !== null ? Number(f['xAxisMax']) : undefined;
    capabilities.valueAxisMin = f['valueAxisMin'] !== undefined && f['valueAxisMin'] !== '' && f['valueAxisMin'] !== null ? Number(f['valueAxisMin']) : undefined;
    capabilities.valueAxisMax = f['valueAxisMax'] !== undefined && f['valueAxisMax'] !== '' && f['valueAxisMax'] !== null ? Number(f['valueAxisMax']) : undefined;
    capabilities.animation = f['animation'] !== false;
    // Interaction.
    capabilities.enableZoom = f['enableZoom'] === true;
    capabilities.showZoomSlider = f['showZoomSlider'] !== false;
    capabilities.highlightOnClick = f['highlightOnClick'] === true;
    capabilities.crossFilter = f['crossFilter'] === true;
    capabilities.emitSelection = f['emitSelection'] === true;
    // Overlay tuning groups (Quadrant / Regression / Correlation / Cluster) —
    // inert unless the matching variantFlag is active, so mapping them
    // unconditionally is safe.
    capabilities.showQuadrantLabels = f['showQuadrantLabels'] !== false;
    capabilities.quadrantLabels = [f['quadrantLabelTR'], f['quadrantLabelTL'], f['quadrantLabelBL'], f['quadrantLabelBR']]
      .map((v) => (v ? String(v) : ''));
    capabilities.quadrantLineColor = (f['quadrantLineColor'] as string) || undefined;
    capabilities.regressionType = f['regressionType'] ? String(f['regressionType']) : undefined;
    capabilities.regressionLineColor = (f['regressionLineColor'] as string) || undefined;
    capabilities.showEquation = f['showEquation'] !== false;
    capabilities.showConfidenceBand = f['showConfidenceBand'] !== false;
    if (f['confidenceBandOpacity'] !== undefined && f['confidenceBandOpacity'] !== '' && f['confidenceBandOpacity'] !== null) capabilities.confidenceBandOpacity = Number(f['confidenceBandOpacity']);
    capabilities.correlationMethod = f['correlationMethod'] ? String(f['correlationMethod']) : undefined;
    capabilities.showCoefficient = f['showCoefficient'] !== false;
    capabilities.correlationGradientStart = (f['correlationGradientStart'] as string) || undefined;
    capabilities.correlationGradientEnd = (f['correlationGradientEnd'] as string) || undefined;
    if (f['clusterCount'] !== undefined && f['clusterCount'] !== '' && f['clusterCount'] !== null) capabilities.clusterCount = Number(f['clusterCount']);
    capabilities.showClusterHulls = f['showClusterHulls'] !== false;
    if (f['clusterHullOpacity'] !== undefined && f['clusterHullOpacity'] !== '' && f['clusterHullOpacity'] !== null) capabilities.clusterHullOpacity = Number(f['clusterHullOpacity']);
    return capabilities;
  }

  /** `[data]` input for <app-unified-scatter-bubble-chart>. */
  private buildScatterBubbleWidgetData(tileId: string, format?: Record<string, unknown>): Record<string, unknown> {
    const rows = this.applyBarComputedColumns(this.entityRowsCache.get(this.drillDataset) || []);
    const capabilities = this.applyScatterBubbleFormatToCapabilities(this.buildScatterBubbleCapabilities(), format);
    return {
      widgetId: 'dvm-tile-sb-' + tileId,
      title: this.title.trim(),
      capabilities,
      staticData: rows,
      height: '100%',
    };
  }

  private distinctDropdownOptions(rows: Array<Record<string, unknown>>, field: string): string[] {
    return this.uniqueSortedOptions((rows || []).map((row) => row?.[field]));
  }

  private dropdownOptionsFromComputeResponse(response: Record<string, unknown> | null, field: string): string[] {
    const root = response ?? {};
    const result = root['result'] as Record<string, unknown> | undefined;
    const dataRecord = root['data'] as Record<string, unknown> | undefined;
    const optionArrays = [
      root['optionsList'],
      root['options'],
      root['values'],
      result?.['optionsList'],
      result?.['options'],
      dataRecord?.['optionsList'],
      dataRecord?.['options'],
    ];
    const explicit = optionArrays.find(Array.isArray) as unknown[] | undefined;
    if (explicit) return this.uniqueSortedOptions(explicit);

    const rows =
      this.dropdownRowsFromComputeResponse(root['data']) ??
      this.dropdownRowsFromComputeResponse(result?.['data']) ??
      this.dropdownRowsFromComputeResponse(root['responseList']);
    return rows ? this.distinctDropdownOptions(rows, field) : [];
  }

  private dropdownRowsFromComputeResponse(source: unknown): Array<Record<string, unknown>> | null {
    if (!Array.isArray(source)) return null;
    if (!source.length) return [];
    if (source.every((item) => item && typeof item === 'object' && !Array.isArray(item))) {
      const rows = source as Array<Record<string, unknown>>;
      const nested = rows.map((item) => item['data']).find(Array.isArray) as Array<Record<string, unknown>> | undefined;
      return nested ?? rows;
    }
    return null;
  }

  private uniqueSortedOptions(values: unknown[]): string[] {
    return [...new Set(
      values
        .filter((value) => value !== undefined && value !== null && String(value).trim() !== '')
        .map((value) => String(value)),
    )].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
  }

  private buildDropdownTile(payload: Record<string, unknown>, options: string[]): DashboardTile {
    const base = this.newTileBase(payload);
    const defaultValues = this.dropdownDefaultValue.trim()
      ? this.dropdownDefaultValue.split(',').map((value) => value.trim()).filter((value) => options.includes(value))
      : [];
    const value = this.dropdownIsMultiSelect ? defaultValues : defaultValues.slice(0, 1);
    // Default footprint: grid rows are 40px, so rowSpan 1 rendered header-only
    // ("compressed"). 3 fits label + select (also the resize clamp's minimum);
    // button/tile mode gets more room for the wrapping option pills.
    const isButtonMode = this.dropdownDisplayMode === 'buttons' || this.dropdownDisplayMode === 'tiles';
    return {
      ...base,
      payload: { ...payload, value },
      colSpan: 4,
      rowSpan: isButtonMode ? 5 : 3,
      // No `format`: Dropdown takes no card-header formatting (builder or
      // published), by request.
      dropdownControl: {
        field: this.dropdownField,
        options,
        value,
        targets: [...this.selectedTargetCharts],
        widgetData: {
          name: this.dropdownDisplayLabel.trim() || this.title.trim() || this.dropdownField,
          key: this.dropdownField,
          isMultiSelect: this.dropdownIsMultiSelect,
          displayMode: this.dropdownDisplayMode || 'dropdown',
          textCase: 'capital',
          optionsList: options,
        },
      },
    };
  }

  private buildFileUploadTile(payload: Record<string, unknown>): DashboardTile {
    const base = this.newTileBase(payload);
    const buttonLabel = this.fileUploadButtonLabel.trim() || 'Upload file';
    const accept = this.fileUploadAccept.trim() || '.xlsx,.xls,.csv';
    return {
      ...base,
      // Base footprint for the upload tile. No `format`: File Upload takes no
      // card-header formatting (builder or published), by request.
      colSpan: 4,
      rowSpan: 4,
      fileUploadControl: {
        targets: [...this.selectedTargetCharts],
        buttonLabel,
        accept,
        widgetData: {
          title: this.title.trim() || 'File Upload',
          buttonLabel,
          accept,
          // Targets echoed here so the published widget can drive them without
          // digging into the payload.
          targets: this.selectedTargetCharts.map((componentId) => ({
            componentId,
            label: this.targetLabelForId(componentId),
          })),
        },
      },
    };
  }

  /** Shared skeleton for a new tile: id, title, and a default grid footprint
   *  (half-width, 8 row-units). The grid flows tiles in array order — no
   *  positions to compute and no overlap. */
  private newTileBase(payload: Record<string, unknown>): DashboardTile {
    return {
      id: this.nextTileId(),
      componentId: this.componentId,
      layoutType: this.selectedLayoutType,
      title: this.title.trim() || 'Untitled',
      colSpan: 6,
      rowSpan: 8,
      payload,
    };
  }

  /** Every REAL tile (chart/input), flattening GROUP members out to look like
   *  top-level tiles — target pickers, type/step resolution, and recompute
   *  matching all need to find a grouped chart exactly like a standalone one.
   *  The GROUP wrapper itself is never a valid target, so it's excluded, not
   *  flattened alongside its members. */
  private allTilesFlat(): DashboardTile[] {
    const out: DashboardTile[] = [];
    this.dashboardTiles.forEach((t) => {
      if (t.layoutType === 'GROUP') { if (t.groupMembers) out.push(...t.groupMembers); return; }
      out.push(t);
    });
    return out;
  }

  /** Where a tile id lives if it's nested inside a GROUP's members (not a
   *  top-level dashboardTiles entry). */
  private findGroupMemberLocation(id: string): { group: DashboardTile; index: number } | null {
    for (const t of this.dashboardTiles) {
      if (t.layoutType !== 'GROUP' || !t.groupMembers) continue;
      const index = t.groupMembers.findIndex((m) => m.id === id);
      if (index >= 0) return { group: t, index };
    }
    return null;
  }

  /** Find a tile by id anywhere — top-level OR nested inside a group. Editing,
   *  format, interactions, and recompute all need to reach a grouped chart the
   *  same way they reach a standalone one. */
  private findTileAnywhere(id: string): DashboardTile | undefined {
    const top = this.dashboardTiles.find((t) => t.id === id);
    if (top) return top;
    return this.findGroupMemberLocation(id)?.group.groupMembers?.find((m) => m.id === id);
  }

  /** Replace a tile by id wherever it lives (top-level array, or nested inside
   *  a group's members) and bump `dashboardTiles` to a new reference so the
   *  template re-renders — the single write-back path for anything that edits
   *  a tile in place (format popup, interactions popup, table capabilities). */
  private updateTileInPlace(next: DashboardTile): void {
    const topIndex = this.dashboardTiles.findIndex((t) => t.id === next.id);
    if (topIndex >= 0) {
      const updated = [...this.dashboardTiles];
      updated[topIndex] = next;
      this.dashboardTiles = updated;
      return;
    }
    const loc = this.findGroupMemberLocation(next.id);
    if (!loc) return;
    const members = [...(loc.group.groupMembers || [])];
    members[loc.index] = next;
    loc.group.groupMembers = members;
    this.dashboardTiles = [...this.dashboardTiles];
  }

  private addTile(tile: DashboardTile): boolean {
    if (this.editingTileId) {
      const index = this.dashboardTiles.findIndex((item) => item.id === this.editingTileId);
      if (index >= 0) {
        const previous = this.dashboardTiles[index];
        const replacement: DashboardTile = {
          ...tile,
          id: previous.id,
          colSpan: previous.colSpan,
          rowSpan: previous.rowSpan,
          x: previous.x,
          y: previous.y,
          customSize: previous.customSize,
          format: previous.format ?? tile.format,
        };
        const next = [...this.dashboardTiles];
        next[index] = replacement;
        this.dashboardTiles = next;
        this.editingTileId = replacement.id;
        // Reapply the tile's existing visual choices to the fresh API response.
        if (replacement.format) this.onTileFormatChange(replacement);
        return true;
      }
      // Not a top-level tile — check whether it's a GROUP member being edited
      // in place (grouping doesn't take away a chart's own Edit/Format popup;
      // "Update Configuration" here must land back inside its group, not get
      // appended as a brand-new top-level tile).
      const loc = this.findGroupMemberLocation(this.editingTileId);
      if (loc) {
        const previous = loc.group.groupMembers![loc.index];
        const replacement: DashboardTile = {
          ...tile,
          id: previous.id,
          colSpan: previous.colSpan,
          rowSpan: previous.rowSpan,
          x: previous.x,
          y: previous.y,
          customSize: previous.customSize,
          format: previous.format ?? tile.format,
        };
        const members = [...loc.group.groupMembers!];
        members[loc.index] = replacement;
        loc.group.groupMembers = members;
        this.dashboardTiles = [...this.dashboardTiles];
        this.editingTileId = replacement.id;
        if (replacement.format) this.onTileFormatChange(replacement);
        return true;
      }
      this.editingTileId = null;
    }
    this.dashboardTiles = [...this.dashboardTiles, tile];
    return false;
  }

  /** Extract the horizontal-bar rows from the response and wrap them as the
   *  shared <app-horizontal-bar-chart>'s `data` input. */
  private buildHbWidgetData(response: Record<string, unknown> | null, tileId: string, format?: Record<string, unknown>): Record<string, unknown> {
    // The rows live at responseList[0].data[0].horizontalBar (sample envelope)
    // OR data[0].horizontalBar (flat compute response) — try both.
    const rows =
      this.extractHorizontalBarRows(response?.['responseList']) ??
      this.extractHorizontalBarRows(response) ??
      [];
    return {
      staticData: rows,
      categoryKey: 'department',
      valueKey: 'value',
      widgetId: 'dvm-tile-hbar-' + tileId,
      title: this.title.trim(),
      height: '100%',
      // Show full (wrapped) category names, center the bar value labels.
      fullYAxisLabels: true,
      yAxisLabelWidth: 150,
      valueLabelPosition: 'inside',
      numberFormat: this.toKpiNumberFormat(format),
      numberScale: this.toNumberScale(format?.['numberFormat']),
      decimals: Number(format?.['decimals'] ?? 1),
      yAxisName: this.hbGroupByLabel || '',
      xAxisName: this.hbValueLabel || 'Value',
      showXAxis: true,
    };
  }

  // ── Dashboard tile lifecycle + free drag/resize ───────────────────────────

  /** Keeps each tile's chart component instance stable across format-popup
   *  edits — without this, replacing the edited tile's object reference in
   *  `dashboardTiles` (onTileFormatChange) makes NgFor's default identity
   *  tracking tear down and recreate that tile's chart on every keystroke. */
  trackTileById(_index: number, tile: DashboardTile): string {
    return tile.id;
  }

  private nextTileId(): string {
    this.tileIdCounter += 1;
    return `tile-${this.tileIdCounter}`;
  }

  private restoreTileId(value: unknown): string {
    const id = String(value ?? '').trim();
    if (!id) return this.nextTileId();
    this.rememberTileId(id);
    return id;
  }

  private rememberTileId(id: string): void {
    const match = /^tile-(\d+)$/.exec(id);
    if (!match) return;
    this.tileIdCounter = Math.max(this.tileIdCounter, Number(match[1]) || 0);
  }

  private readPublishedLayoutItems(layout: Record<string, unknown> | null): Array<Record<string, unknown>> {
    const savedLayout = this.cloneRecord(layout);
    if (!savedLayout) return [];

    const layoutItems = Array.isArray(savedLayout['items']) ? savedLayout['items'] as Array<Record<string, unknown>> : [];
    const breakpoints = savedLayout['breakpoints'] as Record<string, unknown> | undefined;
    const breakpointItems = Array.isArray(breakpoints?.['xl']) ? breakpoints['xl'] as Array<Record<string, unknown>> : [];
    const rawItems = breakpointItems.length ? breakpointItems : layoutItems;
    if (!rawItems.length) return [];

    const componentIdByLayoutId = new Map<string, string>();
    layoutItems.forEach((item) => {
      const id = String(item['id'] ?? '').trim();
      const componentId = String(item['componentId'] ?? '').trim();
      if (id && componentId) componentIdByLayoutId.set(id, componentId);
    });

    return rawItems.map((item) => {
      const id = String(item['id'] ?? '').trim();
      return { ...item, componentId: item['componentId'] ?? componentIdByLayoutId.get(id) ?? '' };
    });
  }

  private applyPublishedLayoutToTiles(tiles: DashboardTile[], layout: Record<string, unknown> | null): DashboardTile[] {
    const rawItems = this.readPublishedLayoutItems(layout);
    if (!rawItems.length) return tiles;

    const tileById = new Map(tiles.map((tile) => [tile.id, tile]));
    const tileByComponentId = new Map(tiles.map((tile) => [tile.componentId, tile]));
    const seen = new Set<string>();
    const ordered: Array<{ order: number; index: number; tile: DashboardTile }> = [];

    rawItems.forEach((item, index) => {
      const id = String(item['id'] ?? '').trim();
      const componentId = String(item['componentId'] ?? '').trim();
      const tile = (id ? tileById.get(id) : undefined) ?? (componentId ? tileByComponentId.get(componentId) : undefined);
      if (!tile || seen.has(tile.id)) return;
      seen.add(tile.id);

      const colSpan = this.toFiniteNumber(item['colSpan']);
      const rowSpan = this.toFiniteNumber(item['rowSpan']);
      const x = this.toFiniteNumber(item['x']);
      const y = this.toFiniteNumber(item['y']);
      const order = this.toFiniteNumber(item['order']);
      // Same minimums as the interactive corner-resize (2 cols / 3 rows): a
      // saved layout with smaller spans would render tiles too small to show
      // their chart/KPI at all.
      const nextColSpan = colSpan === null ? tile.colSpan : this.clamp(Math.round(colSpan), 2, DvmLayoutBuilderComponent.DASH_COLS);
      ordered.push({
        order: order ?? index,
        index,
        tile: {
          ...tile,
          colSpan: nextColSpan,
          rowSpan: rowSpan === null ? tile.rowSpan : Math.max(3, Math.round(rowSpan)),
          x: x === null ? tile.x : this.clamp(Math.round(x), 0, DvmLayoutBuilderComponent.DASH_COLS - nextColSpan),
          y: y === null ? tile.y : Math.max(0, Math.round(y)),
          customSize: item['customSize'] === true || tile.customSize === true,
        },
      });
    });

    ordered.sort((a, b) => a.order - b.order || a.index - b.index);
    return [
      ...ordered.map((item) => item.tile),
      ...tiles.filter((tile) => !seen.has(tile.id)),
    ];
  }



  /** The cross button on a tile — confirms with the user first (this deletes
   *  the tile's persisted `component_definitions` row, not just its spot on the
   *  canvas), then calls `deleteComponentApi` before removing it locally. */
  async removeTile(tile: DashboardTile): Promise<void> {
    const result = await firstValueFrom(this.confirmationDialog.open({
      title: 'Delete chart',
      message: `Do you want to delete "${tile.title || 'this chart'}" permanently? This cannot be undone.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
    }));
    const confirmed = typeof result === 'boolean' ? result : !!result?.confirmed;
    if (!confirmed) return;

    const deleteUrl = (this.layout.apiEndpoints?.deleteComponentApi || '').trim();
    if (deleteUrl && tile.componentId) {
      try {
        const response = await firstValueFrom(this.datasetService.deleteEntityRow({
          entityName: (this.layout.apiEndpoints?.deleteComponentEntityName || 'component_definitions').trim(),
          keyColumn: (this.layout.apiEndpoints?.deleteComponentKeyColumn || 'component_id').trim(),
          keyValue: tile.componentId,
        }));
        if (response?.status && response.status !== 'SUCCESS') {
          this.ui.showError(response.message || 'The server rejected the delete — the chart was not removed.');
          return;
        }
      } catch (error) {
        this.ui.showError(error instanceof Error ? error.message : 'Unable to delete the chart — please try again.');
        return;
      }
    }

    this.dashboardTiles = this.dashboardTiles.filter((item) => item.id !== tile.id);
    if (this.editingTileId === tile.id) this.editingTileId = null;
    if (deleteUrl && tile.componentId) this.ui.showSuccess('Chart deleted.');
  }

  // ── Grouping: club several existing tiles into one titled card ─────────────
  toggleGroupSelectMode(): void {
    this.groupSelectMode = !this.groupSelectMode;
    if (!this.groupSelectMode) this.selectedTileIds = new Set();
  }

  isTileSelected(tile: DashboardTile): boolean {
    return this.selectedTileIds.has(tile.id);
  }

  toggleTileSelected(tile: DashboardTile): void {
    if (tile.layoutType === 'GROUP') return; // v1: no grouping a group
    const next = new Set(this.selectedTileIds);
    if (next.has(tile.id)) next.delete(tile.id); else next.add(tile.id);
    this.selectedTileIds = next;
  }

  get canGroupSelectedTiles(): boolean {
    return this.selectedTileIds.size >= 2;
  }

  /** Club the selected tiles into one card: they leave the flat dashboard grid
   *  and become `groupMembers`, stacked inside the new GROUP tile that takes
   *  their place (at the first selected tile's position). Each member keeps
   *  its own render data untouched — grouping only changes WHERE it renders,
   *  not what it renders. */
  groupSelectedTiles(): void {
    const ids = this.selectedTileIds;
    if (ids.size < 2) return;
    const selected = this.dashboardTiles.filter((t) => ids.has(t.id));
    if (selected.length < 2) return;

    const innerCols = this.clamp(Math.max(...selected.map((t) => t.colSpan)), 2, DvmLayoutBuilderComponent.DASH_COLS);
    const groupTile: DashboardTile = {
      id: `group-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      componentId: this.createComponentId('GRP'),
      layoutType: 'GROUP',
      title: 'Grouped card',
      groupTitle: 'Grouped card',
      colSpan: innerCols,
      rowSpan: selected.reduce((sum, t) => sum + t.rowSpan, 0),
      // Seed the card design controls (border / backgrounds / transparency) from
      // their configured defaults — the Format popup only renders for a tile that
      // HAS a format object.
      format: this.defaultTileFormat('GROUP'),
      payload: {},
      groupMembers: selected.map((t) => ({ ...t })),
    };

    let inserted = false;
    const next: DashboardTile[] = [];
    this.dashboardTiles.forEach((t) => {
      if (!ids.has(t.id)) { next.push(t); return; }
      if (!inserted) { next.push(groupTile); inserted = true; }
    });
    this.dashboardTiles = next;
    this.selectedTileIds = new Set();
    this.groupSelectMode = false;
    if (this.editingTileId && ids.has(this.editingTileId)) this.editingTileId = null;
    this.ui.showSuccess(`Grouped ${selected.length} charts into "${groupTile.title}".`);
  }

  /** Reverse of groupSelectedTiles: the group's members go back to being
   *  independent top-level tiles, in its place, in their original order. */
  ungroupTile(tile: DashboardTile): void {
    if (tile.layoutType !== 'GROUP' || !tile.groupMembers?.length) return;
    const index = this.dashboardTiles.findIndex((t) => t.id === tile.id);
    if (index === -1) return;
    this.dashboardTiles = [
      ...this.dashboardTiles.slice(0, index),
      ...tile.groupMembers,
      ...this.dashboardTiles.slice(index + 1),
    ];
  }

  removeGroupMember(group: DashboardTile, member: DashboardTile): void {
    if (!group.groupMembers) return;
    group.groupMembers = group.groupMembers.filter((m) => m.id !== member.id);
  }

  /** Move a chart within its group card. `groupMembers` order IS the layout —
   *  buildGroupCardWidget walks it in sequence to lay the children out, and the
   *  published card renders them in that same order — so swapping entries is
   *  the whole operation. Stacked: top-to-bottom; 2-column: left-to-right then
   *  wrap, hence "earlier / later" rather than "up / down".
   *  The swap keeps the SAME member objects, so *ngFor moves the existing views
   *  instead of rebuilding them and the charts don't re-initialise. */
  moveGroupMember(group: DashboardTile, member: DashboardTile, direction: -1 | 1, event?: Event): void {
    event?.stopPropagation();
    const members = group.groupMembers;
    if (!members?.length) return;
    const from = members.findIndex((m) => m.id === member.id);
    const to = from + direction;
    if (from === -1 || to < 0 || to >= members.length) return;
    const next = [...members];
    [next[from], next[to]] = [next[to], next[from]];
    group.groupMembers = next;
  }

  /** Card design (Format popup, appliesTo:["GROUP"]) as inline styles: border,
   *  background, corner radius, or fully transparent. Returned as a style map so
   *  the same values can be published verbatim and re-applied by the published
   *  view — see DvmPublishedDashboardComponent.groupCardStyles. */
  groupCardStyles(tile: DashboardTile): Record<string, string> {
    if (tile?.layoutType !== 'GROUP') return {};
    return DvmLayoutBuilderComponent.groupCardStyleMap(tile.format);
  }

  /** Shared translation of a group card's `format` into CSS declarations. Static
   *  so the publish path can reuse it without a component instance. */
  static groupCardStyleMap(format: Record<string, unknown> | undefined): Record<string, string> {
    const styles: Record<string, string> = {};
    if (!format) return styles;
    const transparent = format['cardTransparent'] === true || format['cardTransparent'] === 'true';
    const border = String(format['cardBorderColor'] ?? '').trim();
    const width = Number(format['cardBorderWidth']);
    const background = String(format['cardBackground'] ?? '').trim();
    const radius = Number(format['cardCornerRadius']);

    if (transparent) {
      // Transparent means exactly that: no fill, no border, no shadow — the
      // members float on the dashboard background.
      styles['background'] = 'transparent';
      styles['border-color'] = 'transparent';
      styles['box-shadow'] = 'none';
    } else {
      if (background) styles['background'] = background;
      if (border) styles['border-color'] = border;
    }
    if (Number.isFinite(width)) styles['border-width'] = `${Math.max(0, width)}px`;
    if (Number.isFinite(radius)) styles['border-radius'] = `${Math.max(0, radius)}px`;
    return styles;
  }

  canMoveGroupMember(group: DashboardTile, member: DashboardTile, direction: -1 | 1): boolean {
    const members = group.groupMembers;
    if (!members?.length) return false;
    const index = members.findIndex((m) => m.id === member.id);
    if (index === -1) return false;
    const target = index + direction;
    return target >= 0 && target < members.length;
  }

  /** Cycle a group card's inner layout between 1 (stacked) and 2 side-by-side
   *  columns. Members keep whatever they were rendering — this only changes
   *  how many sit per row inside the card. */
  toggleGroupColumns(group: DashboardTile): void {
    group.groupColumns = (group.groupColumns || 1) >= 2 ? 1 : 2;
  }

  /** Load a generated tile's saved request back into the appropriate builder. */
  editTile(tile: DashboardTile, event: MouseEvent): void {
    event.stopPropagation();
    // Defer the form-fill one tick so the many state fields this sets (incl.
    // tmNavEnabled) don't mutate mid change-detection — that raced the checkbox
    // binding and threw NG0100 (ExpressionChangedAfterItHasBeenChecked), which
    // made the restored Navigation toggle read back as unchecked. Matches the
    // Back-to-Builder auto-load path, which already defers via setTimeout(0).
    setTimeout(() => this.loadTileIntoForm(tile), 0);
  }

  /** Load a tile's config into the left build form (autofill). Used by the tile
   *  pencil (editTile) AND on "Back to Builder" so the form is pre-filled with the
   *  restored chart without needing a manual click. */
  loadTileIntoForm(tile: DashboardTile): void {
    // Loading a real tile for editing — discard any in-progress draft preview.
    this.removePreviewTile();
    const payload = tile.payload || {};
    this.editingTileId = tile.id;
    this.selectedLayoutType = tile.layoutType;
    this.componentId = tile.componentId;
    this.title = String(payload['title'] ?? tile.title ?? '');
    this.generateError = '';
    this.stepsPanelCollapsed = false;
    this.openFormatTileId = null;

    const dataset = String(payload['dataset'] ?? '');
    const datasets = Array.isArray(payload['datasets'])
      ? (payload['datasets'] as unknown[]).map(String)
      : dataset ? [dataset] : [];
    // Older published payloads may not carry the dataset at top level (e.g.
    // margin-estimator dashboards published before `dataset` was emitted) —
    // recover it from layout-specific payload fields before giving up.
    if (!datasets.length) {
      const filters = Array.isArray(payload['filters']) ? (payload['filters'] as Array<Record<string, unknown>>) : [];
      filters.forEach((filter) => {
        const filterDataset = String(filter?.['dataset'] ?? '');
        if (filterDataset && !datasets.includes(filterDataset)) datasets.push(filterDataset);
      });
      const steps = Array.isArray(payload['steps']) ? (payload['steps'] as Array<Record<string, unknown>>) : [];
      steps.forEach((step) => {
        const stepDataset = String(step?.['dataset'] ?? '');
        if (stepDataset && !datasets.includes(stepDataset)) datasets.push(stepDataset);
      });
    }
    // Apply only when the payload actually names dataset(s): a restored tile
    // WITHOUT dataset info must not wipe the current selection — clearing it
    // disabled every dataset-driven picker ("Select a dataset first") and made
    // Back-to-Builder autofill look broken for those tiles.
    if (datasets.length) this.onSelectedDatasetsChange(datasets);

    // Restore this component's interaction targets into the form (and RESET
    // them when the payload has none, so a previous edit's selection can't
    // leak in). The input/dropdown branches below rebuild the same field
    // together with their per-target formulas — same ids, so harmless.
    const payloadTargets = Array.isArray(payload['targets']) ? payload['targets'] as Array<Record<string, unknown>> : [];
    this.selectedTargetCharts = payloadTargets.map((target) => String(target['componentId'] ?? '')).filter(Boolean);
    // The Chart-interactions accordion: show it open when this tile already has
    // targets wired, keep it tucked away otherwise.
    this.interactionsSectionOpen = this.selectedTargetCharts.length > 0;

    if (tile.layoutType === 'WATERFALL') {
      const savedSteps = Array.isArray(payload['steps']) ? payload['steps'] as Array<Record<string, unknown>> : [];
      this.steps = savedSteps.map((step) => ({
        id: this.nextStepId(),
        stepType: String(step['stepType'] ?? 'CHANGE') as BridgeStepType,
        label: String(step['label'] ?? ''),
        dataset: String(step['dataset'] ?? ''),
        field: String(step['field'] ?? ''),
        aggregation: String(step['aggregation'] ?? ''),
        sign: String(step['sign'] ?? 'AUTO') as BridgeSign,
        formula: String(step['formula'] ?? ''),
        formulaMode: !!step['formula'] || String(step['stepType']) === 'TOTAL',
      }));
      this.steps.forEach((step) => { if (step.dataset) this.loadColumnsForDataset(step.dataset); });
      // Restore the Build-form format accordions (Title/Axis/Legend/…) from the
      // tile's format so they come back filled — mirrors the BAR branch below.
      this.barFormat = { ...(tile.format || {}) };
    } else if (tile.layoutType === 'DRILL_DOWN_TABLE') {
      const dimensions = Array.isArray(payload['dimensions']) ? payload['dimensions'] as Array<Record<string, unknown>> : [];
      const measures = Array.isArray(payload['measures']) ? payload['measures'] as Array<Record<string, unknown>> : [];
      // Restore the Build-form Format accordion from the tile's format so it
      // comes back filled — mirrors the BAR / WATERFALL branches.
      this.barFormat = { ...(tile.format || {}) };
      this.drillDownDimensions = dimensions.map((item) => ({
        id: this.nextDrillDimensionId(), field: String(item['field'] ?? ''), label: String(item['label'] ?? ''),
        valueOrder: this.valueOrderToText(item['valueOrder']),
      }));
      this.drillDownMeasures = measures.map((item) => ({
        id: this.nextDrillMeasureId(), field: String(item['field'] ?? ''), label: String(item['label'] ?? ''),
        aggregation: String(item['aggregation'] ?? 'SUM'), rollUpStrategy: String(item['rollUpStrategy'] ?? 'SUM'), total: !!item['total'],
      }));
    } else if (tile.layoutType === 'TABLE_MATRIX') {
      // Restore the table type + its per-type inputs. `subType` was persisted in
      // the payload by buildPayload; fall back to the render data's capabilities.
      const caps = ((tile.tableMatrixData as Record<string, unknown>)?.['capabilities'] as Record<string, unknown>) || {};
      this.tmType = String(payload['subType'] ?? caps['tableType'] ?? 'normal');
      // Restore the Build-form format accordions from the tile's format so they
      // come back filled — mirrors the BAR / WATERFALL branches.
      this.barFormat = { ...(tile.format || {}) };
      // Drill-Down surfaces Title via a 'Title' format accordion (bound to
      // barFormat['title']) — seed it from the restored title so it isn't blank.
      if (this.isTmDrilldown && this.barFormat['title'] == null) this.barFormat['title'] = this.title;
      if (this.isTmDrilldown) {
        const dimensions = Array.isArray(payload['dimensions']) ? payload['dimensions'] as Array<Record<string, unknown>> : [];
        const measures = Array.isArray(payload['measures']) ? payload['measures'] as Array<Record<string, unknown>> : [];
        this.drillDownDimensions = dimensions.map((item) => ({
          id: this.nextDrillDimensionId(), field: String(item['field'] ?? ''), label: String(item['label'] ?? ''),
          valueOrder: this.valueOrderToText(item['valueOrder']),
        }));
        this.drillDownMeasures = measures.map((item) => ({
          id: this.nextDrillMeasureId(), field: String(item['field'] ?? ''), label: String(item['label'] ?? ''),
          aggregation: String(item['aggregation'] ?? 'SUM'), rollUpStrategy: String(item['rollUpStrategy'] ?? 'SUM'), total: !!item['total'],
        }));
      } else {
        this.restoreTmColumns(payload['columns'], payload['initialFilters'] ?? caps['initialFilters']);
        this.tmGroupBy = String(payload['groupBy'] ?? caps['groupBy'] ?? '');
        this.tmAggregation = String(payload['aggregation'] ?? caps['aggregation'] ?? 'none');
        // New shape types restore from the saved capabilities (authoritative for
        // per-type config, which the flat compute payload doesn't carry).
        this.tmRowField = String(caps['rowField'] ?? '');
        this.tmColumnField = String(caps['columnField'] ?? '');
        this.tmValueField = String(caps['valueField'] ?? '');
        this.tmRowFields = Array.isArray(caps['rowFields']) ? (caps['rowFields'] as unknown[]).map(String) : [];
        this.tmColumnFields2 = Array.isArray(caps['columnFields']) ? (caps['columnFields'] as unknown[]).map(String) : [];
        this.tmGroupByFields = Array.isArray(caps['groupByFields']) ? (caps['groupByFields'] as unknown[]).map(String) : [];
        this.tmGroupsExpanded = caps['groupsExpanded'] !== false;
        this.tmTreeIdField = String(caps['treeIdField'] ?? '');
        this.tmTreeParentField = String(caps['treeParentField'] ?? '');
        this.tmRoleField = String(caps['roleField'] ?? '');
        this.tmIndentField = String(caps['indentField'] ?? '');
        // Restore navigation from the payload, falling back to the tile's saved
        // capabilities (published layouts spread capabilities into the widget but
        // may not round-trip the builder payload's `navigation`, so read both).
        const nav = ((payload['navigation'] as Record<string, unknown>)
          || (caps['navigation'] as Record<string, unknown>)
          || {}) as Record<string, unknown>;
        this.tmNavEnabled = nav['enabled'] === true;
        this.tmNavFilterKey = String(nav['filterKey'] ?? '');
        this.tmNavColumns = Array.isArray(nav['navigableColumns']) ? (nav['navigableColumns'] as unknown[]).map(String) : [];
        this.tmNavTargetDashboard = String(nav['targetDashboard'] ?? '');
        this.tmNavTargetComponents = Array.isArray(nav['targetComponents']) ? (nav['targetComponents'] as unknown[]).map(String) : [];
        this.tmNavButtonLabel = String(nav['buttonLabel'] ?? 'Navigate');
        this.tmNavAccordionOpen = this.tmNavEnabled;
        // If the saved target dashboard isn't in the current dropdown list yet
        // (the API list loads async), ensure it appears so the ng-select can
        // render it as selected instead of showing blank — otherwise a
        // re-Generate would silently drop it.
        if (this.tmNavTargetDashboard) this.ensureNavTargetInList(this.tmNavTargetDashboard);
      }
    } else if (tile.layoutType === 'HORIZONTAL_BAR_CHART') {
      const groupBy = (payload['groupBy'] as Record<string, unknown>) || {};
      const value = (payload['value'] as Record<string, unknown>) || {};
      this.hbGroupByField = String(groupBy['field'] ?? '');
      this.hbGroupByLabel = String(groupBy['label'] ?? '');
      this.hbValueLabel = String(value['label'] ?? 'Value');
      this.hbValueFormula = String(value['formula'] ?? '');
      const groupFormulas = (value['groupFormulas'] as Record<string, unknown>) || {};
      this.hbGroupFormulas = Object.keys(groupFormulas).map((group) => ({ group, formula: String(groupFormulas[group] ?? '') }));
      this.rehydrateOwnFormulaReferences(this.componentId, value['references']);
      // Restore the Build-form Format accordion from the tile's format so it
      // comes back filled — mirrors the BAR / WATERFALL branches.
      this.barFormat = { ...(tile.format || {}) };
    } else if (tile.layoutType === 'KPI') {
      this.kpiField = String(payload['field'] ?? '');
      this.kpiAggregation = String(payload['aggregation'] ?? 'SUM');
      // Restore the formula + its typed [Token] references (kaveri-lite-dev
      // parity — a bad merge had moved these into the SCATTER_BUBBLE branch,
      // so editing a KPI blanked its formula and Update then SAVED it blank).
      this.kpiFormula = String(payload['formula'] ?? '');
      this.rehydrateOwnFormulaReferences(this.componentId, payload['references']);
      // Restore the Build-form Format accordion from the tile's format so it
      // comes back filled — mirrors the BAR / WATERFALL branches.
      this.barFormat = { ...(tile.format || {}) };
    } else if (tile.layoutType === 'BAR') {
      // Rehydrate the whole Bar build form from the tile's capabilities. Prefer
      // the payload (builder-saved), fall back to barData.capabilities (which is
      // the copy that survives publish → so "Back to Builder" re-fills too).
      const caps = (payload['capabilities'] as Record<string, unknown> | undefined)
        ?? ((tile.barData?.['capabilities'] as Record<string, unknown> | undefined))
        ?? {};
      this.barOrientation = (String(caps['orientation'] ?? 'vertical') as BarColumnOrientation);
      this.barVariant = (String(caps['variant'] ?? 'simple') as BarColumnVariant);
      this.barCategory = String(caps['category'] ?? '');
      this.barCategoryParent = String(caps['categoryParent'] ?? '');
      this.barCategoryLabel = String(caps['categoryLabel'] ?? '');
      this.barSeries = String(caps['series'] ?? '');
      const barValue = (payload['value'] as Record<string, unknown> | undefined) ?? {};
      this.barValueLabel = String(barValue['label'] ?? '');
      const savedBarFormula = String(barValue['formula'] ?? '');
      // A bare SUM(col) is our auto-generated fallback, not user input — don't
      // restore it, or it outranks the measure on the next save.
      this.barValueFormula = /^SUM\([^()]*\)$/i.test(savedBarFormula.trim()) ? '' : savedBarFormula;
      const barSavedGroupFormulas = (barValue['groupFormulas'] as Record<string, unknown>) || {};
      this.barGroupFormulas = Object.keys(barSavedGroupFormulas)
        .map((group) => ({ group, formula: String(barSavedGroupFormulas[group] ?? '') }));
      this.rehydrateOwnFormulaReferences(this.componentId, barValue['references']);
      const savedMeasures = Array.isArray(caps['measures']) ? caps['measures'] as Array<Record<string, unknown>> : [];
      this.barMeasures = savedMeasures.length
        ? savedMeasures.map((m) => ({
            id: this.nextBarMeasureId(),
            field: String(m['field'] ?? ''),
            label: String(m['label'] ?? ''),
            renderAs: (String(m['renderAs'] ?? 'bar') as 'bar' | 'line' | 'area'),
            axis: (String(m['axis'] ?? 'y1') as 'y1' | 'y2'),
          }))
        : [];
      // Split a dual-axis measure back onto the Secondary picker so it round-trips.
      this.barSecondaryXField = '';
      this.barSecondaryYField = '';
      const secondaryMeasure = this.barMeasures.find((m) => m.axis === 'y2');
      if (secondaryMeasure) {
        if (this.barOrientation === 'horizontal') this.barSecondaryXField = secondaryMeasure.field;
        else this.barSecondaryYField = secondaryMeasure.field;
      }
      const savedRefLines = Array.isArray(caps['referenceLines']) ? caps['referenceLines'] as Array<Record<string, unknown>> : [];
      this.barReferenceLines = savedRefLines.map((line) => {
        this.barRefLineIdCounter += 1;
        return {
          id: `bar-refline-${this.barRefLineIdCounter}`,
          value: line['value'] == null ? null : Number(line['value']),
          label: String(line['label'] ?? ''),
          color: String(line['color'] ?? '#64748b'),
        };
      });
      // Restore the Formula-Builder computed columns.
      const savedComputed = Array.isArray(caps['computedColumns']) ? caps['computedColumns'] as Array<Record<string, unknown>> : [];
      this.barComputedColumns = savedComputed
        .map((c) => ({ name: String(c['name'] ?? ''), expression: String(c['expression'] ?? '') }))
        .filter((c) => c.name && c.expression);
      // Restore the Format-form values (Formatting/Tooltip/Interaction/Advanced)
      // from the tile's format so the accordions come back filled.
      this.barFormat = { ...(tile.format || {}) };
      if (this.drillDataset) this.loadColumnsForDataset(this.drillDataset);
    } else if (tile.layoutType === 'LINE_AREA') {
      const caps = (payload['capabilities'] as Record<string, unknown> | undefined)
        ?? ((tile.lineAreaData?.['capabilities'] as Record<string, unknown> | undefined))
        ?? {};
      this.laVariant = (String(caps['variant'] ?? 'line') as LineAreaVariant);
      this.laCategory = String(caps['category'] ?? '');
      this.laCategoryParent = String(caps['categoryParent'] ?? '');
      this.laCategoryLabel = String(caps['categoryLabel'] ?? '');
      this.laSeries = String(caps['series'] ?? '');
      const savedMeasures = Array.isArray(caps['measures']) ? caps['measures'] as Array<Record<string, unknown>> : [];
      this.laMeasures = savedMeasures.length
        ? savedMeasures.map((m) => ({
            id: this.nextLaMeasureId(),
            field: String(m['field'] ?? ''),
            label: String(m['label'] ?? ''),
            renderAs: (String(m['renderAs'] ?? 'line') as 'line' | 'area'),
            axis: (String(m['axis'] ?? 'y1') as 'y1' | 'y2'),
          }))
        : [];
      // Split a dual-axis measure back onto the Secondary-Y picker so it round-trips.
      this.laSecondaryYField = '';
      const secondaryMeasure = this.laMeasures.find((m) => m.axis === 'y2');
      if (secondaryMeasure) this.laSecondaryYField = secondaryMeasure.field;
      this.laSecondaryXField = String(caps['secondaryXField'] ?? '');
      this.laSecondaryXValueField = String(caps['secondaryXValueField'] ?? '');
      const savedRefLines = Array.isArray(caps['referenceLines']) ? caps['referenceLines'] as Array<Record<string, unknown>> : [];
      this.laReferenceLines = savedRefLines.map((line) => {
        this.laRefLineIdCounter += 1;
        return {
          id: `la-refline-${this.laRefLineIdCounter}`,
          value: line['value'] == null ? null : Number(line['value']),
          label: String(line['label'] ?? ''),
          color: String(line['color'] ?? '#64748b'),
        };
      });
      this.barFormat = { ...(tile.format || {}) };
      if (this.drillDataset) this.loadColumnsForDataset(this.drillDataset);
    } else if (tile.layoutType === 'SCATTER_BUBBLE') {
      // Rehydrate the Scatter/Bubble build form from the tile's capabilities.
      const caps = (payload['capabilities'] as Record<string, unknown> | undefined)
        ?? ((tile.scatterBubbleData?.['capabilities'] as Record<string, unknown> | undefined))
        ?? {};
      const savedVariant = String(caps['variant'] ?? 'scatter');
      const isBaseVariant = this.scatterBubbleVariants.some(
        (v) => v.value === savedVariant && !this.sbVariantIsAdvanced(v),
      );
      if (isBaseVariant) {
        this.sbVariant = savedVariant as ScatterBubbleVariant;
        this.sbAdvanced = String(caps['advanced'] ?? '');
      } else {
        // Legacy tile: an advanced pick was saved AS the variant — split it into
        // base 'scatter' + overlay so the form (and re-save) use the new model.
        this.sbVariant = 'scatter';
        this.sbAdvanced = savedVariant;
      }
      this.sbMode = this.sbModeForVariant(this.sbVariant);
      // Surface a saved overlay: reopen the Advanced accordion; else collapsed.
      this.sbAdvancedAccordionOpen = !!this.sbAdvanced;
      this.sbXField = String(caps['xField'] ?? '');
      this.sbYField = String(caps['yField'] ?? '');
      this.sbSizeField = String(caps['sizeField'] ?? '');
      this.sbColorField = String(caps['colorField'] ?? '');
      this.sbSeries = String(caps['series'] ?? '');
      this.sbAggregation = String(caps['aggregation'] ?? 'none');
      this.sbTooltipColumns = Array.isArray(caps['tooltipColumns']) ? (caps['tooltipColumns'] as string[]).map(String) : [];
      this.sbXLabel = String(caps['xLabel'] ?? '');
      this.sbYLabel = String(caps['yLabel'] ?? '');
      this.sbFormat = { ...(tile.format || {}) };
      if (this.drillDataset) this.loadColumnsForDataset(this.drillDataset);
    } else if (tile.layoutType === 'DROPDOWN') {
      this.dropdownField = String(payload['field'] ?? '');
      this.dropdownDisplayLabel = String(payload['displayLabel'] ?? '');
      this.dropdownIsMultiSelect = !!payload['isMultiSelect'];
      this.dropdownDisplayMode = String(payload['displayMode'] ?? 'dropdown');
      const rawDefault = payload['defaultValue'];
      this.dropdownDefaultValue = Array.isArray(rawDefault) ? rawDefault.map(String).join(', ') : String(rawDefault ?? '');
      const targets = Array.isArray(payload['targets']) ? payload['targets'] as Array<Record<string, unknown>> : [];
      this.selectedTargetCharts = targets.map((target) => String(target['componentId'] ?? '')).filter(Boolean);
      this.rehydrateOwnFormulaReferences(this.componentId, payload['references']);
    } else if (tile.layoutType === 'FILE_UPLOAD') {
      this.fileUploadButtonLabel = String(payload['buttonLabel'] ?? 'Upload file');
      this.fileUploadAccept = String(payload['accept'] ?? '.xlsx,.xls,.csv');
      const targets = Array.isArray(payload['targets']) ? payload['targets'] as Array<Record<string, unknown>> : [];
      this.selectedTargetCharts = targets.map((target) => String(target['componentId'] ?? '')).filter(Boolean);
    } else if (tile.layoutType === 'INPUT_BOX' || tile.layoutType === 'SLIDER') {
      this.inputDefaultValue = payload['defaultValue'] == null ? null : Number(payload['defaultValue']);
      const targets = Array.isArray(payload['targets']) ? payload['targets'] as Array<Record<string, unknown>> : [];
      this.selectedTargetCharts = targets.map((target) => String(target['componentId'] ?? '')).filter(Boolean);
      // Rebuild per-target formulas from each target's `formulas` array (one
      // entry per bar for a waterfall target, one entry overall otherwise).
      // Falls back to the legacy single top-level `formula` string (older
      // dashboards saved before the `formulas` array existed).
      const legacyFormula = String(payload['formula'] ?? '');
      this.targetFormulas = {};
      this.targetStepFormulas = {};
      this.targetFormulaReferences = {};
      targets.forEach((target) => {
        const id = String(target['componentId'] ?? '');
        if (!id) return;
        const entries = Array.isArray(target['formulas']) ? target['formulas'] as Array<Record<string, unknown>> : null;
        if (entries) {
          const withStep = entries.filter((entry) => !!entry['step']);
          if (withStep.length) {
            const perStep: Record<string, string> = {};
            withStep.forEach((entry) => { perStep[String(entry['step'])] = String(entry['formula'] ?? ''); });
            this.targetStepFormulas[id] = perStep;
          } else {
            this.targetFormulas[id] = String(entries[0]?.['formula'] ?? '');
          }
          // Rehydrate every entry's `references` map back into typed-in rows —
          // one row per token, scoped back to whichever bar/step it came from
          // (undefined for a non-waterfall target's single formula entry).
          const referenceRows: TargetFormulaReferenceEntry[] = [];
          entries.forEach((entry) => {
            const references = entry['references'] as Record<string, Record<string, unknown>> | undefined;
            if (!references || typeof references !== 'object') return;
            const entryStep = entry['step'] ? String(entry['step']) : undefined;
            Object.keys(references).forEach((token) => {
              const ref = references[token] || {};
              referenceRows.push({
                token,
                inputTitle: String(ref['inputTitle'] ?? ''),
                targetLabel: String(ref['targetLabel'] ?? ''),
                rowLabel: String(ref['rowLabel'] ?? ''),
                step: entryStep,
              });
            });
          });
          if (referenceRows.length) this.targetFormulaReferences[id] = referenceRows;
        } else {
          // Legacy shapes: a per-target `formula` string, a `stepFormulas` map,
          // or (oldest) just the input's single top-level `formula`.
          const legacyStepFormulas = target['stepFormulas'] as Record<string, string> | undefined;
          if (legacyStepFormulas && typeof legacyStepFormulas === 'object') {
            this.targetStepFormulas[id] = { ...legacyStepFormulas };
          } else {
            this.targetFormulas[id] = String(target['formula'] ?? legacyFormula);
          }
        }
      });
      const range = (payload['range'] as Record<string, unknown>) || {};
      this.sliderMin = Number(range['min'] ?? 0);
      this.sliderMax = Number(range['max'] ?? 100);
      this.sliderStep = Number(range['step'] ?? 1);
    }

    this.onLayoutTypeChange();
  }

  /** Reset the builder form for the next chart (keeps datasets + layout type so
   *  several charts can be built from the same data). */
  /** Clear the ENTIRE dashboard — remove every tile, reset the name and the
   *  active saved-dashboard link (so the next Save creates a fresh record), and
   *  reset the chart form. Confirms first when there are unsaved tiles to lose. */
  startNewDashboard(): void {
    if (
      this.dashboardTiles.length &&
      !window.confirm('Start a new dashboard? This clears all charts and the dashboard name.')
    ) {
      return;
    }
    this.dashboardTiles = [];
    this.dashboardName = '';
    this.activeSavedDashboardId = '';
    this.restoredDashboardId = '';
    this.restoredPublishedLayout = null;
    this.openFormatTileId = null;
    this.previewEditLayout = false;
    this.startNewChart();
    this.ui.showSuccess('Started a new dashboard.');
  }

  startNewChart(): void {
    // Discard any un-generated draft preview tile (after Generate this is already
    // null, so it only removes a still-in-progress preview).
    this.removePreviewTile();
    this.editingTileId = null;
    this.title = '';
    this.componentId = this.createComponentId();
    this.steps = [];
    this.interactionsSectionOpen = false;
    this.drillDownDimensions = [];
    this.drillDownMeasures = [];
    this.tmType = 'normal';
    this.tmColumns = [];
    this.tmGroupBy = '';
    this.tmAggregation = 'none';
    this.tmRowField = '';
    this.tmColumnField = '';
    this.tmValueField = '';
    this.tmRowFields = [];
    this.tmColumnFields2 = [];
    this.tmGroupByFields = [];
    this.tmGroupsExpanded = true;
    this.tmTreeIdField = '';
    this.tmTreeParentField = '';
    this.tmRoleField = '';
    this.tmIndentField = '';
    this.tableMatrixWidgetData = null;
    this.tmNavEnabled = false;
    this.tmNavFilterKey = '';
    this.tmNavColumns = [];
    this.tmNavTargetDashboard = '';
    this.tmNavTargetComponents = [];
    this.tmNavButtonLabel = 'Navigate';
    this.tmNavAccordionOpen = false;
    this.navPinnedTarget = null;
    this.navDashboardsMemo = null;
    this.hbGroupByField = '';
    this.hbGroupByLabel = '';
    this.hbValueLabel = 'Value';
    this.hbValueFormula = '';
    this.hbGroupFormulas = [];
    this.kpiField = '';
    this.kpiAggregation = 'SUM';
    this.barCategory = '';
    this.barCategoryParent = '';
    this.barCategoryLabel = '';
    this.barSeries = '';
    this.barValueFormula = '';
    this.barValueLabel = '';
    this.barGroupFormulas = [];
    this.barSecondaryXField = '';
    this.barSecondaryYField = '';
    this.barMeasures = [];
    this.barReferenceLines = [];
    this.barComputedColumns = [];
    this.newBarColumnName = '';
    // Line & Area reset (reuses barFormat, already cleared above).
    this.laVariant = 'line';
    this.laCategory = '';
    this.laCategoryParent = '';
    this.laCategoryLabel = '';
    this.laSeries = '';
    this.laSecondaryYField = '';
    this.laSecondaryXField = '';
    this.laSecondaryXValueField = '';
    this.laMeasures = [];
    this.laReferenceLines = [];
    this.newBarColumnExpression = '';
    this.newBarColumnError = '';
    this.barFormat = {};
    // Scatter & Bubble reset — defaults come from config: first mode entry,
    // then that side's first base variant.
    this.sbMode = this.sbDefaultMode;
    this.sbAdvanced = '';
    this.sbAdvancedAccordionOpen = false;
    this.sbVariant = (this.sbVariantItems[0]?.value ?? 'scatter') as ScatterBubbleVariant;
    this.sbXField = '';
    this.sbYField = '';
    this.sbSizeField = '';
    this.sbColorField = '';
    this.sbSeries = '';
    this.sbAggregation = 'none';
    this.sbTooltipColumns = [];
    this.sbXLabel = '';
    this.sbYLabel = '';
    this.sbFormat = {};
    if (this.isBarLayout) this.addBarMeasure();
    if (this.isLineAreaLayout) this.addLaMeasure();
    if (this.isBarLayout || this.isWaterfallLayout || this.isLineAreaLayout) this.seedBarFormatDefaults();
    if (this.isScatterBubbleLayout) this.seedScatterBubbleFormatDefaults();
    this.kpiFormula = '';
    this.dropdownField = '';
    this.dropdownDisplayLabel = '';
    this.dropdownIsMultiSelect = false;
    this.dropdownDefaultValue = '';
    this.dropdownDisplayMode = 'dropdown';
    this.fileUploadButtonLabel = 'Upload file';
    this.fileUploadAccept = '.xlsx,.xls,.csv';
    this.inputFormula = '';
    this.clearInputTargetState();
    this.inputDefaultValue = null;
    this.generateError = '';
  }

  // ── Reorder (HTML5 drag between tiles) ────────────────────────────────────

  onTileReorderStart(index: number, event: DragEvent): void {
    this.tileDragIndex = index;
    event.dataTransfer?.setData(TILE_DRAG_TYPE, String(index));
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
  }

  onTileReorderOver(event: DragEvent): void {
    if (this.tileDragIndex === null) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
  }

  onTileReorderDrop(targetIndex: number, event: DragEvent): void {
    event.preventDefault();
    const from = this.tileDragIndex;
    this.tileDragIndex = null;
    if (from === null || from === targetIndex) return;
    const next = [...this.dashboardTiles];
    const [moved] = next.splice(from, 1);
    next.splice(targetIndex, 0, moved);
    this.dashboardTiles = next;
    this.reflowCharts();
  }

  onTileReorderEnd(): void {
    this.tileDragIndex = null;
  }

  // ── Corner resize (grid-snapped) ──────────────────────────────────────────

  startTileResize(tile: DashboardTile, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.activeTileInteraction = {
      tile,
      startX: event.clientX,
      startY: event.clientY,
      startColSpan: tile.colSpan,
      startRowSpan: tile.rowSpan,
    };
  }

  toggleDashboardFullscreen(): void {
    this.isDashboardFullscreen = !this.isDashboardFullscreen;
    // Leaving preview always drops back to the clean (non-edit) view.
    if (!this.isDashboardFullscreen) this.previewEditLayout = false;
    this.reflowCharts();
  }

  // ── Save / publish (same stores the dashboard builder uses) ─

  isPublishing = false;

  get canSaveDashboard(): boolean {
    return !this.isSavingDashboard && !this.isPublishing && this.dashboardTiles.length > 0;
  }

  get canPublish(): boolean {
    // Only needs at least one chart — the name falls back to "Untitled dashboard"
    // (the header field is empty until typed; its text is just a placeholder).
    return !this.isPublishing && this.dashboardTiles.length > 0;
  }

  get publishButtonLabel(): string {
    if (this.isPublishing) return this.isExistingPublishedDashboard ? 'Updating…' : 'Publishing…';
    return this.isExistingPublishedDashboard ? 'Update' : 'Publish';
  }

  async saveDashboard(): Promise<void> {
    if (!this.canSaveDashboard) {
      this.ui.showError('Add at least one chart before saving the dashboard.');
      return;
    }

    this.isSavingDashboard = true;
    try {
      await this.saveDashboardInternal('draft');
    } finally {
      this.isSavingDashboard = false;
    }
  }

  private async saveDashboardInternal(status: 'draft' | 'published'): Promise<SavedSelfServiceDashboard | null> {
    const now = new Date().toISOString();
    const dashboardName = this.dashboardName.trim() || 'Untitled dashboard';
    const config = this.buildDashboardLayoutJson(status);
    const savedDashboards = this.persistence.consolidateSavedDashboards(
      readJson<SavedSelfServiceDashboard[]>(DvmLayoutBuilderComponent.SAVED_DASHBOARDS_STORAGE_KEY, []),
    );
    const normalizedName = this.normalizeDashboardName(dashboardName);
    const existing = savedDashboards.find((dashboard) =>
      dashboard.id === this.activeSavedDashboardId
      || (this.restoredDashboardId && dashboard.config?.['dashboardId'] === this.restoredDashboardId)
      || this.normalizeDashboardName(dashboard.name) === normalizedName,
    );

    // Resolved once, up front, so both the server-side existing-dashboard
    // lookup below AND the actual publish call use the SAME email.
    let emailId = '';
    try {
      if (await this.keycloak.isLoggedIn()) {
        emailId = (await this.keycloak.loadUserProfile()).email || '';
      }
    } catch {
      // Not logged in / Keycloak unavailable — publish anonymously.
    }

    // Neither the route restore NOR the local cache told us this dashboard
    // already has a backend row (e.g. reopening the builder fresh after time
    // has passed, on another browser, or once localStorage has been cleared —
    // `activeSavedDashboardId`/`restoredDashboardId` are empty and the local
    // `savedDashboards` cache no longer has a matching entry). Without this,
    // the code below would mint a BRAND NEW dashboard_id from the name and
    // search `dynamic_filter` for that instead of the dashboard's real,
    // already-published id — which never matches, so publish silently inserts
    // a duplicate row even though the dashboard is plainly sitting in that
    // same dynamic_filter response under its real id. Ask the backend by name
    // (same dynamic_filter query the sidenav/list already use) before falling
    // back to minting anything.
    let resolvedDashboardId = this.restoredDashboardId || (existing?.config?.['dashboardId'] as string | undefined) || '';
    if (!resolvedDashboardId && emailId) {
      try {
        const owned = await firstValueFrom(this.dashboardService.fetchOwnedDashboardsFromApi(emailId));
        const match = owned.find((o) =>
          this.normalizeDashboardName(String(o.layoutJson?.['dashboard-name'] ?? o.layoutJson?.['name'] ?? '')) === normalizedName,
        );
        // `OwnedDashboardLayout.name` IS the backend's `dashboard_id` (see
        // fetchOwnedDashboardsFromApi) — not the display name.
        if (match) resolvedDashboardId = match.name;
      } catch {
        // Best-effort — fall through to minting a dashboardId as before.
      }
    }
    if (resolvedDashboardId) this.restoredDashboardId = resolvedDashboardId;
    this.isExistingPublishedDashboard = !!existing || !!resolvedDashboardId;

    // The publicly-facing dashboard_id/URL slug used to be JUST the slugified
    // name — two dashboards named the same collided (same backend record, same
    // URL). Append a short suffix derived from this dashboard's OWN stable
    // internal id (reused across saves via `existing.id`, so the suffix never
    // changes once assigned) so same-named dashboards get distinct identities.
    const stableId = existing?.id || this.createId();
    // Reuse the slug this dashboard was already published under whenever we
    // have one (route restore, local cache, or the backend name lookup above)
    // — recomputing from name+stableId here would mint a different slug,
    // silently forking a new backend row instead of updating the one being edited.
    const dashboardId = resolvedDashboardId || this.buildDashboardId(dashboardName, stableId);
    (config as Record<string, unknown>)['dashboardId'] = dashboardId;

    const savedDashboard: SavedSelfServiceDashboard = {
      id: stableId,
      name: dashboardName,
      createdAt: existing?.createdAt || now,
      updatedAt: now,
      items: [],
      layout: (config['layout'] as SavedSelfServiceDashboard['layout']) ?? { columns: DvmLayoutBuilderComponent.DASH_COLS },
      status,
      publishedAt: status === 'published' ? now : existing?.publishedAt,
      config,
    };

    try {
      // Save-dashboard-draft API call intentionally disabled — only publishApi
      // is called now. `savedDashboard` is already the full record; use it
      // directly instead of round-tripping through that endpoint.
      const persistedDashboard = savedDashboard;

      // Only publishApi persists the dashboard now — lands it in the shared
      // `dashboard_layouts` store, matching how DvmPublishedDashboardComponent.
      // saveLayout() republishes on every edit.
      //
      // This call used to be swallowed (catch → console.warn only), so on any
      // backend failure (expired token, network blip, CORS, 500…) the UI still
      // showed "Dashboard saved" even though nothing actually landed on the
      // server that time — the dashboard only looked saved because the LOCAL
      // cache below still got updated. Track success and surface a real error
      // instead of a false-positive toast.
      let publishSucceeded = false;
      try {
        const updatedExisting = await this.publishLayoutJsonToApi(config, emailId, dashboardId);
        this.isExistingPublishedDashboard = this.isExistingPublishedDashboard || updatedExisting;
        publishSucceeded = true;
      } catch (publishError) {
        console.warn('[dvm-layout-builder] saveDashboardInternal: publishApi sync failed:', publishError);
      }

      const nextDashboards = existing
        ? [
            persistedDashboard,
            ...savedDashboards.filter((dashboard) => (
              dashboard.id !== existing.id
              && this.normalizeDashboardName(dashboard.name) !== normalizedName
            )),
          ]
        : [persistedDashboard, ...savedDashboards];

      // The backend call above is the durable save — it already succeeded once we
      // get here. persistSavedDashboards only refreshes the local list cache (used
      // to populate this builder's saved-dashboard picker without a round trip);
      // on a full browser storage quota it can fail even though the dashboard is
      // safely stored server-side. Treat that as a soft/local-only miss, not a
      // save failure — surfacing "Unable to save... too large for browser
      // storage" here was misleading users into thinking the save itself failed.
      const cached = this.persistence.persistSavedDashboards(nextDashboards, { silent: true });
      this.activeSavedDashboardId = persistedDashboard.id;
      if (publishSucceeded) {
        this.ui.showSuccess(status === 'published' ? 'Dashboard saved and ready to publish.' : existing ? 'Dashboard updated.' : 'Dashboard saved.');
      } else {
        // The server call failed — this dashboard is NOT durably saved, even
        // though it's cached locally in this browser. Say so explicitly
        // instead of a misleading success toast.
        this.ui.showError('Saved locally, but the server could not be reached — this dashboard is not saved yet. Try again once you\'re back online.');
      }
      if (!cached) {
        console.warn('[dvm-layout-builder] Dashboard saved to the server, but the local saved-dashboards cache is full and was not updated.');
      }
      return persistedDashboard;
    } catch (error) {
      this.ui.showError(error instanceof Error ? error.message : 'Unable to save the dashboard.');
      return null;
    }
  }

  private normalizeDashboardName(name: string): string {
    return String(name || '').trim().toLowerCase();
  }

  /** Name → URL slug, byte-for-byte identical to the sidenav's `nameToSlug` and
   *  the dashboard service's `slugify`, so the redirect target matches the menu
   *  link and `getPublishedLayoutBySlug` resolves the same record. */
  private dashboardSlug(name: string): string {
    return String(name || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  /** Unique id for a saved dashboard record (mirrors the dashboard builder's
   *  createId — used when persisting a brand-new dashboard). */
  private createId(): string {
    return `dvm-dashboard-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }

  /** The public dashboard_id / URL slug: the name's slug plus a short suffix
   *  from this dashboard's stable internal `id` (reused across saves), so two
   *  dashboards sharing a name no longer collide into the same backend record
   *  / published URL. `stableId` looks like `dvm-dashboard-<ts>-<rand>` — the
   *  trailing random segment (after the last '-') IS the uniqueness we want. */
  private buildDashboardId(name: string, stableId: string): string {
    const namePart = this.dashboardSlug(name) || 'untitled-dashboard';
    const suffix = stableId.split('-').pop() || stableId;
    return `${namePart}-${suffix}`;
  }

  /** Best-effort human-readable detail from a failed HTTP call — status +
   *  whatever message the backend body/status text carried — so publish
   *  failures point at the actual cause instead of a generic string. */
  private extractHttpErrorDetail(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const bodyMessage =
        (typeof error.error === 'string' && error.error) ||
        error.error?.message ||
        error.error?.detail ||
        error.error?.error ||
        '';
      return [error.status ? `${error.status}` : '', bodyMessage || error.statusText || error.message]
        .filter(Boolean)
        .join(' ');
    }
    return error instanceof Error ? error.message : '';
  }

  /** Publish the whole dashboard so it lands in the shared `dashboard_layouts`
   *  store and appears in the left-side saved-dashboards menu — mirrors
   *  DashboardBuilderComponent.publishDashboard(). Uses the config's `publishApi`
   *  when set, else the shared SelfServiceDashboardService insert. */
  async publishDashboard(): Promise<void> {
    if (!this.canPublish) {
      this.ui.showError('Add at least one chart and name the dashboard before publishing.');
      return;
    }
    this.isPublishing = true;
    try {
      // saveDashboardInternal already syncs to publishApi itself (see there) —
      // no need to publish a second time here.
      const savedDashboard = await this.saveDashboardInternal('published');
      if (!savedDashboard) return;

      const layoutJson = savedDashboard.config;
      // saveDashboardInternal computed the real (unique, suffixed) dashboard_id
      // and stamped it onto config — read it back instead of recomputing from
      // the raw name, so the redirect slug matches what was actually published.
      const dashboardId = String(layoutJson['dashboardId'] ?? this.dashboardName.trim() ?? 'Untitled dashboard');
      // The sidenav listens for this to refresh the saved-dashboards list. Carry
      // the just-published dashboard's id + name in the event so the sidebar can
      // add it OPTIMISTICALLY — the API re-query it also runs can race the
      // backend's indexing of the brand-new row (see the cache-warm note below),
      // which would otherwise leave the new dashboard missing until a reload.
      window.dispatchEvent(new CustomEvent('dvm:dashboard-published', {
        detail: { dashboardId, name: String(layoutJson['dashboard-name'] ?? layoutJson['name'] ?? this.dashboardName.trim() ?? dashboardId) },
      }));
      this.ui.showSuccess('Dashboard published — opening it now.');
      // Warm the dynamic-dashboard cache with the config we just published so the
      // published view paints it INSTANTLY on redirect — otherwise it depended on
      // the API/sidenav having indexed the brand-new record, which isn't
      // guaranteed the moment after insert (that gap showed an empty dashboard).
      const slug = this.dashboardSlug(dashboardId);
      writeDynamicDashboardCache(slug, layoutJson);
      // Redirect to the published dashboard view (the API-backed dynamic route,
      // resolved by name-slug — the same URL the sidenav's menu item points at).
      this.router.navigate(['/dvm-published', slug]);
    } catch (error) {
      // Surface the real backend error instead of a blanket message — publishUrl
      // (when configured) is posted to directly, with no catchError swallow, so
      // whatever the server actually returned (404/400/500 + body) is what threw.
      console.error('[dvm-layout-builder] publishDashboard failed:', error);
      const detail = this.extractHttpErrorDetail(error);
      this.ui.showError(detail ? `Unable to publish the dashboard: ${detail}` : 'Unable to publish the dashboard.');
    } finally {
      this.isPublishing = false;
    }
  }

  /** POST a dashboard's layout_json to `apiEndpoints.publishApi` (or the shared
   *  SelfServiceDashboardService insert when that's not configured) — used by
   *  saveDashboardInternal so EVERY save (draft or published) syncs to the
   *  shared `dashboard_layouts` store, not just an explicit Publish click.
   *  Returns true when an existing owned row was found (and thus `update`d)
   *  instead of inserted — callers use this to drive the Publish/Update label. */
  private async publishLayoutJsonToApi(layoutJson: Record<string, unknown>, emailId: string, dashboardId: string): Promise<boolean> {
    // This email already owns a `dashboard_layouts` row for this dashboard →
    // update that row instead of inserting a duplicate one (see
    // SelfServiceDashboardService.findOwnedDashboardLayoutRecord — publish was
    // always `insert`, so re-publishing kept appending new rows for the same
    // dashboard).
    const existingRecord = emailId
      ? await firstValueFrom(this.dashboardService.findOwnedDashboardLayoutRecord(dashboardId, emailId))
      : null;
    // .trim() so a whitespace-only config value (e.g. `"publishApi": " "`, as
    // shipped in dvm-layout-builder.json) is treated as unconfigured — a bare
    // space is truthy in JS and was wrongly routing every publish through the
    // direct-http.post branch below, which (unlike the shared service call)
    // has no catchError and threw on any failure instead of degrading safely.
    const configuredPublishApi = (this.layout.apiEndpoints?.publishApi || '').trim();
    const publishUrl = configuredPublishApi ? resolveSelfServiceApiUrl(configuredPublishApi) : '';
    const publish$ = publishUrl
      ? this.http.post(publishUrl, this.buildPublishEnvelope(layoutJson, emailId, dashboardId, existingRecord?.uuid ?? null))
      : this.dashboardService.publishLayoutToApi(layoutJson, emailId, dashboardId);
    await firstValueFrom(publish$);
    return !!existingRecord;
  }

  /** Insert envelope matching SelfServiceDashboardService.publishLayoutToApi, for
   *  when a custom `publishApi` URL is configured. `uuid` present → this
   *  dashboard already has an owned row, so the operation becomes `update`. */
  private buildPublishEnvelope(layoutJson: Record<string, unknown>, emailId: string, dashboardId: string, uuid: string | null = null): Record<string, unknown> {
    return {
      operation: uuid ? 'update' : 'insert',
      requestData: {
        email_id: emailId,
        role: 'User',
        
        layout_json: layoutJson,
        dashboard_id: dashboardId,
        ...(uuid ? { uuid } : {}),
      },
      responseList: [],
      entity: 'dashboard_layouts',
      batchId: 'test',
      workflowId: 'ABC-123',
      endpoints: [],
      version: '1.0',
    };
  }

  /** Build the published `layout_json` from the dashboard tiles — the same
   *  envelope shape the dashboard builder emits (dashboard-name / layout /
   *  status / pageContent rows of widgets), so the saved-dashboards list and
   *  published view can consume it. */
  private buildDashboardLayoutJson(status: 'draft' | 'published' = 'published'): Record<string, unknown> {
    const columns = DvmLayoutBuilderComponent.DASH_COLS;
    // Map each DVM layout type to the renderer type whose component consumes the
    // SAME `data` shape we build for the preview tile — so the published view
    // renders identically. DRILL_DOWN_TABLE → 'drill-down' (DrillDownComponent,
    // the pre-built headers/staticData shape), NOT 'generic-drill-down' (which
    // self-fetches + builds its own tree from raw rows).
    const typeMap: Record<string, string> = {
      WATERFALL: 'waterfall-chart',
      DRILL_DOWN_TABLE: 'drill-down',
      HORIZONTAL_BAR_CHART: 'horizontal-bar-chart',
      KPI: 'kpi-card',
      BAR: 'bar-column',
      SCATTER_BUBBLE: 'scatter-bubble',
      LINE_AREA: 'line-area',
      SLIDER: 'slider',
      INPUT_BOX: 'input-box',
      DROPDOWN: 'dropdown',
      TABLE_MATRIX: 'table-matrix',
      FILE_UPLOAD: 'file-upload',
    };

    const layoutItems = this.buildPublishedLayoutItems();
    const layoutById = new Map(layoutItems.map((item) => [item.id, item]));

    // Pack tiles into rows that sum to <= columns (same as the builder's
    // buildPageContent), so the published grid mirrors the preview layout.
    const pageContent: Record<string, unknown>[] = [];
    let buffer: Record<string, unknown>[] = [];
    let used = 0;
    this.dashboardTiles.forEach((tile, index) => {
      // Skip the un-generated draft preview tile — it's not a real saved chart
      // until the user clicks Generate (which promotes it).
      if (tile.id === this.previewTileId) return;
      const tileLayout = layoutById.get(tile.id) ?? this.layoutItemForTile(tile, index);
      const span = Math.min(tileLayout.colSpan, columns);
      const widget = tile.layoutType === 'GROUP' && tile.groupMembers?.length
        ? this.buildGroupCardWidget(tile, tileLayout, typeMap)
        : this.buildTileWidget(tile, typeMap, tileLayout.rowSpan, tileLayout.x, tileLayout.y, tileLayout.order, tileLayout.customSize, span);
      if (used + span > columns && buffer.length) {
        pageContent.push({ id: `row${pageContent.length + 1}`, columns: buffer });
        buffer = [];
        used = 0;
      }
      buffer.push(widget);
      used += span;
    });
    if (buffer.length) pageContent.push({ id: `row${pageContent.length + 1}`, columns: buffer });

    const name = this.dashboardName.trim() || 'Untitled dashboard';
    // Return a JSON-safe clone: the drill-down tree nodes carry a runtime
    // `parent` back-reference (set by DrillDownComponent), which makes a circular
    // structure that breaks JSON.stringify on both the publish POST and the
    // localStorage save. toSerializable strips those refs / cuts cycles.
    return toSerializable({
      'dashboard-name': name,
      name,
      // Marks this dashboard as built by the DVM layout builder so the published
      // view's "Back to Builder" returns HERE (not the old dashboard builder).
      builder: 'dvm-layout',
      screenFloating: true,
      enableEdit: true,
      layout: this.buildPublishedLayoutSnapshot(layoutItems),
      status,
      pageContent,
    });
  }

  /** Build one tile's published widget object — extracted from the per-tile
   *  loop above so GROUP members (below) can build theirs the exact same way. */
  private buildTileWidget(
    tile: DashboardTile,
    typeMap: Record<string, string>,
    rowSpan: number,
    x: number,
    y: number,
    order: number,
    customSize: boolean,
    span: number,
  ): Record<string, unknown> {
    // ChartRendererService assigns the WHOLE widget object to the rendered
    // component's `data` input, and each component reads its fields
    // (headers/staticData/key/…) directly off `data`. So the render payload
    // must be SPREAD at the top level of the widget — NOT nested under a
    // `data` key (that was why the published view showed "no data").
    const renderData: Record<string, unknown> =
      (tile.drillData as Record<string, unknown>) ??
      (tile.tableMatrixData as Record<string, unknown>) ??
      (tile.hbData as Record<string, unknown>) ??
      (tile.kpiData as Record<string, unknown>) ??
      (tile.barData as Record<string, unknown>) ??
      (tile.lineAreaData as Record<string, unknown>) ??
      (tile.scatterBubbleData as Record<string, unknown>) ??
      (tile.inputControl?.widgetData as Record<string, unknown>) ??
      (tile.dropdownControl?.widgetData as Record<string, unknown>) ??
      (tile.fileUploadControl?.widgetData as Record<string, unknown>) ??
      // Waterfall: publish the raw BARS (not just the pre-built option). The
      // option's number formatters are FUNCTIONS, which JSON.stringify drops on
      // publish — so the published view rebuilds the option from bars + format
      // at render time (functions restored). `option` kept as a fallback.
      (tile.waterfallBars
        ? { waterfallBars: tile.waterfallBars, option: tile.previewOption }
        : (tile.previewOption ? { option: tile.previewOption } : {}));
    const isInputControl = tile.layoutType === 'SLIDER' || tile.layoutType === 'INPUT_BOX' || tile.layoutType === 'DROPDOWN' || tile.layoutType === 'FILE_UPLOAD';
    return {
      ...renderData,
      type: typeMap[tile.layoutType] || 'bar-chart',
      title: tile.title,
      widgetId: tile.id,
      componentId: tile.componentId,
      // DashboardComponent derives a widget's width from Bootstrap-style
      // column classes. Keep layout.colSpan as structured metadata as well,
      // but emit the class so the published footprint matches the builder.
      class: `col-${span}`,
      layout: { colSpan: span, rowSpan, x, y, order, customSize },
      // The published grid sizes each tile from a pixel `height` (it ignores
      // `rowSpan`) and, with NO height, falls back to an oversized default —
      // which made small controls (KPI / slider / input box) render huge. Emit
      // a height proportional to the builder's rowSpan so the published tile
      // mirrors the footprint the user laid out here.
      height: Math.max(rowSpan, 1) * DvmLayoutBuilderComponent.DASH_ROW_PX,
      payload: tile.payload,
      // Persist the per-tile Format popup selections so the published view can
      // preserve them on recompute (rebuilds the option from `format`) and so
      // re-opening in the builder restores them — without this the waterfall's
      // formatting was lost after an input change / on edit.
      ...(tile.format ? { format: tile.format } : {}),
      // Cross-filter config: the column this tile publishes + which tiles react.
      // Persist the EFFECTIVE values (explicit override, else the same defaults
      // the builder itself uses — category column + every reactable tile), NOT
      // the raw tile fields. Otherwise a tile that cross-filters in the builder
      // by default published NOTHING (raw filterKey/listenerTileIds were unset),
      // so the published view lost the wiring. Emitted for client-side charts
      // AND recompute participants (waterfall).
      ...(this.isCrossFilterTile(tile) || this.isRecomputeCrossFilterTile(tile)
        ? {
            filterKey: this.effectiveTileFilterKey(tile),
            listenerTileIds: this.effectiveListenerTileIds(tile),
          }
        : {}),
      // Waterfall interaction flags travel on the WIDGET (the published view
      // hands rec.widget straight to <app-waterfall-chart>, which reads
      // data.crossFilter/highlightOnClick) + the compute URL it needs to re-run
      // the bridge with a cross-filter (layout_json omits apiEndpoints).
      ...(this.isRecomputeCrossFilterTile(tile)
        ? {
            crossFilter: tile.format?.['crossFilter'] === true,
            highlightOnClick: tile.format?.['highlightOnClick'] === true,
            recomputeUrl: this.layout.apiEndpoints?.generateConfigApi?.trim() || '/components/compute',
          }
        : {}),
      // Input controls drive a live recompute in the published view. The
      // published layout_json omits `apiEndpoints`, so persist the compute URL
      // onto the control's widget so the published dashboard can re-post here
      // on change (it can't otherwise resolve generateConfigApi).
      ...(isInputControl
        ? { recomputeUrl: this.layout.apiEndpoints?.generateConfigApi?.trim() || '/components/compute' }
        : {}),
    };
  }

  /** A GROUP tile publishes as ONE 'group-card' widget whose `children` are the
   *  member tiles' own widgets (built via buildTileWidget, same as any other
   *  tile) — the published view renders one card and lays children out inside
   *  it. Children's x/y/order are relative to the CARD's own inner grid, not
   *  the outer dashboard grid — they stack top-to-bottom, full card width. */
  private buildGroupCardWidget(
    tile: DashboardTile,
    tileLayout: PublishedLayoutSnapshotItem,
    typeMap: Record<string, string>,
  ): Record<string, unknown> {
    const members = tile.groupMembers || [];
    let cursorY = 0;
    const children = members.map((member, memberIndex) => {
      const memberSpan = this.clamp(member.colSpan, 1, tileLayout.colSpan);
      const child = this.buildTileWidget(member, typeMap, member.rowSpan, 0, cursorY, memberIndex, false, memberSpan);
      cursorY += member.rowSpan;
      return child;
    });
    return {
      type: 'group-card',
      title: tile.groupTitle || tile.title || 'Grouped card',
      widgetId: tile.id,
      componentId: tile.componentId,
      // The card's own design (border / backgrounds / radius / transparency)
      // travels with the published widget exactly like a chart's format, so the
      // published view can re-apply it and it survives save/reload.
      ...(tile.format ? { format: tile.format } : {}),
      class: `col-${tileLayout.colSpan}`,
      layout: { colSpan: tileLayout.colSpan, rowSpan: tileLayout.rowSpan, x: tileLayout.x, y: tileLayout.y, order: tileLayout.order, customSize: tileLayout.customSize },
      height: Math.max(tileLayout.rowSpan, 1) * DvmLayoutBuilderComponent.DASH_ROW_PX,
      innerColumns: tileLayout.colSpan,
      groupColumns: tile.groupColumns || 1,
      children,
    };
  }

  private buildPublishedLayoutSnapshot(items = this.buildPublishedLayoutItems()): Record<string, unknown> {
    const existing = this.cloneRecord(this.restoredPublishedLayout) ?? {};
    return {
      ...existing,
      columns: Number(existing['columns'] ?? DvmLayoutBuilderComponent.DASH_COLS),
      items,
      breakpoints: {
        ...((existing['breakpoints'] as Record<string, unknown> | undefined) ?? {}),
        xl: items.map(({ id, colSpan, rowSpan, x, y, order, customSize }) => ({ id, colSpan, rowSpan, x, y, order, customSize })),
      },
    };
  }

  private buildPublishedLayoutItems(): PublishedLayoutSnapshotItem[] {
    const restoredItems = this.readPublishedLayoutItems(this.restoredPublishedLayout);
    const restoredById = new Map(restoredItems.map((item) => [String(item['id'] ?? '').trim(), item]));
    const restoredByComponentId = new Map(restoredItems.map((item) => [String(item['componentId'] ?? '').trim(), item]));
    const seeds = this.dashboardTiles.map((tile, index) => {
      const restored = restoredById.get(tile.id) ?? restoredByComponentId.get(tile.componentId);
      return this.layoutItemForTile(tile, index, restored);
    });
    return this.placeMissingLayoutItems(seeds);
  }

  /** Build one tile's layout-snapshot entry.
   *
   *  EXISTING component (has a `restored` entry from the published layout we got
   *  via the dynamic_filter API): keep its saved layout VERBATIM — same
   *  x/y/colSpan/rowSpan/order/customSize. The builder must never re-derive or
   *  re-flow a tile that already has a published position, or it would reset the
   *  arrangement the user set in the published view.
   *
   *  NEW component (no `restored` entry — just added in the builder): emit it
   *  UNPLACED (x/y = -1) so placeMissingLayoutItems appends it below everything
   *  else. This is the ONLY case where the builder decides a position. */
  private layoutItemForTile(tile: DashboardTile, order: number, restored?: Record<string, unknown>): PublishedLayoutSnapshotItem {
    if (restored) {
      const colSpan = this.clamp(Math.round(this.toFiniteNumber(restored['colSpan']) ?? this.toFiniteNumber(tile.colSpan) ?? 6), 1, DvmLayoutBuilderComponent.DASH_COLS);
      const restoredX = this.toFiniteNumber(restored['x']);
      const restoredY = this.toFiniteNumber(restored['y']);
      return {
        id: tile.id,
        componentId: tile.componentId,
        colSpan,
        rowSpan: Math.max(1, Math.round(this.toFiniteNumber(restored['rowSpan']) ?? this.toFiniteNumber(tile.rowSpan) ?? 8)),
        // Keep the published position exactly; only fall back to append (-1) if
        // the saved entry genuinely had no position (legacy published layout).
        x: restoredX === null ? -1 : Math.max(0, Math.round(restoredX)),
        y: restoredY === null ? -1 : Math.max(0, Math.round(restoredY)),
        order: this.toFiniteNumber(restored['order']) ?? order,
        customSize: restored['customSize'] === true,
      };
    }
    // Brand-new tile: leave it unplaced so it's appended after existing ones.
    return {
      id: tile.id,
      componentId: tile.componentId,
      colSpan: this.clamp(Math.round(this.toFiniteNumber(tile.colSpan) ?? 6), 1, DvmLayoutBuilderComponent.DASH_COLS),
      rowSpan: Math.max(1, Math.round(this.toFiniteNumber(tile.rowSpan) ?? 8)),
      x: -1,
      y: -1,
      order,
      customSize: tile.customSize === true,
    };
  }

  private placeMissingLayoutItems(items: PublishedLayoutSnapshotItem[]): PublishedLayoutSnapshotItem[] {
    let cursorX = 0;
    let cursorY = items.reduce((bottom, item) => item.x >= 0 && item.y >= 0 ? Math.max(bottom, item.y + item.rowSpan) : bottom, 0);
    let rowHeight = 0;

    return items.map((item) => {
      if (item.x >= 0 && item.y >= 0) return item;
      if (cursorX + item.colSpan > DvmLayoutBuilderComponent.DASH_COLS) {
        cursorX = 0;
        cursorY += rowHeight;
        rowHeight = 0;
      }
      const placed = { ...item, x: cursorX, y: cursorY };
      cursorX += item.colSpan;
      rowHeight = Math.max(rowHeight, item.rowSpan);
      return placed;
    });
  }

  private toFiniteNumber(value: unknown): number | null {
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  }


  private cloneRecord(value: unknown): Record<string, unknown> | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    return toSerializable(value as Record<string, unknown>);
  }

  /** The reused app-mat-slider emits { start, end } on change — take the end as
   *  the control's value and re-post the tile's config so its target charts
   *  recompute, then live-update every returned chart tile with its new data. */
  onTileSliderChange(tile: DashboardTile, range: { start: number; end: number }): void {
    this.pushInputValueAndRecompute(tile, range.end);
  }

  /** The reused app-input-box emits its value on change — same recompute path. */
  onTileInputValueChange(tile: DashboardTile, value: number | string): void {
    this.pushInputValueAndRecompute(tile, value);
  }

  /** A Table & Matrix tile fired navigation — route to the target published
   *  dashboard, passing the clicked value as query params so that dashboard
   *  filters its charts on load. Works from the builder preview too (the target
   *  is a published dashboard, independent of this builder session). */
  onTileNavigate(_tile: DashboardTile, event: { targetDashboard: string; targetComponents: string[]; filterKey: string; filterValue: string }): void {
    if (!event?.targetDashboard) return;
    this.router.navigate(['/dvm-published', event.targetDashboard], {
      queryParams: {
        filterKey: event.filterKey,
        filterValue: event.filterValue,
        ...(event.targetComponents?.length ? { filterTargets: event.targetComponents.join(',') } : {}),
      },
    });
  }

  /** A Normal/Editable table tile requested a refresh — re-fetch its dataset rows
   *  and re-render (same getEntityRows path the initial generate uses). */
  onTileTableRefresh(tile: DashboardTile): void {
    if (!tile.tableMatrixData) return;
    const caps = (tile.tableMatrixData['capabilities'] as Record<string, unknown>) || {};
    const dataset = String(caps['dataset'] || tile.payload?.['dataset'] || '');
    if (!dataset) return;
    this.datasetService.getEntityRows(dataset).subscribe({
      next: (rows) => {
        tile.tableMatrixData = { ...(tile.tableMatrixData as Record<string, unknown>), staticData: rows };
        this.dashboardTiles = this.dashboardTiles.map((t) => (t.id === tile.id ? { ...tile } : t));
      },
      error: () => { /* keep current rows on failure */ },
    });
  }

  /** The table preview emitted a new drag-reordered column order — persist it
   *  into the tile's capabilities.columns so the order survives Generate/publish
   *  (the child's runtime `columnOrder` alone doesn't round-trip). When columns
   *  were "all" (empty config), materialize them from the new order first. */
  onTileColumnReorder(tile: DashboardTile, order: string[]): void {
    if (!tile.tableMatrixData || !Array.isArray(order) || !order.length) return;
    const data = tile.tableMatrixData as Record<string, unknown>;
    const caps = { ...((data['capabilities'] as Record<string, unknown>) || {}) };
    const existing = (Array.isArray(caps['columns']) ? caps['columns'] : []) as Array<Record<string, unknown>>;
    const byField = new Map(existing.map((c) => [String(c['field']), c]));
    // Reorder existing column configs by the new key order; any key without an
    // existing config (columns were inferred/"all") becomes a bare {field}.
    const reordered = order.map((field) => byField.get(field) || { field });
    caps['columns'] = reordered;
    tile.tableMatrixData = { ...data, capabilities: caps };
    // Keep the tile's PERSISTED request payload in the same order too — otherwise
    // a re-Generate/Update (which rebuilds from payload.columns) would drop back
    // to the old column order and lose the reorder. Only reorder the keys that
    // were already in the payload (preserve "all"/empty-means-all semantics).
    const payload = tile.payload as Record<string, unknown> | undefined;
    if (payload && Array.isArray(payload['columns']) && (payload['columns'] as unknown[]).length) {
      // Entries are {dataset, field, displayName} objects now (legacy payloads
      // may still hold plain strings) — reorder by field key either way.
      const entries = payload['columns'] as unknown[];
      const fieldOf = (entry: unknown): string =>
        entry && typeof entry === 'object' ? String((entry as Record<string, unknown>)['field'] ?? '') : String(entry ?? '');
      const byField = new Map(entries.map((entry) => [fieldOf(entry), entry]));
      payload['columns'] = order.filter((field) => byField.has(field)).map((field) => byField.get(field));
    }
    // Mirror into the build form when THIS tile is the one being edited, so a
    // subsequent Generate/Update keeps the reordered order.
    if (this.editingTileId === tile.id && this.tmColumns.length) {
      const byField = new Map(this.tmColumns.map((column) => [column.field, column]));
      const reordered = order.map((field) => byField.get(field)).filter((c): c is TmColumnDef => !!c);
      // Keep any configured rows the runtime order didn't mention (e.g. a row
      // whose field is still blank) at the end rather than dropping them.
      const mentioned = new Set(reordered.map((column) => column.id));
      this.tmColumns = [...reordered, ...this.tmColumns.filter((column) => !mentioned.has(column.id))];
    }
    this.dashboardTiles = this.dashboardTiles.map((t) => (t.id === tile.id ? { ...tile } : t));
  }

  onTileDropdownChange(tile: DashboardTile, value: string | string[]): void {
    if (!tile.dropdownControl) return;
    const selected = Array.isArray(value) ? value.map(String) : value == null || value === '' ? [] : [String(value)];
    tile.dropdownControl.value = selected;
    // The client-side chart family (bar / line-area / scatter — the cross-filter
    // registry tiles) filters LOCALLY from its cached original rows: the compute
    // API has no reliable recompute for those types, so they'd either never
    // update or get clobbered with unfiltered rows. Track this dropdown's
    // selection (several dropdowns compose) and re-apply.
    const dropdownField = tile.dropdownControl.field ?? '';
    if (selected.length && dropdownField) {
      this.activeDropdownFilters.set(String(tile.componentId ?? ''), { field: dropdownField, values: selected });
    } else {
      this.activeDropdownFilters.delete(String(tile.componentId ?? ''));
    }
    this.applyDropdownFiltersToClientTiles();
    // Registry tiles whose rows carry the dropdown's field are fully handled
    // client-side — keep them OUT of the server target list so a compute
    // response can't overwrite the local filter with unfiltered rows.
    const clientHandledIds = new Set(
      this.dashboardTiles
        .filter((item) => this.isCrossFilterTile(item) && !!dropdownField && this.tileClientRows(item).some((row) => dropdownField in row))
        .map((item) => String(item.componentId ?? '')),
    );
    // Drive EVERY remaining chart tile on the dashboard, not just a hand-picked
    // target — a dropdown is a global filter, so its selection should refresh
    // all charts. Build the targets list from all current chart tiles (excluding
    // input controls, the dropdown itself, and the client-side-handled tiles) so
    // the backend returns a result per chart, then apply each to its tile.
    const chartTargets = this.dashboardTiles
      .filter((item) => this.isChartTile(item) && item.componentId !== tile.componentId && !clientHandledIds.has(String(item.componentId ?? '')))
      .map((item) => ({ componentId: item.componentId, label: item.title, field: tile.dropdownControl?.field ?? '' }));
    const payload: Record<string, unknown> = {
      ...tile.payload,
      value: selected,
      targets: chartTargets,
      otherInputs: this.otherActiveInputValues(tile.componentId),
    };
    tile.payload = payload;
    const targetIds = chartTargets.map((target) => String(target.componentId ?? '')).filter(Boolean);
    if (!targetIds.length) return;
    this.isRecomputing = true;
    this.recomputingComponentIds = new Set(targetIds);
    const url = resolveSelfServiceApiUrl(this.layout.apiEndpoints?.generateConfigApi, '/api/dvm/self-service/waterfall/generate');
    this.http.post<Record<string, unknown>>(url, payload).subscribe({
      next: (response) => {
        this.applyRecomputeResults(response, targetIds);
        this.isRecomputing = false;
        this.recomputingComponentIds = new Set();
      },
      error: (error: HttpErrorResponse) => {
        this.isRecomputing = false;
        this.recomputingComponentIds = new Set();
        console.warn('[dvm-layout-builder] dropdown value push failed:', error);
      },
    });
  }

  /** A tile that renders a chart (not an input control) — the set a dropdown
   *  filter recomputes when its value changes. */
  private isChartTile(tile: DashboardTile): boolean {
    return tile.layoutType !== 'SLIDER' && tile.layoutType !== 'INPUT_BOX' && tile.layoutType !== 'DROPDOWN' && tile.layoutType !== 'FILE_UPLOAD';
  }

  // ── File upload control (preview) — POST the file, re-render targets ────────
  private uploadingTileIds = new Set<string>();
  private tileUploadMessages: Record<string, string> = {};
  isTileUploading(tile: DashboardTile): boolean { return this.uploadingTileIds.has(tile.id); }
  tileUploadMessage(tile: DashboardTile): string { return this.tileUploadMessages[tile.id] || ''; }

  /** A file was picked on a FILE_UPLOAD tile → base64-encode it into a JSON
   *  payload and POST to the compute API (the compute API is JSON-only — a
   *  multipart FormData gives 415), tagged with the tile's targets. Then apply
   *  the per-target results to the matching tiles (same machinery a dropdown
   *  value push uses). */
  onTileFilePicked(tile: DashboardTile, file: File): void {
    if (!tile.fileUploadControl || !file) return;
    const targetIds = (tile.fileUploadControl.targets || []).filter(Boolean);
    this.uploadingTileIds = new Set(this.uploadingTileIds).add(tile.id);
    this.tileUploadMessages = { ...this.tileUploadMessages, [tile.id]: `Uploading ${file.name}…` };
    if (targetIds.length) this.recomputingComponentIds = new Set(targetIds);

    this.fileToBase64(file).then((base64) => {
      // JSON payload (Content-Type application/json) — file as base64 so the
      // JSON-only compute API accepts it.
      const payload: Record<string, unknown> = {
        componentId: tile.componentId,
        dashboardId: this.dashboardId,
        componentType: 'INPUT',
        inputType: 'FILE_UPLOAD',
        fileName: file.name,
        fileType: file.type,
        fileContent: base64,
        // The compute API requires every INPUT to carry a `value` (same field
        // dropdowns/sliders send). A file-upload's value IS the file — send the
        // base64 there too so the backend's "No value provided" check passes.
        value: base64,
        defaultValue: base64,
        targets: targetIds.map((componentId) => ({ componentId, label: this.targetLabelForId(componentId) })),
      };
      const url = resolveSelfServiceApiUrl(this.layout.apiEndpoints?.generateConfigApi, '/components/compute');
      this.http.post<Record<string, unknown>>(url, payload).subscribe({
        next: (response) => {
          this.uploadingTileIds.delete(tile.id);
          this.uploadingTileIds = new Set(this.uploadingTileIds);
          if (targetIds.length) this.applyRecomputeResults(response, targetIds);
          this.recomputingComponentIds = new Set();
          this.tileUploadMessages = { ...this.tileUploadMessages, [tile.id]: `Uploaded ${file.name}` };
        },
        error: () => {
          this.uploadingTileIds.delete(tile.id);
          this.uploadingTileIds = new Set(this.uploadingTileIds);
          this.recomputingComponentIds = new Set();
          this.tileUploadMessages = { ...this.tileUploadMessages, [tile.id]: `Upload failed for ${file.name}` };
        },
      });
    }).catch(() => {
      this.uploadingTileIds.delete(tile.id);
      this.uploadingTileIds = new Set(this.uploadingTileIds);
      this.recomputingComponentIds = new Set();
      this.tileUploadMessages = { ...this.tileUploadMessages, [tile.id]: `Could not read ${file.name}` };
    });
  }

  /** Read a File as a base64 string (no data-URL prefix — just the base64). */
  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || '');
        // Strip the `data:<mime>;base64,` prefix so only the base64 payload goes.
        const comma = result.indexOf(',');
        resolve(comma >= 0 ? result.slice(comma + 1) : result);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  /** POST the input control's payload with its new `value` and apply the
   *  per-chart `results` from the response to the matching dashboard tiles.
   *  Non-blocking: the control keeps its value locally regardless. */
  /** componentIds of the tiles a recompute currently in flight will update —
   *  drives `isTileRecomputing()` so only THOSE tiles show a loading overlay
   *  instead of the whole preview canvas. */
  private recomputingComponentIds = new Set<string>();

  /** True when this tile is one of the targets of a recompute currently in
   *  flight — used by the template to show a per-tile loading overlay. */
  isTileRecomputing(tile: DashboardTile): boolean {
    const id = String(tile?.componentId ?? '');
    return !!id && this.recomputingComponentIds.has(id);
  }

  /** Every OTHER input control tile currently on the dashboard, with its live
   *  value — bundled into every single-input recompute call so a target/KPI
   *  formula referencing another input's [Token] resolves against what that
   *  input is showing RIGHT NOW, not a value the backend may have cached from
   *  an earlier call. Mirrors dvm-published-dashboard's otherActiveInputValues. */
  private otherActiveInputValues(excludeComponentId: string): Array<{ componentId: string; title: string; value: unknown }> {
    return this.dashboardTiles
      .filter((item) => !this.isChartTile(item) && item.layoutType !== 'FILE_UPLOAD' && item.componentId !== excludeComponentId)
      .map((item) => ({
        componentId: item.componentId,
        title: item.title || '',
        value: (item.payload as Record<string, unknown> | undefined)?.['value'] ?? item.inputControl?.value ?? null,
      }))
      .filter((entry) => !!entry.componentId);
  }

  /** SSA $ resolves Price Inflation %/Cost Inflation %'s own cross-referenced
   *  values (DN Price, Total Cost, ...) from whatever the backend last cached
   *  for THEM - but every input tile's compute call is independent, so moving
   *  SSA $ alone never refreshes that cache unless Price Inflation %/Cost
   *  Inflation % have ALSO been (re-)submitted since. Replaying their own
   *  compute call first - with whatever value they currently show, even their
   *  default/zero - keeps every cross-reference SSA $ depends on fresh.
   *  Mirrors dvm-published-dashboard's sideEffectInputsFor. */
  private sideEffectInputsFor(tile: DashboardTile): DashboardTile[] {
    const title = String(tile?.title ?? '').trim().toLowerCase();
    if (title !== 'ssa $') return [];
    const wanted = new Set(['price inflation %', 'cost inflation %']);
    return this.dashboardTiles.filter((item) => !this.isChartTile(item) && item.layoutType !== 'FILE_UPLOAD'
      && item.componentId !== tile.componentId
      && wanted.has(String(item.title ?? '').trim().toLowerCase()));
  }

  private pushInputValueAndRecompute(tile: DashboardTile, value: number | string): void {
    if (!tile.inputControl) return;
    tile.inputControl.value = value;
    const payload = { ...tile.payload, value, otherInputs: this.otherActiveInputValues(tile.componentId) };
    tile.payload = payload;
    const url = resolveSelfServiceApiUrl(this.layout.apiEndpoints?.generateConfigApi, '/api/dvm/self-service/waterfall/generate');
    // Only THIS input's own targets may be updated from its recompute response —
    // the compute API can echo back entries for other charts too, and those
    // must be left alone even if they carry real-looking data.
    const payloadTargets = (payload as Record<string, unknown>)['targets'];
    const targetIds = (Array.isArray(payloadTargets) ? (payloadTargets as Array<Record<string, unknown>>) : [])
      .map((t) => String(t?.['componentId'] ?? ''))
      .filter(Boolean);

    const sideEffectTiles = this.sideEffectInputsFor(tile);
    this.isRecomputing = true;
    this.recomputingComponentIds = new Set([...targetIds, ...sideEffectTiles.map((t) => t.componentId)]);

    const runMainCompute = () => this.http.post<Record<string, unknown>>(url, payload).subscribe({
      next: (response) => {
        this.applyRecomputeResults(response, targetIds);
        this.isRecomputing = false;
        this.recomputingComponentIds = new Set();
      },
      error: (error: HttpErrorResponse) => {
        this.isRecomputing = false;
        this.recomputingComponentIds = new Set();
        console.warn('[dvm-layout-builder] input value push failed:', error);
      },
    });

    if (!sideEffectTiles.length) {
      runMainCompute();
      return;
    }

    // Replay Price Inflation %/Cost Inflation %'s own compute call FIRST -
    // with whatever value they currently show, even default/zero - so their
    // cross-referenced values are freshly cached before SSA $'s own call
    // resolves against them.
    const sideEffectRequests = sideEffectTiles.map((sideTile) => {
      const sidePayload = { ...sideTile.payload, otherInputs: this.otherActiveInputValues(sideTile.componentId) };
      return this.http.post<Record<string, unknown>>(url, sidePayload).pipe(
        map((response) => ({ sideTile, response, error: null as unknown })),
        catchError((error) => of({ sideTile, response: null, error })),
      );
    });

    forkJoin(sideEffectRequests).subscribe((results) => {
      results.forEach(({ sideTile, response, error }) => {
        if (response) {
          const sideTargets = (Array.isArray((sideTile.payload as Record<string, unknown>)?.['targets'])
            ? ((sideTile.payload as Record<string, unknown>)['targets'] as Array<Record<string, unknown>>)
            : []).map((t) => String(t?.['componentId'] ?? '')).filter(Boolean);
          this.applyRecomputeResults(response, sideTargets);
        } else if (error) {
          console.warn('[dvm-layout-builder] SSA $ side-effect recompute for', sideTile.componentId, 'failed:', error);
        }
      });
      runMainCompute();
    });
  }

  /** Apply an input-control recompute response `{ inputComponentId, results:[…] }`
   *  to the dashboard tiles: each result is matched to a tile by `componentId`
   *  (charts carry a fixed, type-derived id — see componentIdForLayout) and its
   *  chart data is rebuilt in place. */
  private applyRecomputeResults(response: Record<string, unknown> | null, targetIds: string[] = []): void {
    const results = (response?.['results'] ?? []) as Array<Record<string, unknown>>;
    if (!Array.isArray(results) || !results.length) return;
    const allowedIds = new Set(targetIds);
    let changed = false;
    results.forEach((entry) => {
      // Only apply entries that actually carry data — the compute API returns
      // placeholder entries for untouched target charts, and applying an empty
      // one would blank/load that chart.
      if (!recomputeEntryHasData(entry)) return;
      const result = (entry?.['result'] ?? entry) as Record<string, unknown>;
      const entryComponentId = String(entry?.['componentId'] ?? result?.['componentId'] ?? '').trim();
      const chartType = resolveRecomputeChartType(entry);
      const tile = this.findRecomputeTargetTile(entryComponentId, chartType, allowedIds);
      if (tile && this.applyResultToTile(tile, entry)) changed = true;
    });
    if (changed) {
      // New array ref so the *ngFor tiles re-evaluate their inputs, then nudge
      // ECharts widgets to re-fit.
      this.dashboardTiles = [...this.dashboardTiles];
      this.reflowCharts();
    }
  }

  private findRecomputeTargetTile(entryComponentId: string, chartType: string, allowedIds: Set<string>): DashboardTile | undefined {
    const allTiles = this.allTilesFlat();
    if (entryComponentId && (!allowedIds.size || allowedIds.has(entryComponentId))) {
      const direct = allTiles.find((tile) => tile.componentId === entryComponentId);
      if (direct) return direct;
    }

    if (!chartType) return undefined;
    const candidates = allowedIds.size
      ? allTiles.filter((tile) => allowedIds.has(String(tile.componentId ?? '')))
      : allTiles;
    // Type fallback only when unambiguous — previously returned the FIRST
    // same-type tile, so a dropdown/input driving two same-type charts (or a
    // stale target id) silently drove the WRONG tile. Refuse to guess when
    // more than one candidate matches. (Mirrors findRecomputeTargetWidget in
    // the published view.)
    const sameType = candidates.filter((tile) => tile.layoutType === chartType);
    return sameType.length === 1 ? sameType[0] : undefined;
  }

  /** Rebuild ONE tile's chart data from a recompute result entry
   *  `{ componentId, label, result: { chartType, data, … } }`, reusing the same
   *  builders the initial generate uses. Returns true if the tile was updated. */
  private applyResultToTile(tile: DashboardTile, entry: Record<string, unknown>): boolean {
    const result = (entry?.['result'] ?? entry) as Record<string, unknown>;
    if (tile.layoutType === 'DROPDOWN' && resolveRecomputeChartType(entry) === 'INPUT') {
      const rawValue = result['value'] ?? result['data'];
      const value = Array.isArray(rawValue)
        ? rawValue.map(String)
        : rawValue == null || rawValue === '' ? [] : [String(rawValue)];
      if (!tile.dropdownControl) return false;
      tile.payload = { ...tile.payload, value };
      tile.dropdownControl = { ...tile.dropdownControl, value };
      return true;
    }
    // A target that is itself an input control (slider/input box) comes back
    // as `componentType: "INPUT"` — it just needs its live value pushed in,
    // not rebuilt like a chart's data.
    if ((tile.layoutType === 'SLIDER' || tile.layoutType === 'INPUT_BOX') && resolveRecomputeChartType(entry) === 'INPUT') {
      const value = this.extractKpiValue(result);
      if (value === null || !tile.inputControl) return false;
      tile.payload = { ...tile.payload, value };
      // The rendered widget reads from `inputControl.widgetData`, NOT
      // `tile.payload`/`inputControl.value` — those must get a NEW widgetData
      // reference too, or app-mat-slider/app-input-box's [data] input never
      // sees a change and the displayed value never updates.
      const isSlider = tile.layoutType === 'SLIDER';
      const widgetData = tile.inputControl.widgetData || {};
      tile.inputControl = {
        ...tile.inputControl,
        value,
        widgetData: isSlider
          ? { ...widgetData, data: { ...(widgetData['data'] as Record<string, unknown> | undefined), data: { ...((widgetData['data'] as Record<string, unknown> | undefined)?.['data'] as Record<string, unknown> | undefined), rangeStart: value, rangeEnd: value } } }
          : { ...widgetData, value },
      };
      return true;
    }
    const chartType = String(result?.['chartType'] ?? tile.layoutType ?? '');
    const format = (tile.format as Record<string, unknown>) || this.defaultTileFormat(tile.layoutType);
    switch (chartType) {
      case 'WATERFALL': {
        const rawBars = (Array.isArray(result['data'])
          ? result['data']
          : ((result['data'] as { data?: WaterfallBar[] })?.data || [])) as WaterfallBar[];
        const bars = this.applyStepSignsToWaterfallBars(rawBars, tile.payload);
        tile.waterfallBars = bars;
        tile.previewOption = this.buildWaterfallPreviewOption(bars, format);
        tile.waterfallData = this.buildWaterfallTileData(tile);
        return true;
      }
      case 'DRILL_DOWN_TABLE': {
        const drillData = this.buildDrillDownWidgetData(result, format);
        drillData['widgetId'] = 'dvm-tile-drill-' + tile.id;
        tile.drillData = drillData;
        return true;
      }
      case 'HORIZONTAL_BAR_CHART': {
        const hbData = this.buildHbWidgetData(result, tile.id, tile.format);
        // Keep the tile's own title (the builders read the ACTIVE builder state).
        hbData['title'] = tile.title;
        tile.hbData = hbData;
        return true;
      }
      case 'KPI': {
        tile.kpiData = this.buildKpiWidgetData(result, tile.id, tile.format);
        return true;
      }
      case 'BAR': {
        // Server-computed envelope (`data[0].bar`, pre-aggregated
        // {department, value} rows) → rebuild the widget with remapped
        // capabilities, mirroring the LINE_AREA case below.
        const envRows = extractBarComputeRows(result['responseList']) ?? extractBarComputeRows(result);
        if (envRows?.length && tile.barData) {
          tile.barData = buildBarComputeWidgetData(result, {
            widgetId: 'dvm-tile-bar-' + tile.id,
            title: tile.title,
            capabilities: (tile.barData['capabilities'] as Record<string, unknown> | undefined) ?? {},
            payload: (tile.payload as Record<string, unknown>) ?? {},
            format: tile.format,
          });
          tile.barRows = envRows;
          return true;
        }
        // A recompute returns fresh rows — keep the tile's existing capabilities,
        // just swap in the new rows so the client-side render updates.
        const rows = (Array.isArray(result['data'])
          ? result['data']
          : ((result['data'] as { data?: unknown })?.data ?? [])) as Array<Record<string, unknown>>;
        if (tile.barData && this.rowsFitClientChart(rows, tile.barData, 'category')) {
          tile.barData = { ...tile.barData, staticData: Array.isArray(rows) ? rows : [] };
          return true;
        }
        return false;
      }
      case 'LINE_AREA': {
        // Server-computed envelope (`data[0].lineArea`) → rebuild the widget
        // data with remapped capabilities (rows are pre-aggregated
        // {department, value}, not the configured dataset columns).
        const envRows = extractLineAreaComputeRows(result['responseList']) ?? extractLineAreaComputeRows(result);
        if (envRows?.length && tile.lineAreaData) {
          tile.lineAreaData = buildLineAreaComputeWidgetData(result, {
            widgetId: 'dvm-tile-line-area-' + tile.id,
            title: tile.title,
            capabilities: (tile.lineAreaData['capabilities'] as Record<string, unknown> | undefined),
            format: tile.format,
          });
          tile.lineAreaRows = null; // server-computed → not client-filterable
          return true;
        }
        const rows = (Array.isArray(result['data'])
          ? result['data']
          : ((result['data'] as { data?: unknown })?.data ?? [])) as Array<Record<string, unknown>>;
        if (tile.lineAreaData && this.rowsFitClientChart(rows, tile.lineAreaData, 'category')) {
          tile.lineAreaData = { ...tile.lineAreaData, staticData: Array.isArray(rows) ? rows : [] };
          return true;
        }
        return false;
      }
      case 'SCATTER_BUBBLE': {
        const rows = (Array.isArray(result['data'])
          ? result['data']
          : ((result['data'] as { data?: unknown })?.data ?? [])) as Array<Record<string, unknown>>;
        if (tile.scatterBubbleData && this.rowsFitClientChart(rows, tile.scatterBubbleData, 'xField')) {
          tile.scatterBubbleData = { ...tile.scatterBubbleData, staticData: Array.isArray(rows) ? rows : [] };
          return true;
        }
        return false;
      }
      case 'TABLE_MATRIX': {
        // A file-upload (or other input) drove this table target — swap in the
        // response rows while PRESERVING the table's existing capabilities so it
        // re-renders with the new data. Drill-Down tables carry a tree instead.
        const rows = this.extractTableMatrixRows(result);
        const existing = (tile.tableMatrixData as Record<string, unknown>) || {};
        const caps = existing['capabilities'] as Record<string, unknown> | undefined;
        const isDrill = !!caps && (caps['tableType'] === 'drilldown' || (caps['typeFlags'] as Record<string, unknown> | undefined)?.['drilldown'] === true);
        if (isDrill) {
          const drilldownData = this.buildDrillDownWidgetData(result, format);
          drilldownData['widgetId'] = 'dvm-tile-table-matrix-' + tile.id + '-drill';
          tile.tableMatrixData = { ...existing, drilldownData };
        } else {
          tile.tableMatrixData = { ...existing, staticData: rows };
        }
        return true;
      }
      default:
        return false;
    }
  }

  /** A recompute row set fits a CLIENT-SIDE chart only when it carries the field
   *  the chart renders its axis from (capabilities[fieldKey]). The compute API
   *  can answer a dropdown/input push with rows in a DIFFERENT shape (e.g.
   *  {department, value}) — swapping those into staticData blanked the chart,
   *  since none of its configured columns exist on them. Empty rows pass (a
   *  legitimately-empty filter result must still render). */
  private rowsFitClientChart(
    rows: Array<Record<string, unknown>>,
    data: Record<string, unknown>,
    fieldKey: 'category' | 'xField',
  ): boolean {
    if (!Array.isArray(rows) || !rows.length) return true;
    const field = String((data['capabilities'] as Record<string, unknown> | undefined)?.[fieldKey] ?? '');
    return !field || rows.some((row) => field in row);
  }

  /** Pull the `horizontalBar` array out of either envelope shape. Accepts the
   *  raw response, or its `responseList` array, and digs through `data[0]`. */
  private extractHorizontalBarRows(source: unknown): Array<Record<string, unknown>> | null {
    const container = Array.isArray(source) ? (source[0] as Record<string, unknown>) : (source as Record<string, unknown>);
    const dataArr = container?.['data'] as Array<Record<string, unknown>> | undefined;
    const bar = dataArr?.[0]?.['horizontalBar'];
    return Array.isArray(bar) ? (bar as Array<Record<string, unknown>>) : null;
  }

  /** Builds the shared DrillDownComponent's `data` input from an ALREADY-fetched
   *  compute response. Each node's measures arrive nested under `measures`
   *  (keyed by label) — we flatten them onto the node as top-level keys so the
   *  table's per-column props (and its subtotal rows) resolve with a plain
   *  getter, avoiding ngx-datatable's dot-notation deep-getter (which would
   *  choke on measure labels containing spaces/dots). `nameProp: 'label'` tells
   *  the component our nodes label themselves with `label`, not `name`. */
  private buildDrillDownWidgetData(response: Record<string, unknown> | null, format?: Record<string, unknown>): Record<string, unknown> {
    const tree = (response?.['data'] ?? response?.['Data'] ?? []) as Array<Record<string, unknown>>;
    const flattenedTree = this.flattenNodeMeasures(tree);

    // Columns are derived from the RESPONSE, not from the builder inputs — the
    // response's actual depth + measure keys are what must render. (Using the
    // configured dimension/measure labels breaks when they don't match the
    // backend's: e.g. one column for 2 levels of data, or a measure prop that
    // doesn't match the response's measure keys → blank cells.)
    const measureKeys = this.collectMeasureKeys(tree);

    // ONE tree column for the whole hierarchy — every level renders in this
    // same column, indented by depth (indentByLevel), matching the classic
    // Power-BI matrix look. Header name comes from the top-level node's
    // `levelLabel` in the response, falling back to the configured dimension's
    // display name, then a generic label.
    const treeHeader = {
      name: (tree[0]?.['levelLabel'] as string) || this.drillDownDimensions[0]?.label || 'Name',
      prop: 'label',
      expandabile: true,
      indentByLevel: true,
      width: 260,
    };

    // One numeric column per measure key found in the response, keyed by that
    // exact string (matches the flat keys flattenNodeMeasures produced).
    const measureHeaders = measureKeys.map((key) => ({
      name: key,
      prop: key,
      type: 'number',
      width: 130,
    }));

    const decimalsRaw = format?.['decimals'];
    const decimals = decimalsRaw !== undefined && decimalsRaw !== null && decimalsRaw !== '' ? Number(decimalsRaw) : undefined;
    const fmt = format || {};
    return {
      staticData: { data: flattenedTree, total: response?.['total'] ?? response?.['Total'] ?? null },
      nameProp: 'label',
      widgetId: 'dvm-layout-builder-drilldown-preview',
      title: this.title.trim(),
      // Fill the preview pane's full width — `force` distributes columns to the
      // container width instead of `standard`'s fixed sum, so no empty gap on
      // the right (per-column `width` becomes a ratio hint).
      columnMode: 'force',
      tableWidth: '100%',
      headers: [treeHeader, ...measureHeaders],
      // Round-off precision for measure cells + the Total row (Format popup's
      // `decimals`); DrillDownComponent falls back to whole numbers when absent.
      ...(decimals !== undefined && Number.isFinite(decimals) ? { decimals } : {}),
      // ── Visual + reporting capabilities (config-driven; DrillDownComponent
      // reads these off `data`). Per-column visual rules travel in `visualRules`
      // (built from the Format popup / defaults). ──
      // `=== true` like its siblings — with `!== false` this forwarded `true` for
      // every tile, so the component's own default could never take effect.
      enableExpandCollapseAll: fmt['enableExpandCollapseAll'] === true,
      enableExport: fmt['enableExport'] === true,
      enableExcelExport: fmt['enableExcelExport'] === true,
      enablePdfExport: fmt['enablePdfExport'] === true,
      enablePrint: fmt['enablePrint'] === true,
      enableSavedViews: fmt['enableSavedViews'] === true,
      showColumnDividers: fmt['showColumnDividers'] === true,
      visualRules: this.buildDrillDownVisualRules(measureKeys, fmt),
    };
  }

  /** Assemble per-column visual rules (data bar / heat map / conditional / …)
   *  for the drill-down from the Format popup. A single "Apply to all measures"
   *  visual can be chosen (`drillVisualKind`), or left as plain numbers. All
   *  config-driven — no hardcoded columns. */
  private buildDrillDownVisualRules(measureKeys: string[], fmt: Record<string, unknown>): Record<string, unknown> {
    const kind = String(fmt['drillVisualKind'] || '').trim();
    if (!kind || kind === 'none') return {};
    const color = String(fmt['drillVisualColor'] || '#3b82f6');
    const rules: Record<string, unknown> = {};
    measureKeys.forEach((key) => { rules[key] = { kind, color }; });
    return rules;
  }

  // ── Table & Matrix widget data ─────────────────────────────────────────────

  /** Build the `[data]` object for <app-unified-table-matrix-chart>. ONE family,
   *  three types: Drill-Down reuses the drill-down tree widget under
   *  `drilldownData` (the component passes it to <lib-drill-down>); Normal /
   *  Editable get flat rows under `staticData` for the client-side buildTableModel.
   *  All styling/behaviour lives in `capabilities` (config-driven). */
  private buildTableMatrixWidgetData(response: Record<string, unknown> | null, tileId: string, format?: Record<string, unknown>): Record<string, unknown> {
    const capabilities = this.buildTableMatrixCapabilities(format);
    const widgetId = 'dvm-tile-table-matrix-' + tileId;
    if (this.isTmDrilldown) {
      const drilldownData = this.buildDrillDownWidgetData(response, format);
      drilldownData['widgetId'] = widgetId + '-drill';
      return {
        widgetId,
        title: this.title.trim(),
        type: 'table-matrix',
        capabilities,
        drilldownData,
      };
    }
    // Normal / Editable — flat rows from the compute response envelope.
    const rows = this.extractTableMatrixRows(response);
    return {
      widgetId,
      title: this.title.trim(),
      type: 'table-matrix',
      capabilities,
      staticData: rows,
    };
  }

  /** Assemble the config-driven `capabilities` object for the table-matrix tile
   *  from the active table type + the tile's format selections + config defaults.
   *  Every value is config-sourced (nothing baked in). */
  private buildTableMatrixCapabilities(format?: Record<string, unknown>): TableMatrixCapabilities {
    const fmt = format || {};
    const options = (this.layout.options as Record<string, unknown> | undefined) || {};
    const chartDefaults = (options['tableMatrixChartDefaults'] as Record<string, unknown>) || {};
    const typeFlags = this.tmFlagsFor(this.tmType);

    const caps: TableMatrixCapabilities = {
      tableType: this.tmType,
      typeFlags,
      chartDefaults: chartDefaults as TableMatrixCapabilities['chartDefaults'],
      // ── Formatting (from the tile Format popup, config-declared controls) ──
      showValues: fmt['showValues'] !== false,
      numberFormat: fmt['numberFormat'] as string | undefined,
      decimals: fmt['decimals'] != null ? Number(fmt['decimals']) : undefined,
      density: (fmt['density'] as string) || 'comfortable',
      striped: fmt['striped'] !== false,
      headerBackground: fmt['headerBackground'] as string | undefined,
      headerColor: fmt['headerColor'] as string | undefined,
      // ── Columns / interaction ──
      stickyHeader: fmt['stickyHeader'] !== false,
      stickyFooter: fmt['stickyFooter'] === true,
      freezeFirstColumn: fmt['freezeFirstColumn'] === true,
      freezeColumnCount: fmt['freezeColumnCount'] != null ? Number(fmt['freezeColumnCount']) : undefined,
      freezeRowCount: fmt['freezeRowCount'] != null ? Number(fmt['freezeRowCount']) : undefined,
      enableColumnManagement: fmt['enableColumnManagement'] !== false,
      enableColumnReorder: fmt['enableColumnReorder'] === true,
      enableColumnResize: fmt['enableColumnResize'] === true,
      virtualScroll: fmt['virtualScroll'] === true,
      pageSize: fmt['pageSize'] != null ? Number(fmt['pageSize']) : undefined,
      enableSearch: fmt['enableSearch'] !== false,
      enableColumnSearch: fmt['enableColumnSearch'] === true,
      enableAdvancedFilter: fmt['enableAdvancedFilter'] === true,
      enableRefresh: fmt['enableRefresh'] === true,
      enableSavePrefs: fmt['enableSavePrefs'] === true,
      enableExcelExport: fmt['enableExcelExport'] === true,
      enablePdfExport: fmt['enablePdfExport'] === true,
      enablePrint: fmt['enablePrint'] === true,
      rowSelection: (fmt['rowSelection'] as string) || 'single',
      crossFilter: fmt['crossFilter'] === true,
      enableExport: fmt['enableExport'] !== false,
      // ── Totals (Normal) ──
      showSubtotals: fmt['showSubtotals'] === true,
      showGrandTotal: fmt['showGrandTotal'] === true,
      // ── Editable (Application Table) — client-side CRUD + change-tracking ──
      editMode: (fmt['editMode'] as 'cell' | 'row') || 'cell',
      enableRowAdd: fmt['enableRowAdd'] !== false,
      enableRowInsert: fmt['enableRowInsert'] !== false,
      enableRowDelete: fmt['enableRowDelete'] !== false,
      enableSoftDelete: fmt['enableSoftDelete'] === true,
      enableRowDuplicate: fmt['enableRowDuplicate'] !== false,
      enableCopyPaste: fmt['enableCopyPaste'] === true,
      enableUndoRedo: fmt['enableUndoRedo'] !== false,
      showRowState: fmt['showRowState'] !== false,
      enableValidation: fmt['enableValidation'] !== false,
      enableRowSave: fmt['enableRowSave'] === true,
      enableBulkEdit: fmt['enableBulkEdit'] === true,
      enableAutoSave: fmt['enableAutoSave'] === true,
      autoSaveDebounceMs: fmt['autoSaveDebounceMs'] != null ? Number(fmt['autoSaveDebounceMs']) : undefined,
      saveOnRowExit: fmt['saveOnRowExit'] === true,
      // ── Backend CRUD (generic config-driven contract) ──
      saveUrl: (fmt['saveUrl'] as string) || undefined,
      bulkSaveUrl: (fmt['bulkSaveUrl'] as string) || undefined,
      createUrl: (fmt['createUrl'] as string) || undefined,
      updateUrl: (fmt['updateUrl'] as string) || undefined,
      deleteUrl: (fmt['deleteUrl'] as string) || undefined,
      enableOptimisticLock: fmt['enableOptimisticLock'] === true,
      versionField: (fmt['versionField'] as string) || undefined,
      transactional: fmt['transactional'] === true,
      // ── Workflow ──
      enableWorkflow: fmt['enableWorkflow'] === true,
      workflowUrl: (fmt['workflowUrl'] as string) || undefined,
      enableComments: fmt['enableComments'] === true,
      enableAttachments: fmt['enableAttachments'] === true,
      enableAuditTrail: fmt['enableAuditTrail'] === true,
      // ── File upload (file → compute API → table re-renders) ──
      enableUpload: fmt['enableUpload'] === true,
      uploadButtonLabel: (fmt['uploadButtonLabel'] as string) || 'Upload',
      uploadAccept: (fmt['uploadAccept'] as string) || '.xlsx,.xls,.csv',
      uploadUrl: (fmt['uploadUrl'] as string) || undefined,
      // Context so the upload knows which table + where to POST (works in the
      // published view too, where these are baked into the widget capabilities).
      componentId: this.componentId,
      dataset: this.drillDataset || undefined,
      computeUrl: resolveSelfServiceApiUrl(this.layout.apiEndpoints?.generateConfigApi, '/components/compute'),
    };

    // ── Navigation (cell → target dashboard) — from the builder form ──
    // Written whenever navigation is ENABLED (target dashboard optional) so the
    // component shows the button + clickable cells immediately; the button stays
    // disabled until a target is picked (component's canNavigate/navHasTarget).
    if (this.tmNavEnabled) {
      caps.navigation = {
        enabled: true,
        filterKey: this.tmNavFilterKey || undefined,
        navigableColumns: this.tmNavColumns.length ? [...this.tmNavColumns] : undefined,
        targetDashboard: this.tmNavTargetDashboard || undefined,
        targetComponents: this.tmNavTargetComponents.length ? [...this.tmNavTargetComponents] : [],
        buttonLabel: this.tmNavButtonLabel || 'Navigate',
      };
    }

    // Normal / Editable: pick the chosen columns (empty = all); Editable adds a
    // basic per-column control (number/text) inferred from the first row later
    // by the pure builder. Group-by + aggregation apply to Normal only.
    if (!this.isTmDrilldown) {
      const cols = this.tmColumnEntries();
      if (cols.length) {
        caps.columns = cols.map((column) => ({
          field: column.field,
          label: column.displayName,
          ...(this.isTmEditable ? { control: 'text' as const } : {}),
        }));
      }
      if (this.isTmNormal) {
        if (this.tmGroupBy) caps.groupBy = this.tmGroupBy;
        caps.aggregation = (this.tmAggregation as TableMatrixCapabilities['aggregation']) || 'none';
      }
      // ── New shape types map their own per-type config ──
      if (this.isTmMatrix) {
        caps.rowField = this.tmRowField || undefined;
        caps.columnField = this.tmColumnField || undefined;
        caps.valueField = this.tmValueField || undefined;
        caps.aggregation = (this.tmAggregation as TableMatrixCapabilities['aggregation']) || 'sum';
      }
      if (this.isTmPivot) {
        caps.rowFields = this.tmRowFields.length ? [...this.tmRowFields] : undefined;
        caps.columnFields = this.tmColumnFields2.length ? [...this.tmColumnFields2] : undefined;
        caps.valueField = this.tmValueField || undefined;
        caps.aggregation = (this.tmAggregation as TableMatrixCapabilities['aggregation']) || 'sum';
      }
      if (this.isTmGrouped) {
        caps.groupByFields = this.tmGroupByFields.length ? [...this.tmGroupByFields] : undefined;
        caps.aggregation = (this.tmAggregation as TableMatrixCapabilities['aggregation']) || 'sum';
        caps.groupsExpanded = this.tmGroupsExpanded;
      }
      if (this.isTmTreegrid) {
        caps.treeIdField = this.tmTreeIdField || undefined;
        caps.treeParentField = this.tmTreeParentField || undefined;
        caps.groupsExpanded = this.tmGroupsExpanded;
      }
      if (this.isTmFinancial) {
        caps.roleField = this.tmRoleField || undefined;
        caps.indentField = this.tmIndentField || undefined;
      }
    } else {
      // Drill-Down type: surface the dimensions/measures on capabilities too
      // (the tree render uses drilldownData, but keep the model for save/debug).
      caps.dimensions = this.drillDownDimensions.map((d) => ({ field: d.field, label: d.label }));
      caps.measures = this.drillDownMeasures.map((m) => ({
        field: m.field,
        label: m.label,
        aggregation: m.aggregation,
        rollUpStrategy: m.rollUpStrategy,
        total: !!m.total,
      }));
    }
    return caps;
  }

  /** Merge only the Format-popup styling/behaviour keys onto an EXISTING
   *  capabilities object — leaves columns/dimensions/measures/tableType intact.
   *  Used by onTileFormatChange so a format edit never disturbs the tile's data
   *  shape. Every key mirrors buildTableMatrixCapabilities' format reads. */
  private applyTableMatrixFormatToCapabilities(
    caps: Record<string, unknown>,
    format: Record<string, unknown>,
  ): Record<string, unknown> {
    return {
      ...caps,
      showValues: format['showValues'] !== false,
      numberFormat: format['numberFormat'],
      decimals: format['decimals'] != null ? Number(format['decimals']) : caps['decimals'],
      density: (format['density'] as string) || 'comfortable',
      striped: format['striped'] !== false,
      headerBackground: format['headerBackground'],
      headerColor: format['headerColor'],
      stickyHeader: format['stickyHeader'] !== false,
      stickyFooter: format['stickyFooter'] === true,
      freezeFirstColumn: format['freezeFirstColumn'] === true,
      freezeColumnCount: format['freezeColumnCount'] != null ? Number(format['freezeColumnCount']) : caps['freezeColumnCount'],
      freezeRowCount: format['freezeRowCount'] != null ? Number(format['freezeRowCount']) : caps['freezeRowCount'],
      enableColumnManagement: format['enableColumnManagement'] !== false,
      enableColumnReorder: format['enableColumnReorder'] === true,
      enableColumnResize: format['enableColumnResize'] === true,
      virtualScroll: format['virtualScroll'] === true,
      pageSize: format['pageSize'] != null ? Number(format['pageSize']) : caps['pageSize'],
      enableSearch: format['enableSearch'] !== false,
      enableColumnSearch: format['enableColumnSearch'] === true,
      enableAdvancedFilter: format['enableAdvancedFilter'] === true,
      enableRefresh: format['enableRefresh'] === true,
      enableSavePrefs: format['enableSavePrefs'] === true,
      enableExcelExport: format['enableExcelExport'] === true,
      enablePdfExport: format['enablePdfExport'] === true,
      enablePrint: format['enablePrint'] === true,
      rowSelection: (format['rowSelection'] as string) || 'single',
      crossFilter: format['crossFilter'] === true,
      enableExport: format['enableExport'] !== false,
      showSubtotals: format['showSubtotals'] === true,
      showGrandTotal: format['showGrandTotal'] === true,
      groupsExpanded: format['groupsExpanded'] !== false,
      // File upload knobs (componentId/dataset/computeUrl are preserved via ...caps).
      enableUpload: format['enableUpload'] === true,
      uploadButtonLabel: (format['uploadButtonLabel'] as string) || 'Upload',
      uploadAccept: (format['uploadAccept'] as string) || '.xlsx,.xls,.csv',
      uploadUrl: (format['uploadUrl'] as string) || caps['uploadUrl'],
    };
  }

  /** The compute payload for a Normal/Editable table. The backend only computes
   *  tables from non-empty `dimensions` + `measures` arrays (tree response) —
   *  so: numeric columns become measures (with the form's aggregation), the
   *  rest become dimension levels in pick order. Group-by mode collapses the
   *  dimensions to just the group column; raw-rows ('none') mode keeps every
   *  non-numeric column as a level so the tree's leaf paths ARE the table rows.
   *  Labels are the raw field names so the flattened row keys line up with
   *  `capabilities.columns`. */
  private buildTableMatrixComputePayload(): Record<string, unknown> {
    // Normal / Editable now use the FLAT table contract: send the chosen columns
    // as a plain list + tableType so the backend returns flat rows directly (no
    // dimensions/measures tree). Only Drill-Down keeps the hierarchy contract.
    if (!this.isTmDrilldown) {
      return this.buildFlatTableComputePayload();
    }
    return this.buildTreeTableComputePayload();
  }

  /** FLAT payload for a Normal/Editable table: the selected columns (empty = all)
   *  sent as `{dataset, field, displayName}` objects with `tableType`/`subType`
   *  so the backend can join columns across datasets and returns flat rows in
   *  `data[]`. No dimensions/measures. */
  private buildFlatTableComputePayload(): Record<string, unknown> {
    const columns = this.tmColumnEntries();
    // Send the selected columns (empty = all) + everything the table payload
    // carried before (aggregation, chart targets, and the `format` styling blob)
    // so the backend receives the full Normal-table definition + formatting.
    return {
      componentId: this.componentId,
      componentType: 'TABLE',
      tableType: 'NORMAL',
      subType: this.tmType,
      dashboardId: this.dashboardId,
      title: this.title.trim(),
      dataset: this.drillDataset,
      columns,
      ...(this.tmGroupBy ? { groupBy: this.tmGroupBy } : {}),
      aggregation: this.tmAggregation,
      ...this.chartTargetsPayload(),
      ...this.headerFormatPayload(),
    };
  }

  /** TREE payload (dimensions + measures) for the Drill-Down table type. */
  private buildTreeTableComputePayload(): Record<string, unknown> {
    const selected = this.tmColumnFields.filter(Boolean);
    const columns = selected.length ? selected : [...(this.entityColumnsCache.get(this.drillDataset) || [])];
    const rows = this.entityRowsCache.get(this.drillDataset) || [];
    const isNumeric = (field: string): boolean => {
      const sample = rows.find((row) => row[field] != null && row[field] !== '');
      return typeof sample?.[field] === 'number';
    };
    const aggregationMap: Record<string, string> = { sum: 'SUM', avg: 'AVG', count: 'COUNT' };
    const aggregation = aggregationMap[this.tmAggregation] || 'SUM';

    const numericFields = columns.filter(isNumeric);
    let dimensionFields = this.tmGroupBy && this.tmAggregation !== 'none'
      ? [this.tmGroupBy]
      : columns.filter((field) => !numericFields.includes(field));
    let measureFields = numericFields.filter((field) => !dimensionFields.includes(field));
    if (!dimensionFields.length && measureFields.length) {
      dimensionFields = [measureFields[0]];
      measureFields = measureFields.slice(1);
    }
    const measures = measureFields.length
      ? measureFields.map((field) => ({ field, label: field, aggregation, rollUpStrategy: 'SUM', total: false }))
      : [{ field: dimensionFields[dimensionFields.length - 1], label: dimensionFields[dimensionFields.length - 1], aggregation: 'COUNT', rollUpStrategy: 'SUM', total: false }];

    return {
      componentId: this.componentId,
      componentType: 'TABLE',
      tableType: 'TABLE_MATRIX',
      subType: this.tmType,
      dashboardId: this.dashboardId,
      title: this.title.trim(),
      dataset: this.drillDataset,
      dimensions: dimensionFields.map((field, index) => ({ level: index + 1, field, label: field })),
      measures,
      ...this.chartTargetsPayload(),
      ...this.headerFormatPayload(),
    };
  }

  /** Flatten the compute API's dimension tree into flat table rows — one row
   *  per leaf path, dimension values keyed by their `levelLabel` (= raw field
   *  name, see buildTableMatrixComputePayload) plus the leaf's measures.
   *  Dimension values win key collisions (the no-numeric-column COUNT fallback
   *  reuses a dimension field as its measure). */
  private flattenComputeTreeToRows(response: Record<string, unknown> | null): Array<Record<string, unknown>> {
    const tree = (response?.['data'] ?? response?.['Data'] ?? []) as Array<Record<string, unknown>>;
    if (!Array.isArray(tree)) return [];
    const out: Array<Record<string, unknown>> = [];
    const walk = (nodes: Array<Record<string, unknown>>, path: Record<string, unknown>): void => {
      for (const node of nodes || []) {
        const level = { ...path, [String(node['levelLabel'] ?? 'level')]: node['label'] };
        const children = node['children'] as Array<Record<string, unknown>> | undefined;
        if (Array.isArray(children) && children.length) {
          walk(children, level);
        } else {
          out.push({ ...((node['measures'] as Record<string, unknown>) || {}), ...level });
        }
      }
    };
    walk(tree, {});
    return out;
  }

  /** Pull flat rows out of the compute response, trying the common envelope
   *  shapes (responseList[0].data, data, top-level array). */
  private extractTableMatrixRows(response: Record<string, unknown> | null): Array<Record<string, unknown>> {
    if (response == null) return [];
    if (Array.isArray(response)) return response as Array<Record<string, unknown>>;
    const list = (response as { responseList?: Array<Record<string, unknown>> }).responseList;
    const fromList = Array.isArray(list) ? (list[0]?.['data'] as unknown) : undefined;
    const dataField = fromList ?? (response as { data?: unknown }).data ?? (response as { Data?: unknown }).Data;
    if (Array.isArray(dataField)) return dataField as Array<Record<string, unknown>>;
    // A drill-tree response for a Normal table → flatten to its leaf rows.
    return [];
  }

  /** Union of measure keys across every node's `measures` object, in first-seen
   *  order — these become the numeric columns. */
  private collectMeasureKeys(nodes: Array<Record<string, unknown>>): string[] {
    const keys: string[] = [];
    const seen = new Set<string>();
    const walk = (list: Array<Record<string, unknown>>) => {
      (list || []).forEach((node) => {
        const measures = (node['measures'] as Record<string, unknown>) || {};
        Object.keys(measures).forEach((key) => {
          if (!seen.has(key)) {
            seen.add(key);
            keys.push(key);
          }
        });
        if (Array.isArray(node['children'])) walk(node['children'] as Array<Record<string, unknown>>);
      });
    };
    walk(nodes);
    return keys;
  }

  /** Deep-copies the tree, copying each node's `measures` entries up to flat
   *  top-level keys (e.g. node.measures['Dn Price'] → node['Dn Price']) so the
   *  drill-down table can read them with a plain per-column prop. */
  private flattenNodeMeasures(nodes: Array<Record<string, unknown>>): Array<Record<string, unknown>> {
    return (nodes || []).map((node) => {
      const measures = (node['measures'] as Record<string, unknown>) || {};
      const rawChildren = Array.isArray(node['children']) ? (node['children'] as Array<Record<string, unknown>>) : [];
      const children = this.flattenNodeMeasures(rawChildren);
      return {
        ...node,
        ...measures,
        children,
        // Default-expand any node that has children so the preview shows the
        // full hierarchy (with each child's measures) immediately, rather than
        // requiring the user to click every "+". Respect an explicit `expanded`
        // flag from the backend if it sent one.
        expanded: (node['expanded'] as boolean | undefined) ?? children.length > 0,
      };
    });
  }

  private invalidatePreview(): void {
    // Steps/levels changed since the last generate — the current preview no
    // longer reflects the working config. Leave it rendered (still useful
    // context) but the user must click Generate again to refresh it.
  }

  /** `{ targets: [...] }` for a CHART's payload — the component(s) this chart
   *  interacts with, selected in the build form or the tile's Interactions
   *  popup. Empty selection → no key at all (keeps older payloads unchanged). */
  private chartTargetsPayload(): Record<string, unknown> {
    if (!this.selectedTargetCharts.length) return {};
    return {
      targets: this.selectedTargetCharts.map((componentId) => ({
        componentId,
        label: this.targetLabelForId(componentId),
      })),
    };
  }
// PAYLOAD BUILDER FOR DVM LAYOUT 
  /** Card-header colours from the ACTIVE Build-form format state — included in
   *  the compute payload (Generate/Update Configuration) so the backend gets
   *  them alongside the chart definition (live-verified: /components/compute
   *  accepts and ignores the extra `format` key). The published view still
   *  reads them from layout_json — this is an additional, informational copy. */
  private headerFormatPayload(): Record<string, unknown> {
    const source = this.isScatterBubbleLayout ? this.sbFormat : this.barFormat;
    if (!source || !Object.keys(source).length) return {};
    return { format: { ...source } };
  }

  private buildPayload(): Record<string, unknown> {
    if (this.isInputControlLayout) {
      return this.buildInputControlPayload();
    }

    if (this.isDrillDownLayout) {
      return {
        componentId: this.componentId,
        componentType: 'TABLE',
        tableType: this.selectedLayoutType,
        dashboardId: this.dashboardId,
        title: this.title.trim(),
        dataset: this.drillDataset,
        dimensions: this.drillDownDimensions.map((dimension, index) => ({
          level: index + 1,
          field: dimension.field,
          label: dimension.label,
          ...this.valueOrderPayload(dimension),
        })),
        measures: this.drillDownMeasures.map((measure) => ({
          field: measure.field,
          label: measure.label,
          aggregation: measure.aggregation,
          rollUpStrategy: measure.rollUpStrategy,
          total: !!measure.total,
        })),
        ...this.chartTargetsPayload(),
        ...this.headerFormatPayload(),
      };
    }

    if (this.isTableMatrixLayout) {
      // ONE unified family, THREE table types. Drill-Down sends the same
      // dimensions/measures payload as DRILL_DOWN_TABLE (identical compute
      // contract → tree response). Normal/Editable send the chosen columns as
      // flat group-by dimensions so the compute API returns flat rows the
      // client-side buildTableModel can render.
      const base = {
        componentId: this.componentId,
        componentType: 'TABLE',
        // Normal/Editable are the FLAT table contract → tableType 'NORMAL' (matches
        // the compute payload); Drill-Down keeps the hierarchy contract 'TABLE_MATRIX'.
        tableType: this.isTmDrilldown ? 'TABLE_MATRIX' : 'NORMAL',
        subType: this.tmType,
        dashboardId: this.dashboardId,
        title: this.title.trim(),
        dataset: this.drillDataset,
        ...this.chartTargetsPayload(),
        ...this.headerFormatPayload(),
      };
      if (this.isTmDrilldown) {
        return {
          ...base,
          dimensions: this.drillDownDimensions.map((dimension, index) => ({
            level: index + 1,
            field: dimension.field,
            label: dimension.label,
            ...this.valueOrderPayload(dimension),
          })),
          measures: this.drillDownMeasures.map((measure) => ({
            field: measure.field,
            label: measure.label,
            aggregation: measure.aggregation,
            rollUpStrategy: measure.rollUpStrategy,
            total: !!measure.total,
          })),
        };
      }
      // Normal / Editable — flat table. Columns are {dataset, field, displayName}
      // objects (cross-dataset capable); an empty list still means "all columns
      // of the primary dataset".
      const columns = this.tmColumnEntries();
      return {
        ...base,
        columns,
        ...(this.tmGroupBy ? { groupBy: this.tmGroupBy } : {}),
        aggregation: this.tmAggregation,
        ...(this.tmNavEnabled
          ? {
              navigation: {
                enabled: true,
                filterKey: this.tmNavFilterKey,
                navigableColumns: this.tmNavColumns,
                targetDashboard: this.tmNavTargetDashboard,
                targetComponents: this.tmNavTargetComponents,
                buttonLabel: this.tmNavButtonLabel,
              },
            }
          : {}),
      };
    }

    if (this.isHorizontalBarLayout) {
      const hbFormula = this.hbValueFormula.trim();
      // Same [Token] → other-input reference mechanism an input control's
      // target formula uses, keyed by THIS chart's own componentId instead of
      // a target id — lets the bar's value formula reference another
      // chart/input's live output directly, not only when wired up as a target.
      const hbReferences = this.referencesForFormula(this.componentId, hbFormula);
      // Per-group overrides: a group missing here falls back to `hbFormula`.
      const groupFormulaEntries = this.hbGroupFormulas
        .map((entry) => ({ group: entry.group.trim(), formula: entry.formula.trim() }))
        .filter((entry) => entry.group && entry.formula);
      const groupFormulas = groupFormulaEntries.length
        ? Object.fromEntries(groupFormulaEntries.map((entry) => [entry.group, entry.formula]))
        : undefined;
      // Auto-scope the chart to exactly the groups that have their own formula -
      // otherwise a group with neither a group-formula nor a default `hbFormula`
      // (e.g. a "Routing Cost" row the spec doesn't cover) would error out.
      // Skipped when `hbFormula` is also set, since that's a deliberate
      // "everyone else falls back to this" default covering the rest.
      const hbFilters = groupFormulaEntries.length && !hbFormula
        ? [{ dataset: this.drillDataset, field: this.hbGroupByField, operator: 'IN', value: groupFormulaEntries.map((entry) => entry.group) }]
        : undefined;
      return {
        componentId: this.componentId,
        dashboardId: this.dashboardId,
        componentType: 'CHART',
        chartType: this.selectedLayoutType,
        title: this.title.trim(),
        dataset: this.drillDataset,
        groupBy: {
          field: this.hbGroupByField,
          label: this.hbGroupByLabel || this.hbGroupByField,
        },
        value: {
          label: this.hbValueLabel || 'Value',
          ...(hbFormula ? { formula: hbFormula } : {}),
          ...(groupFormulas ? { groupFormulas } : {}),
          ...(hbReferences ? { references: hbReferences } : {}),
        },
        ...this.chartTargetsPayload(),
        ...(hbFilters ? { filters: hbFilters } : {}),
        ...this.headerFormatPayload(),
      };
    }

    if (this.isKpiLayout) {
      const kpiFormula = this.kpiFormula.trim();
      // Same [Token] → other-input reference mechanism an input control's
      // target formula uses, keyed by THIS KPI's own componentId instead of a
      // target id — lets a KPI's formula reference another chart/input's live
      // output directly, not only when the KPI is wired up as an input's target.
      const kpiReferences = this.referencesForFormula(this.componentId, kpiFormula);
      return {
        componentId: this.componentId,
        dashboardId: this.dashboardId,
        componentType: 'CHART',
        chartType: this.selectedLayoutType, // 'KPI'
        title: this.title.trim(),
        dataset: this.drillDataset,
        // A KPI card is a single column aggregated to one value — or, when the
        // user gave a formula, a computed value in place of that (backend
        // prioritizes `formula` over `field`/`aggregation` when both are sent).
        field: this.kpiField,
        aggregation: this.kpiAggregation,
        ...(kpiFormula ? { formula: kpiFormula } : {}),
        ...(kpiReferences ? { references: kpiReferences } : {}),
        ...this.chartTargetsPayload(),
        ...this.headerFormatPayload(),
      };
    }

    if (this.isFileUploadLayout) {
      // A file-upload control: componentType INPUT (drives targets), inputType
      // FILE_UPLOAD. The actual FILE is sent as multipart FormData at upload time
      // (published view / preview), NOT in this JSON descriptor — this just
      // registers the control + its targets.
      return {
        componentId: this.componentId,
        dashboardId: this.dashboardId,
        componentType: 'INPUT',
        inputType: 'FILE_UPLOAD',
        title: this.title.trim(),
        buttonLabel: this.fileUploadButtonLabel.trim() || 'Upload file',
        accept: this.fileUploadAccept.trim() || '.xlsx,.xls,.csv',
        // The compute API requires every INPUT to carry a `value`/`defaultValue`
        // (see "No value provided" error). At GENERATE time no file is picked
        // yet, so register the control with an empty value — the real file is
        // sent as `value` later when the user actually uploads.
        value: '',
        defaultValue: '',
        targets: this.selectedTargetCharts.map((componentId) => ({
          componentId,
          label: this.targetLabelForId(componentId),
        })),
      };
    }

    if (this.isBarLayout) {
      //  BAR computes via the `groupBy` + `value`
      // shape — category → groupBy, first measure → SUM formula. The full
      // `capabilities` object still rides along for the client-side render and
      // Back-to-Builder form restore (the backend ignores it; live-verified
      // 200 with real grouped data).
      const firstBarMeasure = this.barMeasures.find((measure) => !!measure.field);
      const barFormula = this.barValueFormula.trim();
      const barReferences = this.referencesForFormula(this.componentId, barFormula);
      const barGroupFormulaEntries = this.barGroupFormulas
        .map((entry) => ({ group: entry.group.trim(), formula: entry.formula.trim() }))
        .filter((entry) => entry.group && entry.formula);
      const barGroupFormulas = barGroupFormulaEntries.length
        ? Object.fromEntries(barGroupFormulaEntries.map((entry) => [entry.group, entry.formula]))
        : undefined;
      // Scope to the listed categories — without a shared fallback the others
      // have nothing to compute and error the request.
      const barFilters = barGroupFormulaEntries.length && !barFormula
        ? [{ dataset: this.drillDataset, field: this.barCategory, operator: 'IN', value: barGroupFormulaEntries.map((entry) => entry.group) }]
        : undefined;
      const barValueFallback = firstBarMeasure?.field ? `SUM(${firstBarMeasure.field})` : '';
      return {
        componentId: this.componentId,
        dashboardId: this.dashboardId,
        componentType: 'CHART',
        chartType: this.selectedLayoutType, // 'BAR'
        title: this.title.trim(),
        dataset: this.drillDataset,
        groupBy: {
          field: this.barCategory,
          label: this.barCategoryLabel || this.barCategory,
        },
        value: {
          label: this.barValueLabel.trim() || firstBarMeasure?.label || firstBarMeasure?.field || 'Value',
          formula: barFormula || (barGroupFormulas ? '' : barValueFallback),
          ...(barGroupFormulas ? { groupFormulas: barGroupFormulas } : {}),
          ...(barReferences ? { references: barReferences } : {}),
        },
        capabilities: this.buildBarCapabilities() as unknown as Record<string, unknown>,
        ...(barFilters ? { filters: barFilters } : {}),
        ...this.headerFormatPayload(),
      };
    }

    if (this.isLineAreaLayout) {
      const firstLaMeasure = this.laMeasures.find((measure) => !!measure.field);
      return {
        componentId: this.componentId,
        dashboardId: this.dashboardId,
        componentType: 'CHART',
        chartType: this.selectedLayoutType, // 'LINE_AREA'
        title: this.title.trim(),
        dataset: this.drillDataset,
        groupBy: {
          field: this.laCategory,
          label: this.laCategoryLabel || this.laCategory,
        },
        value: {
          label: firstLaMeasure?.label || firstLaMeasure?.field || 'Value',
          formula: firstLaMeasure?.field ? `SUM(${firstLaMeasure.field})` : '',
        },
        capabilities: this.buildLineAreaCapabilities() as unknown as Record<string, unknown>,
        ...this.headerFormatPayload(),
      };
    }

    if (this.isScatterBubbleLayout) {
      return {
        componentId: this.componentId,
        dashboardId: this.dashboardId,
        componentType: 'CHART',
        chartType: this.selectedLayoutType, // 'SCATTER_BUBBLE'
        title: this.title.trim(),
        dataset: this.drillDataset,
        capabilities: this.buildScatterBubbleCapabilities() as unknown as Record<string, unknown>,
        ...this.headerFormatPayload(),
      };
    }

    if (this.isDropdownLayout) {
      const defaultValue = this.dropdownDefaultValue.trim()
        ? this.dropdownDefaultValue.split(',').map((value) => value.trim()).filter(Boolean)
        : [];
      return {
        componentId: this.componentId,
        dashboardId: this.dashboardId,
        componentType: 'INPUT',
        inputType: 'DROPDOWN',
        title: this.title.trim(),
        dataset: this.drillDataset,
        field: this.dropdownField,
        displayLabel: this.dropdownDisplayLabel.trim() || this.title.trim(),
        isMultiSelect: this.dropdownIsMultiSelect,
        displayMode: this.dropdownDisplayMode || 'dropdown',
        defaultValue: this.dropdownIsMultiSelect ? defaultValue : (defaultValue[0] ?? ''),
        value: this.dropdownIsMultiSelect ? defaultValue : (defaultValue.length ? [defaultValue[0]] : []),
        targets: this.selectedTargetCharts.map((componentId) => ({
          componentId,
          label: this.targetLabelForId(componentId),
          field: this.dropdownField,
        })),
      };
    }

    return {
      componentId: this.componentId,
      dashboardId: this.dashboardId,
      componentType: this.componentType,
      chartType: this.selectedLayoutType,
      title: this.title.trim(),
      datasets: [...this.selectedDatasets],
      steps: this.steps.map((step) => this.sanitizeStepForPayload(step)),
      ...this.chartTargetsPayload(),
      ...this.headerFormatPayload(),
    };
  }

  /** Compute-API payload for an input control (INPUT_BOX / SLIDER). The control
   *  captures a title, a formula/measure expression, and the set of dashboard
   *  charts it drives; each `target` carries the target chart's component id +
   *  label. SLIDER additionally sends its numeric bounds under `range`. */
  private buildInputControlPayload(): Record<string, unknown> {
    const payload: Record<string, unknown> = {
      componentId: this.componentId,
      dashboardId: this.dashboardId,
      componentType: 'INPUT',
      inputType: this.selectedLayoutType, // 'INPUT_BOX' | 'SLIDER'
      title: this.title.trim(),
      // A formula may reference a dataset's fields — carry the chosen dataset
      // when one was picked (optional for input controls).
      dataset: this.selectedDatasets[0] || '',
      defaultValue: this.inputDefaultValue,
      // Seed value the tile starts with (the configured default). Once the tile
      // is on the canvas, its live value is pushed via onTileInputValueChange.
      value: this.inputDefaultValue ?? (this.isSliderLayout ? this.sliderMin : null),
      // Every target carries the SAME `formulas` array shape, fed by this
      // control's value — a non-waterfall target sends one entry (no `step`),
      // a WATERFALL target sends one entry PER bridge step/bar (Start, Change,
      // Total are each independently calculated). One key, always an array —
      // no separate "formula" vs "stepFormulas" for the backend to branch on.
      targets: this.selectedTargetCharts.map((value) => ({
        componentId: value,
        label: this.targetLabelForId(value),
        formulas: this.targetFormulasForPayload(value),
      })),
    };

    if (this.isSliderLayout) {
      payload['range'] = {
        min: this.sliderMin,
        max: this.sliderMax,
        step: this.sliderStep,
      };
    }

    return payload;
  }

  private sanitizeStepForPayload(step: BridgeStep): Record<string, unknown> {
    const sanitized: Record<string, unknown> = {
      label: step.label,
      stepType: step.stepType,
    };

    if (step.stepType === 'TOTAL' || ((step.stepType === 'CHANGE' || step.stepType === 'START') && step.formulaMode)) {
      sanitized['formula'] = step.formula;
    } else {
      sanitized['dataset'] = step.dataset;
      sanitized['field'] = step.field;
      sanitized['aggregation'] = step.aggregation;
    }

    // "AUTO" is a UI-only helper meaning "let the database handle sign logic" -
    // strip it from the payload rather than sending a placeholder sign value.
    // Formula-mode CHANGE steps still need this because the formula only computes
    // the value; the direction controls how that value impacts the bridge.
    if (step.stepType === 'CHANGE' && step.sign && step.sign !== 'AUTO') {
      sanitized['sign'] = step.sign;
    }

    return sanitized;
  }

  /**
   * Attaches each step's configured "Direction" to its bar as `colorSign` —
   * a display-only hint the color-picker (see `colorFor` in
   * buildWaterfallOption) can use instead of the bar's own delta sign.
   * Deliberately does NOT touch delta/bottom/top: those are the backend's
   * own computed bridge values, and "Direction" must only ever choose a
   * color, never recompute the numbers or bridge position it's painted on.
   */
  private applyStepSignsToWaterfallBars(bars: WaterfallBar[], payload?: Record<string, unknown>): WaterfallBar[] {
    if (!Array.isArray(bars) || !bars.length) return [];
    const steps = Array.isArray(payload?.['steps']) ? payload['steps'] as Array<Record<string, unknown>> : [];
    if (!steps.length) return bars;

    const normalize = (value: unknown) => String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
    const stepsByLabel = new Map<string, Record<string, unknown>>();
    steps.forEach((step) => {
      const key = normalize(step['label']);
      if (key) stepsByLabel.set(key, step);
    });

    return bars.map((bar, index) => {
      const matchingStep = stepsByLabel.get(normalize(bar.label)) ?? steps[index];
      const sign = String(matchingStep?.['sign'] ?? 'AUTO').toUpperCase();
      return sign === 'AUTO' ? bar : { ...bar, colorSign: sign };
    });
  }

  private buildWaterfallPreviewOption(bars: WaterfallBar[], format?: Record<string, unknown>): echarts.EChartsOption {
    // DUAL-BUILDER rule: the builder preview, the published dashboard AND the
    // waterfall chart component all render through the ONE shared builder in
    // self-service-recompute.utils, so format handling (colors, legend, axes,
    // tooltip, number format) can never drift between preview and published.
    return buildWaterfallOption(bars, format) as echarts.EChartsOption;
  }

  private toAxisLabelRotation(value: unknown, fallback: number): number {
    const rotation = Number(value ?? fallback);
    return Number.isFinite(rotation) ? rotation : fallback;
  }

  private withWaterfallXAxisLabelRotation(option: echarts.EChartsOption | null | undefined, format?: Record<string, unknown>): echarts.EChartsOption | null | undefined {
    if (!option || typeof option !== 'object') return option;
    const rotate = this.toAxisLabelRotation(format?.['xAxisLabelRotation'], 30);
    const patchAxis = (axis: unknown): unknown => ({
      ...((axis as Record<string, unknown> | undefined) || {}),
      axisLabel: {
        ...(((axis as Record<string, unknown> | undefined)?.['axisLabel'] as Record<string, unknown> | undefined) || {}),
        rotate,
      },
    });
    const next = { ...(option as Record<string, unknown>) } as echarts.EChartsOption;
    next.xAxis = Array.isArray(option.xAxis)
      ? option.xAxis.map(patchAxis) as echarts.EChartsOption['xAxis']
      : patchAxis(option.xAxis) as echarts.EChartsOption['xAxis'];
    return next;
  }
  private toKpiNumberFormat(format?: Record<string, unknown>): string {
    const selected = String(format?.['numberFormat'] ?? 'NONE');
    if (selected === 'THOUSANDS') return 'thousands';
    if (selected === 'PERCENT') return 'percent';
    return 'plain';
  }

  private toNumberScale(value: unknown): string {
    switch (String(value ?? 'NONE')) {
      case 'COMPACT': return 'compact';
      case 'SCALE_THOUSANDS': return 'thousands';
      case 'MILLIONS': return 'millions';
      case 'BILLIONS': return 'billions';
      default: return 'none';
    }
  }

  // ── Per-tile Format popup (chart tiles only) ──────────────────────────────

  /** Every tile is formattable EXCEPT Slider / Input Box — those two take no
   *  card-header formatting (by request), in the builder or published view.
   *  Everything else (charts + dropdown + file upload) gets the Format popup. */
  isFormattableTile(tile: DashboardTile): boolean {
    // Every tile gets a Format popup EXCEPT Dropdown/File-upload (no useful
    // card-header/number formatting for those) — Slider/Input Box DO get one
    // (decimals + number-scale on the displayed value), and any new chart
    // type (BAR/SCATTER_BUBBLE/…) is formattable automatically without
    // needing to be listed here.
    // GROUP is included, but its popup holds only the card design controls
    // (appliesTo:["GROUP"] — border / backgrounds / transparency), never the
    // per-chart ones its members already carry. See applicableFormatControls.
    return (
      !!tile &&
      tile.layoutType !== 'DROPDOWN' &&
      tile.layoutType !== 'FILE_UPLOAD'
    );
  }

  /** Format controls that apply to this tile (enabled + matching appliesTo,
   *  deduped by key — chart-specific wins over shared). Table & Matrix tiles are
   *  further gated per sub-type via `visibleWhenType`. */
  formatControlsFor(tile: DashboardTile): FormatControl[] {
    const subType = this.tileTableSubType(tile);
    return this.applicableFormatControls(tile.layoutType)
      .filter((control) => this.controlVisibleForSubType(control, subType));
  }

  /** A TABLE_MATRIX tile's sub-type (normal/editable/drilldown), '' otherwise. */
  private tileTableSubType(tile: DashboardTile): string {
    if (tile.layoutType !== 'TABLE_MATRIX') return '';
    const caps = ((tile.tableMatrixData as Record<string, unknown> | undefined)?.['capabilities'] as Record<string, unknown>) || {};
    return String(caps['tableType'] ?? '');
  }

  /** Preferred accordion section order in the Format popup. Any group a control
   *  names that isn't listed here is appended after these, in first-seen order. */
  /** Default Format accordion section order — fallback when config omits `formatGroupOrder`. */
  private static readonly FORMAT_GROUP_ORDER = ['Title', 'Axis', 'Tooltip', 'Formatting', 'Interaction', 'Advanced'];

  /** Config-driven Format accordion section order (falls back to the default). */
  private get formatGroupOrder(): string[] {
    return this.layout.options?.formatGroupOrder?.length
      ? this.layout.options.formatGroupOrder
      : DvmLayoutBuilderComponent.FORMAT_GROUP_ORDER;
  }

  /** The set of Format-popup accordion sections open for a tile (keyed by tile id
   *  → set of open group names). First group opens by default. */
  private openFormatGroups = new Map<string, Set<string>>();

  /** Group this tile's applicable controls into ordered accordion sections. A
   *  control with no `group` falls into the first configured section (so a flat,
   *  ungrouped config still renders as one open section).
   *  MEMOIZED per tile — the template calls this every CD cycle, and a fresh
   *  array of fresh group objects makes the popup's *ngFor tear down and
   *  recreate every control (incl. appendTo:'body' ng-selects, which re-trigger
   *  CD on rebuild → freeze); see [[project_ngselect_appendtobody_memoize]]. */
  private tileFormatGroupsCache = new Map<string, { key: string; groups: FormatControlGroup[] }>();
  formatGroupsFor(tile: DashboardTile): FormatControlGroup[] {
    // formatControls loads async with the layout config — key on its size so
    // the cache refills once the real control list arrives. Table tiles also key
    // on their sub-type so a regenerated tile's popup regroups its controls.
    const key = `${tile.layoutType}::${this.formatControls.length}::${this.tileTableSubType(tile)}`;
    const cached = this.tileFormatGroupsCache.get(tile.id);
    if (cached && cached.key === key) return cached.groups;
    const groups = this.buildFormatGroupsFor(tile);
    this.tileFormatGroupsCache.set(tile.id, { key, groups });
    return groups;
  }

  private buildFormatGroupsFor(tile: DashboardTile): FormatControlGroup[] {
    const controls = this.formatControlsFor(tile);
    const byGroup = new Map<string, FormatControl[]>();
    const seen: string[] = [];
    controls.forEach((control) => {
      const group = control.group || 'Format';
      if (!byGroup.has(group)) {
        byGroup.set(group, []);
        seen.push(group);
      }
      byGroup.get(group)!.push(control);
    });
    const order = this.formatGroupOrder;
    seen.sort((a, b) => {
      const ia = order.indexOf(a);
      const ib = order.indexOf(b);
      return (ia === -1 ? order.length + seen.indexOf(a) : ia) - (ib === -1 ? order.length + seen.indexOf(b) : ib);
    });
    return seen.map((name) => ({ name, controls: byGroup.get(name)! }));
  }

  /** Whether a Format-popup accordion section is expanded (first one defaults open). */
  isFormatGroupOpen(tile: DashboardTile, group: string, index: number): boolean {
    const set = this.openFormatGroups.get(tile.id);
    if (!set) return index === 0;
    return set.has(group);
  }

  /** Toggle a Format-popup accordion section. */
  toggleFormatGroup(tile: DashboardTile, group: string, index: number): void {
    let set = this.openFormatGroups.get(tile.id);
    if (!set) {
      // Seed with the default-open first section so toggling behaves predictably.
      set = new Set<string>();
      const groups = this.formatGroupsFor(tile);
      if (groups[0]) set.add(groups[0].name);
      this.openFormatGroups.set(tile.id, set);
    }
    if (set.has(group)) set.delete(group);
    else set.add(group);
  }

  toggleTileFormat(tile: DashboardTile, event?: MouseEvent): void {
    if (this.openFormatTileId === tile.id) {
      this.openFormatTileId = null;
      return;
    }
    // The popup template renders only for a tile that HAS a format object, so a
    // tile predating its controls (a GROUP card grouped before the card-design
    // controls existed, or restored from an older published layout) would open
    // nothing at all. Seed it from the configured defaults on first open.
    if (!tile.format) tile.format = this.defaultTileFormat(tile.layoutType);
    this.formatPopupAlign = this.resolveFormatPopupAlign(event);
    this.openFormatTileId = tile.id;
  }

  // ── Per-tile Interactions popup (self-contained; does NOT touch the left
  //    builder form / sidebar). Lets an INPUT control's targets be (re)wired
  //    after other components exist. State is independent of the create-form
  //    fields so opening it never loads the tile into the sidebar. ──────────
  /** The input-control tile whose Interactions popup is open. */
  interactionTile: DashboardTile | null = null;
  /** componentIds this control drives, edited in the popup. */
  interactionTargets: string[] = [];
  /** Non-waterfall target → its single formula. */
  interactionFormulas: Record<string, string> = {};
  /** Waterfall target → { bar/step label → formula }. */
  interactionStepFormulas: Record<string, Record<string, string>> = {};
  /** Target → typed reference rows (token → other-input mapping). */
  interactionReferences: Record<string, TargetFormulaReferenceEntry[]> = {};

  /** Every tile gets the Interactions popup: INPUT controls wire targets WITH
   *  per-target formulas/references; CHART tiles wire plain targets — the
   *  component(s) the chart interacts with (besides filterKey/filterListeners). */
  canEditInteractions(tile: DashboardTile): boolean {
    // Interactions are per-chart (targets & references). A GROUP card is a
    // container — its members each have their own button.
    return !!tile && tile.layoutType !== 'GROUP';
  }

  /** True when the open popup belongs to an input control — only those get the
   *  per-target formula + reference editors; charts just pick their targets. */
  get interactionTileIsInput(): boolean {
    const layoutType = this.interactionTile?.layoutType;
    return layoutType === 'SLIDER' || layoutType === 'INPUT_BOX' || layoutType === 'DROPDOWN';
  }

  /** Open/close the popup. Seeds its OWN state from the tile's persisted
   *  payload — the left builder form is left completely untouched. */
  toggleTileInteractions(tile: DashboardTile, event?: MouseEvent): void {
    event?.stopPropagation();
    if (this.openInteractionTileId === tile.id) {
      this.closeTileInteractions();
      return;
    }
    this.openFormatTileId = null;
    this.formatPopupAlign = this.resolveFormatPopupAlign(event);
    this.seedInteractionState(tile);
    this.interactionTargetOptionsList = this.buildInteractionTargetOptions(tile);
    this.interactionTile = tile;
    this.openInteractionTileId = tile.id;
  }

  closeTileInteractions(): void {
    this.openInteractionTileId = null;
    this.interactionTile = null;
    this.interactionTargets = [];
    this.interactionFormulas = {};
    this.interactionStepFormulas = {};
    this.interactionReferences = {};
    this.interactionTargetOptionsList = [];
  }

  /** Every OTHER tile on the dashboard, as target options for the popup.
   *  Computed ONCE when the popup opens and held in a STABLE array — binding a
   *  method call as [items] hands ng-select a fresh array every change-detection
   *  cycle, which rebuilds its option list mid-click and swallows the selection
   *  (same bug the memoized `targetCharts` getter fixes for the builder form).
   *  Tiles can't change while the modal overlay is open, so once is enough. */
  interactionTargetOptionsList: LabelValueOption[] = [];

  private buildInteractionTargetOptions(tile: DashboardTile): LabelValueOption[] {
    const selfId = String(tile.componentId ?? '');
    const seen = new Set<string>();
    const options: LabelValueOption[] = [];
    this.dashboardTiles.forEach((item) => {
      const value = String(item.componentId ?? '');
      if (!value || value === selfId || seen.has(value)) return;
      seen.add(value);
      const isInput = item.layoutType === 'SLIDER' || item.layoutType === 'INPUT_BOX' || item.layoutType === 'DROPDOWN' || item.layoutType === 'FILE_UPLOAD';
      options.push({ label: (item.title || item.layoutType) + (isInput ? ' (Input)' : ''), value });
    });
    return options;
  }

  interactionStepFormula(id: string, step: string): string {
    return this.interactionStepFormulas[id]?.[step] ?? '';
  }
  setInteractionStepFormula(id: string, step: string, value: string): void {
    const perStep = { ...(this.interactionStepFormulas[id] ?? {}) };
    perStep[step] = value ?? '';
    this.interactionStepFormulas = { ...this.interactionStepFormulas, [id]: perStep };
  }
  interactionRefs(id: string, step?: string): TargetFormulaReferenceEntry[] {
    return (this.interactionReferences[id] || []).filter((entry) => (step ? entry.step === step : !entry.step));
  }
  addInteractionRef(id: string, step?: string): void {
    const rows = [...(this.interactionReferences[id] || []), { token: '', inputTitle: '', targetLabel: '', rowLabel: '', step }];
    this.interactionReferences = { ...this.interactionReferences, [id]: rows };
  }
  removeInteractionRef(id: string, entry: TargetFormulaReferenceEntry): void {
    const rows = (this.interactionReferences[id] || []).filter((item) => item !== entry);
    this.interactionReferences = { ...this.interactionReferences, [id]: rows };
  }

  /** Load the popup's state from a tile's persisted `payload.targets`. */
  private seedInteractionState(tile: DashboardTile): void {
    this.interactionTargets = [];
    this.interactionFormulas = {};
    this.interactionStepFormulas = {};
    this.interactionReferences = {};
    const targets = Array.isArray(tile.payload?.['targets']) ? tile.payload!['targets'] as Array<Record<string, unknown>> : [];
    targets.forEach((target) => {
      const id = String(target?.['componentId'] ?? '').trim();
      if (!id || this.interactionTargets.includes(id)) return;
      this.interactionTargets.push(id);
      const entries = Array.isArray(target['formulas']) ? target['formulas'] as Array<Record<string, unknown>> : null;
      if (entries) {
        const withStep = entries.filter((e) => !!e['step']);
        if (withStep.length) {
          const perStep: Record<string, string> = {};
          withStep.forEach((e) => { perStep[String(e['step'])] = String(e['formula'] ?? ''); });
          this.interactionStepFormulas[id] = perStep;
        } else {
          this.interactionFormulas[id] = String(entries[0]?.['formula'] ?? '');
        }
        const rows: TargetFormulaReferenceEntry[] = [];
        entries.forEach((e) => {
          const refs = e['references'] as Record<string, Record<string, unknown>> | undefined;
          if (!refs || typeof refs !== 'object') return;
          const entryStep = e['step'] ? String(e['step']) : undefined;
          Object.keys(refs).forEach((token) => {
            const r = refs[token] || {};
            rows.push({ token, inputTitle: String(r['inputTitle'] ?? ''), targetLabel: String(r['targetLabel'] ?? ''), rowLabel: String(r['rowLabel'] ?? ''), step: entryStep });
          });
        });
        if (rows.length) this.interactionReferences[id] = rows;
      } else {
        this.interactionFormulas[id] = String(target['formula'] ?? '');
      }
    });
  }

  /** Build one target's `formulas[]` payload entry (matches buildInputControlPayload). */
  private interactionFormulasForPayload(id: string): Array<Record<string, unknown>> {
    const refsFor = (step?: string): Record<string, unknown> | undefined => {
      const entries = (this.interactionReferences[id] || []).filter((e) => (step ? e.step === step : !e.step));
      const map: Record<string, unknown> = {};
      entries.forEach((e) => {
        const token = e.token.trim();
        if (!token) return;
        map[token] = { inputTitle: e.inputTitle.trim(), targetLabel: e.targetLabel.trim(), rowLabel: e.rowLabel.trim() };
      });
      return Object.keys(map).length ? map : undefined;
    };
    if (this.isWaterfallTarget(id)) {
      return this.targetSteps(id)
        .map((step) => {
          const formula = this.interactionStepFormula(id, step.label).trim();
          const references = refsFor(step.label);
          return { step: step.label, formula, ...(references ? { references } : {}) };
        })
        .filter((entry) => !!entry['formula']);
    }
    const formula = (this.interactionFormulas[id] || '').trim();
    // Same pass-through default as targetFormulasForPayload: an input-control
    // target with no formula gets `value`, so it receives the driving value
    // instead of being silently skipped by the recompute.
    if (!formula) return this.isInputControlTargetId(id) ? [{ formula: 'value' }] : [];
    const references = refsFor();
    return [{ formula, ...(references ? { references } : {}) }];
  }

  /** Persist the wired targets onto the tile and recompute them — WITHOUT going
   *  through the left form (so the sidebar stays as-is). */
  saveTileInteractions(): void {
    const tile = this.interactionTile;
    if (!tile) return;
    const isInput = this.interactionTileIsInput;
    // Charts persist plain targets (who they interact with); only input
    // controls carry per-target formulas.
    const targets = this.interactionTargets.map((id) => ({
      componentId: id,
      label: this.targetLabelForId(id),
      ...(isInput ? { formulas: this.interactionFormulasForPayload(id) } : {}),
    }));
    const payload = { ...tile.payload, targets };
    tile.payload = payload;
    if (tile.inputControl) tile.inputControl.targets = [...this.interactionTargets];
    if (tile.dropdownControl) tile.dropdownControl.targets = [...this.interactionTargets];
    this.updateTileInPlace({ ...tile });

    // Recompute the targets so the preview reflects the new wiring immediately
    // (same POST + apply path the live input/dropdown change uses). Only input
    // controls push values into their targets — a chart's target wiring is
    // metadata for its click-interactions, nothing to recompute yet.
    const targetIds = isInput ? targets.map((t) => t.componentId).filter(Boolean) : [];
    if (targetIds.length) {
      const url = resolveSelfServiceApiUrl(this.layout.apiEndpoints?.generateConfigApi, '/api/dvm/self-service/waterfall/generate');
      this.isRecomputing = true;
      this.recomputingComponentIds = new Set(targetIds);
      this.http.post<Record<string, unknown>>(url, payload).subscribe({
        next: (response) => { this.applyRecomputeResults(response, targetIds); this.isRecomputing = false; this.recomputingComponentIds = new Set(); },
        error: () => { this.isRecomputing = false; this.recomputingComponentIds = new Set(); },
      });
    }
    this.ui.showSuccess('Interactions updated.');
    this.closeTileInteractions();
  }

  cancelTileInteractions(): void {
    this.closeTileInteractions();
  }

  private resolveFormatPopupAlign(event?: MouseEvent): 'left' | 'right' {
    const trigger = event?.currentTarget as HTMLElement | null;
    const canvas = this.dashboardCanvasRef?.nativeElement;
    if (!trigger || !canvas) return 'right';
    const triggerRect = trigger.getBoundingClientRect();
    const canvasRect = canvas.getBoundingClientRect();
    const popupWidth = 280;
    const gutter = 12;
    return triggerRect.right - popupWidth < canvasRect.left + gutter ? 'left' : 'right';
  }

  private buildWaterfallTileData(tile: DashboardTile): Record<string, unknown> {
    return {
      type: 'waterfall-chart',
      title: tile.title,
      widgetId: `dvm-tile-waterfall-${tile.id}`,
      height: '100%',
      hideCardHeader: true,
      waterfallBars: tile.waterfallBars,
      option: tile.previewOption,
      format: tile.format,
      // Interaction accordion → the chart component's click handler. The column
      // actually published is re-resolved by onTileCrossFilter from the tile's
      // CURRENT filterKey, so a stale copy here is harmless.
      crossFilter: tile.format?.['crossFilter'] === true,
      highlightOnClick: tile.format?.['highlightOnClick'] === true,
      filterKey: tile.filterKey || undefined,
    };
  }
  /** Re-derive the tile's rendered chart from its stored source data + format. */
  onTileFormatChange(tile: DashboardTile): void {
    const format = tile.format || {};
    if (format['title']) tile.title = format['title'] as string;
    if (tile.layoutType === 'WATERFALL') {
      tile.previewOption = tile.waterfallBars
        ? this.buildWaterfallPreviewOption(tile.waterfallBars, format)
        : this.withWaterfallXAxisLabelRotation(tile.previewOption, format) as echarts.EChartsOption | undefined;
      tile.waterfallData = this.buildWaterfallTileData(tile);
    } else if (tile.layoutType === 'HORIZONTAL_BAR_CHART' && tile.hbData) {
      // The shared bar widget reads a few of these directly; remount so it picks
      // up the new values (title / label visibility / bar color).
      tile.hbData = {
        ...tile.hbData,
        title: (format['title'] as string) || tile.title,
        valueLabelPosition: format['showValues'] === false ? 'none' : 'inside',
        color: format['positiveColor'] || (tile.hbData['color'] as string),
        barColor: format['positiveColor'] || (tile.hbData['barColor'] as string),
        numberFormat: this.toKpiNumberFormat(format),
        numberScale: this.toNumberScale(format['numberFormat']),
        decimals: Number(format['decimals'] ?? 1),
      };
    } else if (tile.layoutType === 'KPI' && tile.kpiData) {
      tile.kpiData = {
        ...tile.kpiData,
        title: (format['title'] as string) || tile.title,
        numberFormat: this.toKpiNumberFormat(format),
        numberScale: this.toNumberScale(format['numberFormat']),
        decimals: Number(format['decimals'] ?? 1),
        prefix: (format['prefix'] as string) || '',
        suffix: (format['suffix'] as string) || '',
      };
    } else if (tile.layoutType === 'DRILL_DOWN_TABLE' && tile.drillData) {
      // Round-off precision on the measure cells + Total row — DrillDownComponent
      // reads `decimals` straight off its data input, so a remount re-renders.
      tile.drillData = {
        ...tile.drillData,
        decimals: Number(format['decimals'] ?? 0),
      };
    } else if (tile.layoutType === 'BAR' && tile.barData) {
      // Fold ALL format sections (Title/Axis/Tooltip/Formatting/Advanced) into the
      // tile's stored capabilities + remount so the chart re-renders. Structural
      // fields (category/measures/series/orientation) are preserved.
      const capabilities = this.applyBarFormatToCapabilities(
        { ...(tile.barData['capabilities'] as unknown as BarColumnCapabilities) },
        format,
      );
      tile.barData = {
        ...tile.barData,
        title: (format['title'] as string) || tile.title,
        capabilities: capabilities as unknown as Record<string, unknown>,
      };
    } else if (tile.layoutType === 'LINE_AREA' && tile.lineAreaData) {
      const capabilities = this.applyLineAreaFormatToCapabilities(
        { ...(tile.lineAreaData['capabilities'] as unknown as LineAreaCapabilities) },
        format,
      );
      tile.lineAreaData = {
        ...tile.lineAreaData,
        title: (format['title'] as string) || tile.title,
        capabilities: capabilities as unknown as Record<string, unknown>,
      };
    } else if (tile.layoutType === 'SCATTER_BUBBLE' && tile.scatterBubbleData) {
      const capabilities = this.applyScatterBubbleFormatToCapabilities(
        { ...(tile.scatterBubbleData['capabilities'] as unknown as ScatterBubbleCapabilities) },
        format,
      );
      tile.scatterBubbleData = {
        ...tile.scatterBubbleData,
        title: (format['title'] as string) || tile.title,
        capabilities: capabilities as unknown as Record<string, unknown>,
      };
    } else if ((tile.layoutType === 'SLIDER' || tile.layoutType === 'INPUT_BOX') && tile.inputControl) {
      const numberFormat = (format['numberFormat'] as string) || 'NONE';
      const decimals = Number(format['decimals'] ?? 1);
      tile.inputControl = {
        ...tile.inputControl,
        widgetData: { ...tile.inputControl.widgetData, numberFormat, decimals },
      };
    } else if (tile.layoutType === 'TABLE_MATRIX' && tile.tableMatrixData) {
      // Merge ONLY the styling/behaviour keys from the (changed) format into the
      // tile's existing capabilities — preserving its own columns / dimensions /
      // measures / tableType (which don't change in the Format popup). This keeps
      // the format edit independent of whatever the builder form currently holds.
      const caps = (tile.tableMatrixData['capabilities'] as Record<string, unknown>) || {};
      const styled = this.applyTableMatrixFormatToCapabilities(caps, format);
      tile.tableMatrixData = {
        ...tile.tableMatrixData,
        title: (format['title'] as string) || tile.title,
        capabilities: styled,
      };
    }
    this.updateTileInPlace({ ...tile });
    this.reflowCharts();
  }

  // ── Cross-filter (bar tile click → filter the OTHER tiles) ─────────────────

  /** A bar tile emitted a cross-filter click. Set/clear the active filter and
   *  re-render the OTHER bar tiles from their rows filtered by column = value.
   *  Clicking the same value again (value === null) clears it. */
  onTileCrossFilter(sourceTile: DashboardTile, event: { column: string; value: string | null; values?: string[] }): void {
    // The published column is the tile's configured filterKey (override → category),
    // falling back to whatever the chart emitted.
    const column = this.effectiveTileFilterKey(sourceTile) || event.column;
    // Multi-select: the chart emits the FULL current selection in `values`. Empty
    // selection → clear the filter. (Older single-value events fall back to value.)
    const values = Array.isArray(event.values)
      ? event.values
      : (event.value ? [event.value] : []);
    if (!values.length) {
      this.activeCrossFilter = null;
    } else {
      this.activeCrossFilter = { column, values, sourceTileId: sourceTile.id };
    }
    // Notify the backend which value was clicked and where the filter applied.
    // Best-effort: filtering stays client-side (instant), so a failed/absent
    // response never blocks the UI. Skipped when the filter is cleared (no values).
    this.postCrossFilterEvent(sourceTile, column, values);
    this.applyCrossFilterToTiles();
  }

  /** Best-effort POST to /{entity}/dynamic_filter describing a cross-filter click:
   *  the column published, the clicked value(s), the source tile, and the tiles
   *  that react. Fire-and-forget — the UI never waits and errors are swallowed
   *  (client-side filtering already happened). Dataset comes from the source
   *  tile's own payload, so it's config-free and generic. */
  private postCrossFilterEvent(sourceTile: DashboardTile, column: string, values: string[]): void {
    if (!values.length) return; // filter cleared — nothing to report
    const dataset = this.tileDatasets(sourceTile)[0] ?? '';
    if (!dataset) return; // no dataset to scope the call to
    const url = buildSelfServiceApiUrl(`/${encodeURIComponent(dataset)}/dynamic_filter`);
    const body = {
      uuid: null,
      // The cross-filter selection: column, clicked value(s), source tile, and the
      // tiles that react. `filters` is the shape a filter-aware backend consumes.
      crossFilter: {
        column,
        values,
        sourceTileId: sourceTile.id,
        listenerTileIds: this.effectiveListenerTileIds(sourceTile),
      },
      filters: [{ column, operator: 'IN', values }],
    };
    this.http.post(url, body).pipe(catchError(() => of(null))).subscribe();
  }

  /** Clear the active cross-filter (chip × button) → restore all tiles' rows. */
  clearCrossFilter(): void {
    if (!this.activeCrossFilter) return;
    this.activeCrossFilter = null;
    this.applyCrossFilterToTiles();
  }

  // ── Generic client-side cross-filter accessors (registry-driven) ──────────

  /** The cross-filter spec for a tile's layoutType, or null when the tile isn't
   *  a client-side cross-filter chart (waterfall/kpi/drill/input don't render
   *  from client rows). Adding a chart type to the registry makes it participate. */
  private crossFilterSpec(tile: DashboardTile): ClientChartCrossFilterSpec | null {
    return CLIENT_CHART_CROSS_FILTER[tile.layoutType] ?? null;
  }

  /** True when a tile can publish/react to cross-filters (is in the registry AND
   *  has its render data + original rows populated). */
  isCrossFilterTile(tile: DashboardTile): boolean {
    const spec = this.crossFilterSpec(tile);
    return !!spec && !!tile[spec.dataField] && Array.isArray(tile[spec.rowsField]);
  }

  /** Tiles whose funnel (listener editor) opens: registry tiles PLUS the
   *  waterfall, which both PUBLISHES (clicked step label) and REACTS via server
   *  recompute (see recomputeWaterfallTile). */
  canConfigureCrossFilter(tile: DashboardTile): boolean {
    return this.isCrossFilterTile(tile) || (tile.layoutType === 'WATERFALL' && !!tile.waterfallData);
  }

  /** A tile's ORIGINAL unfiltered rows (registry-resolved), or []. */
  private tileClientRows(tile: DashboardTile): Array<Record<string, unknown>> {
    const spec = this.crossFilterSpec(tile);
    const rows = spec ? tile[spec.rowsField] : undefined;
    return Array.isArray(rows) ? (rows as Array<Record<string, unknown>>) : [];
  }

  /** A tile's widget `[data]` object (registry-resolved), or null. */
  private tileClientData(tile: DashboardTile): Record<string, unknown> | null {
    const spec = this.crossFilterSpec(tile);
    const data = spec ? tile[spec.dataField] : undefined;
    return data && typeof data === 'object' ? (data as Record<string, unknown>) : null;
  }

  /** Return a copy of `tile` whose rendered rows (its data's `staticData`) are
   *  replaced with `rows`, using the registry field for this chart type. */
  private withTileClientRows(tile: DashboardTile, rows: Array<Record<string, unknown>>): DashboardTile {
    const spec = this.crossFilterSpec(tile);
    const data = this.tileClientData(tile);
    if (!spec || !data) return tile;
    return { ...tile, [spec.dataField]: { ...data, staticData: rows } } as DashboardTile;
  }

  // ── Cross-filter apply (generic across every registered client chart) ──────

  /** Re-render the tiles that REACT to the active cross-filter from their original
   *  rows filtered by column IN values (or restore all rows when cleared). Only
   *  the source tile's configured LISTENERS react; the source keeps its own data.
   *  Works for ANY registered client-side chart (BAR today, Pie/Line/… later).
   *  RECOMPUTE listeners (WATERFALL) can't filter client-side — they re-run their
   *  compute payload with the filter attached (see recomputeWaterfallTile). */
  private applyCrossFilterToTiles(): void {
    const filter = this.activeCrossFilter;
    const sourceTile = filter ? this.dashboardTiles.find((t) => t.id === filter.sourceTileId) : undefined;
    const listeners = sourceTile ? new Set(this.effectiveListenerTileIds(sourceTile)) : new Set<string>();
    const filterValues = filter ? new Set(filter.values) : null;
    this.dashboardTiles = this.dashboardTiles.map((tile) => {
      // Only registered client-side chart tiles with kept rows participate.
      if (!this.isCrossFilterTile(tile)) return tile;
      if (filter && tile.id === filter.sourceTileId) return tile;
      const rows = this.tileClientRows(tile);
      // A tile reacts only if it's in the source's listener set AND actually has
      // the filter column. Non-listeners always show their full rows.
      const reacts = !!filter && listeners.has(tile.id) && rows.some((row) => filter.column in row);
      const nextRows = reacts && filterValues
        ? rows.filter((row) => filterValues.has(String(row[filter!.column] ?? '')))
        : rows;
      return this.withTileClientRows(tile, nextRows);
    });
    // Server-recompute listeners (waterfall bridges): aggregated charts can only
    // react by recomputing on the filtered data. Fire per-tile recomputes for
    // listening waterfalls; restore every other/cleared waterfall to its
    // unfiltered bars (instant, cached — no API call).
    this.dashboardTiles.forEach((tile) => {
      if (!this.isRecomputeCrossFilterTile(tile)) return;
      if (filter && tile.id === filter.sourceTileId) return;
      if (filter && listeners.has(tile.id)) this.recomputeWaterfallTile(tile, filter);
      else this.restoreWaterfallTile(tile);
    });
    this.reflowCharts();
  }

  // ── Dropdown → client-side chart filtering (preview) ───────────────────────

  /** Active dropdown selections, keyed by the dropdown tile's componentId, so
   *  SEVERAL dropdowns compose: each re-apply starts from every registry tile's
   *  ORIGINAL rows and applies all active filters in turn. */
  private activeDropdownFilters = new Map<string, { field: string; values: string[] }>();

  /** Filter the client-side chart family (the cross-filter registry tiles —
   *  bar / line-area / scatter) locally from their cached original rows. A tile
   *  ignores filters whose field its rows don't carry; with no active filters
   *  every registry tile is restored to its full rows. */
  private applyDropdownFiltersToClientTiles(): void {
    // Drop filters whose source dropdown tile no longer exists.
    const liveIds = new Set(this.dashboardTiles.map((t) => String(t.componentId ?? '')));
    [...this.activeDropdownFilters.keys()].forEach((key) => { if (!liveIds.has(key)) this.activeDropdownFilters.delete(key); });
    const filters = [...this.activeDropdownFilters.values()].filter((f) => f.field && f.values.length);
    this.dashboardTiles = this.dashboardTiles.map((tile) => {
      if (!this.isCrossFilterTile(tile)) return tile;
      const original = this.tileClientRows(tile);
      let rows = original;
      filters.forEach(({ field, values }) => {
        if (!original.some((row) => field in row)) return;
        const wanted = new Set(values.map(String));
        rows = rows.filter((row) => wanted.has(String(row[field] ?? '')));
      });
      return this.withTileClientRows(tile, rows);
    });
    this.reflowCharts();
  }

  /** WATERFALL tiles react to cross-filters via SERVER recompute (they render
   *  aggregated bars, not client rows) — needs the compute payload to re-run. */
  private isRecomputeCrossFilterTile(tile: DashboardTile): boolean {
    return tile.layoutType === 'WATERFALL' && !!tile.waterfallBars && !!tile.payload;
  }

  /** Monotonic per-tile sequence so a slow recompute response can never clobber
   *  a newer filter state (or a restore) that happened while it was in flight. */
  private waterfallFilterSeq = new Map<string, number>();

  /** Re-run a waterfall tile's own compute payload with the active cross-filter
   *  attached (`filters: [{column, operator:'IN', values}]`) and redraw from the
   *  response. Best-effort by design: if the backend ignores/errs on `filters`,
   *  the tile simply keeps showing its current bars. */
  private recomputeWaterfallTile(tile: DashboardTile, filter: { column: string; values: string[] }): void {
    const seq = (this.waterfallFilterSeq.get(tile.id) ?? 0) + 1;
    this.waterfallFilterSeq.set(tile.id, seq);
    const url = resolveSelfServiceApiUrl(this.layout.apiEndpoints?.generateConfigApi, '/api/dvm/self-service/waterfall/generate');
    const payload = {
      ...tile.payload,
      filters: [{ column: filter.column, operator: 'IN', values: filter.values }],
    };
    this.http.post<Record<string, unknown>>(url, payload).subscribe({
      next: (response) => {
        if (this.waterfallFilterSeq.get(tile.id) !== seq) return; // stale response
        const rawBars = (Array.isArray(response) ? response : (response as { data?: WaterfallBar[] })?.data || []) as WaterfallBar[];
        if (!rawBars.length) return; // nothing usable — keep the current view
        const bars = this.applyStepSignsToWaterfallBars(rawBars, tile.payload);
        const current = this.dashboardTiles.find((t) => t.id === tile.id);
        if (!current) return;
        const updated: DashboardTile = {
          ...current,
          // Keep the original unfiltered bars ONCE so clearing restores instantly.
          waterfallBarsAll: current.waterfallBarsAll ?? current.waterfallBars,
          waterfallBars: bars,
        };
        updated.previewOption = this.buildWaterfallPreviewOption(bars, updated.format);
        updated.waterfallData = this.buildWaterfallTileData(updated);
        this.dashboardTiles = this.dashboardTiles.map((t) => (t.id === tile.id ? updated : t));
        this.reflowCharts();
      },
      // Best-effort: a failed recompute leaves the tile's current bars untouched.
      error: () => undefined,
    });
  }

  /** Put a waterfall tile back on its unfiltered bars (kept when the first
   *  filtered recompute landed). Also invalidates any in-flight recompute. */
  private restoreWaterfallTile(tile: DashboardTile): void {
    this.waterfallFilterSeq.set(tile.id, (this.waterfallFilterSeq.get(tile.id) ?? 0) + 1);
    if (!tile.waterfallBarsAll) return; // never showed filtered bars
    const restored: DashboardTile = { ...tile, waterfallBars: tile.waterfallBarsAll, waterfallBarsAll: undefined };
    restored.previewOption = this.buildWaterfallPreviewOption(restored.waterfallBars!, restored.format);
    restored.waterfallData = this.buildWaterfallTileData(restored);
    this.dashboardTiles = this.dashboardTiles.map((t) => (t.id === tile.id ? restored : t));
  }

  // ── Cross-filter listener editor (per-tile filterKey + who reacts) ─────────

  /** Every OTHER cross-filter-capable tile that could react to `sourceTile`:
   *  registered client-side charts (filter their cached rows) PLUS recompute
   *  listeners (waterfall — re-runs its compute payload with the filter). KPI/
   *  drill/input tiles still never appear: they have neither rows nor recompute. */
  reactableTilesFor(sourceTile: DashboardTile): DashboardTile[] {
    return this.dashboardTiles.filter(
      (t) =>
        t.id !== sourceTile.id &&
        t.id !== this.previewTileId &&
        (this.isCrossFilterTile(t) || this.isRecomputeCrossFilterTile(t)),
    );
  }

  /** The listener tile ids that actually react: the explicit list when set, else
   *  the default of EVERY other cross-filter-capable tile (so a freshly-added
   *  tile cross-filters out of the box until the user narrows it). */
  private effectiveListenerTileIds(sourceTile: DashboardTile): string[] {
    if (Array.isArray(sourceTile.listenerTileIds)) return sourceTile.listenerTileIds;
    return this.reactableTilesFor(sourceTile).map((t) => t.id);
  }

  /** The filter column a tile publishes: its explicit override, else its category
   *  column (read generically from the tile's widget capabilities) so a click
   *  cross-filters by default. */
  effectiveTileFilterKey(tile: DashboardTile): string {
    if (tile.filterKey && tile.filterKey.trim()) return tile.filterKey.trim();
    const data = this.tileClientData(tile);
    const caps = data?.['capabilities'] as { category?: string } | undefined;
    return caps?.category || '';
  }

  /** The dataset(s) a tile reads — single `dataset` (bar/drill/kpi) or the
   *  multi-select `datasets` (waterfall bridge). */
  private tileDatasets(tile: DashboardTile): string[] {
    const single = String(tile.payload?.['dataset'] ?? '').trim();
    const multi = Array.isArray(tile.payload?.['datasets'])
      ? (tile.payload['datasets'] as unknown[]).map(String).filter(Boolean)
      : [];
    return multi.length ? multi : single ? [single] : [];
  }

  /** Columns a tile can publish a filter on — the union of its dataset(s)' columns.
   *  Memoized per tile — same appendTo:'body' identity-churn hazard as
   *  barColumns/columnsForRow: a fresh array every CD cycle made the listener
   *  editor's filter-key ng-select rebuild its panel mid-click, so selecting a
   *  value silently failed. Stable reference ⇒ clicks stick. */
  private tileFilterColumnsMemo = new Map<
    string,
    { key: string; sources: (string[] | undefined)[]; result: string[] }
  >();
  tileFilterColumns(tile: DashboardTile): string[] {
    const datasets = this.tileDatasets(tile);
    const key = datasets.join('|');
    const sources = datasets.map((dataset) => this.entityColumnsCache.get(dataset));
    const cached = this.tileFilterColumnsMemo.get(tile.id);
    if (cached && cached.key === key && cached.sources.every((source, i) => source === sources[i])) {
      return cached.result;
    }
    const seen = new Set<string>();
    const columns: string[] = [];
    sources.forEach((source) => {
      (source || []).forEach((column) => {
        if (!seen.has(column)) {
          seen.add(column);
          columns.push(column);
        }
      });
    });
    this.tileFilterColumnsMemo.set(tile.id, { key, sources, result: columns });
    return columns;
  }

  /** Open/close the listener editor for a tile (only one open at a time). */
  toggleListenerEditor(tile: DashboardTile, event?: Event): void {
    event?.stopPropagation();
    this.expandedListenerTileId = this.expandedListenerTileId === tile.id ? null : tile.id;
    if (this.expandedListenerTileId === tile.id) {
      // Ensure the tile's dataset columns are loaded for the filter-key dropdown.
      this.tileDatasets(tile).forEach((dataset) => this.loadColumnsForDataset(dataset));
    }
  }

  /** Set/clear the column a tile publishes as its cross-filter key. */
  setTileFilterKey(tile: DashboardTile, value: string): void {
    const trimmed = (value || '').trim();
    this.dashboardTiles = this.dashboardTiles.map((t) =>
      t.id === tile.id ? { ...t, filterKey: trimmed || undefined } : t,
    );
    // A key change invalidates any active filter this tile published.
    if (this.activeCrossFilter?.sourceTileId === tile.id) this.clearCrossFilter();
  }

  /** Is `candidateTileId` currently a listener of `sourceTile`? */
  isTileListener(sourceTile: DashboardTile, candidateTileId: string): boolean {
    return this.effectiveListenerTileIds(sourceTile).includes(candidateTileId);
  }

  /** Toggle a candidate tile in/out of the source tile's listener set. */
  toggleTileListener(sourceTile: DashboardTile, candidateTileId: string): void {
    const current = this.effectiveListenerTileIds(sourceTile);
    const next = current.includes(candidateTileId)
      ? current.filter((id) => id !== candidateTileId)
      : [...current, candidateTileId];
    this.dashboardTiles = this.dashboardTiles.map((t) =>
      t.id === sourceTile.id ? { ...t, listenerTileIds: next } : t,
    );
    // Re-apply so the change is visible immediately if a filter is active.
    if (this.activeCrossFilter?.sourceTileId === sourceTile.id) this.applyCrossFilterToTiles();
  }

  /** Reset a tile's filter config back to defaults (publishes category, all react). */
  resetTileListeners(sourceTile: DashboardTile): void {
    this.dashboardTiles = this.dashboardTiles.map((t) =>
      t.id === sourceTile.id ? { ...t, filterKey: undefined, listenerTileIds: undefined } : t,
    );
    if (this.activeCrossFilter?.sourceTileId === sourceTile.id) this.clearCrossFilter();
  }

  private nextStepId(): string {
    this.stepIdCounter += 1;
    return `bridge-step-${this.stepIdCounter}`;
  }

  private nextDrillDimensionId(): string {
    this.drillDimensionIdCounter += 1;
    return `drill-dimension-${this.drillDimensionIdCounter}`;
  }

  private nextDrillMeasureId(): string {
    this.drillMeasureIdCounter += 1;
    return `drill-measure-${this.drillMeasureIdCounter}`;
  }

  private tmColumnIdCounter = 0;

  private nextTmColumnId(): string {
    this.tmColumnIdCounter += 1;
    return `tm-column-${this.tmColumnIdCounter}`;
  }

  private componentIdCounter = 0;

  /** Unique component id. Optional prefix keeps it human-readable (e.g.
   *  `CMP-KPI-3-482`); counter guarantees uniqueness within a session and the
   *  random suffix avoids collisions with ids restored from a saved dashboard. */
  private createComponentId(prefix = 'CMP'): string {
    this.componentIdCounter += 1;
    return `${prefix}-${this.componentIdCounter}-${Math.floor(100 + Math.random() * 900)}`;
  }

  /** UNIQUE component id per chart (type prefix for readability). Every chart —
   *  including multiple of the same type (e.g. several KPIs) — gets its own id,
   *  so they ALL appear as distinct entries in an input control's target list,
   *  and applyRecomputeResults can match each recompute result back to the ONE
   *  tile it targets (by this id, not just by chart type). */
  private componentIdForLayout(layoutType: string): string {
    const prefix: Record<string, string> = {
      WATERFALL: 'CMP-WATERFALL',
      DRILL_DOWN_TABLE: 'CMP-DRILLDOWN',
      HORIZONTAL_BAR_CHART: 'CMP-HBAR',
      KPI: 'CMP-KPI',
      BAR: 'CMP-BAR',
      SCATTER_BUBBLE: 'CMP-SCATTER',
      LINE_AREA: 'CMP-LINE',
      TABLE_MATRIX: 'CMP-TMATRIX',
      DROPDOWN: 'CMP-DROPDOWN',
      FILE_UPLOAD: 'CMP-FILEUPLOAD',
    };
    return this.createComponentId(prefix[layoutType] || 'CMP');
  }
}






